/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import axios from "../api/axiosInstance"; // con token JWT automático
import "../Reservations.css";
import "./ReservationSettings.css";
import "./FrontDeskSettings.css";
import "./BookingEngineSettings.css";
import "./UsersRolesSettings.css";
import UsersRolesOverview from "./UsersRolesOverview";
import RolePermissionFields from "./RolePermissionFields";
import CorporatePicker from "./admin/CorporatePicker";
import UserHotelAssignments from "./UserHotelAssignments";
import { assignmentsAllowed, roleDraftAllowed, rolePermissionCeiling, userAssignmentRows } from "../utils/hotelManagementOptions";
import { CorporateUserDelegationDialog } from "./CorporateUserDelegation";
import { corporateUserText } from "./corporateUserDelegationMessages";
import { canAccessCorporatePurchasing } from "../corporate-purchasing/corporatePurchasingAccess";
import { roleEditorText } from "./roleEditorMessages";
import useUsersRolesDialog from "./useUsersRolesDialog";
import { getBookingSettingsMessages } from "./bookingSettingsMessages";
import useBookingSettingsDialog from "./useBookingSettingsDialog";
import { FiUsers, FiSliders, FiShield, FiMail, FiBell, FiImage, FiSave, FiCheckCircle, FiAlertCircle, FiClock } from "react-icons/fi";
import PmsThemeSettingsPanel from "../Components/PmsThemeSettingsPanel";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import { getCurrencyMinorUnits } from "../currency/currencyPrecision";
import SettingsExtras from "../Components/SettingsExtras";
import SettingsCharges from "../Components/SettingsCharges";
import SettingsCurrencies from "../Components/SettingsCurrencies";
import { readStoredUser } from "../utils/sessionUser";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode, readDemoUser } from "../demo/demoMode";
import {
  canManageTargetUser,
  canPermanentlyDeleteTargetUser,
  hasUserManagementPermission,
} from "../utils/userManagementAccess";
import {
  RESERVATION_MODIFICATION_ACTION_META,
  RESERVATION_MODIFICATION_SOURCE_META,
  normalizeReservationManualModificationPolicy,
} from "../reservations/reservationModificationPolicy";
import {
  BOOKING_THEME_IDS,
  BOOKING_THEME_SWATCHES,
  normalizeBookingTheme,
} from "../booking/bookingThemes";
import "../booking/bookingThemes.css";
import {
  BOOKING_HERO_BACKGROUND_SCREEN_IDS,
  normalizeBookingHeroBackgroundScreens,
} from "../booking/bookingHeroBackground";
import { validateBookingRoomImageFile } from "../booking/bookingRoomImageRatio";
import { normalizeBookingCardPolicy } from "../booking/bookingCardPolicy";

const DEMO_RESERVATION_SETTING_FIELDS = [
  "max_age_baby", "max_age_child", "min_age_main_guest", "min_age_booker",
  "require_company_booker_contact", "vip_categories", "checkin_time", "checkout_time",
  "clients_require_requested_flow", "group_reservations_default_status",
  "required_customer_fields_reservation", "required_customer_roles_reservation",
  "reservation_manual_modification_policy",
];
const DEMO_FRONT_DESK_SETTING_FIELDS = [
  "timezone", "required_customer_fields_check_in", "required_customer_roles_check_in", "require_full_payment_for_check_in",
];
const DEMO_BOOKING_ENGINE_SETTING_FIELDS = [
  "booking_engine_enabled", "booking_engine_slug", "booking_engine_entry_mode", "booking_engine_theme",
  "booking_engine_hero_background_screens", "booking_engine_languages", "booking_engine_default_language",
  "booking_engine_texts", "booking_engine_terms", "booking_engine_guest_email_templates",
  "booking_engine_price_includes_vat", "booking_engine_price_includes_other_taxes",
  "booking_engine_price_includes_fees", "booking_engine_price_includes_city_tax",
];
const DEMO_SETTINGS_VIEW_PERMISSIONS = [
  "hotel_settings.view_customer_settings", "hotel_settings.view_stay_settings",
  "hotel_settings.view_reservation_flow", "hotel_settings.view_required_fields", "hotel_settings.view_required_roles",
  "hotel_settings.view_front_desk_settings", "hotel_settings.view_extras_tab",
  "hotel_settings.view_reservation_charges_tab",
  "hotel_settings.view_booking_engine_tab",
];
const DEMO_RESERVATION_SETTINGS_MESSAGES = {
  en: { loading: "Loading reservation settings…", update: "Update the demo exporter and JSON to display Reservation Settings.", retry: "Retry", readOnly: "Read-only demo: settings cannot be changed.", noValue: "Not configured" },
  es: { loading: "Cargando configuración de reservas…", update: "Actualiza el exportador y el JSON de la demo para mostrar Reservation Settings.", retry: "Reintentar", readOnly: "Demo de solo consulta: no se puede cambiar la configuración.", noValue: "Sin configurar" },
  ca: { loading: "Carregant configuració de reserves…", update: "Actualitza l'exportador i el JSON de la demo per mostrar Reservation Settings.", retry: "Reintentar", readOnly: "Demo de només consulta: no es pot canviar la configuració.", noValue: "Sense configurar" },
};
const DEMO_FRONT_DESK_SETTINGS_MESSAGES = {
  en: { loading: "Loading Front Desk settings…", update: "Update the demo JSON to display Front Desk Settings." },
  es: { loading: "Cargando configuración de Front Desk…", update: "Actualiza el JSON de la demo para mostrar Front Desk Settings." },
  ca: { loading: "Carregant configuració de Front Desk…", update: "Actualitza el JSON de la demo per mostrar Front Desk Settings." },
};
const DEMO_BOOKING_ENGINE_SETTINGS_MESSAGES = {
  en: { loading: "Loading Booking Engine settings…", update: "Update the demo exporter and JSON to display Booking Engine settings.", media: "Images are not included in this demo. Only their stored status and quantity are shown.", inert: "Read-only demonstration: links and installation code use example.invalid and cannot open or activate a booking engine.", view: "View details", example: "Template preview with generic fictional example values, not a live reservation." },
  es: { loading: "Cargando configuración de Booking Engine…", update: "Actualiza el exportador y el JSON de la demo para mostrar Booking Engine.", media: "Las imágenes no están incluidas en esta demo. Solo se muestran su estado y cantidad guardados.", inert: "Demostración de solo consulta: los enlaces y códigos usan example.invalid y no permiten abrir ni activar un motor de reservas.", view: "Ver detalle", example: "Vista de plantilla con valores de ejemplo ficticios genéricos, no una reserva en vivo." },
  ca: { loading: "Carregant configuració de Booking Engine…", update: "Actualitza l'exportador i el JSON de la demo per mostrar Booking Engine.", media: "Les imatges no estan incloses en aquesta demo. Només se'n mostren l'estat i la quantitat desats.", inert: "Demostració de només consulta: els enllaços i codis utilitzen example.invalid i no permeten obrir ni activar un motor de reserves.", view: "Veure detall", example: "Vista de plantilla amb valors d'exemple ficticis genèrics, no una reserva en viu." },
};

const RESERVATION_ROLE_DEFINITIONS = [
  {
    key: "booker",
    label: "Booker",
    description: "Reservation owner, billing anchor and future outbound email recipient.",
  },
  {
    key: "main_guest",
    label: "Main guest",
    description: "Primary staying guest attached to the room and operational workflow.",
  },
  {
    key: "accompanying",
    label: "Accompanying guest",
    description: "Additional adult guest profiles captured inside Pilcore reservations.",
  },
  {
    key: "child",
    label: "Child guest",
    description: "Minor guest records with independent validation and required fields.",
  },
];

const FRONT_DESK_CHECK_IN_ROLE_DEFINITIONS = [
  {
    key: "booker",
    label: "Booker",
    description: "Person responsible for the booking.",
  },
  {
    key: "main_guest",
    label: "Main guest",
    description: "Primary guest staying in the room.",
  },
  {
    key: "accompanying",
    label: "Accompanying guest",
    description: "Each additional adult staying in the room.",
  },
  {
    key: "child",
    label: "Child guest",
    description: "Each child staying in the room.",
  },
];

const RESERVATION_CUSTOMER_ROLE_KEYS = RESERVATION_ROLE_DEFINITIONS.map((role) => role.key);
const CHECK_IN_CUSTOMER_ROLE_KEYS = FRONT_DESK_CHECK_IN_ROLE_DEFINITIONS.map((role) => role.key);
const HOTEL_TIMEZONE_OPTIONS = [
  { value: "America/Lima", label: "Peru time" },
  { value: "America/Caracas", label: "Venezuela time" },
  { value: "Europe/Madrid", label: "Spain time" },
  { value: "UTC", label: "UTC" },
];

const uniqueRoleList = (roles) =>
  [...new Set((Array.isArray(roles) ? roles : []).map((role) => String(role || "").trim()).filter(Boolean))];

const getRolesWithRequiredFields = (fieldRules, allowedRoles) => {
  const allowed = new Set(allowedRoles);
  if (!fieldRules || typeof fieldRules !== "object" || Array.isArray(fieldRules)) return [];

  return Object.entries(fieldRules)
    .filter(([role, fields]) => allowed.has(role) && Array.isArray(fields) && fields.length > 0)
    .map(([role]) => role);
};

const ensureReservationRolesCoverFieldRules = (roles, fieldRules) => {
  const nextRoles = uniqueRoleList([
    ...(Array.isArray(roles) && roles.length ? roles : ["booker"]),
    ...getRolesWithRequiredFields(fieldRules, RESERVATION_CUSTOMER_ROLE_KEYS),
  ]);

  if (!nextRoles.includes("booker") && !nextRoles.includes("main_guest")) {
    nextRoles.push("booker");
  }

  return nextRoles;
};

const ensureCheckInRolesCoverFieldRules = (roles, fieldRules) =>
  uniqueRoleList([
    "main_guest",
    ...(Array.isArray(roles) ? roles : []),
    ...getRolesWithRequiredFields(fieldRules, CHECK_IN_CUSTOMER_ROLE_KEYS),
  ]);

const GROUP_STATUS_META = {
  Requested: {
    title: "Requested",
    description: "The team reviews the group before moving it to confirmed.",
  },
  Confirmed: {
    title: "Confirmed",
    description: "The group lands directly as confirmed when it is saved.",
  },
};

const RESERVATION_CONFIRMATION_LANGUAGE_OPTIONS = [
  { key: "en", label: "English", shortLabel: "EN" },
  { key: "es", label: "Spanish", shortLabel: "ES" },
];

const RESERVATION_CONFIRMATION_VARIABLES = [
  { label: "Hotel name", token: "{{HotelName}}" },
  { label: "Guest name", token: "{{GuestName}}" },
  { label: "Guest email", token: "{{GuestEmail}}" },
  { label: "Booking reference", token: "{{BookingReference}}" },
  { label: "Check-in date", token: "{{CheckInDate}}" },
  { label: "Check-out date", token: "{{CheckOutDate}}" },
  { label: "Rooms count", token: "{{RoomsCount}}" },
  { label: "Total price", token: "{{TotalPrice}}" },
  { label: "Nightly prices", token: "{{NightlyPrices}}" },
];

const RESERVATION_CONFIRMATION_ASSET_DEFINITIONS = [
  {
    kind: "reservation_email_logo",
    label: "Logo",
    description: "Top brand mark shown in the confirmation email header.",
    emptyLabel: "No logo selected yet",
    variant: "logo",
  },
  {
    kind: "reservation_email_hero",
    label: "Hero image",
    description: "Wide banner image used at the top of the guest email.",
    emptyLabel: "No hero image selected yet",
    variant: "hero",
  },
  {
    kind: "reservation_email_ambient",
    label: "Ambient artwork",
    description: "Secondary decorative visual for the lower part of the email.",
    emptyLabel: "No ambient artwork selected yet",
    variant: "ambient",
  },
];

const RESERVATION_CONFIRMATION_DEFAULT_TEMPLATES = {
  en: {
    subject: "Your stay at {{HotelName}} is confirmed ({{BookingReference}})",
    body: [
      "Hello,",
      "",
      "Thank you for choosing {{HotelName}}. Your reservation {{BookingReference}} is confirmed.",
      "",
      "Stay snapshot",
      "Check-in: {{CheckInDate}}",
      "Check-out: {{CheckOutDate}}",
      "Rooms: {{RoomsCount}}",
      "Total: {{TotalPrice}}",
      "",
      "Nightly prices",
      "{{NightlyPrices}}",
      "",
      "We attach your reservation confirmation in PDF. If you need anything, please contact the hotel.",
    ].join("\n"),
  },
  es: {
    subject: "Tu estancia en {{HotelName}} esta confirmada ({{BookingReference}})",
    body: [
      "Hola,",
      "",
      "Gracias por elegir {{HotelName}}. Tu reserva {{BookingReference}} esta confirmada.",
      "",
      "Resumen de estancia",
      "Entrada: {{CheckInDate}}",
      "Salida: {{CheckOutDate}}",
      "Habitaciones: {{RoomsCount}}",
      "Total: {{TotalPrice}}",
      "",
      "Precios por noche",
      "{{NightlyPrices}}",
      "",
      "Adjuntamos la confirmacion de la reserva en PDF. Si necesitan cualquier cosa, contacten con el hotel.",
    ].join("\n"),
  },
};

const RESERVATION_CONFIRMATION_PREVIEW_VARS = {
  HotelName: "Pilcore Grand Hotel",
  GuestName: "Alex Turner",
  GuestEmail: "alex.turner@example.com",
  BookingReference: "PGH-20481",
  CheckInDate: "2026-07-18",
  CheckOutDate: "2026-07-21",
  RoomsCount: "2",
  TotalPrice: "1248.00",
  NightlyPrices: [
    "Room 1 - Deluxe Double - Flexible Rate",
    "  2026-07-18: EUR 399.00",
    "  2026-07-19: EUR 425.00",
    "",
    "Room 2 - Deluxe Double - Flexible Rate",
    "  2026-07-20: EUR 424.00",
  ].join("\n"),
};

const RESERVATION_CONFIRMATION_PREVIEW_NIGHT_ROWS = [
  { date: "2026-07-18", amount: 399 },
  { date: "2026-07-19", amount: 425 },
  { date: "2026-07-20", amount: 424 },
];

const CRM_DEFAULT_PIPELINE_STAGES = [
  { name: "New", stage_type: "open", color: "#7dd3fc", probability: 10, status: "active", sort_order: 0 },
  { name: "Qualified", stage_type: "open", color: "#60a5fa", probability: 30, status: "active", sort_order: 1 },
  { name: "Proposal", stage_type: "open", color: "#f8c571", probability: 60, status: "active", sort_order: 2 },
  { name: "Won", stage_type: "won", color: "#4ade80", probability: 100, status: "active", sort_order: 3 },
  { name: "Lost", stage_type: "lost", color: "#fb7185", probability: 0, status: "active", sort_order: 4 },
];

function buildEmptyCrmPipelineDraft() {
  return {
    id: null,
    name: "",
    status: "active",
    is_default: false,
    sort_order: 0,
    stages: CRM_DEFAULT_PIPELINE_STAGES.map((stage, index) => ({
      ...stage,
      temp_id: `new-${Date.now()}-${index}`,
    })),
  };
}

function buildCrmPipelineDraft(pipeline) {
  if (!pipeline) return buildEmptyCrmPipelineDraft();
  return {
    id: pipeline.id || null,
    name: pipeline.name || "",
    status: pipeline.status || "active",
    is_default: Boolean(pipeline.is_default),
    sort_order: Number(pipeline.sort_order || 0),
    stages: (pipeline.stages?.length ? pipeline.stages : CRM_DEFAULT_PIPELINE_STAGES).map((stage, index) => ({
      id: stage.id || null,
      temp_id: stage.id ? `stage-${stage.id}` : `new-${Date.now()}-${index}`,
      name: stage.name || "",
      stage_type: stage.stage_type || "open",
      color: stage.color || CRM_DEFAULT_PIPELINE_STAGES[index]?.color || "#7dd3fc",
      probability: stage.probability ?? "",
      status: stage.status || "active",
      sort_order: Number(stage.sort_order ?? index),
    })),
  };
}

function getReservationPreviewRoomCategoryName(roomCategory, index) {
  const rawName =
    roomCategory?.name ||
    roomCategory?.name_en ||
    roomCategory?.name_es ||
    roomCategory?.code ||
    (roomCategory?.id
      ? `Room category #${roomCategory.id}`
      : `Room category ${index + 1}`);

  return String(rawName || "").trim();
}

export function formatSettingsPreviewMoney(value, currencyCode = "EUR", currencyCatalog = []) {
  const amount = Number(value);
  const code = String(currencyCode || "EUR").trim().toUpperCase() || "EUR";
  if (!Number.isFinite(amount)) return `${code} -`;
  return `${code} ${amount.toFixed(getCurrencyMinorUnits(code, currencyCatalog))}`;
}

export function replaceSettingsPreviewCurrencyAmounts(
  value,
  currencyCode = "EUR",
  currencyCatalog = []
) {
  return String(value || "").replace(
    /EUR\s+(-?\d+(?:\.\d+)?)/g,
    (_match, amount) => formatSettingsPreviewMoney(amount, currencyCode, currencyCatalog)
  );
}

function buildReservationConfirmationPreviewRoomVars(
  roomCategories,
  currencyCode = "EUR",
  currencyCatalog = []
) {
  const names = (Array.isArray(roomCategories) ? roomCategories : [])
    .map((roomCategory, index) =>
      getReservationPreviewRoomCategoryName(roomCategory, index)
    )
    .filter(Boolean);

  if (!names.length) {
    return {
      RoomsCount: "0",
      TotalPrice: formatSettingsPreviewMoney(0, currencyCode, currencyCatalog),
      NightlyPrices: "No room categories configured for this business unit yet.",
    };
  }

  const selectedNames = names.slice(0, 2);
  const roomNightGroups =
    selectedNames.length > 1
      ? [
          RESERVATION_CONFIRMATION_PREVIEW_NIGHT_ROWS.slice(0, 2),
          RESERVATION_CONFIRMATION_PREVIEW_NIGHT_ROWS.slice(2),
        ]
      : [RESERVATION_CONFIRMATION_PREVIEW_NIGHT_ROWS];
  const lines = [];
  let total = 0;

  selectedNames.forEach((roomCategoryName, roomIndex) => {
    if (roomIndex > 0) lines.push("");
    lines.push(`Room ${roomIndex + 1} - ${roomCategoryName}`);

    (roomNightGroups[roomIndex] || []).forEach((night) => {
      total += night.amount;
      lines.push(`  ${night.date}: ${formatSettingsPreviewMoney(
        night.amount,
        currencyCode,
        currencyCatalog
      )}`);
    });
  });

  return {
    RoomsCount: String(selectedNames.length),
    TotalPrice: formatSettingsPreviewMoney(total, currencyCode, currencyCatalog),
    NightlyPrices: lines.join("\n"),
  };
}

const EXTERNAL_RESERVATION_NOTIFICATION_EVENT_OPTIONS = [
  { key: "new", label: "New reservation", shortLabel: "New" },
  { key: "modified", label: "Modified reservation", shortLabel: "Modified" },
  { key: "cancelled", label: "Cancelled reservation", shortLabel: "Cancelled" },
];

const EXTERNAL_RESERVATION_NOTIFICATION_VARIABLES = [
  { label: "Event type", token: "{{EventType}}" },
  { label: "Source name", token: "{{SourceName}}" },
  { label: "Hotel name", token: "{{HotelName}}" },
  { label: "Guest name", token: "{{GuestName}}" },
  { label: "Guest email", token: "{{GuestEmail}}" },
  { label: "Booking reference", token: "{{BookingReference}}" },
  { label: "External booking ID", token: "{{ExternalBookingId}}" },
  { label: "OTA locator", token: "{{OtaLocator}}" },
  { label: "Check-in date", token: "{{CheckInDate}}" },
  { label: "Check-out date", token: "{{CheckOutDate}}" },
  { label: "Rooms count", token: "{{RoomsCount}}" },
  { label: "Adults", token: "{{Adults}}" },
  { label: "Children", token: "{{Children}}" },
  { label: "Total price", token: "{{TotalPrice}}" },
  { label: "Special requests", token: "{{SpecialRequests}}" },
  { label: "Cancellation reason", token: "{{CancellationReason}}" },
  { label: "Nightly prices", token: "{{NightlyPrices}}" },
  { label: "Changes summary", token: "{{ChangesSummary}}" },
];

const EXTERNAL_RESERVATION_NOTIFICATION_DEFAULT_TEMPLATES = {
  en: {
    new: {
      subject: "New external reservation received ({{BookingReference}})",
      body: [
        "A new external reservation has reached Pilcore for {{HotelName}}.",
        "",
        "Source: {{SourceName}}",
        "Guest: {{GuestName}}",
        "Reference: {{BookingReference}}",
      ].join("\n"),
    },
    modified: {
      subject: "External reservation updated ({{BookingReference}})",
      body: [
        "An external reservation has been updated in Pilcore for {{HotelName}}.",
        "",
        "Source: {{SourceName}}",
        "Guest: {{GuestName}}",
        "Reference: {{BookingReference}}",
      ].join("\n"),
    },
    cancelled: {
      subject: "External reservation cancelled ({{BookingReference}})",
      body: [
        "An external reservation has been cancelled in Pilcore for {{HotelName}}.",
        "",
        "Source: {{SourceName}}",
        "Guest: {{GuestName}}",
        "Reference: {{BookingReference}}",
      ].join("\n"),
    },
  },
  es: {
    new: {
      subject: "Nueva reserva externa recibida ({{BookingReference}})",
      body: [
        "Ha entrado una nueva reserva externa en Pilcore para {{HotelName}}.",
        "",
        "Origen: {{SourceName}}",
        "Huesped: {{GuestName}}",
        "Referencia: {{BookingReference}}",
      ].join("\n"),
    },
    modified: {
      subject: "Reserva externa modificada ({{BookingReference}})",
      body: [
        "Se ha modificado una reserva externa en Pilcore para {{HotelName}}.",
        "",
        "Origen: {{SourceName}}",
        "Huesped: {{GuestName}}",
        "Referencia: {{BookingReference}}",
      ].join("\n"),
    },
    cancelled: {
      subject: "Reserva externa cancelada ({{BookingReference}})",
      body: [
        "Se ha cancelado una reserva externa en Pilcore para {{HotelName}}.",
        "",
        "Origen: {{SourceName}}",
        "Huesped: {{GuestName}}",
        "Referencia: {{BookingReference}}",
      ].join("\n"),
    },
  },
};

const EXTERNAL_RESERVATION_NOTIFICATION_PREVIEW_VARS = {
  EventType: "External reservation updated",
  SourceName: "Expedia",
  HotelName: "Pilcore Grand Hotel",
  GuestName: "Alex Turner",
  GuestEmail: "alex.turner@example.com",
  BookingReference: "PGH-20481",
  ExternalBookingId: "EXP-9045112",
  OtaLocator: "OTA-77831",
  CheckInDate: "07/19/2026",
  CheckOutDate: "07/22/2026",
  RoomsCount: "2",
  Adults: "3",
  Children: "1",
  TotalPrice: "EUR 1248.00",
  SpecialRequests: "Late arrival after 23:00",
  CancellationReason: "Guest cancelled in OTA",
  NightlyPrices: [
    "Room 1 - Deluxe Double - Flexible Rate",
    "  2026-07-19: EUR 399.00",
    "  2026-07-20: EUR 425.00",
    "",
    "Room 2 - Deluxe Double - Flexible Rate",
    "  2026-07-19: EUR 424.00",
    "  2026-07-20: EUR 424.00",
  ].join("\n"),
  ChangesSummary: [
    "Room 1 - Deluxe Double - Flexible Rate:",
    "  Check-in: 07/18/2026 -> 07/19/2026",
    "  Check-out: 07/21/2026 -> 07/22/2026",
    "  Total: EUR 1120.00 -> EUR 1248.00",
    "  Nightly prices updated",
  ].join("\n"),
};

const BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS = Object.freeze([
  { id: "en", label: "English", shortLabel: "EN" },
  { id: "es", label: "Spanish", shortLabel: "ES" },
]);

const BOOKING_ENGINE_EMAIL_PREVIEW_VARS = {
  ...RESERVATION_CONFIRMATION_PREVIEW_VARS,
  CancelUrl: "https://pilcore.example.com/manage/cancel/PGH-20481",
};

const BOOKING_ENGINE_STUDIO_MESSAGES = {
  en: {
    tabTitle: "Booking Engine",
    header: {
      eyebrow: "Pilcore Booking Studio",
      title: "Booking Engine",
        subtitle:
          "Shape the public booking journey, the commercial storytelling and every guest-facing touchpoint from one premium control room.",
        urlReady: "Public URL ready",
        urlMissing: "Add a slug to publish the direct booking URL.",
        interfaceLanguages: "Studio UI reacts to EN, ES and CA.",
    },
    actions: {
      save: "Save settings",
      saving: "Saving...",
      edit: "Edit",
      close: "Close",
      cancel: "Cancel",
      copyUrl: "Copy URL",
      copyWidget: "Copy widget code",
      copied: "Copied",
      upload: "Upload",
      uploading: "Uploading...",
      remove: "Remove",
      add: "Add",
      addNew: "Add new",
      openEditor: "Open editor",
      saveChanges: "Save changes",
      clearLanguage: "Clear language",
      reset: "Reset",
      manage: "Manage",
      saveAndClose: "Save and close",
      setAsCover: "Set as cover",
      previous: "Previous",
      next: "Next",
      delete: "Delete",
    },
    status: {
      live: "Live",
      disabled: "Disabled",
      heroReady: "Hero ready",
      heroMissing: "Hero pending",
      copyReady: "Text ready",
      copyMissing: "Text pending",
      configured: "Configured",
      notConfigured: "Not configured",
      loading: "Loading...",
      uploaded: "Uploaded",
    },
    stats: {
      categories: "Room categories",
      categoriesFoot: "Commercial cards, galleries and amenities prepared for sale",
      guestEmails: "Guest emails",
      guestEmailsFoot: "Guest-facing confirmation copy available by language",
      terms: "Terms blocks",
      termsFoot: "Published clauses visible inside the booking flow",
      media: "Media assets",
      mediaFoot: "Hero background plus active room-category imagery",
    },
    launch: {
      eyebrow: "Launch controls",
      title: "Public launch and direct booking URL",
      copy:
        "Switch the direct booking flow on, reserve the public slug and keep the canonical URL ready for campaigns, QR codes and the website.",
      enabledLabel: "Enable booking engine for this hotel",
      disabledNote: "When disabled, the public booking path stays hidden even if a slug exists.",
      slugLabel: "Public URL slug",
      slugHint: "Lowercase letters, numbers and dashes only.",
      slugPlaceholder: "hotel-roma",
      urlLabel: "Public booking URL",
      urlEmpty: "Create a slug to generate the final public URL.",
      urlHint: "Use this URL on the hotel website, ads or email signatures.",
      entryModeLabel: "Website integration",
      hostedMode: "Separate booking URL",
      hostedModeCopy: "The hotel website opens the complete booking engine on Pilcore.",
      widgetMode: "Integrated widget",
      widgetModeCopy: "Dates and guests are selected on the hotel website before continuing in Pilcore.",
      widgetCodeLabel: "Widget installation code",
      widgetCodeHint: "Add this code where the booking search should appear on the hotel website.",
      cardPolicyLabel: "Card requirement",
      cardRequired: "Card required (Stripe guarantee)",
      cardRequiredCopy: "Guests securely register a card with Stripe before confirming. This does not charge or hold funds by itself.",
      cardNotRequired: "Book without a card",
      cardNotRequiredCopy: "Confirm after guest details, without a card, payment or card guarantee. Also useful for a hotel demonstration.",
      cardPolicyHint: "Applies only to this hotel's Booking Engine. A saved card is needed for later card charges or holds. Save settings to apply your choice.",
      installGuide: {
        button: "Installation guide",
        eyebrow: "Website installation",
        title: "Install the booking widget",
        subtitle: "Choose the website type and the way the booking search should open. Every example already uses this hotel's slug.",
        beforeTitle: "Before publishing",
        beforeCopy: "Enable the booking engine, save a public slug, select Integrated widget and load the Pilcore script only once per page.",
        inlineTab: "Embedded",
        dropdownTab: "Below a button",
        floatingTab: "Floating",
        wordpressTab: "WordPress",
        inlineTitle: "Visible inside the page",
        inlineCopy: "Place this block exactly where the calendar should appear: hero, booking section or footer.",
        dropdownTitle: "Expandable below Book now",
        dropdownCopy: "This version uses native HTML details, so it opens below the button without an additional library.",
        floatingTitle: "Fixed booking button",
        floatingCopy: "This version stays at the bottom right and opens the calendar above it. On mobile the panel adapts to the screen width.",
        wordpressTitle: "WordPress, Elementor and similar builders",
        wordpressCopy: "Paste the complete block into Custom HTML in Gutenberg, the HTML widget in Elementor or the equivalent code module.",
        wordpressFallback: "If WordPress removes the script tag, load that script once with WPCode or the theme header and leave only the pilcore-booking-widget element in the page block.",
        scriptOnce: "The script tag must appear only once, even when the page has several booking buttons.",
        copyCode: "Copy code",
        copied: "Copied",
      },
    },
    theme: {
      eyebrow: "Visual identity",
      title: "Booking Engine theme",
      copy: "Choose the visual preset for this hotel. Layout, content and booking behaviour stay exactly the same.",
      ariaLabel: "Booking Engine visual theme",
      recommended: "Recommended",
      presets: {
        "pilcore-light": {
          name: "Pilcore Light",
          description: "Clean, modern and technological with cool white surfaces and Pilcore blues.",
        },
        editorial: {
          name: "Editorial",
          description: "Warm, boutique and personal with serif titles and restrained forms.",
        },
        "premium-dark": {
          name: "Premium Dark",
          description: "Exclusive, nocturnal and sophisticated with luminous blue accents.",
        },
      },
    },
    profile: {
      eyebrow: "Guest-facing identity",
      title: "Hotel name and contact",
      copy:
        "Manage the public hotel name and contact details shown during booking and after confirmation.",
      previewTitle: "Where guests see these details",
      previewHint:
        "Address and contact details appear in Booking Summary. The primary phone also powers the mobile call shortcut. The public name and primary contact details appear on confirmation; the public name is reused on cancellation.",
      modalTitle: "Hotel name and contact",
      modalSubtitle:
        "Edit the guest-facing details used in Booking Summary, the mobile call shortcut and post-booking confirmation. The preview separates their current placements.",
      hotelInfoHeading: "Published details",
      identityPreviewLabel: "Confirmation and cancellation",
      contactPreviewLabel: "Booking Summary · Hotel contact",
      hotelName: "Public hotel name",
      addressLine: "Address line",
      city: "City",
      postalCode: "Postal code",
      country: "Country",
      phones: "Phones",
      emails: "Emails",
      hotelNamePreviewLabel: "Hotel name",
      addressPreviewLabel: "Hotel address",
      phonePreviewLabel: "Phone",
      emailPreviewLabel: "Email",
      phonePlaceholder: "Type a phone and press Enter",
      emailPlaceholder: "Type an email and press Enter",
      empty: "No public details configured",
      emptyHotelName: "Public hotel name not configured",
      emptyContact: "Contact details not configured",
    },
    hero: {
      eyebrow: "Booking journey background",
      title: "Hero background by screen",
      copy:
        "Choose exactly where the hotel's hero image appears throughout the Booking Engine.",
      screensLabel: "Show the background on",
      screensHint: "Select every Booking Engine screen where the uploaded image should appear.",
      widgetScreensHint:
        "The stay search happens in the website widget, so the opening screen is not available here.",
      noScreensSelected:
        "No screens selected. Image upload is hidden and any stored image remains inactive.",
      screenLabels: {
        search: "Initial stay search",
        categories: "Rooms and rates",
        extras: "Extras",
        guest_details: "Guest details",
        payment: "Payment",
        confirmation: "Confirmation",
      },
      openingMessageTitle: "Opening-screen message",
      openingMessageHint:
        "This text appears only on the initial stay-search screen of the standalone Booking Engine.",
      backgroundLabel: "Background image",
      backgroundHint: "Recommended: 1920x1080 or larger, landscape orientation.",
      backgroundLocked: "Remove the current image first to upload a new one.",
      noBackground: "No background uploaded yet.",
      previewLabel: "Hero preview",
      subtitleLabel: "Hero subtitle",
      claimLabel: "Hero claim",
      subtitlePlaceholderEn: "Your stay starts here",
      subtitlePlaceholderEs: "Tu estancia empieza aqui",
      claimPlaceholderEn: "Real availability - Best rate guaranteed - No prepayment",
      claimPlaceholderEs: "Disponibilidad real - Mejor precio garantizado - Sin prepago",
      copyHint:
        "Hero text appears on the public Booking Engine. Fill English and Spanish before publishing.",
    },
    pricing: {
      eyebrow: "Price disclosure",
      title: "Taxes, fees and what the guest sees",
      copy:
        "Clarify exactly what the displayed total includes. This reduces friction and avoids unpleasant surprises before payment.",
      summaryTitle: "How the disclosure reads",
      empty: "No inclusions are active yet.",
      totalLabel: "TOTAL (ALL ROOMS)",
      includingPrefix: "Including",
      conjunction: "and",
      noneIncluded: "Not including any taxes or fees",
      allIncluded: "All taxes and fees included",
      vatLabel: "VAT",
      otherTaxesLabel: "other taxes",
      feesLabel: "fees",
      cityTaxLabel: "city tax",
      vat: "Including VAT",
      otherTaxes: "Including other taxes",
      fees: "Including fees",
      cityTax: "Including city tax",
    },
    emails: {
      eyebrow: "Guest email copy",
      title: "Guest confirmation email",
      copy:
        "Keep the direct-booking confirmation message aligned with the hotel voice. Each language opens a richer editor and live preview.",
      editorTitle: "Guest confirmation email",
      editorSubtitle:
        "Compose a clear subject and body, insert smart variables and review the guest-facing result before saving.",
      variablesTitle: "Variables",
      variablesHint: "Insert tokens into the active field. They are replaced when the message is sent.",
      targetSubject: "Subject",
      targetBody: "Body",
      subjectLabel: "Subject",
      bodyLabel: "Body",
      previewTitle: "Preview",
      previewSubject: "Rendered subject",
      previewBody: "Rendered body",
      emptyLanguage: "No guest email has been configured for this language yet.",
      configured: "Configured",
      missing: "Missing",
    },
    terms: {
      eyebrow: "Booking clarity",
      title: "Booking terms and conditions",
      copy:
        "Organize the policy stack into elegant, readable blocks. Guests can scan them quickly without feeling buried in legal text.",
      empty: "No terms have been added yet.",
      notSet: "Not set",
      editorTitle: "Booking terms and conditions",
      editorSubtitle:
        "Tune the order, title and body copy for each clause. Use the preview to check the hierarchy before publishing.",
      sortOrder: "Sort order",
      titleLabel: "Title",
      textLabel: "Text",
      previewTitle: "Preview",
      previewEmpty: "Start typing to preview this term block.",
    },
    rooms: {
      eyebrow: "Sell each category",
      title: "Room-category storytelling",
      copy:
        "Every category should read like a mini landing page: sharp cover image, essential amenities and concise commercial copy that closes the sale.",
      loading: "Loading room categories...",
      empty: "No room categories are available for this business unit.",
      imagesTab: "Images",
      amenitiesTab: "Amenities",
      copyTab: "Commercial text",
      imagesLabel: "Images",
      imageHint: "Choose whether to upload as cover or gallery. Maximum 10 images per category.",
      imageRatioHint:
        "Required format: landscape 3:2 (for example 1500 x 1000 px). Pilcore does not crop images automatically.",
      imageRatioChecking: "Checking image proportions...",
      imageRatioValid: "3:2 image ready to upload.",
      imageRatioInvalid:
        "This image is not landscape 3:2. Choose a 3:2 image, for example 1500 x 1000 px.",
      imageUnreadable: "This image could not be read. Choose another JPG, PNG or WebP file.",
      noImage: "No images yet. Start with a cover that instantly sells the room.",
      uploadCover: "Upload as cover",
      uploadGallery: "Upload to gallery",
      maxReached: "Maximum 10 images reached",
      noFile: "No file selected",
      coverTag: "Cover",
      galleryTag: "Gallery",
      amenitiesTitle: "Room amenities",
      amenitiesLoadError: "Amenities could not be loaded. Please try again before editing.",
      amenitiesHint: "Choose the amenities guests will see for this room category.",
      selectedAmenities: "Selected amenities",
      noAmenities: "No amenities selected yet.",
      searchAmenities: "Search amenities...",
      allCategories: "All categories",
      noAmenitiesMatch: "No amenities match the current filters.",
      selectedTitle: "Selected",
      clearAmenities: "Remove all",
      manageAmenities: "Select amenities",
      saveAmenitiesAndClose: "Save and close",
      copyTitle: "Commercial text",
      copyHint:
        "These texts appear in the public Booking Engine for this room category. Fill English and Spanish so guests see the right name, description, beds, views and highlights.",
      copyLoading: "Loading commercial text...",
      editorTitle: "Commercial text",
      editorSubtitle:
        "Edit the room story by language, keep the shared specs accurate and review the public-facing preview before saving.",
      prefillTitle: "Start with your room details",
      prefillHint:
        "Use the category and its first room to fill both EN and ES with the same information. Available values replace the current fields; you can edit everything before saving.",
      prefillAction: "Prefill",
      prefillLoading: "Retrieving room details…",
      prefillSuccess: "Details from the category and room {room} are ready in EN and ES. Review them, then save your changes.",
      prefillCategoryOnly: "This category has no rooms yet. Its available details are ready in EN and ES; the remaining fields were kept.",
      prefillEmpty: "There are no room details available to prefill. Your text has been kept.",
      prefillError: "Room details could not be retrieved. Your text has been kept. Try again.",
      sharedTitle: "Shared room details",
      sharedHint: "These values stay the same across languages.",
      languageTitle: "Language copy",
      languageHint: "Switch between EN and ES to tune the guest-facing narrative.",
      previewTitle: "Booking Engine preview",
      previewHint: "This is the public-facing room card guests will see for the selected language.",
      previewEmpty: "Add commercial copy to shape this room card.",
      previewLanguage: "Preview language",
      bedTypeLabel: "Bed type",
      viewLabel: "View",
      highlightsLabel: "Highlights",
      nameEn: "Name (EN) *",
      nameEs: "Name (ES) *",
      shortEn: "Short description (EN)",
      shortEs: "Short description (ES)",
      size: "Size (m2)",
      beds: "Beds",
      maxPax: "Max pax",
      bedTypeEn: "Bed type (EN)",
      bedTypeEs: "Bed type (ES)",
      viewEn: "View (EN)",
      viewEs: "View (ES)",
      highlightsEn: "Highlights (EN)",
      highlightsEs: "Highlights (ES)",
      amenityModalTitle: "Select amenities",
      amenityModalSubtitle: "Mark the amenities that should appear for this room category in the Booking Engine.",
      imageModalTitle: "Image preview",
      imageModalSubtitle: "Browse the room gallery and promote any image to cover.",
    },
    toasts: {
      settingsSaved: "Booking Engine settings saved.",
      settingsError: "Booking Engine settings could not be saved.",
      themeNotPersisted: "The server did not confirm the selected Booking Engine theme.",
      cardPolicyNotPersisted: "The server did not confirm the selected card requirement. Your choice has not been confirmed as saved.",
      urlCopied: "Public URL copied to the clipboard.",
      urlCopyError: "The public URL could not be copied.",
      heroUploaded: "Hero background uploaded.",
      heroRemoved: "Hero background removed.",
      heroUploadError: "The hero image could not be uploaded.",
      selectHeroScreenFirst: "Select at least one screen before uploading a background image.",
      profileSaved: "Public hotel info saved.",
      profileError: "Public hotel info could not be saved.",
      roomCopySaved: "Room-category Booking Engine copy saved.",
      roomCopyError: "Room-category Booking Engine copy could not be saved.",
      roomCopyMissingNames: "Name EN and Name ES are required.",
      amenitiesSaved: "Amenities saved for this room category.",
      amenitiesError: "Amenities could not be saved for this room category.",
      imageUploaded: "Image uploaded successfully.",
      imageRemoved: "Image removed successfully.",
      imageError: "The image action failed.",
      coverUpdated: "Cover image updated.",
      profileUrlMissing: "Create a slug first to generate the public URL.",
      pickImageFirst: "Select an image first.",
      heroAlreadyExists: "A hero image already exists. Remove it first.",
    },
  },
  es: {
    tabTitle: "Motor de reservas",
    header: {
      eyebrow: "Pilcore Booking Studio",
      title: "Motor de reservas",
        subtitle:
          "Da forma al viaje publico de reserva, al relato comercial y a cada punto de contacto con el huesped desde una sola cabina premium.",
        urlReady: "URL publica lista",
        urlMissing: "Anade un slug para publicar la URL directa de reservas.",
        interfaceLanguages: "La interfaz del estudio responde a EN, ES y CA.",
    },
    actions: {
      save: "Guardar ajustes",
      saving: "Guardando...",
      edit: "Editar",
      close: "Cerrar",
      cancel: "Cancelar",
      copyUrl: "Copiar URL",
      copyWidget: "Copiar codigo del widget",
      copied: "Copiada",
      upload: "Subir",
      uploading: "Subiendo...",
      remove: "Eliminar",
      add: "Anadir",
      addNew: "Anadir nuevo",
      openEditor: "Abrir editor",
      saveChanges: "Guardar cambios",
      clearLanguage: "Vaciar idioma",
      reset: "Reiniciar",
      manage: "Gestionar",
      saveAndClose: "Guardar y cerrar",
      setAsCover: "Poner como cover",
      previous: "Anterior",
      next: "Siguiente",
      delete: "Borrar",
    },
    status: {
      live: "Activo",
      disabled: "Desactivado",
      heroReady: "Hero listo",
      heroMissing: "Hero pendiente",
      copyReady: "Texto listo",
      copyMissing: "Texto pendiente",
      configured: "Configurado",
      notConfigured: "Sin configurar",
      loading: "Cargando...",
      uploaded: "Subido",
    },
    stats: {
      categories: "Categorias",
      categoriesFoot: "Cards comerciales, galerias y amenities listas para vender",
      guestEmails: "Emails al huesped",
      guestEmailsFoot: "Copy de confirmacion disponible por idioma",
      terms: "Bloques legales",
      termsFoot: "Clausulas visibles dentro del flujo de reserva",
      media: "Recursos visuales",
      mediaFoot: "Hero principal mas imagenes activas por categoria",
    },
    launch: {
      eyebrow: "Control de lanzamiento",
      title: "Salida publica y URL directa",
      copy:
        "Activa el canal directo, reserva el slug publico y deja la URL canonica lista para web, campanas, QR y firmas.",
      enabledLabel: "Activar motor de reservas para este hotel",
      disabledNote: "Si esta apagado, la ruta publica no se mostrara aunque exista slug.",
      slugLabel: "Slug publico",
      slugHint: "Solo minusculas, numeros y guiones.",
      slugPlaceholder: "hotel-roma",
      urlLabel: "URL publica de reservas",
      urlEmpty: "Crea un slug para generar la URL final.",
      urlHint: "Usa esta URL en la web del hotel, anuncios o firmas de email.",
      entryModeLabel: "Integracion con la web",
      hostedMode: "URL de reservas independiente",
      hostedModeCopy: "La web del hotel abre el motor de reservas completo alojado en Pilcore.",
      widgetMode: "Widget integrado",
      widgetModeCopy: "Las fechas y huespedes se eligen en la web del hotel antes de continuar en Pilcore.",
      widgetCodeLabel: "Codigo de instalacion del widget",
      widgetCodeHint: "Anade este codigo donde deba aparecer el buscador de reservas en la web del hotel.",
      cardPolicyLabel: "Requisito de tarjeta",
      cardRequired: "Tarjeta obligatoria (garantía en Stripe)",
      cardRequiredCopy: "El huésped registra su tarjeta de forma segura en Stripe antes de confirmar. Esto no realiza ningún cobro ni retención por sí solo.",
      cardNotRequired: "Reservar sin tarjeta",
      cardNotRequiredCopy: "Confirma tras los datos del huésped, sin tarjeta, pago ni garantía de tarjeta. También resulta útil para una demostración del hotel.",
      cardPolicyHint: "Solo se aplica al motor de reservas de este hotel. Para cobrar o retener después será necesaria una tarjeta registrada. Guarda los ajustes para aplicar la elección.",
      installGuide: {
        button: "Guia de instalacion",
        eyebrow: "Instalacion en la web",
        title: "Instalar el widget de reservas",
        subtitle: "Elige el tipo de web y como debe abrirse el buscador. Cada ejemplo ya utiliza el slug de este hotel.",
        beforeTitle: "Antes de publicar",
        beforeCopy: "Activa el motor, guarda un slug publico, selecciona Widget integrado y carga el script de Pilcore una sola vez por pagina.",
        inlineTab: "Integrado",
        dropdownTab: "Bajo un boton",
        floatingTab: "Flotante",
        wordpressTab: "WordPress",
        inlineTitle: "Visible dentro de la pagina",
        inlineCopy: "Coloca este bloque exactamente donde quieras mostrar el calendario: hero, seccion de reservas o pie de pagina.",
        dropdownTitle: "Desplegable bajo Reservar ahora",
        dropdownCopy: "Esta version usa el elemento nativo details, por lo que se abre bajo el boton sin instalar ninguna libreria adicional.",
        floatingTitle: "Boton de reserva fijo",
        floatingCopy: "Esta version permanece abajo a la derecha y abre el calendario sobre el boton. En movil el panel se adapta al ancho de la pantalla.",
        wordpressTitle: "WordPress, Elementor y constructores similares",
        wordpressCopy: "Pega el bloque completo en HTML personalizado de Gutenberg, el widget HTML de Elementor o el modulo de codigo equivalente.",
        wordpressFallback: "Si WordPress elimina la etiqueta script, carga ese script una sola vez con WPCode o en la cabecera del tema y deja solo el elemento pilcore-booking-widget dentro de la pagina.",
        scriptOnce: "La etiqueta script debe aparecer una sola vez, aunque la pagina tenga varios botones de reserva.",
        copyCode: "Copiar codigo",
        copied: "Copiado",
      },
    },
    theme: {
      eyebrow: "Identidad visual",
      title: "Tema del motor de reservas",
      copy: "Elige el preset visual de este hotel. La estructura, el contenido y el comportamiento de la reserva no cambian.",
      ariaLabel: "Tema visual del motor de reservas",
      recommended: "Recomendado",
      presets: {
        "pilcore-light": {
          name: "Pilcore Claro",
          description: "Limpio, moderno y tecnológico, con superficies blancas frías y azules Pilcore.",
        },
        editorial: {
          name: "Editorial",
          description: "Cálido, boutique y personal, con títulos serif y formas contenidas.",
        },
        "premium-dark": {
          name: "Premium Oscuro",
          description: "Exclusivo, nocturno y sofisticado, con acentos azules luminosos.",
        },
      },
    },
    profile: {
      eyebrow: "Identidad para huéspedes",
      title: "Nombre y contacto del hotel",
      copy:
        "Configura el nombre público y los datos de contacto que se muestran durante la reserva y después de confirmarla.",
      previewTitle: "Dónde aparecen estos datos",
      previewHint:
        "La dirección y los datos de contacto aparecen en el Resumen de la reserva. El primer teléfono activa también el acceso de llamada en móvil. El nombre público y los datos de contacto principales aparecen en la confirmación; el nombre público se reutiliza en la cancelación.",
      modalTitle: "Nombre y contacto del hotel",
      modalSubtitle:
        "Edita los datos visibles para el huésped que se utilizan en el Resumen de la reserva, el acceso de llamada en móvil y la confirmación. La vista previa separa sus ubicaciones actuales.",
      hotelInfoHeading: "Datos publicados",
      identityPreviewLabel: "Confirmación y cancelación",
      contactPreviewLabel: "Resumen de la reserva · Contacto del hotel",
      hotelName: "Nombre público del hotel",
      addressLine: "Dirección",
      city: "Ciudad",
      postalCode: "Código postal",
      country: "País",
      phones: "Teléfonos",
      emails: "Emails",
      hotelNamePreviewLabel: "Nombre del hotel",
      addressPreviewLabel: "Dirección del hotel",
      phonePreviewLabel: "Teléfono",
      emailPreviewLabel: "Email",
      phonePlaceholder: "Escribe un teléfono y pulsa Enter",
      emailPlaceholder: "Escribe un email y pulsa Enter",
      empty: "No hay datos públicos configurados",
      emptyHotelName: "Nombre público del hotel sin configurar",
      emptyContact: "Datos de contacto sin configurar",
    },
    hero: {
      eyebrow: "Fondo del proceso de reserva",
      title: "Fondo hero por pantalla",
      copy:
        "Elige exactamente en qué pantallas del motor de reservas aparecerá la imagen hero del hotel.",
      screensLabel: "Mostrar el fondo en",
      screensHint: "Selecciona todas las pantallas del motor de reservas donde debe aparecer la imagen subida.",
      widgetScreensHint:
        "La búsqueda de estancia se realiza en el widget de la web, por eso la pantalla inicial no está disponible aquí.",
      noScreensSelected:
        "No hay pantallas seleccionadas. La carga de imagen queda oculta y cualquier imagen guardada permanece inactiva.",
      screenLabels: {
        search: "Búsqueda inicial",
        categories: "Habitaciones y tarifas",
        extras: "Extras",
        guest_details: "Datos del huésped",
        payment: "Pago",
        confirmation: "Confirmación",
      },
      openingMessageTitle: "Mensaje de la pantalla inicial",
      openingMessageHint:
        "Este texto aparece solo en la búsqueda inicial del motor de reservas con URL independiente.",
      backgroundLabel: "Imagen de fondo",
      backgroundHint: "Recomendado: 1920x1080 o superior, en horizontal.",
      backgroundLocked: "Elimina primero la imagen actual para poder subir otra.",
      noBackground: "Todavia no hay imagen de fondo.",
      previewLabel: "Preview del hero",
      subtitleLabel: "Subtitulo hero",
      claimLabel: "Claim hero",
      subtitlePlaceholderEn: "Your stay starts here",
      subtitlePlaceholderEs: "Tu estancia empieza aqui",
      claimPlaceholderEn: "Real availability - Best rate guaranteed - No prepayment",
      claimPlaceholderEs: "Disponibilidad real - Mejor precio garantizado - Sin prepago",
      copyHint:
        "El texto hero se muestra en el Booking Engine publico. Completa ingles y español antes de publicar.",
    },
    pricing: {
      eyebrow: "Transparencia de precio",
      title: "Impuestos, tasas y lo que ve el huesped",
      copy:
        "Aclara exactamente que incluye el total mostrado. Reduce friccion y evita sorpresas desagradables antes del pago.",
      summaryTitle: "Asi se leera el desglose",
      empty: "Todavia no hay inclusiones activas.",
      totalLabel: "TOTAL (TODAS LAS HABITACIONES)",
      includingPrefix: "Incluye",
      conjunction: "y",
      noneIncluded: "No incluye impuestos ni tasas",
      allIncluded: "Todos los impuestos y tasas incluidos",
      vatLabel: "IVA",
      otherTaxesLabel: "otros impuestos",
      feesLabel: "tasas",
      cityTaxLabel: "tasa turistica",
      vat: "Incluye IVA",
      otherTaxes: "Incluye otros impuestos",
      fees: "Incluye tasas",
      cityTax: "Incluye tasa turistica",
    },
    emails: {
      eyebrow: "Copy de email",
      title: "Email de confirmacion al huesped",
      copy:
        "Mantiene el mensaje de confirmacion alineado con la voz del hotel. Cada idioma abre un editor rico con preview en vivo.",
      editorTitle: "Email de confirmacion al huesped",
      editorSubtitle:
        "Redacta asunto y cuerpo, inserta variables inteligentes y revisa el resultado antes de guardarlo.",
      variablesTitle: "Variables",
      variablesHint: "Inserta tokens en el campo activo. Se reemplazan al enviar el mensaje.",
      targetSubject: "Asunto",
      targetBody: "Cuerpo",
      subjectLabel: "Asunto",
      bodyLabel: "Cuerpo",
      previewTitle: "Preview",
      previewSubject: "Asunto renderizado",
      previewBody: "Cuerpo renderizado",
      emptyLanguage: "Todavia no se ha configurado este idioma.",
      configured: "Configurado",
      missing: "Pendiente",
    },
    terms: {
      eyebrow: "Claridad legal",
      title: "Terminos y condiciones",
      copy:
        "Organiza las politicas en bloques elegantes y faciles de leer. El huesped debe escanearlas sin sentir una muralla legal.",
      empty: "Todavia no se han anadido terminos.",
      notSet: "Sin definir",
      editorTitle: "Terminos y condiciones",
      editorSubtitle:
        "Ajusta orden, titulo y texto para cada clausula. Usa el preview para revisar jerarquia antes de publicarla.",
      sortOrder: "Orden",
      titleLabel: "Titulo",
      textLabel: "Texto",
      previewTitle: "Preview",
      previewEmpty: "Empieza a escribir para previsualizar este bloque.",
    },
    rooms: {
      eyebrow: "Vender cada categoria",
      title: "Storytelling por categoria",
      copy:
        "Cada categoria debe sentirse como una mini landing page: cover potente, amenities relevantes y copy comercial que cierre la venta.",
      loading: "Cargando categorias...",
      empty: "No hay categorias disponibles para esta unidad de negocio.",
      imagesTab: "Imagenes",
      amenitiesTab: "Servicios",
      copyTab: "Texto comercial",
      imagesLabel: "Imagenes",
      imageHint: "Elige si subir la imagen como cover o a galeria. Maximo 10 imagenes por categoria.",
      imageRatioHint:
        "Formato obligatorio: horizontal 3:2 (por ejemplo, 1500 x 1000 px). Pilcore no recorta las imagenes automaticamente.",
      imageRatioChecking: "Comprobando las proporciones de la imagen...",
      imageRatioValid: "Imagen 3:2 lista para subir.",
      imageRatioInvalid:
        "Esta imagen no es horizontal 3:2. Elige una imagen 3:2, por ejemplo de 1500 x 1000 px.",
      imageUnreadable: "No se ha podido leer esta imagen. Elige otro archivo JPG, PNG o WebP.",
      noImage: "Todavia no hay imagenes. Empieza por un cover que venda la habitacion de un vistazo.",
      uploadCover: "Subir como cover",
      uploadGallery: "Subir a galeria",
      maxReached: "Se alcanzo el maximo de 10 imagenes",
      noFile: "Ningun archivo seleccionado",
      coverTag: "Cover",
      galleryTag: "Galeria",
      amenitiesTitle: "Servicios de la habitacion",
      amenitiesLoadError: "No se pudieron cargar los servicios. Vuelve a intentarlo antes de editar.",
      amenitiesHint: "Elige los servicios que verá el huésped en esta categoria de habitacion.",
      selectedAmenities: "Servicios seleccionados",
      noAmenities: "Todavia no hay servicios seleccionados.",
      searchAmenities: "Buscar servicios...",
      allCategories: "Todas las categorias",
      noAmenitiesMatch: "No hay servicios que coincidan con los filtros.",
      selectedTitle: "Seleccionados",
      clearAmenities: "Quitar todos",
      manageAmenities: "Seleccionar servicios",
      saveAmenitiesAndClose: "Guardar y cerrar",
      copyTitle: "Texto comercial",
      copyHint:
        "Estos textos se muestran en el Booking Engine publico de esta categoria. Completa ingles y español para que el huesped vea nombre, descripcion, camas, vistas y highlights correctos.",
      copyLoading: "Cargando texto comercial...",
      editorTitle: "Texto comercial",
      editorSubtitle:
        "Edita el relato comercial por idioma, manten correctas las especificaciones compartidas y revisa el preview publico antes de guardar.",
      prefillTitle: "Empieza con los datos de tu habitación",
      prefillHint:
        "Usa la categoría y su primera habitación para rellenar EN y ES con la misma información. Los datos disponibles reemplazan los campos actuales; podrás editarlo todo antes de guardar.",
      prefillAction: "Prerrellenar",
      prefillLoading: "Recuperando datos de la habitación…",
      prefillSuccess: "Los datos de la categoría y la habitación {room} están listos en EN y ES. Revísalos y guarda los cambios.",
      prefillCategoryOnly: "Esta categoría todavía no tiene habitaciones. Sus datos disponibles están listos en EN y ES; los demás campos se han conservado.",
      prefillEmpty: "No hay datos disponibles para prerrellenar. Se han conservado tus textos.",
      prefillError: "No se han podido recuperar los datos. Se han conservado tus textos. Vuelve a intentarlo.",
      sharedTitle: "Detalles compartidos",
      sharedHint: "Estos valores se mantienen iguales en todos los idiomas.",
      languageTitle: "Copy por idioma",
      languageHint: "Cambia entre EN y ES para ajustar el relato que vera el huesped.",
      previewTitle: "Preview Booking Engine",
      previewHint: "Asi se vera la card publica de la habitacion para el idioma seleccionado.",
      previewEmpty: "Anade copy comercial para dar forma a esta card.",
      previewLanguage: "Idioma del preview",
      bedTypeLabel: "Tipo de cama",
      viewLabel: "Vista",
      highlightsLabel: "Highlights",
      nameEn: "Nombre (EN) *",
      nameEs: "Nombre (ES) *",
      shortEn: "Descripcion corta (EN)",
      shortEs: "Descripcion corta (ES)",
      size: "Tamano (m2)",
      beds: "Camas",
      maxPax: "Max pax",
      bedTypeEn: "Tipo de cama (EN)",
      bedTypeEs: "Tipo de cama (ES)",
      viewEn: "Vista (EN)",
      viewEs: "Vista (ES)",
      highlightsEn: "Highlights (EN)",
      highlightsEs: "Highlights (ES)",
      amenityModalTitle: "Seleccionar servicios",
      amenityModalSubtitle: "Marca los servicios que se mostraran para esta categoria en el Booking Engine.",
      imageModalTitle: "Preview de imagen",
      imageModalSubtitle: "Recorre la galeria y promociona cualquier imagen a cover.",
    },
    toasts: {
      settingsSaved: "Ajustes del Booking Engine guardados.",
      settingsError: "No se pudieron guardar los ajustes del Booking Engine.",
      themeNotPersisted: "El servidor no confirmó el tema seleccionado para el Booking Engine.",
      cardPolicyNotPersisted: "El servidor no confirmó el requisito de tarjeta seleccionado. No se ha confirmado que tu elección se haya guardado.",
      urlCopied: "URL publica copiada al portapapeles.",
      urlCopyError: "No se pudo copiar la URL publica.",
      heroUploaded: "Hero subido correctamente.",
      heroRemoved: "Hero eliminado.",
      heroUploadError: "No se pudo subir la imagen hero.",
      selectHeroScreenFirst: "Selecciona al menos una pantalla antes de subir una imagen de fondo.",
      profileSaved: "Informacion publica guardada.",
      profileError: "No se pudo guardar la informacion publica.",
      roomCopySaved: "Copy de categoria guardado.",
      roomCopyError: "No se pudo guardar el copy de categoria.",
      roomCopyMissingNames: "Nombre EN y Nombre ES son obligatorios.",
      amenitiesSaved: "Amenities guardadas para esta categoria.",
      amenitiesError: "No se pudieron guardar las amenities de esta categoria.",
      imageUploaded: "Imagen subida correctamente.",
      imageRemoved: "Imagen eliminada.",
      imageError: "La accion sobre la imagen ha fallado.",
      coverUpdated: "La imagen cover se ha actualizado.",
      profileUrlMissing: "Crea primero un slug para generar la URL publica.",
      pickImageFirst: "Selecciona primero una imagen.",
      heroAlreadyExists: "Ya existe una imagen hero. Eliminala antes de subir otra.",
    },
  },
  ca: {
    tabTitle: "Motor de reserves",
    header: {
      eyebrow: "Pilcore Booking Studio",
      title: "Motor de reserves",
        subtitle:
          "Dona forma al viatge public de reserva, al relat comercial i a cada punt de contacte amb l'hoste des d'una sola cabina premium.",
        urlReady: "URL publica llesta",
        urlMissing: "Afegeix un slug per publicar la URL directa de reserves.",
        interfaceLanguages: "La interfice de l'estudi respon a EN, ES i CA.",
    },
    actions: {
      save: "Desar ajustos",
      saving: "Desant...",
      edit: "Editar",
      close: "Tancar",
      cancel: "Cancelar",
      copyUrl: "Copiar URL",
      copyWidget: "Copiar codi del widget",
      copied: "Copiada",
      upload: "Pujar",
      uploading: "Pujant...",
      remove: "Eliminar",
      add: "Afegir",
      addNew: "Afegir nou",
      openEditor: "Obrir editor",
      saveChanges: "Desar canvis",
      clearLanguage: "Buidar idioma",
      reset: "Reiniciar",
      manage: "Gestionar",
      saveAndClose: "Desar i tancar",
      setAsCover: "Posar com a cover",
      previous: "Anterior",
      next: "Seguent",
      delete: "Esborrar",
    },
    status: {
      live: "Actiu",
      disabled: "Desactivat",
      heroReady: "Hero llest",
      heroMissing: "Hero pendent",
      copyReady: "Text llest",
      copyMissing: "Text pendent",
      configured: "Configurat",
      notConfigured: "Sense configurar",
      loading: "Carregant...",
      uploaded: "Pujat",
    },
    stats: {
      categories: "Categories",
      categoriesFoot: "Cards comercials, galeries i amenities preparades per vendre",
      guestEmails: "Emails a l'hoste",
      guestEmailsFoot: "Copy de confirmacio disponible per idioma",
      terms: "Blocs legals",
      termsFoot: "Clauses visibles dins del flux de reserva",
      media: "Actius visuals",
      mediaFoot: "Hero principal mes imatges actives per categoria",
    },
    launch: {
      eyebrow: "Control de llançament",
      title: "Sortida publica i URL directa",
      copy:
        "Activa el canal directe, reserva el slug public i deixa la URL canonica llesta per a web, campanyes, QR i signatures.",
      enabledLabel: "Activar el motor de reserves per a aquest hotel",
      disabledNote: "Si esta apagat, la ruta publica no es mostrara encara que existeixi slug.",
      slugLabel: "Slug public",
      slugHint: "Nomes minuscules, numeros i guions.",
      slugPlaceholder: "hotel-roma",
      urlLabel: "URL publica de reserves",
      urlEmpty: "Crea un slug per generar la URL final.",
      urlHint: "Utilitza aquesta URL al web de l'hotel, anuncis o signatures d'email.",
      entryModeLabel: "Integracio amb el web",
      hostedMode: "URL de reserves independent",
      hostedModeCopy: "El web de l'hotel obre el motor de reserves complet allotjat a Pilcore.",
      widgetMode: "Widget integrat",
      widgetModeCopy: "Les dates i els hostes es trien al web de l'hotel abans de continuar a Pilcore.",
      widgetCodeLabel: "Codi d'instal·lacio del widget",
      widgetCodeHint: "Afegeix aquest codi on hagi d'apareixer el cercador de reserves al web de l'hotel.",
      cardPolicyLabel: "Requisit de targeta",
      cardRequired: "Targeta obligatòria (garantia a Stripe)",
      cardRequiredCopy: "L'hoste registra la targeta de manera segura a Stripe abans de confirmar. Això no fa cap cobrament ni retenció per si sol.",
      cardNotRequired: "Reservar sense targeta",
      cardNotRequiredCopy: "Confirma després de les dades de l'hoste, sense targeta, pagament ni garantia de targeta. També és útil per a una demostració de l'hotel.",
      cardPolicyHint: "Només s'aplica al motor de reserves d'aquest hotel. Per cobrar o retenir després caldrà una targeta registrada. Desa els ajustos per aplicar la tria.",
      installGuide: {
        button: "Guia d'instal·lacio",
        eyebrow: "Instal·lacio al web",
        title: "Instal·lar el widget de reserves",
        subtitle: "Tria el tipus de web i com s'ha d'obrir el cercador. Cada exemple ja utilitza el slug d'aquest hotel.",
        beforeTitle: "Abans de publicar",
        beforeCopy: "Activa el motor, desa un slug public, selecciona Widget integrat i carrega l'script de Pilcore una sola vegada per pagina.",
        inlineTab: "Integrat",
        dropdownTab: "Sota un boto",
        floatingTab: "Flotant",
        wordpressTab: "WordPress",
        inlineTitle: "Visible dins de la pagina",
        inlineCopy: "Col·loca aquest bloc exactament on vulguis mostrar el calendari: hero, seccio de reserves o peu de pagina.",
        dropdownTitle: "Desplegable sota Reserva ara",
        dropdownCopy: "Aquesta versio utilitza l'element natiu details, de manera que s'obre sota el boto sense cap llibreria addicional.",
        floatingTitle: "Boto de reserva fix",
        floatingCopy: "Aquesta versio es mante a baix a la dreta i obre el calendari sobre el boto. En mobil el panell s'adapta a l'amplada de la pantalla.",
        wordpressTitle: "WordPress, Elementor i constructors similars",
        wordpressCopy: "Enganxa el bloc complet a HTML personalitzat de Gutenberg, al widget HTML d'Elementor o al modul de codi equivalent.",
        wordpressFallback: "Si WordPress elimina l'etiqueta script, carrega aquest script una sola vegada amb WPCode o a la capcalera del tema i deixa nomes l'element pilcore-booking-widget dins de la pagina.",
        scriptOnce: "L'etiqueta script ha d'apareixer una sola vegada, encara que la pagina tingui diversos botons de reserva.",
        copyCode: "Copiar codi",
        copied: "Copiat",
      },
    },
    theme: {
      eyebrow: "Identitat visual",
      title: "Tema del motor de reserves",
      copy: "Tria el preset visual d'aquest hotel. L'estructura, el contingut i el comportament de la reserva no canvien.",
      ariaLabel: "Tema visual del motor de reserves",
      recommended: "Recomanat",
      presets: {
        "pilcore-light": {
          name: "Pilcore Clar",
          description: "Net, modern i tecnològic, amb superfícies blanques fredes i blaus Pilcore.",
        },
        editorial: {
          name: "Editorial",
          description: "Càlid, boutique i personal, amb títols serif i formes contingudes.",
        },
        "premium-dark": {
          name: "Premium Fosc",
          description: "Exclusiu, nocturn i sofisticat, amb accents blaus lluminosos.",
        },
      },
    },
    profile: {
      eyebrow: "Identitat per als hostes",
      title: "Nom i contacte de l'hotel",
      copy:
        "Configura el nom públic i les dades de contacte que es mostren durant la reserva i després de confirmar-la.",
      previewTitle: "On apareixen aquestes dades",
      previewHint:
        "L'adreça i les dades de contacte apareixen al Resum de la reserva. El primer telèfon també activa l'accés de trucada al mòbil. El nom públic i les dades de contacte principals apareixen a la confirmació; el nom públic es reutilitza a la cancel·lació.",
      modalTitle: "Nom i contacte de l'hotel",
      modalSubtitle:
        "Edita les dades visibles per als hostes que s'utilitzen al Resum de la reserva, l'accés de trucada al mòbil i la confirmació. La vista prèvia separa les ubicacions actuals.",
      hotelInfoHeading: "Dades publicades",
      identityPreviewLabel: "Confirmació i cancel·lació",
      contactPreviewLabel: "Resum de la reserva · Contacte de l'hotel",
      hotelName: "Nom públic de l'hotel",
      addressLine: "Adreça",
      city: "Ciutat",
      postalCode: "Codi postal",
      country: "País",
      phones: "Telèfons",
      emails: "Emails",
      hotelNamePreviewLabel: "Nom de l'hotel",
      addressPreviewLabel: "Adreça de l'hotel",
      phonePreviewLabel: "Telèfon",
      emailPreviewLabel: "Email",
      phonePlaceholder: "Escriu un telèfon i prem Enter",
      emailPlaceholder: "Escriu un email i prem Enter",
      empty: "No hi ha dades públiques configurades",
      emptyHotelName: "Nom públic de l'hotel sense configurar",
      emptyContact: "Dades de contacte sense configurar",
    },
    hero: {
      eyebrow: "Fons del procés de reserva",
      title: "Fons hero per pantalla",
      copy:
        "Tria exactament en quines pantalles del motor de reserves apareixerà la imatge hero de l'hotel.",
      screensLabel: "Mostra el fons a",
      screensHint: "Selecciona totes les pantalles del motor de reserves on ha d'aparèixer la imatge pujada.",
      widgetScreensHint:
        "La cerca de l'estada es fa al widget del web, per això la pantalla inicial no està disponible aquí.",
      noScreensSelected:
        "No hi ha pantalles seleccionades. La càrrega d'imatges queda oculta i qualsevol imatge desada roman inactiva.",
      screenLabels: {
        search: "Cerca inicial",
        categories: "Habitacions i tarifes",
        extras: "Extres",
        guest_details: "Dades de l'hoste",
        payment: "Pagament",
        confirmation: "Confirmació",
      },
      openingMessageTitle: "Missatge de la pantalla inicial",
      openingMessageHint:
        "Aquest text apareix només a la cerca inicial del motor de reserves amb URL independent.",
      backgroundLabel: "Imatge de fons",
      backgroundHint: "Recomanat: 1920x1080 o superior, en horitzontal.",
      backgroundLocked: "Elimina primer la imatge actual per poder-ne pujar una altra.",
      noBackground: "Encara no hi ha imatge de fons.",
      previewLabel: "Preview del hero",
      subtitleLabel: "Subtitol hero",
      claimLabel: "Claim hero",
      subtitlePlaceholderEn: "Your stay starts here",
      subtitlePlaceholderEs: "Tu estancia empieza aqui",
      claimPlaceholderEn: "Real availability - Best rate guaranteed - No prepayment",
      claimPlaceholderEs: "Disponibilidad real - Mejor precio garantizado - Sin prepago",
      copyHint:
        "El text hero es mostra al Booking Engine public. Completa angles i castella abans de publicar.",
    },
    pricing: {
      eyebrow: "Transparencia de preu",
      title: "Impostos, taxes i el que veu l'hoste",
      copy:
        "Aclara exactament que inclou el total mostrat. Redueix friccio i evita sorpreses desagradables abans del pagament.",
      summaryTitle: "Aixi es llegira el desglossament",
      empty: "Encara no hi ha inclusions actives.",
      totalLabel: "TOTAL (TOTES LES HABITACIONS)",
      includingPrefix: "Inclou",
      conjunction: "i",
      noneIncluded: "No inclou impostos ni taxes",
      allIncluded: "Tots els impostos i taxes inclosos",
      vatLabel: "IVA",
      otherTaxesLabel: "altres impostos",
      feesLabel: "taxes",
      cityTaxLabel: "taxa turistica",
      vat: "Inclou IVA",
      otherTaxes: "Inclou altres impostos",
      fees: "Inclou taxes",
      cityTax: "Inclou taxa turistica",
    },
    emails: {
      eyebrow: "Copy d'email",
      title: "Email de confirmacio a l'hoste",
      copy:
        "Mante el missatge de confirmacio alineat amb la veu de l'hotel. Cada idioma obre un editor ric amb preview en viu.",
      editorTitle: "Email de confirmacio a l'hoste",
      editorSubtitle:
        "Redacta assumpte i cos, insereix variables intel.ligents i revisa el resultat abans de desar-lo.",
      variablesTitle: "Variables",
      variablesHint: "Insereix tokens al camp actiu. Es reemplacen en enviar el missatge.",
      targetSubject: "Assumpte",
      targetBody: "Cos",
      subjectLabel: "Assumpte",
      bodyLabel: "Cos",
      previewTitle: "Preview",
      previewSubject: "Assumpte renderitzat",
      previewBody: "Cos renderitzat",
      emptyLanguage: "Encara no s'ha configurat aquest idioma.",
      configured: "Configurat",
      missing: "Pendent",
    },
    terms: {
      eyebrow: "Claredat legal",
      title: "Termes i condicions",
      copy:
        "Organitza les politiques en blocs elegants i facils de llegir. L'hoste ha de poder-les escanejar sense sentir un mur legal.",
      empty: "Encara no s'han afegit termes.",
      notSet: "Sense definir",
      editorTitle: "Termes i condicions",
      editorSubtitle:
        "Ajusta ordre, titol i text per a cada clausula. Utilitza el preview per revisar la jerarquia abans de publicar-la.",
      sortOrder: "Ordre",
      titleLabel: "Titol",
      textLabel: "Text",
      previewTitle: "Preview",
      previewEmpty: "Comenca a escriure per previsualitzar aquest bloc.",
    },
    rooms: {
      eyebrow: "Vendre cada categoria",
      title: "Storytelling per categoria",
      copy:
        "Cada categoria ha de sentir-se com una mini landing page: cover potent, amenities rellevants i copy comercial que tanqui la venda.",
      loading: "Carregant categories...",
      empty: "No hi ha categories disponibles per a aquesta unitat de negoci.",
      imagesTab: "Imatges",
      amenitiesTab: "Serveis",
      copyTab: "Text comercial",
      imagesLabel: "Imatges",
      imageHint: "Tria si vols pujar la imatge com a cover o a galeria. Maxim 10 imatges per categoria.",
      imageRatioHint:
        "Format obligatori: horitzontal 3:2 (per exemple, 1500 x 1000 px). Pilcore no retalla les imatges automaticament.",
      imageRatioChecking: "Comprovant les proporcions de la imatge...",
      imageRatioValid: "Imatge 3:2 preparada per pujar.",
      imageRatioInvalid:
        "Aquesta imatge no es horitzontal 3:2. Tria una imatge 3:2, per exemple de 1500 x 1000 px.",
      imageUnreadable: "No s'ha pogut llegir aquesta imatge. Tria un altre arxiu JPG, PNG o WebP.",
      noImage: "Encara no hi ha imatges. Comenca per un cover que vengui l'habitacio d'un cop d'ull.",
      uploadCover: "Pujar com a cover",
      uploadGallery: "Pujar a galeria",
      maxReached: "S'ha arribat al maxim de 10 imatges",
      noFile: "Cap arxiu seleccionat",
      coverTag: "Cover",
      galleryTag: "Galeria",
      amenitiesTitle: "Serveis de l'habitacio",
      amenitiesLoadError: "No s'han pogut carregar els serveis. Torna-ho a provar abans d'editar.",
      amenitiesHint: "Tria els serveis que l'hoste veura en aquesta categoria d'habitacio.",
      selectedAmenities: "Serveis seleccionats",
      noAmenities: "Encara no hi ha serveis seleccionats.",
      searchAmenities: "Buscar serveis...",
      allCategories: "Totes les categories",
      noAmenitiesMatch: "No hi ha serveis que coincideixin amb els filtres.",
      selectedTitle: "Seleccionats",
      clearAmenities: "Treure tots",
      manageAmenities: "Seleccionar serveis",
      saveAmenitiesAndClose: "Desar i tancar",
      copyTitle: "Text comercial",
      copyHint:
        "Aquests textos es mostren al Booking Engine public d'aquesta categoria. Completa angles i castella perque l'hoste vegi el nom, la descripcio, els llits, les vistes i els highlights correctes.",
      copyLoading: "Carregant text comercial...",
      editorTitle: "Text comercial",
      editorSubtitle:
        "Edita el relat comercial per idioma, mante correctes les especificacions compartides i revisa el preview public abans de desar.",
      prefillTitle: "Comença amb les dades de la teva habitació",
      prefillHint:
        "Fes servir la categoria i la seva primera habitació per emplenar EN i ES amb la mateixa informació. Les dades disponibles substitueixen els camps actuals; podràs editar-ho tot abans de desar.",
      prefillAction: "Emplenar",
      prefillLoading: "Recuperant les dades de l'habitació…",
      prefillSuccess: "Les dades de la categoria i l'habitació {room} estan preparades en EN i ES. Revisa-les i desa els canvis.",
      prefillCategoryOnly: "Aquesta categoria encara no té habitacions. Les dades disponibles estan preparades en EN i ES; s'han conservat els altres camps.",
      prefillEmpty: "No hi ha dades disponibles per emplenar. S'han conservat els teus textos.",
      prefillError: "No s'han pogut recuperar les dades. S'han conservat els teus textos. Torna-ho a provar.",
      sharedTitle: "Detalls compartits",
      sharedHint: "Aquests valors es mantenen iguals a tots els idiomes.",
      languageTitle: "Copy per idioma",
      languageHint: "Canvia entre EN i ES per ajustar el relat que veura l'hoste.",
      previewTitle: "Preview Booking Engine",
      previewHint: "Aixi es veura la card publica de l'habitacio per a l'idioma seleccionat.",
      previewEmpty: "Afegeix copy comercial per donar forma a aquesta card.",
      previewLanguage: "Idioma del preview",
      bedTypeLabel: "Tipus de llit",
      viewLabel: "Vista",
      highlightsLabel: "Highlights",
      nameEn: "Nom (EN) *",
      nameEs: "Nom (ES) *",
      shortEn: "Descripcio curta (EN)",
      shortEs: "Descripcio curta (ES)",
      size: "Mida (m2)",
      beds: "Llits",
      maxPax: "Max pax",
      bedTypeEn: "Tipus de llit (EN)",
      bedTypeEs: "Tipus de llit (ES)",
      viewEn: "Vista (EN)",
      viewEs: "Vista (ES)",
      highlightsEn: "Highlights (EN)",
      highlightsEs: "Highlights (ES)",
      amenityModalTitle: "Seleccionar serveis",
      amenityModalSubtitle: "Marca els serveis que es mostraran per a aquesta categoria al Booking Engine.",
      imageModalTitle: "Preview d'imatge",
      imageModalSubtitle: "Recorre la galeria i promociona qualsevol imatge a cover.",
    },
    toasts: {
      settingsSaved: "Ajustos del Booking Engine desats.",
      settingsError: "No s'han pogut desar els ajustos del Booking Engine.",
      themeNotPersisted: "El servidor no ha confirmat el tema seleccionat per al Booking Engine.",
      cardPolicyNotPersisted: "El servidor no ha confirmat el requisit de targeta seleccionat. No s'ha confirmat que la tria s'hagi desat.",
      urlCopied: "URL publica copiada al porta-retalls.",
      urlCopyError: "No s'ha pogut copiar la URL publica.",
      heroUploaded: "Hero pujat correctament.",
      heroRemoved: "Hero eliminat.",
      heroUploadError: "No s'ha pogut pujar la imatge hero.",
      selectHeroScreenFirst: "Selecciona almenys una pantalla abans de pujar una imatge de fons.",
      profileSaved: "Informacio publica desada.",
      profileError: "No s'ha pogut desar la informacio publica.",
      roomCopySaved: "Copy de categoria desat.",
      roomCopyError: "No s'ha pogut desar el copy de categoria.",
      roomCopyMissingNames: "Nom EN i Nom ES son obligatoris.",
      amenitiesSaved: "Amenities desades per a aquesta categoria.",
      amenitiesError: "No s'han pogut desar les amenities d'aquesta categoria.",
      imageUploaded: "Imatge pujada correctament.",
      imageRemoved: "Imatge eliminada.",
      imageError: "L'accio sobre la imatge ha fallat.",
      coverUpdated: "La imatge cover s'ha actualitzat.",
      profileUrlMissing: "Crea primer un slug per generar la URL publica.",
      pickImageFirst: "Selecciona primer una imatge.",
      heroAlreadyExists: "Ja existeix una imatge hero. Elimina-la abans de pujar-ne una altra.",
    },
  },
};

function getBookingEngineStudioMessages(languageId) {
  return BOOKING_ENGINE_STUDIO_MESSAGES[String(languageId || "")] || BOOKING_ENGINE_STUDIO_MESSAGES.en;
}

function escapeTemplateTokenRegex(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function parseTemplateComposerSegments(value, tokenOptions) {
  const input = String(value || "");
  const tokens = (Array.isArray(tokenOptions) ? tokenOptions : [])
    .map((option) => String(option?.token || ""))
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  if (!tokens.length) {
    return input ? [{ type: "text", value: input }] : [];
  }

  const matcher = new RegExp(`(${tokens.map(escapeTemplateTokenRegex).join("|")})`, "g");
  const segments = [];
  let lastIndex = 0;

  input.replace(matcher, (match, _token, offset) => {
    if (offset > lastIndex) {
      segments.push({ type: "text", value: input.slice(lastIndex, offset) });
    }
    segments.push({ type: "token", value: match });
    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < input.length) {
    segments.push({ type: "text", value: input.slice(lastIndex) });
  }

  return segments;
}

function appendComposerTextNode(doc, parent, text) {
  const lines = String(text || "").split("\n");
  lines.forEach((line, index) => {
    if (line) {
      parent.appendChild(doc.createTextNode(line));
    }
    if (index < lines.length - 1) {
      parent.appendChild(doc.createElement("br"));
    }
  });
}

function createComposerTokenNode(
  doc,
  token,
  label,
  className = "reservationStudio__inlineToken"
) {
  const chip = doc.createElement("span");
  chip.className = className;
  chip.setAttribute("data-token", token);
  chip.setAttribute("contenteditable", "false");
  chip.textContent = label || token;
  return chip;
}

function serializeComposerNode(node) {
  if (!node) return "";

  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || "";
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  if (node.nodeName === "BR") {
    return "\n";
  }

  const token = node.getAttribute?.("data-token");
  if (token) {
    return token;
  }

  let output = "";
  node.childNodes.forEach((child) => {
    output += serializeComposerNode(child);
  });

  if (node !== node.ownerDocument?.body && /^(DIV|P)$/.test(node.nodeName)) {
    output += "\n";
  }

  return output;
}

const TemplateTokenComposer = React.forwardRef(function TemplateTokenComposer(
  {
    value,
    onChange,
    onFocus,
    placeholder,
    multiline = false,
    tokenOptions = [],
    className = "",
    tokenClassName = "reservationStudio__inlineToken",
    readOnly = false,
  },
  ref
) {
  const editorRef = React.useRef(null);
  const lastRenderedValueRef = React.useRef("__INIT__");

  const tokenLabelMap = React.useMemo(
    () =>
      Object.fromEntries(
        (Array.isArray(tokenOptions) ? tokenOptions : []).map((option) => [
          String(option?.token || ""),
          option?.label || option?.token || "",
        ])
      ),
    [tokenOptions]
  );

  const renderValueIntoEditor = React.useCallback(
    (nextValue) => {
      const editor = editorRef.current;
      if (!editor) return;

      const doc = editor.ownerDocument;
      editor.innerHTML = "";

      const segments = parseTemplateComposerSegments(nextValue, tokenOptions);
      segments.forEach((segment) => {
        if (segment.type === "token") {
          editor.appendChild(
            createComposerTokenNode(
              doc,
              segment.value,
              tokenLabelMap[segment.value],
              tokenClassName
            )
          );
        } else {
          appendComposerTextNode(doc, editor, segment.value);
        }
      });

      lastRenderedValueRef.current = String(nextValue || "");
    },
    [tokenClassName, tokenLabelMap, tokenOptions]
  );

  const emitCurrentValue = React.useCallback(() => {
    if (readOnly) return;
    const editor = editorRef.current;
    if (!editor) return;
    const nextValue = serializeComposerNode(editor)
      .replace(/\u00a0/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/\n+$/g, "");
    lastRenderedValueRef.current = nextValue;
    onChange?.(nextValue);
  }, [onChange, readOnly]);

  React.useEffect(() => {
    const safeValue = String(value || "");
    if (safeValue !== lastRenderedValueRef.current) {
      renderValueIntoEditor(safeValue);
    }
  }, [renderValueIntoEditor, value]);

  React.useImperativeHandle(
    ref,
    () => ({
      focus() {
        editorRef.current?.focus();
      },
      insertToken(token) {
        if (readOnly) return;
        const editor = editorRef.current;
        if (!editor) return;

        editor.focus();
        const selection = window.getSelection();
        let range =
          selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

        if (!range || !editor.contains(range.commonAncestorContainer)) {
          range = editor.ownerDocument.createRange();
          range.selectNodeContents(editor);
          range.collapse(false);
        }

        const chip = createComposerTokenNode(
          editor.ownerDocument,
          token,
          tokenLabelMap[token] || token,
          tokenClassName
        );
        const spacer = editor.ownerDocument.createTextNode(multiline ? "" : " ");

        range.deleteContents();
        range.insertNode(spacer);
        range.insertNode(chip);

        range.setStartAfter(spacer);
        range.collapse(true);

        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }

        emitCurrentValue();
      },
    }),
    [emitCurrentValue, multiline, readOnly, tokenClassName, tokenLabelMap]
  );

  return (
    <div
      ref={editorRef}
      contentEditable={!readOnly}
      aria-readonly={readOnly || undefined}
      suppressContentEditableWarning
      spellCheck={false}
      data-placeholder={placeholder}
      className={`reservationStudio__tokenComposer ${
        multiline ? "reservationStudio__tokenComposer--multiline" : ""
      } ${className}`.trim()}
      onInput={emitCurrentValue}
      onFocus={onFocus}
      onKeyDown={(event) => {
        if (readOnly) return;
        if (!multiline && event.key === "Enter") {
          event.preventDefault();
        }
      }}
      onPaste={(event) => {
        event.preventDefault();
        if (readOnly) return;
        const text = event.clipboardData?.getData("text/plain") || "";
        document.execCommand("insertText", false, text);
      }}
    />
  );
});

// Demo Booking Engine is default-deny: every new editable control is disabled
// unless that individual control explicitly declares a view-only interaction.
// This never changes the normal PMS tree or mutates the original element props.
function readOnlyBookingTree(node, readOnly, message) {
  if (!readOnly) return node;
  if (Array.isArray(node)) return node.map((child) => readOnlyBookingTree(child, true, message));
  if (!React.isValidElement(node)) return node;
  const props = {};
  const isViewControl = node.props["data-demo-view"] === true;
  const isNativeControl = ["button", "input", "select", "textarea"].includes(node.type);
  const block = (event) => { event?.preventDefault?.(); };
  if (isNativeControl && !isViewControl) {
    props.disabled = true;
    props.title = message;
    for (const key of Object.keys(node.props)) {
      if (/^on[A-Z]/.test(key)) props[key] = block;
    }
  }
  if (node.type === TemplateTokenComposer) {
    props.readOnly = true;
    props.onChange = block;
  }
  if (node.type === "a") {
    props.href = undefined;
    props.target = undefined;
    props.onClick = block;
    props["aria-disabled"] = true;
  }
  if (node.type === "img" || node.type === "iframe" || node.type === "source") {
    props.src = undefined;
    props.srcSet = undefined;
    props.srcDoc = undefined;
  }
  if (node.props.style) {
    props.style = Object.fromEntries(Object.entries(node.props.style).map(([key, value]) =>
      [key, typeof value === "string" && /url\s*\(/i.test(value) ? "none" : value]
    ));
  }
  if (node.type === "form") props.onSubmit = block;
  props.children = React.Children.map(node.props.children, (child) => readOnlyBookingTree(child, true, message));
  return React.cloneElement(node, props);
}

function TemplateTokenInlinePreview({
  value,
  tokenOptions = [],
  emptyText = "",
  className = "",
  tokenClassName = "reservationStudio__inlineToken",
  textClassName = "",
}) {
  const safeValue = String(value || "");
  const tokenLabelMap = React.useMemo(
    () =>
      Object.fromEntries(
        (Array.isArray(tokenOptions) ? tokenOptions : []).map((option) => [
          String(option?.token || ""),
          option?.label || option?.token || "",
        ])
      ),
    [tokenOptions]
  );

  const segments = React.useMemo(
    () => parseTemplateComposerSegments(safeValue, tokenOptions),
    [safeValue, tokenOptions]
  );

  return (
    <div className={className}>
      {segments.length
        ? segments.map((segment, segmentIndex) => {
            if (segment.type === "token") {
              return (
                <span
                  key={`template-token-preview-${segmentIndex}-${segment.value}`}
                  className={tokenClassName}
                >
                  {tokenLabelMap[segment.value] || segment.value}
                </span>
              );
            }

            return (
              <span
                key={`template-token-text-${segmentIndex}`}
                className={textClassName}
              >
                {segment.value}
              </span>
            );
          })
        : emptyText}
    </div>
  );
}






const Settings = ({ forcedTab = null, allowPurchasingSharedAccess = false }) => {
  const demoModeActive = isDemoMode();
  const { theme, themeId, language, languageId } = usePmsTheme();
  const roleText = (text, values) => roleEditorText(languageId, text, values);
  const [demoLoading, setDemoLoading] = useState(demoModeActive);
  const [demoLoadedTab, setDemoLoadedTab] = useState(null);
  const [demoError, setDemoError] = useState("");
  const [demoReload, setDemoReload] = useState(0);
  const {
    baseCurrency,
    currencyCatalog,
    adminEnabled: isMultiCurrencyEnabledByAdmin,
  } = useCurrencyDisplay();
  const isLightTheme = themeId === "light";
  const userExperienceLabel = language.messages.settings.userExperienceTab;
  const extrasTabLabel = language.messages.sidebar.extrasDiscounts;
  const currenciesTabLabel = language.messages.sidebar.currencies || "Currencies";
  const bookingEngineTabLabel = language.messages.sidebar.bookingEngine;
  const [searchParams] = useSearchParams();
  const businessUnitId = demoModeActive ? DEMO_BUSINESS_UNIT_ID : (searchParams.get("business_unit_id") || "1");
  const [sessionUser, setSessionUser] = useState(() => demoModeActive ? readDemoUser() : readStoredUser());
  const currentUser = (demoModeActive ? readDemoUser() : sessionUser) || {};
  const permissions = demoModeActive
    ? Object.fromEntries(DEMO_SETTINGS_VIEW_PERMISSIONS.map((key) => [key, currentUser?.permissions?.[key] === true]))
    : currentUser?.permissions || {};
  const isAdminRole = !demoModeActive && Number(currentUser?.id) === 1;
  const canViewSharedSettingsFromPurchasing =
    !demoModeActive && allowPurchasingSharedAccess &&
    (isAdminRole || Boolean(permissions["purchasing.settings.view"]));
    console.log("🧩 permissions en Settings:", permissions);

  useEffect(() => {
    if (demoModeActive) return undefined;
    const syncSessionFromStorage = () => {
      if (isDemoMode()) return;
      setSessionUser(readStoredUser());
    };

    window.addEventListener("palantir-session-updated", syncSessionFromStorage);
    return () => {
      window.removeEventListener("palantir-session-updated", syncSessionFromStorage);
    };
  }, [demoModeActive]);

  const showReservationTab =
    permissions["hotel_settings.view_customer_settings"] ||
    permissions["hotel_settings.view_stay_settings"] ||
    permissions["hotel_settings.view_reservation_flow"] ||
    permissions["hotel_settings.view_required_fields"] ||
    permissions["hotel_settings.view_required_roles"];
  const canViewFrontDeskSettingsTab =
    isAdminRole || Boolean(permissions["hotel_settings.view_front_desk_settings"]);
  const canViewRevenueTab = Boolean(permissions["hotel_settings.view_revenue_settings"]);
  const hasCurrenciesTabPermission =
    isAdminRole || Boolean(permissions.access_settings) || canViewRevenueTab;
  const canViewCurrenciesTab =
    hasCurrenciesTabPermission && isMultiCurrencyEnabledByAdmin;
  const canViewExtrasTab = Boolean(permissions["hotel_settings.view_extras_tab"]);
  const canViewChargesTab =
    isAdminRole ||
    Boolean(permissions["hotel_settings.view_reservation_charges_tab"]) ||
    (!demoModeActive && canViewExtrasTab);
  const canViewUsersTab =
    isAdminRole ||
    permissions["hotel_settings.view_users_tab"] === true;
  const canManageUsers = !demoModeActive && canViewUsersTab && hasUserManagementPermission(currentUser);
  const canCreateCorporateUsers = !demoModeActive && canAccessCorporatePurchasing(currentUser);
  const canViewBookingEngineTab =
    isAdminRole || Boolean(permissions["hotel_settings.view_booking_engine_tab"]);
  const canViewCrmTab = false;
  const canUpdateCrmPipelines = false;
  const canViewAppearanceTab =
    showReservationTab ||
    canViewRevenueTab ||
    canViewExtrasTab ||
    canViewChargesTab ||
    canViewUsersTab ||
    canViewBookingEngineTab ||
    canViewSharedSettingsFromPurchasing;
  const demoActiveTab = forcedTab === "booking_engine" && canViewBookingEngineTab
    ? "booking_engine" : forcedTab === "appearance" && canViewAppearanceTab
    ? "appearance" : forcedTab === "charges" && canViewChargesTab
    ? "charges" : forcedTab === "extras" && canViewExtrasTab
    ? "extras" : forcedTab === "front_desk" && canViewFrontDeskSettingsTab ? "front_desk" : "reservation";

  const firstAllowedTab = (() => {
    if (showReservationTab) return "reservation";
    if (canViewFrontDeskSettingsTab) return "front_desk";
    if (canViewRevenueTab) return "revenue";
    if (canViewCurrenciesTab) return "currencies";
    if (canViewExtrasTab) return "extras";
    if (canViewChargesTab) return "charges";
    if (canViewUsersTab) return "users";
    if (canViewCrmTab) return "crm";
    if (canViewAppearanceTab) return "appearance";
    if (canViewBookingEngineTab) return "booking_engine";
    return "reservation";
  })();

  const isAllowedTab = (tab) => {
    if (demoModeActive) return (tab === "reservation" && showReservationTab) || (tab === "front_desk" && canViewFrontDeskSettingsTab) || (tab === "extras" && canViewExtrasTab) || (tab === "charges" && canViewChargesTab) || (tab === "appearance" && canViewAppearanceTab) || (tab === "booking_engine" && canViewBookingEngineTab);
    if (tab === "reservation") return showReservationTab;
    if (tab === "front_desk") return canViewFrontDeskSettingsTab;
    if (tab === "revenue") return canViewRevenueTab;
    if (tab === "currencies") return canViewCurrenciesTab;
    if (tab === "extras") return canViewExtrasTab;
    if (tab === "charges") return canViewChargesTab;
    if (tab === "users") return canViewUsersTab;
    if (tab === "crm") return canViewCrmTab;
    if (tab === "appearance") return canViewAppearanceTab;
    if (tab === "booking_engine") return canViewBookingEngineTab;
    return false;
  };

  const [storedActiveTab, setActiveTab] = useState(
    demoModeActive ? demoActiveTab : (forcedTab && isAllowedTab(forcedTab) ? forcedTab : firstAllowedTab)
  );
  const isUsersTabDenied = !demoModeActive && forcedTab === "users" && !canViewUsersTab;
  const activeTab = demoModeActive ? demoActiveTab : isUsersTabDenied ? null : storedActiveTab;
  const isPilcoreSettingsTab = activeTab === "reservation" || activeTab === "front_desk";
  const isDemoStandaloneTab = demoModeActive && ["extras", "charges", "appearance"].includes(activeTab);
  const demoCopy = {
    ...(DEMO_RESERVATION_SETTINGS_MESSAGES[languageId] || DEMO_RESERVATION_SETTINGS_MESSAGES.en),
    ...(activeTab === "front_desk" ? (DEMO_FRONT_DESK_SETTINGS_MESSAGES[languageId] || DEMO_FRONT_DESK_SETTINGS_MESSAGES.en) : {}),
    ...(activeTab === "booking_engine" ? (DEMO_BOOKING_ENGINE_SETTINGS_MESSAGES[languageId] || DEMO_BOOKING_ENGINE_SETTINGS_MESSAGES.en) : {}),
  };
  const demoSettingsTitle = activeTab === "booking_engine" ? "Booking Engine" : activeTab === "front_desk" ? "Front Desk Settings" : "Reservation Settings";

  const [showScraperPopup, setShowScraperPopup] = useState(false);
  const [scraperTargets, setScraperTargets] = useState([]);
  const [roomRateMap, setRoomRateMap] = useState({});

  const [newTarget, setNewTarget] = useState({
    room_rate_id: "",
    source_type: "booking_search",
    mode: "hotel",
    params: {
      name: "",
      start_date: "",
      end_date: "",
      length_of_stay: 2,
      adults: 2,
      children: 0
    }
  });

  useEffect(() => {
    if (demoModeActive) {
      setActiveTab(demoActiveTab);
      return;
    }
    if (forcedTab) {
      const isForcedAllowed =
        (forcedTab === "reservation" && showReservationTab) ||
        (forcedTab === "front_desk" && canViewFrontDeskSettingsTab) ||
        (forcedTab === "revenue" && canViewRevenueTab) ||
        (forcedTab === "currencies" && canViewCurrenciesTab) ||
        (forcedTab === "extras" && canViewExtrasTab) ||
        (forcedTab === "charges" && canViewChargesTab) ||
        (forcedTab === "users" && canViewUsersTab) ||
        (forcedTab === "appearance" && canViewAppearanceTab) ||
        (forcedTab === "booking_engine" && canViewBookingEngineTab);

      setActiveTab(isForcedAllowed ? forcedTab : firstAllowedTab);
      return;
    }

    setActiveTab(firstAllowedTab);
  }, [
    canViewBookingEngineTab,
    canViewAppearanceTab,
    canViewChargesTab,
    canViewExtrasTab,
    canViewFrontDeskSettingsTab,
    canViewRevenueTab,
    canViewCurrenciesTab,
    canViewUsersTab,
    firstAllowedTab,
    forcedTab,
    showReservationTab,
    demoModeActive,
    demoActiveTab,
  ]);

//19.01.2026 - PERSISTIR ICONOS DE IMAGENES

  const [formData, setFormData] = useState({
    max_age_baby: "",
    max_age_child: "",
    min_age_main_guest: "",
    min_age_booker: "",
    require_company_booker_contact: true,
    vip_categories: "",
    checkin_time: "",
    checkout_time: "",
    timezone: "Europe/Madrid",
    clients_require_requested_flow: "",
    required_customer_fields_reservation: {},
    required_customer_fields_check_in: {},
    required_customer_roles_reservation: ["booker"],
    required_customer_roles_check_in: ["main_guest"],
    require_full_payment_for_check_in: false,
    rms_source: "",
    market_price_rules: [],
    exclusive_rate_prices_per_date: false,
    derived_rate_mode: "simple",
    always_round_rate_prices: false,
    reservation_manual_modification_policy:
      normalizeReservationManualModificationPolicy(null),
    // Booking Engine - Hero texts
    booking_engine_texts: {},

    // ✅ Booking Engine - Terms & Conditions
    booking_engine_terms: [],

    booking_engine_guest_email_templates: {
      en: { subject: "", body: "" },
      es: { subject: "", body: "" },
    },

    reservation_confirmation_email_templates: {
      en: { subject: "", body: "" },
      es: { subject: "", body: "" },
    },
    reservation_confirmation_email_languages: ["en"],
    external_reservation_notifications_enabled: false,
    external_reservation_notification_emails: [],
    external_reservation_notification_language: "en",
    external_reservation_notification_templates: {
      en: {
        new: { subject: "", body: "" },
        modified: { subject: "", body: "" },
        cancelled: { subject: "", body: "" },
      },
      es: {
        new: { subject: "", body: "" },
        modified: { subject: "", body: "" },
        cancelled: { subject: "", body: "" },
      },
    },

    // ✅ Booking Engine - Price inclusions (shown under total)
    booking_engine_price_includes_vat: false,
    booking_engine_price_includes_other_taxes: false,
    booking_engine_price_includes_fees: false,
    booking_engine_price_includes_city_tax: false,
    booking_engine_entry_mode: "hosted",
    booking_engine_theme: "pilcore-light",
    booking_engine_card_policy: "required",
    booking_engine_hero_background_screens: normalizeBookingHeroBackgroundScreens(
      undefined,
      "hosted"
    ),
  });

  const [bookingEngineToast, setBookingEngineToast] = useState(null);
  const [bookingEngineSaving, setBookingEngineSaving] = useState(false);
  const [bookingEngineCopiedUrl, setBookingEngineCopiedUrl] = useState(false);
  const bookingSettingsRootRef = React.useRef(null);
  const bookingSettingsSurfaceRef = React.useRef(null);
  const bookingSettingsSaveRef = React.useRef(false);
  const [bookingSettingsSection, setBookingSettingsSection] = useState("general");
  const [bookingCategorySearch, setBookingCategorySearch] = useState("");
  const [bookingRoomCategoryId, setBookingRoomCategoryId] = useState(null);
  const bookingRoomEditorRef = React.useRef(null);
  const bookingRoomTriggerRef = React.useRef(null);
  const [bookingImageToRemove, setBookingImageToRemove] = useState(null);
  const [bookingImageRemoving, setBookingImageRemoving] = useState(false);
  const bookingImageRemoveRef = React.useRef(false);
  const [beProfileSaving, setBeProfileSaving] = useState(false);
  const beProfileSaveRef = React.useRef(false);

  const [termsOpen, setTermsOpen] = useState(false);
  const [termsEditingId, setTermsEditingId] = useState(null);
  const [termsDraft, setTermsDraft] = useState(null);

  const [beProfile, setBeProfile] = useState(null);
  const [businessUnitName, setBusinessUnitName] = useState("");

  useEffect(() => {
    if (demoModeActive || forcedTab === "users") return undefined;
    let isActive = true;
    const sessionBusinessUnitName =
      String(currentUser?.active_business_unit_id || "") === String(businessUnitId)
        ? String(currentUser?.business_unit_name || "").trim()
        : "";

    async function fetchBusinessUnitName() {
      if (!businessUnitId) {
        setBusinessUnitName("");
        return;
      }

      try {
        const res = await axios.get(`/api/business-units/${businessUnitId}`);
        if (!isActive) return;
        setBusinessUnitName(String(res.data?.name || "").trim() || sessionBusinessUnitName);
      } catch (err) {
        console.warn("[SETTINGS][BUSINESS_UNIT][GET][ERROR]", err);
        if (isActive) setBusinessUnitName(sessionBusinessUnitName);
      }
    }

    fetchBusinessUnitName();

    return () => {
      isActive = false;
    };
  }, [businessUnitId, currentUser?.active_business_unit_id, currentUser?.business_unit_name, demoModeActive, forcedTab]);

  async function fetchBeProfile() {
  if (demoModeActive || isDemoMode()) return;
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/booking-engine-profiles/${businessUnitId}`
    );
    // acepta {profile: {...}} o directamente {...}
    setBeProfile(res.data?.profile || res.data || null);
    console.log("[BOOKING_ENGINE_PROFILE][GET][OK]", res.data);
  } catch (err) {
    console.error("[BOOKING_ENGINE_PROFILE][GET][ERROR]", err);
    setBeProfile(null);
  }
}

useEffect(() => {
  if (demoModeActive) return;
  if (activeTab !== "booking_engine" && activeTab !== "reservation") return;
  fetchBeProfile();
}, [activeTab, businessUnitId, demoModeActive]);

  useEffect(() => {
    if (!bookingEngineToast || bookingEngineToast.tone === "error") return undefined;
    const timeoutId = window.setTimeout(() => {
      setBookingEngineToast(null);
    }, 7000);
    return () => window.clearTimeout(timeoutId);
  }, [bookingEngineToast]);

  useEffect(() => {
    if (!bookingEngineCopiedUrl) return undefined;
    const timeoutId = window.setTimeout(() => {
      setBookingEngineCopiedUrl(false);
    }, 1800);
    return () => window.clearTimeout(timeoutId);
  }, [bookingEngineCopiedUrl]);


  const showBookingEngineToast = React.useCallback((message, tone = "info") => {
    setBookingEngineToast(message ? { message, tone } : null);
  }, []);


  // popup editor
  const [beProfileOpen, setBeProfileOpen] = useState(false);
  const [bookingInstallGuideOpen, setBookingInstallGuideOpen] = useState(false);
  const [bookingInstallGuideTab, setBookingInstallGuideTab] = useState("inline");
  const [bookingInstallCopiedTab, setBookingInstallCopiedTab] = useState("");
  const [beProfileDraft, setBeProfileDraft] = useState(null);
  const [beProfilePhoneInput, setBeProfilePhoneInput] = useState("");
  const [beProfileEmailInput, setBeProfileEmailInput] = useState("");
  const [guestEmailInsertTarget, setGuestEmailInsertTarget] = useState("subject"); // "subject" | "body"
  const guestEmailSubjectRef = React.useRef(null);
  const guestEmailBodyRef = React.useRef(null);
  const [roomCopyOpen, setRoomCopyOpen] = useState(false);
  const [roomCopyLangTab, setRoomCopyLangTab] = useState("en");
  const [roomCopyEditingRcId, setRoomCopyEditingRcId] = useState(null);
  const [roomCopyDraft, setRoomCopyDraft] = useState(null);
  const [roomCopyPrefillLoading, setRoomCopyPrefillLoading] = useState(false);
  const [roomCopyPrefillNotice, setRoomCopyPrefillNotice] = useState(null);
  const roomCopyRequestRef = React.useRef({ version: 0, controller: null });
  const roomCopyBusinessUnitRef = React.useRef(businessUnitId);
  roomCopyBusinessUnitRef.current = businessUnitId;

  useEffect(() => {
    setRoomCopyOpen(false);
    setRoomCopyEditingRcId(null);
    setRoomCopyDraft(null);
    setRoomCopyPrefillLoading(false);
    setRoomCopyPrefillNotice(null);
    const requestState = roomCopyRequestRef.current;
    return () => {
      requestState.version += 1;
      requestState.controller?.abort();
      requestState.controller = null;
    };
  }, [businessUnitId]);



  // cuando abras el popup por primera vez, clona lo actual
  useEffect(() => {
    if (!beProfileOpen) return;
    setBeProfileDraft((p) => p || {
      public_hotel_name: (beProfile?.public_hotel_name || ""),
      address_line: (beProfile?.address_line || ""),
      city: (beProfile?.city || ""),
      postal_code: (beProfile?.postal_code || ""),
      country: (beProfile?.country || ""),
      phones: Array.isArray(beProfile?.phones) ? beProfile.phones : [],
      emails: Array.isArray(beProfile?.emails) ? beProfile.emails : [],
    });
    setBeProfilePhoneInput("");
    setBeProfileEmailInput("");
  }, [beProfileOpen, beProfile]);



  function normalizeTerms(input) {
    const arr = Array.isArray(input) ? input : [];
    return arr
      .filter(Boolean)
      .map((x) => ({
          id: x.id != null ? String(x.id) : `term_${Date.now()}`,
          sort_order: Number(x.sort_order) || 0,
          title: {
            en: String(x?.title?.en || ""),
            es: String(x?.title?.es || ""),
          },
          text: {
            en: String(x?.text?.en || ""),
            es: String(x?.text?.es || ""),
          },
        }));
  }



  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClientOptions, setSelectedClientOptions] = useState([]);
  const [useWindowDays, setUseWindowDays] = useState(false);

  useEffect(() => {
    if (!demoModeActive) return undefined;
    setBookingInstallGuideOpen(false);
    setBeProfileOpen(false);
    setBeProfileDraft(null);
    setGuestEmailOpen(false);
    setGuestEmailDraft(null);
    setTermsOpen(false);
    setTermsDraft(null);
    setTermsEditingId(null);
    setRoomCopyOpen(false);
    setRoomCopyDraft(null);
    setRoomCopyEditingRcId(null);
    setAmenityPickerOpen(false);
    setRoomCategoryImagesModalOpen(false);
    // Catalogue tabs own their isolated requests, while appearance is static.
    // None need parent settings queries or state left over from those tabs.
    if (["extras", "charges", "appearance"].includes(activeTab)) {
      setDemoLoading(false);
      setDemoError("");
      setDemoLoadedTab(null);
      setRequiredFieldPicker(null);
      return undefined;
    }
    let cancelled = false;
    setDemoLoading(true);
    setDemoError("");
    setDemoLoadedTab(null);
    setRequiredFieldPicker(null);
    const loadDemoSettings = async () => {
      try {
        const isFrontDesk = activeTab === "front_desk";
        const isBookingEngine = activeTab === "booking_engine";
        const endpoint = isBookingEngine ? "/api/demo/booking-engine-settings" : isFrontDesk ? "/api/demo/front-desk-settings" : "/api/demo/reservation-settings";
        const { data } = await axios.get(endpoint, { params: { business_unit_id: DEMO_BUSINESS_UNIT_ID } });
        if (cancelled) return;
        const source = data?.settings;
        const requiredFields = isBookingEngine ? DEMO_BOOKING_ENGINE_SETTING_FIELDS : isFrontDesk ? DEMO_FRONT_DESK_SETTING_FIELDS : DEMO_RESERVATION_SETTING_FIELDS;
        if (!source || source.business_unit_id !== DEMO_BUSINESS_UNIT_ID
          || (!isFrontDesk && !isBookingEngine && !Array.isArray(data.clients))
          || requiredFields.some((key) => !Object.prototype.hasOwnProperty.call(source, key))) {
          throw new Error(demoCopy.update);
        }
        if (isBookingEngine) {
          if (data.business_unit_id !== DEMO_BUSINESS_UNIT_ID
            || !Object.prototype.hasOwnProperty.call(data, "profile")
            || (data.profile !== null && data.profile?.business_unit_id !== DEMO_BUSINESS_UNIT_ID)
            || ![data.roomCategories, data.roomCategoryCopy, data.amenities, data.roomCategoryAmenities, data.assets?.roomCategoryImages].every(Array.isArray)
            || !Object.prototype.hasOwnProperty.call(data.assets || {}, "heroBackground")
            || data.roomCategories.some((row) => row?.business_unit_id !== DEMO_BUSINESS_UNIT_ID)
            || data.roomCategoryCopy.some((row) => row?.business_unit_id !== DEMO_BUSINESS_UNIT_ID)
            || data.roomCategoryAmenities.some((row) => row?.business_unit_id !== DEMO_BUSINESS_UNIT_ID)) throw new Error(demoCopy.update);
          const copyById = {};
          data.roomCategoryCopy.forEach((row) => { copyById[row.room_category_id] = normalizeBeRoomCategoryCopyRow(row); });
          const amenitiesById = Object.fromEntries(data.roomCategories.map((row) => [row.id, []]));
          data.roomCategoryAmenities.forEach((row) => {
            if (!amenitiesById[row.room_category_id]) throw new Error(demoCopy.update);
            amenitiesById[row.room_category_id].push(row.amenity_id);
          });
          const imagesById = Object.fromEntries(data.roomCategories.map((row) => [row.id, []]));
          data.assets.roomCategoryImages.forEach((row) => {
            if (!imagesById[row.room_category_id]) throw new Error(demoCopy.update);
            imagesById[row.room_category_id].push({ asset_id: row.id, kind: row.kind, sort_order: row.sort_order });
          });
          Object.values(imagesById).forEach((images) => images.sort((a, b) =>
            (a.kind === "cover" ? 0 : 1) - (b.kind === "cover" ? 0 : 1) || (b.asset_id - a.asset_id)
          ));
          setFormData((previous) => ({
            ...previous,
            ...source,
            // Older read-only demo snapshots predate the card policy.
            booking_engine_card_policy: normalizeBookingCardPolicy(source.booking_engine_card_policy),
          }));
          setBeProfile(data.profile);
          setRoomCategories(data.roomCategories);
          setBeRoomCategoryCopyByRcId(copyById);
          setBeRoomCategoryCopyLoaded(true);
          setBeRoomCategoryCopyLoading(false);
          setAmenitiesAll(data.amenities);
          setRcAmenityIdsByRcId(amenitiesById);
          setRcAmenitiesLoadedById(Object.fromEntries(data.roomCategories.map((row) => [row.id, true])));
          setRcTabById({});
          setRoomCategoryImages(imagesById);
          setRoomCategoryImagesLoading(false);
          setHeroBgAsset(data.assets.heroBackground ? { id: data.assets.heroBackground.id } : null);
          setHeroBgLoading(false);
          setDemoLoadedTab(activeTab);
          return;
        }
        if (isFrontDesk) {
          if (typeof source.timezone !== "string" || !source.timezone.trim()
            || !source.required_customer_fields_check_in || typeof source.required_customer_fields_check_in !== "object"
            || Array.isArray(source.required_customer_fields_check_in)
            || !Array.isArray(source.required_customer_roles_check_in)
            || typeof source.require_full_payment_for_check_in !== "boolean") throw new Error(demoCopy.update);
          setFormData((previous) => ({
            ...previous, ...source,
            required_customer_roles_check_in: ensureCheckInRolesCoverFieldRules(
              source.required_customer_roles_check_in, source.required_customer_fields_check_in
            ),
          }));
          setDemoLoadedTab(activeTab);
          return;
        }
        const selectedIds = String(source.clients_require_requested_flow || "").split(",").map((value) => value.trim()).filter(Boolean);
        const options = [
          { label: "Direct Reservation", value: "null" },
          ...data.clients.filter((client) => client.status === "active" || selectedIds.includes(String(client.id)))
            .map((client) => ({ label: client.name, value: String(client.id) })),
        ];
        setClientOptions(options);
        setSelectedClientOptions(options.filter((option) => selectedIds.includes(option.value)));
        // These are the same effective defaults used by the PMS for nullable
        // settings; missing exported fields are rejected above, never defaulted.
        setFormData((previous) => ({
          ...previous, ...source,
          required_customer_fields_reservation: source.required_customer_fields_reservation || {},
          required_customer_roles_reservation: ensureReservationRolesCoverFieldRules(
            source.required_customer_roles_reservation, source.required_customer_fields_reservation || {}
          ),
          group_reservations_default_status: source.group_reservations_default_status || "Requested",
          reservation_manual_modification_policy: normalizeReservationManualModificationPolicy(source.reservation_manual_modification_policy),
        }));
        setDemoLoadedTab(activeTab);
      } catch (error) {
        if (!cancelled) {
          setDemoError(activeTab === "booking_engine" ? demoCopy.update : error?.response?.data?.error || error?.message || demoCopy.update);
          setDemoLoadedTab(activeTab);
        }
      } finally {
        if (!cancelled) setDemoLoading(false);
      }
    };
    loadDemoSettings();
    return () => { cancelled = true; };
  }, [demoModeActive, demoReload, demoCopy.update, activeTab]);


  const customerFieldOptions = [
    { value: "salutation", label: "Salutation" },
    { value: "first_name", label: "First Name" },
    { value: "middle_name", label: "Middle Name" },
    { value: "last_name", label: "Last Name" },
    { value: "second_last_name", label: "Second Last Name" },
    { value: "gender", label: "Gender" },
    { value: "birth_date", label: "Birth Date" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "country", label: "Country" },
    { value: "city", label: "City" },
    { value: "address", label: "Address" },
    { value: "postal_code", label: "Postal Code" },
    { value: "document_type", label: "Document Type" },
    { value: "document_number", label: "Document Number" },
    { value: "is_vip", label: "Is VIP" },
    { value: "vip_level", label: "VIP Level" },
    { value: "photo_url", label: "Photo URL" },
    { value: "customer_description", label: "Customer Description" }
  ];
  const [requiredFieldPicker, setRequiredFieldPicker] = useState(null);
  const [reservationSettingsFeedback, setReservationSettingsFeedback] = useState(null);
  const [reservationSettingsSaving, setReservationSettingsSaving] = useState(false);
  const [reservationAssetToRemove, setReservationAssetToRemove] = useState(null);
  const [reservationAssetRemoving, setReservationAssetRemoving] = useState(false);
  const [reservationActiveSection, setReservationActiveSection] = useState("reservation-basics");
  const reservationSurfaceRef = React.useRef(null);
  const reservationDialogRef = React.useRef(null);
  const reservationDialogBusyRef = React.useRef(false);
  reservationDialogBusyRef.current = reservationAssetRemoving;

  useEffect(() => {
    setReservationSettingsFeedback(null);
  }, [formData, activeTab]);

  useEffect(() => {
    if (!isPilcoreSettingsTab || (!requiredFieldPicker && !reservationAssetToRemove)) return undefined;
    const previousFocus = document.activeElement;
    const dialog = reservationDialogRef.current;
    if (!dialog) return undefined;
    const focusables = () => Array.from(dialog.querySelectorAll(
      'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)'
    ));
    dialog.focus({ preventScroll: true });
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !reservationDialogBusyRef.current) {
        event.preventDefault();
        setRequiredFieldPicker(null);
        setReservationAssetToRemove(null);
      }
      if (event.key !== "Tab") return;
      const elements = focusables();
      const first = elements[0] || dialog;
      const last = elements[elements.length - 1] || dialog;
      if (!dialog.contains(document.activeElement) || document.activeElement === dialog || !elements.length) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const onFocusIn = (event) => {
      if (!dialog.contains(event.target)) dialog.focus({ preventScroll: true });
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [activeTab, isPilcoreSettingsTab, requiredFieldPicker, reservationAssetToRemove]);
  
  const [showMarketRulePopup, setShowMarketRulePopup] = useState(false);
  const [allRoomRates, setAllRoomRates] = useState([]);

  //24.01.2025 - Para añadir amenities a las room categories

  // ✅ Tabs por room category
 const [rcTabById, setRcTabById] = useState({}); // { [rcId]: "images" | "amenities" | "booking_engine" }

// ✅ Amenities master + asignaciones por room category
const [amenitiesAll, setAmenitiesAll] = useState([]); // [{id, code, name_en, name_es, category, sort_order}]
const [amenitiesLoading, setAmenitiesLoading] = useState(false);

const [rcAmenityIdsByRcId, setRcAmenityIdsByRcId] = useState({}); // { [rcId]: number[] }
const [rcAmenitiesLoadedById, setRcAmenitiesLoadedById] = useState({}); // { [rcId]: true }
const [rcAmenitySavingById, setRcAmenitySavingById] = useState({}); // { [rcId]: true }
const [rcAmenityQueryById, setRcAmenityQueryById] = useState({}); // { [rcId]: "wifi" }
const [rcAmenityCategoryById, setRcAmenityCategoryById] = useState({}); // { [rcId]: "Bathroom" | "" }

// ✅ idioma para mostrar (amenities solo exponen EN/ES en este flujo)
const amenitiesLang = languageId === "en" ? "en" : "es";

function amenityLabel(a) {
  if (!a) return "";
  return amenitiesLang === "es" ? (a.name_es || a.name_en || a.code) : (a.name_en || a.name_es || a.code);
}

function uniqNums(arr) {
  const out = [];
  const seen = new Set();
  for (const x of Array.isArray(arr) ? arr : []) {
    const n = Number(x);
    if (!Number.isFinite(n)) continue;
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

function toggleAmenity(rcId, amenityId) {
  if (demoModeActive || isDemoMode()) return;
  setRcAmenityIdsByRcId((prev) => {
    const p = prev || {};
    const current = uniqNums(p[rcId] || []);
    const id = Number(amenityId);
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    return { ...p, [rcId]: next };
  });
}

 // ✅ Booking Engine commercial copy per room category (separate from room_categories)
 const [beRoomCategoryCopyByRcId, setBeRoomCategoryCopyByRcId] = useState({}); // { [rcId]: copyRowDraft }
 const [beRoomCategoryCopyLoaded, setBeRoomCategoryCopyLoaded] = useState(false);
 const [beRoomCategoryCopyLoading, setBeRoomCategoryCopyLoading] = useState(false);
 const [beRoomCategoryCopySavingByRcId, setBeRoomCategoryCopySavingByRcId] = useState({}); // { [rcId]: true }

 function normalizeBeRoomCategoryCopyRow(row) {
   if (!row) return null;
   return {
     ...row,
     room_category_id: Number(row.room_category_id) || null,
     name_en: String(row.name_en || ""),
     name_es: String(row.name_es || ""),

     short_description_en: row.short_description_en == null ? "" : String(row.short_description_en),
     short_description_es: row.short_description_es == null ? "" : String(row.short_description_es),

     size_m2: row.size_m2 == null ? "" : String(row.size_m2),
     beds_count: row.beds_count == null ? "" : String(row.beds_count),
     max_pax: row.max_pax == null ? "" : String(row.max_pax),

     bed_type_en: row.bed_type_en == null ? "" : String(row.bed_type_en),
     bed_type_es: row.bed_type_es == null ? "" : String(row.bed_type_es),
     view_en: row.view_en == null ? "" : String(row.view_en),
     view_es: row.view_es == null ? "" : String(row.view_es),
     highlights_en: row.highlights_en == null ? "" : String(row.highlights_en),
     highlights_es: row.highlights_es == null ? "" : String(row.highlights_es),
   };
 }

 function buildBeRoomCategoryCopyDefault(rc) {
   const rcId = Number(rc?.id);
   const baseName = String(rc?.name || `Room category #${rcId}`);

   return normalizeBeRoomCategoryCopyRow({
     id: null,
     business_unit_id: Number(businessUnitId),
     room_category_id: rcId,

     name_en: demoModeActive ? "" : baseName,
     name_es: demoModeActive ? "" : baseName,

     short_description_en: "",
     short_description_es: "",

     size_m2: null,
     beds_count: null,
     max_pax: null,

     bed_type_en: "",
     bed_type_es: "",
     view_en: "",
     view_es: "",
     highlights_en: "",
     highlights_es: "",
   });
 }

 function normalizeBookingEngineEditorLanguage(lang) {
   return String(lang || "").toLowerCase().startsWith("es") ? "es" : "en";
 }

 function buildRoomCopyHighlightItems(value) {
   const raw = String(value || "").replace(/\r/g, "").trim();
   if (!raw) return [];

   let items = raw
     .split(/\n+/)
     .map((item) => item.trim())
     .filter(Boolean);

   if (items.length <= 1) {
     items = raw
       .split(/[•·]|;\s*|,\s+/)
       .map((item) => item.trim())
       .filter(Boolean);
   }

   return items.slice(0, 8);
 }

 function buildRoomCopyPreviewData(rc, sourceRow, lang) {
   const safeLang = normalizeBookingEngineEditorLanguage(lang);
   const row = sourceRow || buildBeRoomCategoryCopyDefault(rc);
   const defaultRow = buildBeRoomCategoryCopyDefault(rc);
   const localizedNameField = safeLang === "es" ? "name_es" : "name_en";
   const localizedShortField =
     safeLang === "es" ? "short_description_es" : "short_description_en";
   const localizedBedField = safeLang === "es" ? "bed_type_es" : "bed_type_en";
   const localizedViewField = safeLang === "es" ? "view_es" : "view_en";
   const localizedHighlightsField = safeLang === "es" ? "highlights_es" : "highlights_en";
   const displayName =
     String(row?.[localizedNameField] || "").trim() ||
     String(defaultRow?.[localizedNameField] || "").trim();
   const shortDescription = String(row?.[localizedShortField] || "").trim();
   const bedType = String(row?.[localizedBedField] || "").trim();
   const view = String(row?.[localizedViewField] || "").trim();
   const highlightItems = buildRoomCopyHighlightItems(row?.[localizedHighlightsField]);

   const specsParts = [];
   const size = Number(row?.size_m2);
   if (Number.isFinite(size) && size > 0) {
     specsParts.push(`${Math.trunc(size)} m2`);
   }

   const beds = Number(row?.beds_count);
   if (Number.isFinite(beds) && beds > 0) {
     specsParts.push(
       `${Math.trunc(beds)} ${safeLang === "es" ? "camas" : "beds"}`
     );
   }

   const pax = Number(row?.max_pax);
   if (Number.isFinite(pax) && pax > 0) {
     specsParts.push(`${Math.trunc(pax)} pax`);
   }

   return {
     lang: safeLang,
     displayName,
     shortDescription,
     bedType,
     view,
     highlightItems,
     specsLine: specsParts.join(" / "),
   };
 }

 function roomCopyLanguageIsConfigured(rc, row, lang) {
   const safeLang = normalizeBookingEngineEditorLanguage(lang);
   if (!row?.id) return false;

   const preview = buildRoomCopyPreviewData(rc, row, safeLang);
   return Boolean(
     preview.displayName ||
     preview.shortDescription ||
     preview.bedType ||
     preview.view ||
     preview.highlightItems.length
   );
 }

async function fetchBeRoomCategoryCopy() {
  if (demoModeActive || isDemoMode()) return;
   setBeRoomCategoryCopyLoading(true);
   try {
     const endpoint = `${process.env.REACT_APP_API_URL}/api/booking_engine_room_categories/${businessUnitId}`;
     const res = await axios.get(endpoint);
     const rows = Array.isArray(res.data) ? res.data : [];

     const map = {};
     for (const r of rows) {
       const rcId = Number(r?.room_category_id);
       if (!rcId) continue;
       map[rcId] = normalizeBeRoomCategoryCopyRow(r);
     }

     setBeRoomCategoryCopyByRcId(map);
     setBeRoomCategoryCopyLoaded(true);
     console.log("[SETTINGS][BE_ROOM_CATEGORIES][GET][OK]", { businessUnitId, rows: rows.length });
     return map;
   } catch (err) {
     console.error("[SETTINGS][BE_ROOM_CATEGORIES][GET][ERROR]", err);
     setBeRoomCategoryCopyByRcId({});
     setBeRoomCategoryCopyLoaded(true);
     return {};
   } finally {
     setBeRoomCategoryCopyLoading(false);
   }
 }

 async function ensureBeRoomCategoryCopyLoaded() {
   if (demoModeActive || isDemoMode()) return beRoomCategoryCopyByRcId;
   if (beRoomCategoryCopyLoading) return beRoomCategoryCopyByRcId;
   if (beRoomCategoryCopyLoaded) return beRoomCategoryCopyByRcId;
   return await fetchBeRoomCategoryCopy();
 }

 function parseNullableInt(v) {
   const s = String(v == null ? "" : v).trim();
   if (s === "") return null;
   const n = Number(s);
   if (!Number.isFinite(n)) return null;
   return Math.trunc(n);
 }

 function emptyToNull(v) {
   const s = String(v == null ? "" : v).trim();
   return s === "" ? null : s;
 }

async function saveBeRoomCategoryCopy(rc, draftOverride = null) {
  if (demoModeActive || isDemoMode()) return;
    const rcId = Number(rc?.id);
    if (!rcId) return false;
    const bookingEngineMessages = getBookingEngineStudioMessages(languageId);

    const draft =
      normalizeBeRoomCategoryCopyRow(draftOverride) ||
      beRoomCategoryCopyByRcId?.[rcId] ||
      buildBeRoomCategoryCopyDefault(rc);

   const payload = {
     name_en: String(draft?.name_en || "").trim(),
     name_es: String(draft?.name_es || "").trim(),

     short_description_en: emptyToNull(draft?.short_description_en),
     short_description_es: emptyToNull(draft?.short_description_es),

     size_m2: parseNullableInt(draft?.size_m2),
     beds_count: parseNullableInt(draft?.beds_count),
     max_pax: parseNullableInt(draft?.max_pax),

     bed_type_en: emptyToNull(draft?.bed_type_en),
     bed_type_es: emptyToNull(draft?.bed_type_es),
     view_en: emptyToNull(draft?.view_en),
     view_es: emptyToNull(draft?.view_es),
     highlights_en: emptyToNull(draft?.highlights_en),
     highlights_es: emptyToNull(draft?.highlights_es),
    };

    if (!payload.name_en || !payload.name_es) {
      showBookingEngineToast(bookingEngineMessages.toasts.roomCopyMissingNames, "error");
      return false;
    }

   setBeRoomCategoryCopySavingByRcId((p) => ({ ...(p || {}), [rcId]: true }));

   try {
     const endpoint = `${process.env.REACT_APP_API_URL}/api/booking_engine_room_categories/${businessUnitId}/${rcId}`;
     const res = await axios.put(endpoint, payload);
      const row = normalizeBeRoomCategoryCopyRow(res.data);

      setBeRoomCategoryCopyByRcId((prev) => ({ ...(prev || {}), [rcId]: row }));
      console.log("[SETTINGS][BE_ROOM_CATEGORIES][UPSERT][OK]", { businessUnitId, rcId });
      showBookingEngineToast(bookingEngineMessages.toasts.roomCopySaved, "success");
      return row;
    } catch (err) {
      console.error("[SETTINGS][BE_ROOM_CATEGORIES][UPSERT][ERROR]", { businessUnitId, rcId, err });
      showBookingEngineToast(
        err.response?.data?.error || bookingEngineMessages.toasts.roomCopyError,
        "error"
      );
      return false;
    } finally {
      setBeRoomCategoryCopySavingByRcId((p) => ({ ...(p || {}), [rcId]: false }));
    }
  }

 function resetRoomCopyPrefill() {
   roomCopyRequestRef.current.version += 1;
   roomCopyRequestRef.current.controller?.abort();
   roomCopyRequestRef.current.controller = null;
   setRoomCopyPrefillLoading(false);
   setRoomCopyPrefillNotice(null);
 }

async function prefillRoomCopyEditor() {
  if (demoModeActive || isDemoMode()) return;
   const rcId = Number(roomCopyEditingRcId);
   const requestBusinessUnitId = Number(businessUnitId);
   if (!roomCopyOpen || !roomCopyDraft || !rcId || !requestBusinessUnitId ||
       roomCopyRequestRef.current.controller || beRoomCategoryCopySavingByRcId?.[rcId]) return;

   const controller = new AbortController();
   const version = ++roomCopyRequestRef.current.version;
   roomCopyRequestRef.current.controller = controller;
   const isCurrentRequest = () =>
     roomCopyRequestRef.current.version === version &&
     Number(roomCopyBusinessUnitRef.current) === requestBusinessUnitId &&
     !controller.signal.aborted;
   setRoomCopyPrefillLoading(true);
   setRoomCopyPrefillNotice(null);

   try {
     const endpoint = `${process.env.REACT_APP_API_URL}/api/booking_engine_room_categories/${requestBusinessUnitId}/${rcId}/prefill`;
     const { data } = await axios.get(endpoint, { signal: controller.signal });
     if (!isCurrentRequest()) return;
     if (Number(data?.room_category_id) !== rcId ||
         Number(data?.business_unit_id) !== requestBusinessUnitId) {
       throw new Error("Room-copy suggestions belong to a different category.");
     }

     const suggestions = data?.suggestions || {};
     const fields = [
       "name_en", "name_es", "short_description_en", "short_description_es",
       "size_m2", "beds_count", "max_pax", "bed_type_en", "bed_type_es",
       "view_en", "view_es", "highlights_en", "highlights_es",
     ];
     const patch = {};
     fields.forEach((field) => {
       const value = suggestions[field];
       if (typeof value !== "string" && typeof value !== "number") return;
       const normalized = String(value).trim();
       if (normalized && (typeof value !== "number" || Number.isFinite(value))) {
         patch[field] = normalized;
       }
     });

     if (!Object.keys(patch).length) {
       setRoomCopyPrefillNotice({ kind: "empty" });
       return;
     }

     setRoomCopyDraft((current) => normalizeBeRoomCategoryCopyRow({ ...current, ...patch }));
     setRoomCopyPrefillNotice(data?.source?.room_id
       ? { kind: "success", room: String(data.source.room_number || data.source.room_id) }
       : { kind: "categoryOnly" });
   } catch {
     if (isCurrentRequest()) setRoomCopyPrefillNotice({ kind: "error" });
   } finally {
     if (isCurrentRequest()) {
       roomCopyRequestRef.current.controller = null;
       setRoomCopyPrefillLoading(false);
     }
   }
 }

 function closeRoomCopyEditor(saved = false) {
   if (beRoomCategoryCopySavingByRcId?.[roomCopyEditingRcId] && saved !== true) return;
   resetRoomCopyPrefill();
   setRoomCopyOpen(false);
   setRoomCopyEditingRcId(null);
   setRoomCopyDraft(null);
 }

 async function openRoomCopyEditor(rc, lang = "en") {
   const rcId = Number(rc?.id);
   if (!rcId) return;

   resetRoomCopyPrefill();
   const version = roomCopyRequestRef.current.version;
   const requestBusinessUnitId = Number(businessUnitId);
   const loadedMap = await ensureBeRoomCategoryCopyLoaded();
   if (roomCopyRequestRef.current.version !== version ||
       Number(roomCopyBusinessUnitRef.current) !== requestBusinessUnitId) return;
   const safeLang = normalizeBookingEngineEditorLanguage(lang);
   const sourceMap =
     loadedMap && typeof loadedMap === "object"
       ? loadedMap
       : beRoomCategoryCopyByRcId || {};
   const nextDraft =
     normalizeBeRoomCategoryCopyRow(sourceMap?.[rcId]) ||
     normalizeBeRoomCategoryCopyRow(beRoomCategoryCopyByRcId?.[rcId]) ||
     buildBeRoomCategoryCopyDefault(rc);

   setRoomCopyEditingRcId(rcId);
   setRoomCopyLangTab(safeLang);
   setRoomCopyDraft(nextDraft);
   setRoomCopyOpen(true);
 }

// ✅ Amenity Picker Modal (Add -> pick chips)
const [amenityPickerOpen, setAmenityPickerOpen] = useState(false);
const [amenityPickerRcId, setAmenityPickerRcId] = useState(null);
const [amenityPickerQuery, setAmenityPickerQuery] = useState("");
const [amenityPickerCategory, setAmenityPickerCategory] = useState("");
const amenityPickerSnapshotRef = React.useRef(null);
const amenitySaveRef = React.useRef(new Set());

function openAmenityPicker(rcId, loadedIds) {
  const id = Number(rcId);
  amenityPickerSnapshotRef.current = { id, ids: [...(loadedIds || rcAmenityIdsByRcId?.[id] || [])] };
  setAmenityPickerRcId(id);
  setAmenityPickerQuery("");
  setAmenityPickerCategory("");
  setAmenityPickerOpen(true);
}

function closeAmenityPicker(discard = true) {
  if (amenitySaveRef.current.has(Number(amenityPickerRcId))) return;
  const snapshot = amenityPickerSnapshotRef.current;
  if (discard && snapshot && !demoModeActive) {
    setRcAmenityIdsByRcId((prev) => ({ ...prev, [snapshot.id]: snapshot.ids }));
  }
  amenityPickerSnapshotRef.current = null;
  setAmenityPickerOpen(false);
  setAmenityPickerRcId(null);
  setAmenityPickerQuery("");
  setAmenityPickerCategory("");
}

function isAmenitySelected(rcId, amenityId) {
  const ids = uniqNums(rcAmenityIdsByRcId?.[Number(rcId)] || []);
  return ids.includes(Number(amenityId));
}

function addAmenity(rcId, amenityId) {
  if (demoModeActive || isDemoMode()) return;
  const id = Number(amenityId);
  setRcAmenityIdsByRcId((prev) => {
    const p = prev || {};
    const current = uniqNums(p[Number(rcId)] || []);
    if (current.includes(id)) return p;
    return { ...p, [Number(rcId)]: [...current, id] };
  });
}

function removeAmenity(rcId, amenityId) {
  if (demoModeActive || isDemoMode()) return;
  const id = Number(amenityId);
  setRcAmenityIdsByRcId((prev) => {
    const p = prev || {};
    const current = uniqNums(p[Number(rcId)] || []);
    return { ...p, [Number(rcId)]: current.filter((x) => x !== id) };
  });
}



  // Keep only one room_rate per group_key (representative = lowest id)
  const uniqueRoomRates = React.useMemo(() => {
    const byGk = new Map();
    for (const r of allRoomRates || []) {
      const gk = r?.group_key || `__NO_GK__:${r?.id}`;
      const existing = byGk.get(gk);
      if (!existing || (r?.id ?? Number.MAX_SAFE_INTEGER) < (existing?.id ?? Number.MAX_SAFE_INTEGER)) {
        byGk.set(gk, r);
      }
    }
    const arr = Array.from(byGk.values()).sort((a, b) => {
      const catA = (a?.room_category?.name || "").toLowerCase();
      const catB = (b?.room_category?.name || "").toLowerCase();
      if (catA < catB) return -1;
      if (catA > catB) return 1;
      const nA = (a?.name || "").toLowerCase();
      const nB = (b?.name || "").toLowerCase();
      if (nA < nB) return -1;
      if (nA > nB) return 1;
      return 0;
    });
    console.log("[Settings][Rates] uniqueRoomRates=", arr.length, "from allRoomRates=", (allRoomRates || []).length);
    return arr;
  }, [allRoomRates]);

  // 👉 Categories string for the currently picked room_rate (includes sisters)
  const selectedRateCategories = React.useMemo(() => {
    const id = Number(newTarget?.room_rate_id);
    if (!id) return "";
    const picked = (allRoomRates || []).find(r => r?.id === id);
    if (!picked) return "";
    const gk = picked?.group_key || `__NO_GK__:${picked?.id}`;
    const sisters = (allRoomRates || []).filter(r => (r?.group_key || `__NO_GK__:${r?.id}`) === gk);
    const names = Array.from(new Set(sisters.map(r => r?.room_category?.name).filter(Boolean)));
    const txt = names.join(" + ");
    console.log("[Targets] room_rate_id=", id, "group_key=", gk, "categories=", txt || "(none)");
    return txt;
  }, [newTarget?.room_rate_id, allRoomRates]);

  const [roomCategories, setRoomCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [userList, setUserList] = useState([]);
  const [roles, setRoles] = useState([]);

  const managementContextKey = JSON.stringify([currentUser, businessUnitId]);
  const [managementSnapshot, setManagementSnapshot] = useState(null);
  const [managementRevision, setManagementRevision] = useState(0);
  const managementKeyRef = React.useRef(managementContextKey);
  managementKeyRef.current = managementContextKey;
  const managementOptions = managementSnapshot?.key === managementContextKey ? managementSnapshot.data : null;
  const availableBusinessUnits = managementOptions?.business_units || [];
  const [showRolePopup, setShowRolePopup] = useState(false);
  const usersRolesRootRef = React.useRef(null);
  const usersRolesBusyRef = React.useRef(false);
  const [usersRolesBusy, setUsersRolesBusy] = useState(false);
  const [usersRolesFeedback, setUsersRolesFeedback] = useState(null);
  const [usersRolesConfirmation, setUsersRolesConfirmation] = useState(null);
  const notifyUsersRoles = React.useCallback((message, tone = "error") => setUsersRolesFeedback({ message, tone }), []);
  const runUsersRolesWrite = async (action) => {
    if (demoModeActive || isDemoMode() || !canViewUsersTab || usersRolesBusyRef.current) return;
    usersRolesBusyRef.current = true;
    setUsersRolesBusy(true);
    setUsersRolesFeedback(null);
    try { await action(); } finally { usersRolesBusyRef.current = false; setUsersRolesBusy(false); }
  };
  const [selectedRole, setSelectedRole] = useState(null);
  const [editablePermissions, setEditablePermissions] = useState({});
  const [selectedRoleBusinessUnitIds, setSelectedRoleBusinessUnitIds] = useState([]);
  const [crmPipelines, setCrmPipelines] = useState([]);
  const [crmPipelinesLoading, setCrmPipelinesLoading] = useState(false);
  const [crmPipelinesSaving, setCrmPipelinesSaving] = useState(false);
  const [crmPipelinesError, setCrmPipelinesError] = useState("");
  const [crmSelectedPipelineId, setCrmSelectedPipelineId] = useState("");
  const [crmPipelineDraft, setCrmPipelineDraft] = useState(() => buildEmptyCrmPipelineDraft());

const fetchUsers = React.useCallback(async () => {
  if (demoModeActive || isDemoMode() || !canViewUsersTab) return;
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`);
    if (managementKeyRef.current === managementContextKey) setUserList(res.data);
  } catch (err) {
    console.error("❌ Error cargando usuarios:", err);
    notifyUsersRoles("Users could not be loaded. Refresh the page to try again.");
  }
}, [demoModeActive, canViewUsersTab, notifyUsersRoles, managementContextKey]);


  const [showNewRolePopup, setShowNewRolePopup] = useState(false);
  const [newRolePermissions, setNewRolePermissions] = useState({});
  const [newRoleData, setNewRoleData] = useState({
    name: "",
    description: "",
    business_unit_ids: []
  });
  const ALLOWED_HOURS = { start: 8, end: 22 };
  const getTimeStatus = () => {
    const now = new Date();
    const hour = now.getHours();

    const isInside =
      hour >= ALLOWED_HOURS.start && hour < ALLOWED_HOURS.end;

    return {
      inside: isInside,
      label: `🕒 Activo de ${String(ALLOWED_HOURS.start).padStart(2, "0")}:00 a ${String(ALLOWED_HOURS.end).padStart(2, "0")}:00`,
      now: `${String(hour).padStart(2, "0")}:00`,
    };
  };

  const [showNewUserPopup, setShowNewUserPopup] = useState(false);
  const [showCorporateUserPopup, setShowCorporateUserPopup] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: "",
    email: ""
  });
  const EMPTY_USER_ASSIGNMENT = { business_unit_id: "", role_id: "" };
  const [newUserAssignments, setNewUserAssignments] = useState([{ ...EMPTY_USER_ASSIGNMENT }]);

  // Estado para modal de edición
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [editName, setEditName] = useState("");
  const [editAssignments, setEditAssignments] = useState([]);

  useEffect(() => {
    setShowNewUserPopup(false);
    setShowUserModal(false);
    setSelectedUser(null);
    setShowNewRolePopup(false);
    setShowRolePopup(false);
    setSelectedRole(null);
    setUsersRolesConfirmation(null);
    setUserList([]);
    setRoles([]);
  }, [managementContextKey]);
  



  async function handleEditUser(user) {
    if (!managementOptions || !canManageUsers || user?.management?.can_edit !== true) return;
    try {
      setLoadingUser(true);
      console.log("[Users][Edit] Fetching user from API:", user.id);

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/${user.id}`);
      const data = res.data;

      if (managementKeyRef.current !== managementContextKey) return;
      if (data?.ok && data.user?.management?.can_edit === true) {
        setSelectedUser(data.user);
        setEditName(data.user.name || "");   // 👈 aquí inicializamos el input con el nombre actual
        setShowUserModal(true);
      } else {
        console.error("[Users][Edit] Failed to fetch user:", data);
        notifyUsersRoles("User details could not be loaded. Please try again.");
      }
    } catch (e) {
      console.error("[Users][Edit] Network error:", e);
      notifyUsersRoles("User details could not be loaded. Please try again.");
    } finally {
      setLoadingUser(false);
    }
  }

function setBookingEngineText(textKey, lang, value) {
  if (demoModeActive || isDemoMode()) return;
  setFormData(prev => {
    const cur = prev.booking_engine_texts || {};
    const curKey = cur[textKey] || {};
    return {
      ...prev,
      booking_engine_texts: {
        ...cur,
        [textKey]: {
          ...curKey,
          [lang]: value,
        },
      },
    };
  });
}

function getBookingEngineText(textKey, lang) {
  const cur = formData.booking_engine_texts || {};
  const curKey = cur[textKey] || {};
  return curKey?.[lang] ?? "";
}

function toggleBookingHeroBackgroundScreen(screenId) {
  if (demoModeActive || isDemoMode()) return;
  setFormData((prev) => {
    const entryMode = prev.booking_engine_entry_mode === "widget" ? "widget" : "hosted";
    const current = normalizeBookingHeroBackgroundScreens(
      prev.booking_engine_hero_background_screens,
      entryMode
    );
    const next = current.includes(screenId)
      ? current.filter((value) => value !== screenId)
      : [...current, screenId];

    return {
      ...prev,
      booking_engine_hero_background_screens: normalizeBookingHeroBackgroundScreens(
        next,
        entryMode
      ),
    };
  });
}

//18.01.2026 - para la carga de imagenes

// ✅ Booking Engine - Hero background upload
const [heroBgFile, setHeroBgFile] = useState(null);
const [heroBgUploading, setHeroBgUploading] = useState(false);

//20.01.2026 - para imagenes de room categories

// ✅ Active hero asset (read from booking_engine_assets via backend)
const [heroBgAsset, setHeroBgAsset] = useState(null);
const [heroBgLoading, setHeroBgLoading] = useState(false);

const [reservationEmailAssets, setReservationEmailAssets] = useState({});
const [reservationEmailAssetFiles, setReservationEmailAssetFiles] = useState({});
const [reservationEmailAssetLoadingByKind, setReservationEmailAssetLoadingByKind] = useState({});
const [reservationEmailAssetUploadingByKind, setReservationEmailAssetUploadingByKind] = useState({});
const [isDownloadingReservationPdfSample, setIsDownloadingReservationPdfSample] = useState(false);

// ✅ Room category images (Settings) — explicit cover/gallery upload mode (max 10)
const [roomCategoryImages, setRoomCategoryImages] = useState({}); // { [room_category_id]: [{ asset_id, kind, public_url }] }
const [roomCategoryImageFileById, setRoomCategoryImageFileById] = useState({}); // { [room_category_id]: File }
const [roomCategoryImageValidationById, setRoomCategoryImageValidationById] = useState({});
const [roomCategoryImagesLoading, setRoomCategoryImagesLoading] = useState(false);

  const [termsLangTab, setTermsLangTab] = useState("en"); // "en" | "es"



async function uploadHeroBackground() {
  if (demoModeActive || isDemoMode()) return;
  const bookingEngineMessages = getBookingEngineStudioMessages(languageId);
  try {
    const enabledScreens = normalizeBookingHeroBackgroundScreens(
      formData.booking_engine_hero_background_screens,
      formData.booking_engine_entry_mode
    );
    if (!enabledScreens.length) {
      showBookingEngineToast(bookingEngineMessages.toasts.selectHeroScreenFirst, "error");
      return;
    }
    if (!heroBgFile) {
      showBookingEngineToast(bookingEngineMessages.toasts.pickImageFirst, "error");
      return;
    }
    if (heroBgAsset?.id) {
      showBookingEngineToast(bookingEngineMessages.toasts.heroAlreadyExists, "error");
      return;
    }

    setHeroBgUploading(true);

    const fd = new FormData();
    fd.append("file", heroBgFile);
    fd.append("business_unit_id", String(businessUnitId));
    fd.append("kind", "hero_background");

    const endpoint = `${process.env.REACT_APP_API_URL}/api/booking-assets/upload`;

    console.log("[BOOKING_ENGINE][HERO_BG][UPLOAD][OUT]", {
      business_unit_id: businessUnitId,
      fileName: heroBgFile?.name,
      size: heroBgFile?.size,
      type: heroBgFile?.type,
      endpoint
    });

    const res = await axios.post(endpoint, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    console.log("[BOOKING_ENGINE][HERO_BG][UPLOAD][OK]", res.data);

    setHeroBgFile(null);
    await fetchActiveHeroBg();

    showBookingEngineToast(bookingEngineMessages.toasts.heroUploaded, "success");
  } catch (err) {
    console.error("[BOOKING_ENGINE][HERO_BG][UPLOAD][ERROR]", err);
    showBookingEngineToast(
      err.response?.data?.error || bookingEngineMessages.toasts.heroUploadError,
      "error"
    );
  } finally {
    setHeroBgUploading(false);
  }
}

async function fetchRoomCategoryImages() {
  if (demoModeActive || isDemoMode()) return;
  try {
    setRoomCategoryImagesLoading(true);

    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/booking-assets/room-category-images`,
      { params: { business_unit_id: businessUnitId } }
    );

    const map = {};
    for (const r of res.data || []) {
      const rcId = Number(r.room_category_id);
      if (!map[rcId]) map[rcId] = [];
      map[rcId].push({
        asset_id: Number(r.asset_id),
        kind: r.kind,
        public_url: r.public_url,
      });
    }

    // ensure cover first
    for (const k of Object.keys(map)) {
      map[k] = (map[k] || []).sort((a, b) => {
        if (a.kind === "cover" && b.kind !== "cover") return -1;
        if (a.kind !== "cover" && b.kind === "cover") return 1;
        return (b.asset_id || 0) - (a.asset_id || 0);
      });
    }

    console.log("[SETTINGS][ROOM_CATEGORY_IMAGES][OK]", { count: Object.keys(map).length });
    setRoomCategoryImages(map);
  } catch (err) {
    console.error("[SETTINGS][ROOM_CATEGORY_IMAGES][ERROR]", err);
    setRoomCategoryImages({});
  } finally {
    setRoomCategoryImagesLoading(false);
  }
}

async function validateRoomCategoryImageSelection(roomCategoryId, file) {
  if (demoModeActive || isDemoMode()) return;
  const rcId = Number(roomCategoryId);

  if (!file) {
    setRoomCategoryImageValidationById((prev) => {
      const next = { ...(prev || {}) };
      delete next[rcId];
      return next;
    });
    return { valid: false, reason: "missing", status: "idle", file: null };
  }

  setRoomCategoryImageValidationById((prev) => ({
    ...(prev || {}),
    [rcId]: { status: "checking", file },
  }));

  try {
    const result = await validateBookingRoomImageFile(file);
    const nextValidation = {
      ...result,
      status: result.valid ? "valid" : "invalid",
      file,
    };

    setRoomCategoryImageValidationById((prev) =>
      prev?.[rcId]?.file === file
        ? { ...(prev || {}), [rcId]: nextValidation }
        : prev
    );
    return nextValidation;
  } catch (_error) {
    const nextValidation = {
      valid: false,
      reason: "unreadable",
      status: "invalid",
      file,
    };

    setRoomCategoryImageValidationById((prev) =>
      prev?.[rcId]?.file === file
        ? { ...(prev || {}), [rcId]: nextValidation }
        : prev
    );
    return nextValidation;
  }
}

async function handleRoomCategoryImageFileChange(roomCategoryId, file) {
  if (demoModeActive || isDemoMode()) return;
  const rcId = Number(roomCategoryId);
  setRoomCategoryImageFileById((prev) => ({
    ...(prev || {}),
    [rcId]: file || null,
  }));
  await validateRoomCategoryImageSelection(rcId, file);
}

//21.01.2026 - para mejorar el comportamiento de cover y gallery
async function uploadRoomCategoryImage(roomCategoryId, kindOverride = null) {
  if (demoModeActive || isDemoMode()) return;
  const bookingEngineMessages = getBookingEngineStudioMessages(languageId);
  try {
    const rcId = Number(roomCategoryId);
    const file = roomCategoryImageFileById?.[rcId];
    if (!file) {
      showBookingEngineToast(bookingEngineMessages.toasts.pickImageFirst, "error");
      return;
    }

    const imageValidation = await validateRoomCategoryImageSelection(rcId, file);
    if (!imageValidation.valid) {
      showBookingEngineToast(
        imageValidation.reason === "unreadable"
          ? bookingEngineMessages.rooms.imageUnreadable
          : bookingEngineMessages.rooms.imageRatioInvalid,
        "error"
      );
      return;
    }

    const kindToSend = (kindOverride && String(kindOverride).trim() !== "")
      ? String(kindOverride).trim().toLowerCase()
      : "gallery";

    const fd = new FormData();
    fd.append("file", file);
    fd.append("business_unit_id", String(businessUnitId));
    fd.append("kind", kindToSend);
    fd.append("entity_type", "room_category");
    fd.append("entity_id", String(rcId));

    const endpoint = `${process.env.REACT_APP_API_URL}/api/booking-assets/upload`;

    console.log("[SETTINGS][ROOM_CATEGORY_IMAGE][UPLOAD][OUT]", {
      business_unit_id: businessUnitId,
      room_category_id: rcId,
      kind: kindToSend,
      fileName: file?.name,
      size: file?.size,
      type: file?.type,
      endpoint,
    });

    await axios.post(endpoint, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    setRoomCategoryImageFileById((prev) => {
      const next = { ...(prev || {}) };
      delete next[rcId];
      return next;
    });
    setRoomCategoryImageValidationById((prev) => {
      const next = { ...(prev || {}) };
      delete next[rcId];
      return next;
    });

    await fetchRoomCategoryImages();
    showBookingEngineToast(bookingEngineMessages.toasts.imageUploaded, "success");
  } catch (err) {
    console.error("[SETTINGS][ROOM_CATEGORY_IMAGE][UPLOAD][ERROR]", err);
    showBookingEngineToast(
      err.response?.data?.error || bookingEngineMessages.toasts.imageError,
      "error"
    );
  }
}


async function deactivateRoomCategoryImage(assetId) {
  if (demoModeActive || isDemoMode()) return;
  const bookingEngineMessages = getBookingEngineStudioMessages(languageId);
  try {
    const endpoint = `${process.env.REACT_APP_API_URL}/api/booking-assets/deactivate/${assetId}`;
    console.log("[SETTINGS][ROOM_CATEGORY_IMAGE][DEACTIVATE][OUT]", { assetId, endpoint });

    await axios.post(endpoint);

    await fetchRoomCategoryImages();
    showBookingEngineToast(bookingEngineMessages.toasts.imageRemoved, "success");
    return true;
  } catch (err) {
    console.error("[SETTINGS][ROOM_CATEGORY_IMAGE][DEACTIVATE][ERROR]", err);
    showBookingEngineToast(
      err.response?.data?.error || bookingEngineMessages.toasts.imageError,
      "error"
    );
    return false;
  }
}

const [roomCategoryImagesModalOpen, setRoomCategoryImagesModalOpen] = useState(false);
const [roomCategoryImagesModalReady, setRoomCategoryImagesModalReady] = useState(false);
const [roomCategoryImagesModalRcId, setRoomCategoryImagesModalRcId] = useState(null);
const [roomCategoryImagesModalIndex, setRoomCategoryImagesModalIndex] = useState(0);

function openRoomCategoryImagesModal(rcId, startIndex = 0) {
  console.log("[SETTINGS][ROOM_CATEGORY_IMAGES][MODAL][OPEN]", { rcId, startIndex });
  setRoomCategoryImagesModalRcId(Number(rcId));
  setRoomCategoryImagesModalIndex(Number(startIndex) || 0);
  setRoomCategoryImagesModalOpen(true);
}

function closeRoomCategoryImagesModal() {
  console.log("[SETTINGS][ROOM_CATEGORY_IMAGES][MODAL][CLOSE]");
  setRoomCategoryImagesModalOpen(false);
}

useEffect(() => {
  if (!roomCategoryImagesModalOpen) return;

  setRoomCategoryImagesModalReady(false);

  async function load() {
    // No extra fetch needed: we already have roomCategoryImages in memory
    setRoomCategoryImagesModalReady(true);
  }

  load();
}, [roomCategoryImagesModalOpen]);

async function sendImageToCover(assetId) {
  if (demoModeActive || isDemoMode()) return;
  const bookingEngineMessages = getBookingEngineStudioMessages(languageId);
  try {
    const endpoint = `${process.env.REACT_APP_API_URL}/api/booking-assets/room-category/make-cover/${assetId}`;

    console.log("[SETTINGS][ROOM_CATEGORY_IMAGES][MAKE_COVER][OUT]", { assetId, endpoint });

    await axios.post(endpoint);

    await fetchRoomCategoryImages();
    showBookingEngineToast(bookingEngineMessages.toasts.coverUpdated, "success");
  } catch (err) {
    console.error("[SETTINGS][ROOM_CATEGORY_IMAGES][MAKE_COVER][ERROR]", err);
    showBookingEngineToast(
      err.response?.data?.error || bookingEngineMessages.toasts.imageError,
      "error"
    );
  }
}

useEffect(() => {
  if (demoModeActive) return;
  if (activeTab !== "booking_engine") return;

  // ✅ reset caches por BU (evita mezclar amenities seleccionadas entre hoteles)
  setRcAmenitiesLoadedById({});
  setRcAmenityIdsByRcId({});
  setRcAmenitySavingById({});
  setRcAmenityQueryById({});
  setRcAmenityCategoryById({});
  setRcTabById({});
  setBeRoomCategoryCopyByRcId({});
  setBeRoomCategoryCopyLoaded(false);
  setBeRoomCategoryCopySavingByRcId({});

  fetchRoomCategoryImages();   // ya existente
  fetchBookingEngineRoomCategories();
  fetchAmenitiesAll();         // ✅ master list
  fetchBeRoomCategoryCopy();   // ✅ booking engine commercial copy
}, [activeTab, businessUnitId, demoModeActive]);


  async function fetchBookingEngineRoomCategories() {
    if (demoModeActive || isDemoMode()) return;
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/room_categories`, {
        params: { business_unit_id: businessUnitId },
      });
      setRoomCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("[SETTINGS][BOOKING_ENGINE][ROOM_CATEGORIES][ERROR]", err);
      setRoomCategories([]);
    }
  }

  useEffect(() => {
    if (demoModeActive) return;
    if (activeTab !== "reservation") return;
    setRoomCategories([]);
    fetchBookingEngineRoomCategories();
  }, [activeTab, businessUnitId, demoModeActive]);

async function fetchAmenitiesAll() {
  if (demoModeActive || isDemoMode()) return;
  setAmenitiesLoading(true);
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/amenities`, {
      params: { business_unit_id: businessUnitId },
    });
    const rows = Array.isArray(res.data?.rows) ? res.data.rows : Array.isArray(res.data) ? res.data : [];
    setAmenitiesAll(rows);
  } catch (err) {
    console.error("[SETTINGS][AMENITIES][LIST] ERROR", err);
    setAmenitiesAll([]);
  } finally {
    setAmenitiesLoading(false);
  }
}


async function ensureRoomCategoryAmenitiesLoaded(rcId) {
  if (demoModeActive || isDemoMode() || rcAmenitiesLoadedById?.[rcId]) return uniqNums(rcAmenityIdsByRcId?.[rcId] || []);

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/amenities/room-category/${rcId}`
    );

    const ids = uniqNums(res.data?.amenity_ids || res.data?.amenityIds || res.data?.rows || []);
    setRcAmenityIdsByRcId((p) => ({ ...(p || {}), [rcId]: ids }));
    setRcAmenitiesLoadedById((p) => ({ ...(p || {}), [rcId]: true }));
    return ids;
  } catch (err) {
    console.error("[SETTINGS][RC_AMENITIES][GET] ERROR", { rcId, err });
    showBookingEngineToast(getBookingEngineStudioMessages(languageId).rooms.amenitiesLoadError, "error");
    return null;
  }
}


async function saveRoomCategoryAmenities(rcId) {
  if (demoModeActive || isDemoMode() || amenitySaveRef.current.has(Number(rcId))) return false;
  amenitySaveRef.current.add(Number(rcId));
  const ids = uniqNums(rcAmenityIdsByRcId?.[rcId] || []);
  const bookingEngineMessages = getBookingEngineStudioMessages(languageId);
  setRcAmenitySavingById((p) => ({ ...(p || {}), [rcId]: true }));

  try {
    await axios.put(
      `${process.env.REACT_APP_API_URL}/api/amenities/room-category/${rcId}`,
      { amenity_ids: ids }
    );
    console.log("[SETTINGS][RC_AMENITIES][SAVE] OK", { rcId, count: ids.length });
    showBookingEngineToast(bookingEngineMessages.toasts.amenitiesSaved, "success");
    return true;
  } catch (err) {
    console.error("[SETTINGS][RC_AMENITIES][SAVE] ERROR", { rcId, err });
    showBookingEngineToast(
      err.response?.data?.error || bookingEngineMessages.toasts.amenitiesError,
      "error"
    );
    return false;
  } finally {
    amenitySaveRef.current.delete(Number(rcId));
    setRcAmenitySavingById((p) => ({ ...(p || {}), [rcId]: false }));
  }
}



async function fetchActiveHeroBg() {
  if (demoModeActive || isDemoMode()) return;
  try {
    setHeroBgLoading(true);

    const endpoint = `${process.env.REACT_APP_API_URL}/api/booking-assets/active`;
    const res = await axios.get(endpoint, {
      params: { business_unit_id: businessUnitId, kind: "hero_background" },
    });

    setHeroBgAsset(res.data?.asset || null);

    console.log("[BOOKING_ENGINE][HERO_BG][ACTIVE][OK]", res.data);
  } catch (err) {
    console.error("[BOOKING_ENGINE][HERO_BG][ACTIVE][ERROR]", err);
    setHeroBgAsset(null);
  } finally {
    setHeroBgLoading(false);
  }
}

async function removeActiveHeroBg() {
  if (demoModeActive || isDemoMode()) return;
  const bookingEngineMessages = getBookingEngineStudioMessages(languageId);
  try {
    if (!heroBgAsset?.id) return;

    const endpoint = `${process.env.REACT_APP_API_URL}/api/booking-assets/active`;
    await axios.delete(endpoint, {
      params: { business_unit_id: businessUnitId, kind: "hero_background" },
    });

    setHeroBgAsset(null);
    setHeroBgFile(null);

    console.log("[BOOKING_ENGINE][HERO_BG][REMOVE][OK]");
    showBookingEngineToast(bookingEngineMessages.toasts.heroRemoved, "success");
    return true;
  } catch (err) {
    console.error("[BOOKING_ENGINE][HERO_BG][REMOVE][ERROR]", err);
    showBookingEngineToast(
      err.response?.data?.error || bookingEngineMessages.toasts.heroUploadError,
      "error"
    );
    return false;
  }
}

async function fetchReservationEmailAsset(kind) {
  const safeKind = String(kind || "").trim().toLowerCase();
  if (!safeKind) return null;

  try {
    setReservationEmailAssetLoadingByKind((prev) => ({ ...(prev || {}), [safeKind]: true }));

    const endpoint = `${process.env.REACT_APP_API_URL}/api/booking-assets/active`;
    const res = await axios.get(endpoint, {
      params: { business_unit_id: businessUnitId, kind: safeKind },
    });

    const asset = res.data?.asset || null;
    setReservationEmailAssets((prev) => ({ ...(prev || {}), [safeKind]: asset }));
    return asset;
  } catch (err) {
    console.error("[RESERVATION_EMAIL_ASSET][ACTIVE][ERROR]", { kind: safeKind, err });
    setReservationEmailAssets((prev) => ({ ...(prev || {}), [safeKind]: null }));
    return null;
  } finally {
    setReservationEmailAssetLoadingByKind((prev) => ({ ...(prev || {}), [safeKind]: false }));
  }
}

async function fetchReservationEmailAssets() {
  await Promise.all(
    RESERVATION_CONFIRMATION_ASSET_DEFINITIONS.map((assetDef) =>
      fetchReservationEmailAsset(assetDef.kind)
    )
  );
}

async function uploadReservationEmailAsset(kind) {
  const safeKind = String(kind || "").trim().toLowerCase();
  const file = reservationEmailAssetFiles?.[safeKind] || null;
  if (!file) {
    setReservationSettingsFeedback({ tone: "error", message: "Select an image before uploading." });
    return;
  }

  try {
    setReservationEmailAssetUploadingByKind((prev) => ({ ...(prev || {}), [safeKind]: true }));

    const fd = new FormData();
    fd.append("file", file);
    fd.append("business_unit_id", String(businessUnitId));
    fd.append("kind", safeKind);

    await axios.post(`${process.env.REACT_APP_API_URL}/api/booking-assets/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    setReservationEmailAssetFiles((prev) => {
      const next = { ...(prev || {}) };
      delete next[safeKind];
      return next;
    });

    await fetchReservationEmailAsset(safeKind);
    setReservationSettingsFeedback({ tone: "success", message: "Email visual uploaded. This change is already saved." });
  } catch (err) {
    console.error("[RESERVATION_EMAIL_ASSET][UPLOAD][ERROR]", { kind: safeKind, err });
    setReservationSettingsFeedback({ tone: "error", message: err.response?.data?.error || "The image could not be uploaded. Please try again." });
  } finally {
    setReservationEmailAssetUploadingByKind((prev) => ({ ...(prev || {}), [safeKind]: false }));
  }
}

async function removeReservationEmailAsset(kind) {
  if (demoModeActive || isDemoMode() || reservationAssetRemoving) return;
  const safeKind = String(kind || "").trim().toLowerCase();
  const currentAsset = reservationEmailAssets?.[safeKind] || null;
  if (!currentAsset?.id) return;

  try {
    setReservationAssetRemoving(true);
    await axios.delete(`${process.env.REACT_APP_API_URL}/api/booking-assets/active`, {
      params: { business_unit_id: businessUnitId, kind: safeKind },
    });

    setReservationEmailAssets((prev) => ({ ...(prev || {}), [safeKind]: null }));
    setReservationEmailAssetFiles((prev) => {
      const next = { ...(prev || {}) };
      delete next[safeKind];
      return next;
    });
    setReservationAssetToRemove(null);
    setReservationSettingsFeedback({ tone: "success", message: "Email visual removed. This change is already saved." });
  } catch (err) {
    console.error("[RESERVATION_EMAIL_ASSET][REMOVE][ERROR]", { kind: safeKind, err });
    setReservationAssetToRemove(null);
    setReservationSettingsFeedback({ tone: "error", message: err.response?.data?.error || "The image could not be removed. Please try again." });
  } finally {
    setReservationAssetRemoving(false);
  }
}

async function downloadReservationConfirmationSamplePdf(languages) {
  try {
    setIsDownloadingReservationPdfSample(true);

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/hotel-settings/${businessUnitId}/reservation-confirmation-preview-pdf`,
      {
        reservation_confirmation_email_languages: Array.isArray(languages) ? languages : [],
      },
      {
        responseType: "blob",
      }
    );

    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "reservation-confirmation-preview.pdf";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("[RESERVATION_CONFIRMATION][PDF_PREVIEW_DOWNLOAD][ERROR]", err);
    setReservationSettingsFeedback({ tone: "error", message: "The sample PDF could not be downloaded. Please try again." });
  } finally {
    setIsDownloadingReservationPdfSample(false);
  }
}



  
  const handleUserStatusChange = async (user) => {
    if (!managementOptions || user?.management?.can_change_status !== true || !canManageTargetUser(currentUser, user)) return;
    setUsersRolesFeedback(null);
    setUsersRolesConfirmation({ kind: "status", user });
  };

  const handleDeleteUser = async (user) => {
    if (!managementOptions || user?.management?.can_delete !== true || !canPermanentlyDeleteTargetUser(currentUser, user)) return;
    setUsersRolesFeedback(null);
    setUsersRolesConfirmation({ kind: "delete-user", user });
  };

  const handleDeleteRole = (role) => {
    if (!managementOptions || role?.management?.can_delete !== true) return;
    setUsersRolesFeedback(null);
    setUsersRolesConfirmation({ kind: "delete-role", role });
  };

  const confirmUsersRolesAction = () => runUsersRolesWrite(async () => {
    const { kind, user, role } = usersRolesConfirmation || {};
    try {
      if (kind === "status") {
        if (!managementOptions || user?.management?.can_change_status !== true || !canManageTargetUser(currentUser, user)) return;
        await updateUserStatus(user);
      } else if (kind === "delete-user") {
        if (!managementOptions || user?.management?.can_delete !== true || !canPermanentlyDeleteTargetUser(currentUser, user)) return;
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/${user.id}`);
        await fetchUsers();
      } else if (kind === "delete-role") {
        if (!managementOptions || role?.management?.can_delete !== true) return;
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/roles/${role.id}`);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/roles`);
        setRoles(response.data);
        setManagementRevision((revision) => revision + 1);
      } else return;
      setUsersRolesConfirmation(null);
      notifyUsersRoles(kind === "status" ? "User status updated." : kind === "delete-user" ? "User deleted." : "Role deleted.", "success");
    } catch (error) {
      notifyUsersRoles(error.response?.data?.error || "The change could not be completed. Please try again.");
    }
  });

  const updateUserStatus = async (user) => {
    const nextIsActive = !user.is_active;
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/users/${user.id}/status`,
        { is_active: nextIsActive }
      );
      setUserList((previousUsers) =>
        previousUsers.map((row) =>
          row.id === user.id ? { ...row, is_active: response.data?.user?.is_active ?? nextIsActive } : row
        )
      );
      await fetchUsers();
  };








    const runNow = async () => {
      try {
        const res = await axios.post("/api/cron/run-now");
        alert("✅ Ejecutado: " + res.data.message);
        console.log("📝 Output:", res.data.output);
        fetchCronStatus();

      } catch (err) {
        console.error("🔥 ERROR completo:", err);
        alert("❌ Error ejecutando runner: " + (err.response?.data?.error || err.message));
      }
    };


  const [logs, setLogs] = useState("");
  const [cronStatus, setCronStatus] = useState("unknown");


  const pauseCron = async () => {
    await axios.post("/api/cron/stop");
    fetchCronStatus();
  };

  const resumeCron = async () => {
    await axios.post("/api/cron/start");
    fetchCronStatus();
  };

  const deleteCron = async () => {
    await axios.post("/api/cron/delete");
    fetchCronStatus();
  };

  const fetchLogs = async () => {
    const res = await axios.get("/api/cron/logs");
    setLogs(res.data);
  };

  const fetchCronStatus = React.useCallback(async () => {
    if (demoModeActive || isDemoMode()) return;
    try {
      const res = await axios.get("/api/cron/status");
      setCronStatus(res.data.status || "unknown");
    } catch (err) {
      console.error("❌ Error al consultar estado del cron:", err);
      setCronStatus("error");
    }
  }, [demoModeActive]);





  
  useEffect(() => {
    if (demoModeActive || !canViewUsersTab || activeTab !== "users") return;

    fetchUsers();

    axios.get(`${process.env.REACT_APP_API_URL}/api/roles`)
      .then((res) => {
        console.log("🎭 Roles:", res.data);
        if (managementKeyRef.current === managementContextKey) setRoles(res.data);
      })
      .catch((err) => {
        console.error("❌ Error loading roles:", err);
        if (managementKeyRef.current === managementContextKey) setRoles([]);
      });
  }, [activeTab, demoModeActive, canViewUsersTab, fetchUsers, managementContextKey]);

  useEffect(() => {
    let cancelled = false;
    setManagementSnapshot(null);
    if (demoModeActive || !canViewUsersTab || activeTab !== "users") return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/users/management-options`)
      .then(({ data }) => {
        if (!Array.isArray(data?.business_units)) throw new Error("Invalid management options");
        if (!cancelled) setManagementSnapshot({ key: managementContextKey, data });
      })
      .catch(() => {
        if (!cancelled) notifyUsersRoles(roleText("Hotel permissions could not be loaded. Refresh the page to try again."));
      });
    return () => { cancelled = true; };
    // roleText is only used for an error; session changes must reload authorization.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, demoModeActive, canViewUsersTab, managementContextKey, managementRevision, notifyUsersRoles]);




  useEffect(() => {
    if (demoModeActive || !formData.rms_source) return;

    axios.get(`${process.env.REACT_APP_API_URL}/api/roomRates/all`, {
      params: { business_unit_id: businessUnitId }
    })
      .then((res) => {
        console.log("[Settings][RoomRates] Loaded all rates for BU:", businessUnitId, "count=", res.data?.length ?? 0);
        setAllRoomRates(res.data);
      })
      .catch((err) => {
        console.error("❌ Error loading room rates (all BU):", err);
      });
  }, [formData.rms_source, demoModeActive]);


  const availableRoomRates = React.useMemo(() => {
    const filtered = uniqueRoomRates.filter(
      (rate) => !(formData.market_price_rules || []).some((rule) => rule.room_rate_id === rate.id)
    );
    console.log("[Settings][Rates] availableRoomRates (unique, not in rules) =", filtered.length);
    return filtered;
  }, [uniqueRoomRates, formData.market_price_rules]);


  const handleEditRole = (role) => {
    if (!managementOptions || role?.management?.can_edit !== true) return;
    setSelectedRole(role);
    setEditablePermissions(role.permissions || {});
    setSelectedRoleBusinessUnitIds(
      (role.assigned_units || [])
        .map((ru) => Number(ru?.business_unit_id))
        .filter((id) => Number.isInteger(id))
    );
    setShowRolePopup(true);
  };




  useEffect(() => {
    if (demoModeActive || forcedTab === "users") return;
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
          params: { business_unit_id: businessUnitId },
        });
        console.log("✅ Settings loaded:", response.data);
        const bookingEngineEntryMode =
          response.data.booking_engine_entry_mode === "widget" ? "widget" : "hosted";

        setFormData(prev => ({
          ...prev,
          ...response.data,
          booking_engine_entry_mode: bookingEngineEntryMode,
          booking_engine_theme: normalizeBookingTheme(response.data.booking_engine_theme),
          booking_engine_card_policy: normalizeBookingCardPolicy(response.data.booking_engine_card_policy),
          booking_engine_hero_background_screens: normalizeBookingHeroBackgroundScreens(
            response.data.booking_engine_hero_background_screens,
            bookingEngineEntryMode
          ),
          booking_engine_texts: response.data.booking_engine_texts || {}, 
          booking_engine_guest_email_templates: ensureGuestEmailTemplates(response.data),
          reservation_confirmation_email_templates:
            ensureReservationConfirmationStoredTemplates(response.data),
          reservation_confirmation_email_languages: normalizeReservationConfirmationLanguages(
            response.data.reservation_confirmation_email_languages ||
              response.data.booking_engine_default_language ||
              "en"
          ),
          external_reservation_notifications_enabled:
            response.data.external_reservation_notifications_enabled ?? false,
          external_reservation_notification_emails: normalizeExternalReservationNotificationEmails(
            response.data.external_reservation_notification_emails
          ),
          external_reservation_notification_language:
            String(response.data.external_reservation_notification_language || "en")
              .toLowerCase()
              .startsWith("es")
              ? "es"
              : "en",
          external_reservation_notification_templates:
            ensureExternalReservationNotificationTemplates(response.data),
          required_customer_roles_reservation: ensureReservationRolesCoverFieldRules(
            response.data.required_customer_roles_reservation?.length
              ? response.data.required_customer_roles_reservation
              : ["booker"],
            response.data.required_customer_fields_reservation || {}
          ),
          required_customer_fields_check_in: response.data.required_customer_fields_check_in || {},
          required_customer_roles_check_in: ensureCheckInRolesCoverFieldRules(
            response.data.required_customer_roles_check_in,
            response.data.required_customer_fields_check_in || {}
          ),
          require_company_booker_contact:
            response.data.require_company_booker_contact ?? true,
          require_full_payment_for_check_in:
            response.data.require_full_payment_for_check_in ?? false,
          group_reservations_default_status: response.data.group_reservations_default_status || "Requested",
          exclusive_rate_prices_per_date: response.data.exclusive_rate_prices_per_date ?? false,
          always_round_rate_prices: response.data.always_round_rate_prices ?? false,
          reservation_manual_modification_policy:
            normalizeReservationManualModificationPolicy(
              response.data.reservation_manual_modification_policy
            ),
        }));

      } catch (error) {
        if (error.response?.status === 404) {
          console.warn("ℹ No settings yet for this business unit");

          setFormData({
            required_customer_fields_reservation: {},
            required_customer_fields_check_in: {},
            required_customer_roles_reservation: ["booker"],
            required_customer_roles_check_in: ["main_guest"],
            require_company_booker_contact: true,
            require_full_payment_for_check_in: false,
            timezone: "Europe/Madrid",
            group_reservations_default_status: "Requested",
            exclusive_rate_prices_per_date: false,
            derived_rate_mode: "simple",
            always_round_rate_prices: false,
            reservation_manual_modification_policy:
              normalizeReservationManualModificationPolicy(null),
            booking_engine_guest_email_templates: ensureGuestEmailTemplates(null),
            booking_engine_entry_mode: "hosted",
            booking_engine_theme: "pilcore-light",
            booking_engine_card_policy: "required",
            booking_engine_hero_background_screens: normalizeBookingHeroBackgroundScreens(
              undefined,
              "hosted"
            ),
            reservation_confirmation_email_templates:
              ensureReservationConfirmationStoredTemplates(null),
            reservation_confirmation_email_languages: ["en"],
            external_reservation_notifications_enabled: false,
            external_reservation_notification_emails: [],
            external_reservation_notification_language: "en",
            external_reservation_notification_templates:
              ensureExternalReservationNotificationTemplates(null),
          });
        } else {
          console.error("❌ Error loading settings:", error);
        }
      } finally {
        await Promise.all([fetchActiveHeroBg(), fetchReservationEmailAssets()]);
      }
    };

    fetchSettings();
  }, [businessUnitId, demoModeActive, forcedTab]);



  


  useEffect(() => {
    if (demoModeActive || forcedTab === "users") return;
    const fetchClients = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients`);
        const activeClients = res.data.filter((c) => c.status === "active");

        const options = [
          { label: "Direct Reservation", value: "null" },
          ...activeClients.map((c) => ({
            label: c.name,
            value: String(c.id),
          })),
        ];

        setClientOptions(options);

        // Preselección si ya hay datos
        if (formData.clients_require_requested_flow) {
          const ids = formData.clients_require_requested_flow.split(",");
          const preselected = options.filter((opt) => ids.includes(opt.value));
          setSelectedClientOptions(preselected);
        }
      } catch (err) {
        console.error("❌ Error fetching clients:", err);
      }
    };

    fetchClients();
  }, [formData.clients_require_requested_flow, demoModeActive, forcedTab]);

  useEffect(() => {
    if (demoModeActive || (formData.rms_source !== "RMS" && formData.rms_source !== "Scraper")) return;

    axios.get(`${process.env.REACT_APP_API_URL}/api/room_categories`, {
      params: { business_unit_id: businessUnitId }
    })
      .then(res => {
        setRoomCategories(res.data);
      })
      .catch(err => {
        console.error("❌ Error loading room categories:", err);
      });
  }, [formData.rms_source, demoModeActive]);

  useEffect(() => {
    if (demoModeActive || formData.rms_source !== "Scraper") return;

    axios.get(`${process.env.REACT_APP_API_URL}/api/scraper-targets`, {
      params: { business_unit_id: businessUnitId }
    })
      .then((res) => {
        setScraperTargets(res.data);
      })
      .catch((err) => {
        console.error("❌ Error fetching scraper targets:", err);
        setScraperTargets([]);
      });
  }, [formData.rms_source, showScraperPopup, demoModeActive]); // <-- vuelve a cargar después de guardar

  useEffect(() => {
    if (demoModeActive || formData.rms_source !== "Scraper") return;

    axios.get(`${process.env.REACT_APP_API_URL}/api/roomRates/all`, {
      params: { business_unit_id: businessUnitId }
    })
      .then((res) => {
        const map = {};
        res.data.forEach((rate) => {
          map[rate.id] = `${rate.name}${rate.room_category ? ` (${rate.room_category.name})` : ""}`;
        });
        setRoomRateMap(map);
      })
      .catch((err) => {
        console.error("❌ Error loading room rates for mapping:", err);
      });
  }, [formData.rms_source, demoModeActive]);



  useEffect(() => {
    if (demoModeActive || formData.rms_source !== "Scraper") return;

    axios.get(`${process.env.REACT_APP_API_URL}/api/roomRates/all`, {
      params: { business_unit_id: businessUnitId }
    })
      .then((res) => {
        console.log("[Settings][ScraperPopup] Loaded all rates for BU (no category filter):", businessUnitId, "count=", res.data?.length ?? 0);
        setAllRoomRates(res.data);
      })
      .catch((err) => {
        console.error("❌ Error loading room rates for Scraper (all BU):", err);
      });
  }, [formData.rms_source, demoModeActive]);


  useEffect(() => {
    if (demoModeActive || forcedTab === "users") return;
    fetchCronStatus();
  }, [demoModeActive, fetchCronStatus, forcedTab]);



  
  const [autoRule, setAutoRule] = useState({ room_rate_id: "", adjustment_type: "equal", unit: "absolute", value: 0 });

  
  const handleChange = (e) => {
    if (demoModeActive || isDemoMode()) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (demoModeActive || isDemoMode()) return;
    if (isPilcoreSettingsTab && reservationSettingsSaving) return;
    if (isPilcoreSettingsTab) {
      setReservationSettingsSaving(true);
      setReservationSettingsFeedback(null);
    }
  
    // ✅ Filtra solo campos con valor definido y convierte "" en null
    const normalizedExternalReservationNotificationEmails =
      mergeExternalReservationNotificationEmailList(
        formData.external_reservation_notification_emails,
        externalReservationNotificationEmailInput
      );

    if (String(externalReservationNotificationEmailInput || "").trim()) {
      setFormData((prev) => ({
        ...prev,
        external_reservation_notification_emails:
          normalizedExternalReservationNotificationEmails,
      }));
      setExternalReservationNotificationEmailInput("");
    }

    const normalizedFormData = {
      ...formData,
      external_reservation_notification_emails:
        normalizedExternalReservationNotificationEmails,
      required_customer_roles_reservation: ensureReservationRolesCoverFieldRules(
        formData.required_customer_roles_reservation,
        formData.required_customer_fields_reservation
      ),
      required_customer_roles_check_in: ensureCheckInRolesCoverFieldRules(
        formData.required_customer_roles_check_in,
        formData.required_customer_fields_check_in
      ),
      reservation_manual_modification_policy:
        normalizeReservationManualModificationPolicy(
          formData.reservation_manual_modification_policy
        ),
    };

    const filteredData = {};
    Object.entries(normalizedFormData).forEach(([key, value]) => {
      // Only the dedicated Booking Engine save may change its card requirement.
      // In particular, general Settings access does not grant this permission.
      if (key === "booking_engine_card_policy") return;
      if (Array.isArray(value)) {
        filteredData[key] = value; // deja arrays como están
      } else if (value !== "" && value !== undefined) {
        filteredData[key] = value;
      } else {
        filteredData[key] = null;
      }
    });

  
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/hotel-settings/${businessUnitId}`,
        filteredData
      );
      console.log("✅ Settings guardados:", response.data);
      if (isPilcoreSettingsTab) {
        setReservationSettingsFeedback({ tone: "success", message: activeTab === "front_desk" ? "Front Desk settings saved." : "Reservation settings saved." });
      } else {
        alert("Settings saved successfully");
      }
    } catch (error) {
      console.error("❌ Error al guardar settings:", error);
      if (isPilcoreSettingsTab) {
        setReservationSettingsFeedback({ tone: "error", message: "Settings could not be saved. Your changes are still here; please try again." });
      } else {
        alert("There was a problem saving the settings");
      }
    } finally {
      if (isPilcoreSettingsTab) setReservationSettingsSaving(false);
    }
  };

  const updateReservationEmailTemplate = (lang, patch) => {
    const safeLang = String(lang || "en").toLowerCase().startsWith("es") ? "es" : "en";

    setFormData((prev) => ({
      ...prev,
      reservation_confirmation_email_templates: {
        ...ensureReservationConfirmationStoredTemplates(prev),
        [safeLang]: {
          ...(ensureReservationConfirmationStoredTemplates(prev)?.[safeLang] || {
            subject: "",
            body: "",
          }),
          ...(patch || {}),
        },
      },
    }));
  };

  const clearReservationEmailLanguage = () => {
    const lang = reservationEmailLangTab;
    setFormData((prev) => ({
      ...prev,
      reservation_confirmation_email_templates: {
        ...ensureReservationConfirmationStoredTemplates(prev),
        [lang]: { subject: "", body: "" },
      },
    }));
    setReservationEmailActiveField("subject");
  };

  const copyBookingEngineIntoReservationTemplate = () => {
    const lang = reservationEmailLangTab;
    const bookingTemplate = ensureGuestEmailTemplates(formData)?.[lang] || { subject: "", body: "" };
    const sanitized = {
      subject: sanitizeReservationTemplateSubject(bookingTemplate.subject),
      body: sanitizeReservationTemplateBody(bookingTemplate.body),
    };

    updateReservationEmailTemplate(lang, {
      subject: String(sanitized.subject || ""),
      body: String(sanitized.body || ""),
    });
  };

  const insertReservationConfirmationToken = (token) => {
    const targetField = reservationEmailActiveField === "body" ? "body" : "subject";
    const targetRef =
      targetField === "body" ? reservationEmailBodyRef.current : reservationEmailSubjectRef.current;

    if (targetRef && typeof targetRef.insertToken === "function") {
      targetRef.insertToken(token);
      return;
    }

    const storedTemplate =
      ensureReservationConfirmationStoredTemplates(formData)?.[reservationEmailLangTab] || {};
    updateReservationEmailTemplate(reservationEmailLangTab, {
      [targetField]: `${String(storedTemplate?.[targetField] || "")}${token}`,
    });
  };

  const toggleReservationConfirmationLanguage = (lang) => {
    const safeLang = String(lang || "en").toLowerCase().startsWith("es") ? "es" : "en";
    setFormData((prev) => {
      const current = normalizeReservationConfirmationLanguages(
        prev?.reservation_confirmation_email_languages
      );

      if (current.includes(safeLang)) {
        if (current.length <= 1) return prev;
        return {
          ...prev,
          reservation_confirmation_email_languages: current.filter((item) => item !== safeLang),
        };
      }

      return {
        ...prev,
        reservation_confirmation_email_languages: [...current, safeLang],
      };
    });
  };

  const updateExternalReservationNotificationTemplate = (lang, eventKey, patch) => {
    const safeLang = String(lang || "en").toLowerCase().startsWith("es") ? "es" : "en";
    const safeEvent = EXTERNAL_RESERVATION_NOTIFICATION_EVENT_OPTIONS.some(
      (item) => item.key === eventKey
    )
      ? eventKey
      : "modified";

    setFormData((prev) => ({
      ...prev,
      external_reservation_notification_templates: {
        ...ensureExternalReservationNotificationTemplates(prev),
        [safeLang]: {
          ...(ensureExternalReservationNotificationTemplates(prev)?.[safeLang] || {}),
          [safeEvent]: {
            ...(ensureExternalReservationNotificationTemplates(prev)?.[safeLang]?.[safeEvent] || {
              subject: "",
              body: "",
            }),
            ...(patch || {}),
          },
        },
      },
    }));
  };

  const clearExternalReservationNotificationTemplate = () => {
    updateExternalReservationNotificationTemplate(
      externalReservationNotificationLangTab,
      externalReservationNotificationEventTab,
      { subject: "", body: "" }
    );
    setExternalReservationNotificationActiveField("subject");
  };

  const insertExternalReservationNotificationToken = (token) => {
    const targetField =
      externalReservationNotificationActiveField === "body" ? "body" : "subject";
    const targetRef =
      targetField === "body"
        ? externalReservationNotificationBodyRef.current
        : externalReservationNotificationSubjectRef.current;

    if (targetRef && typeof targetRef.insertToken === "function") {
      targetRef.insertToken(token);
      return;
    }

    const storedTemplate =
      ensureExternalReservationNotificationTemplates(formData)?.[
        externalReservationNotificationLangTab
      ]?.[externalReservationNotificationEventTab] || {};

    updateExternalReservationNotificationTemplate(
      externalReservationNotificationLangTab,
      externalReservationNotificationEventTab,
      {
        [targetField]: `${String(storedTemplate?.[targetField] || "")}${token}`,
      }
    );
  };

  const addExternalReservationNotificationRecipients = (rawValue) => {
    setFormData((prev) => ({
      ...prev,
      external_reservation_notification_emails:
        mergeExternalReservationNotificationEmailList(
          prev.external_reservation_notification_emails,
          rawValue
        ),
    }));
  };

  const commitExternalReservationNotificationRecipientInput = () => {
    const rawValue = String(externalReservationNotificationEmailInput || "");
    if (!rawValue.trim()) return;
    addExternalReservationNotificationRecipients(rawValue);
    setExternalReservationNotificationEmailInput("");
  };

  const removeExternalReservationNotificationRecipient = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      external_reservation_notification_emails: normalizeExternalReservationNotificationEmails(
        prev.external_reservation_notification_emails
      ).filter((_, index) => index !== indexToRemove),
    }));
  };

const [guestEmailOpen, setGuestEmailOpen] = useState(false);
const [guestEmailLangTab, setGuestEmailLangTab] = useState("en"); // "en" | "es"
const [guestEmailDraft, setGuestEmailDraft] = useState(null);

  const insertBookingEngineGuestEmailToken = (token) => {
    if (demoModeActive || isDemoMode()) return;
    const targetField = guestEmailInsertTarget === "body" ? "body" : "subject";
    const targetRef =
      targetField === "body" ? guestEmailBodyRef.current : guestEmailSubjectRef.current;

    if (targetRef && typeof targetRef.insertToken === "function") {
      targetRef.insertToken(token);
      return;
    }

    setGuestEmailDraft((prev) => {
      const current = prev || {
        lang: guestEmailLangTab,
        subject: "",
        body: "",
      };

      return {
        ...current,
        [targetField]: `${String(current?.[targetField] || "")}${token}`,
      };
    });
  };

const [reservationEmailLangTab, setReservationEmailLangTab] = useState("en");
const [reservationEmailActiveField, setReservationEmailActiveField] = useState("subject");
const reservationEmailSubjectRef = React.useRef(null);
const reservationEmailBodyRef = React.useRef(null);
const [externalReservationNotificationLangTab, setExternalReservationNotificationLangTab] =
  useState("en");
const [externalReservationNotificationEventTab, setExternalReservationNotificationEventTab] =
  useState("modified");
const [externalReservationNotificationActiveField, setExternalReservationNotificationActiveField] =
  useState("subject");
const externalReservationNotificationSubjectRef = React.useRef(null);
const externalReservationNotificationBodyRef = React.useRef(null);
const [externalReservationNotificationEmailInput, setExternalReservationNotificationEmailInput] =
  useState("");

// Normaliza el objeto en formData (evita undefined)
function ensureGuestEmailTemplates(fd) {
  const raw = fd?.booking_engine_guest_email_templates || null;
  const base = (raw && typeof raw === "object") ? raw : {};
  return {
    en: { subject: String(base?.en?.subject || ""), body: String(base?.en?.body || "") },
    es: { subject: String(base?.es?.subject || ""), body: String(base?.es?.body || "") },
  };
}

function normalizeReservationConfirmationLanguages(value) {
  const rawItems = Array.isArray(value)
    ? value
    : value == null || value === ""
      ? []
      : [value];

  const out = [];
  rawItems.forEach((item) => {
    const normalized = String(item || "").toLowerCase().startsWith("es") ? "es" : "en";
    if (!out.includes(normalized)) {
      out.push(normalized);
    }
  });

  if (!out.length) return ["en"];
  if (out.includes("es")) {
    return ["es", ...out.filter((lang) => lang !== "es")];
  }

  return out;
}

function hasReservationTemplateContent(template) {
  return Boolean(String(template?.subject || "").trim() || String(template?.body || "").trim());
}

function sanitizeReservationTemplateSubject(value) {
  return String(value || "")
    .replace(/{{\s*Cancel(?:Link|Url)(?:Html)?\s*}}/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function sanitizeReservationTemplateBody(value) {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .filter((line) => !/{{\s*Cancel(?:Link|Url)(?:Html)?\s*}}/i.test(line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function ensureReservationConfirmationStoredTemplates(fd) {
  const raw = fd?.reservation_confirmation_email_templates || null;
  const base = raw && typeof raw === "object" ? raw : {};
  return {
    en: {
      subject: String(base?.en?.subject || ""),
      body: String(base?.en?.body || ""),
    },
    es: {
      subject: String(base?.es?.subject || ""),
      body: String(base?.es?.body || ""),
    },
  };
}

function getReservationConfirmationTemplateSource(fd, lang) {
  const stored = ensureReservationConfirmationStoredTemplates(fd)?.[lang];
  if (hasReservationTemplateContent(stored)) return "reservation";

  const bookingEngine = ensureGuestEmailTemplates(fd)?.[lang];
  const sanitizedBookingEngine = {
    subject: sanitizeReservationTemplateSubject(bookingEngine?.subject),
    body: sanitizeReservationTemplateBody(bookingEngine?.body),
  };

  if (hasReservationTemplateContent(sanitizedBookingEngine)) return "booking_engine";
  return "default";
}

function sanitizeReservationTemplate(template) {
  return {
    subject: sanitizeReservationTemplateSubject(template?.subject),
    body: sanitizeReservationTemplateBody(template?.body),
  };
}

function getEffectiveReservationConfirmationTemplate(fd, lang) {
  const sanitizedStored = sanitizeReservationTemplate(
    ensureReservationConfirmationStoredTemplates(fd)?.[lang]
  );
  const sanitizedBookingEngine = sanitizeReservationTemplate(
    ensureGuestEmailTemplates(fd)?.[lang]
  );
  const sanitizedDefault = sanitizeReservationTemplate(
    RESERVATION_CONFIRMATION_DEFAULT_TEMPLATES[lang] ||
      RESERVATION_CONFIRMATION_DEFAULT_TEMPLATES.en
  );

  return {
    subject:
      sanitizedStored.subject ||
      sanitizedBookingEngine.subject ||
      sanitizedDefault.subject,
    body:
      sanitizedStored.body ||
      sanitizedBookingEngine.body ||
      sanitizedDefault.body,
  };
}

function renderReservationTemplateValue(templateValue, vars) {
  let output = String(templateValue || "");
  Object.entries(vars || {}).forEach(([key, value]) => {
    output = output.split(`{{${key}}}`).join(value == null ? "" : String(value));
  });
  return output;
}

function buildReservationPreviewParagraphs(value) {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .trim()
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function normalizeExternalReservationNotificationEmails(value) {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => {
        if (typeof item === "string") return [item];
        if (item && typeof item === "object") {
          return [item.email, item.value, item.address].filter(Boolean);
        }
        return [];
      })
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }

  const raw = String(value || "").trim();
  if (!raw) return [];

  return raw
    .split(/[,;\n]+/g)
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

function mergeExternalReservationNotificationEmailList(currentValue, incomingValue) {
  const merged = [
    ...normalizeExternalReservationNotificationEmails(currentValue),
    ...normalizeExternalReservationNotificationEmails(incomingValue),
  ];
  const seen = new Set();

  return merged.filter((item) => {
    const normalizedKey = String(item || "").trim().toLowerCase();
    if (!normalizedKey || seen.has(normalizedKey)) return false;
    seen.add(normalizedKey);
    return true;
  });
}

function ensureExternalReservationNotificationTemplates(fd) {
  const raw = fd?.external_reservation_notification_templates || null;
  const base = raw && typeof raw === "object" ? raw : {};
  const buildEventMap = (lang) => ({
    new: {
      subject: String(base?.[lang]?.new?.subject || ""),
      body: String(base?.[lang]?.new?.body || ""),
    },
    modified: {
      subject: String(base?.[lang]?.modified?.subject || ""),
      body: String(base?.[lang]?.modified?.body || ""),
    },
    cancelled: {
      subject: String(base?.[lang]?.cancelled?.subject || ""),
      body: String(base?.[lang]?.cancelled?.body || ""),
    },
  });

  return {
    en: buildEventMap("en"),
    es: buildEventMap("es"),
  };
}

function getEffectiveExternalReservationNotificationTemplate(fd, lang, eventKey) {
  const stored =
    ensureExternalReservationNotificationTemplates(fd)?.[lang]?.[eventKey] || {
      subject: "",
      body: "",
    };
  const fallback =
    EXTERNAL_RESERVATION_NOTIFICATION_DEFAULT_TEMPLATES?.[lang]?.[eventKey] ||
    EXTERNAL_RESERVATION_NOTIFICATION_DEFAULT_TEMPLATES.en.modified;

  return {
    subject: String(stored?.subject || fallback.subject || ""),
    body: String(stored?.body || fallback.body || ""),
  };
}

function getExternalReservationNotificationUiTexts(lang) {
  return lang === "es"
    ? {
        eventNames: {
          new: "Nueva reserva externa",
          modified: "Reserva externa modificada",
          cancelled: "Reserva externa cancelada",
        },
        labels: {
          event: "Evento",
          source: "Origen",
          reference: "Referencia",
          externalBookingId: "ID reserva externa",
          otaLocator: "Localizador OTA",
          guest: "Huesped",
          guestEmail: "Email huesped",
          rooms: "Habitaciones",
          total: "Total",
          changes: "Que ha cambiado",
          currentSnapshot: "Estado actual de la reserva",
          operationalBlock: "Bloque operativo obligatorio",
          recipientHint:
            "Pulsa Enter o Anadir para crear chips. Tambien puedes pegar varios destinatarios separados por coma, punto y coma o salto de linea.",
          recipientInputPlaceholder: "reservations@hotel.com",
          recipientAddAction: "Anadir",
          recipientEmpty: "Todavia no hay destinatarios configurados.",
          toggleCopy:
            "Cuando esta activo, Pilcore avisa al hotel cada vez que una reserva que llega desde fuera de Pilcore, como una OTA, una agencia o el channel manager, se crea, cambia o se cancela.",
          bodyHint:
            "El texto que redactes se envia arriba y Pilcore anade despues un bloque fijo con cambios y nightly prices.",
          emptyPreview: "Empieza a redactar el cuerpo para ver la vista previa en vivo.",
          sendLanguage: "Idioma de envio",
          previewTitle: "Hotel preview",
        },
      }
    : {
        eventNames: {
          new: "New external reservation",
          modified: "External reservation updated",
          cancelled: "External reservation cancelled",
        },
        labels: {
          event: "Event",
          source: "Source",
          reference: "Reference",
          externalBookingId: "External booking ID",
          otaLocator: "OTA locator",
          guest: "Guest",
          guestEmail: "Guest email",
          rooms: "Rooms",
          total: "Total",
          changes: "What changed",
          currentSnapshot: "Current reservation snapshot",
          operationalBlock: "Mandatory operational block",
          recipientHint:
            "Press Enter or Add to create chips. You can also paste multiple recipients separated by commas, semicolons or line breaks.",
          recipientInputPlaceholder: "reservations@hotel.com",
          recipientAddAction: "Add",
          recipientEmpty: "No recipients configured yet.",
          toggleCopy:
            "When enabled, Pilcore emails the hotel whenever a reservation coming from outside Pilcore, such as an OTA, an agency or a channel manager, is created, changed or cancelled.",
          bodyHint:
            "The copy you write is sent first and Pilcore appends a fixed block with changes and nightly prices afterwards.",
          emptyPreview: "Start writing the body to see the live preview.",
          sendLanguage: "Send language",
          previewTitle: "Hotel preview",
        },
      };
}

function buildExternalReservationNotificationSystemBlock({ lang, eventKey, vars }) {
  const uiTexts = getExternalReservationNotificationUiTexts(lang);
  const labels = uiTexts.labels;
  const eventName = uiTexts.eventNames[eventKey] || vars?.EventType || "";
  const lines = [
    `${labels.event}: ${eventName}`,
    `${labels.source}: ${vars?.SourceName || "-"}`,
    `${labels.reference}: ${vars?.BookingReference || "-"}`,
  ];

  if (String(vars?.ExternalBookingId || "").trim()) {
    lines.push(`${labels.externalBookingId}: ${vars.ExternalBookingId}`);
  }

  if (String(vars?.OtaLocator || "").trim()) {
    lines.push(`${labels.otaLocator}: ${vars.OtaLocator}`);
  }

  lines.push(
    `${labels.guest}: ${vars?.GuestName || "-"}`,
    `${labels.guestEmail}: ${vars?.GuestEmail || "-"}`,
    `${labels.rooms}: ${vars?.RoomsCount || "0"}`,
    `${labels.total}: ${vars?.TotalPrice || "-"}`,
    "",
    labels.changes,
    `${vars?.ChangesSummary || "-"}`,
    "",
    labels.currentSnapshot,
    `${vars?.NightlyPrices || "-"}`
  );

  return lines.join("\n").trim();
}

  const bookingEngineStudio = React.useMemo(
    () => getBookingEngineStudioMessages(languageId),
    [languageId]
  );
  const bookingSettingsCopy = getBookingSettingsMessages(languageId);
  const bookingSettingsSections = ["general", "appearance", "guest", "rooms"];
  const selectBookingSettingsSection = (section) => {
    if (!bookingSettingsSections.includes(section)) return;
    setBookingSettingsSection(section);
    if (bookingSettingsSurfaceRef.current) bookingSettingsSurfaceRef.current.scrollTop = 0;
  };
  const revealBookingRoomEditor = React.useCallback(() => {
    const editor = bookingRoomEditorRef.current;
    const surface = bookingSettingsSurfaceRef.current;
    if (!editor || !surface) return;
    editor.focus({ preventScroll: true });
    const top = Math.max(0, surface.scrollTop + editor.getBoundingClientRect().top - surface.getBoundingClientRect().top - 14);
    if (typeof surface.scrollTo === "function") {
      surface.scrollTo({
        top,
        behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? "auto" : "smooth",
      });
    } else {
      surface.scrollTop = top;
    }
  }, []);
  const openBookingRoomEditor = (rcId, trigger) => {
    bookingRoomTriggerRef.current = trigger;
    if (bookingRoomCategoryId === Number(rcId)) revealBookingRoomEditor();
    else setBookingRoomCategoryId(Number(rcId));
  };
  const closeBookingRoomEditor = () => {
    setBookingRoomCategoryId(null);
    const trigger = bookingRoomTriggerRef.current;
    const fallback = bookingSettingsRootRef.current?.querySelector("#booking-category-search");
    (trigger?.isConnected ? trigger : fallback)?.focus({ preventScroll: true });
  };
  useEffect(() => {
    if (bookingRoomCategoryId !== null && activeTab === "booking_engine" && bookingSettingsSection === "rooms") {
      revealBookingRoomEditor();
    }
  }, [bookingRoomCategoryId, activeTab, bookingSettingsSection, revealBookingRoomEditor]);
  useEffect(() => {
    setBookingRoomCategoryId(null);
  }, [businessUnitId]);
  const closeBookingProfile = () => {
    if (beProfileSaveRef.current) return;
    setBeProfileOpen(false);
    setBeProfileDraft(null);
  };
  const confirmBookingImageRemoval = async () => {
    if (demoModeActive || isDemoMode() || !bookingImageToRemove || bookingImageRemoveRef.current) return;
    bookingImageRemoveRef.current = true;
    setBookingImageRemoving(true);
    try {
      const removed = bookingImageToRemove.kind === "hero"
        ? await removeActiveHeroBg()
        : await deactivateRoomCategoryImage(bookingImageToRemove.assetId);
      if (removed) setBookingImageToRemove(null);
    } finally {
      bookingImageRemoveRef.current = false;
      setBookingImageRemoving(false);
    }
  };
  const bookingDialogKey = activeTab !== "booking_engine" ? null
    : bookingImageToRemove ? "image-removal"
    : roomCategoryImagesModalOpen && roomCategoryImagesModalReady ? "gallery"
    : termsOpen ? "terms"
    : guestEmailOpen ? "email"
    : roomCopyOpen && roomCopyDraft ? "room-copy"
    : beProfileOpen ? "profile"
    : bookingInstallGuideOpen ? "installation"
    : amenityPickerOpen ? `amenities-${amenityPickerRcId}` : null;
  useBookingSettingsDialog({
    rootRef: bookingSettingsRootRef,
    dialogKey: bookingDialogKey,
    busy: beProfileSaving || bookingImageRemoving || Boolean(beRoomCategoryCopySavingByRcId?.[roomCopyEditingRcId]) || Boolean(rcAmenitySavingById?.[amenityPickerRcId]),
    onClose: () => {
      if (bookingImageToRemove) setBookingImageToRemove(null);
      else if (roomCategoryImagesModalOpen) closeRoomCategoryImagesModal();
      else if (termsOpen) { setTermsOpen(false); setTermsDraft(null); setTermsEditingId(null); }
      else if (guestEmailOpen) { setGuestEmailOpen(false); setGuestEmailDraft(null); }
      else if (roomCopyOpen) closeRoomCopyEditor();
      else if (beProfileOpen) closeBookingProfile();
      else if (bookingInstallGuideOpen) setBookingInstallGuideOpen(false);
      else if (amenityPickerOpen) closeAmenityPicker();
    },
  });
  const bookingEngineContentLanguage = languageId === "en" ? "en" : "es";
  const closeUsersRolesDialog = () => {
    if (usersRolesBusyRef.current) return;
    setUsersRolesConfirmation(null);
    setShowNewUserPopup(false);
    setShowNewRolePopup(false);
    setShowUserModal(false);
    setShowRolePopup(false);
    setSelectedRoleBusinessUnitIds([]);
  };
  useUsersRolesDialog({
    rootRef: usersRolesRootRef,
    dialogKey: activeTab !== "users" ? null : usersRolesConfirmation ? "confirm" : showNewUserPopup ? "new-user" : showNewRolePopup ? "new-role" : showUserModal ? "edit-user" : showRolePopup ? "edit-role" : null,
    busy: usersRolesBusy,
    onClose: closeUsersRolesDialog,
  });
  const bookingEnginePublicStudio = React.useMemo(
    () => getBookingEngineStudioMessages(bookingEngineContentLanguage),
    [bookingEngineContentLanguage]
  );
  const publicBookingUrl = React.useMemo(() => {
    if (!formData.booking_engine_slug || typeof window === "undefined") return "";
    return `${demoModeActive ? "https://example.invalid" : window.location.origin}/booking/${formData.booking_engine_slug}`;
  }, [demoModeActive, formData.booking_engine_slug]);
  const bookingWidgetEmbedCode = React.useMemo(() => {
    if (!formData.booking_engine_slug || typeof window === "undefined") return "";
    const origin = demoModeActive ? "https://example.invalid" : window.location.origin;
    const widgetLanguage = formData.booking_engine_default_language || "en";
    return [
      `<script src="${origin}/widgets/pilcore-booking-widget.js" defer></script>`,
      "<pilcore-booking-widget",
      `  slug="${formData.booking_engine_slug}"`,
      `  lang="${widgetLanguage}"`,
      `  booking-base-url="${origin}/booking"`,
      "></pilcore-booking-widget>",
    ].join("\n");
  }, [demoModeActive, formData.booking_engine_default_language, formData.booking_engine_slug]);
  const bookingInstallSnippets = React.useMemo(() => {
    const origin = demoModeActive ? "https://example.invalid" : typeof window !== "undefined" ? window.location.origin : "https://app.pilcore.com";
    const slug = formData.booking_engine_slug || "your-hotel-slug";
    const widgetLanguage = formData.booking_engine_default_language || "en";
    const bookNowLabel = languageId === "en" ? "Book now" : languageId === "ca" ? "Reserva ara" : "Reservar ahora";
    const scriptTag = `<script src="${origin}/widgets/pilcore-booking-widget.js" defer></script>`;
    const widgetTag = [
      "<pilcore-booking-widget",
      `  slug="${slug}"`,
      `  lang="${widgetLanguage}"`,
      `  booking-base-url="${origin}/booking"`,
      "></pilcore-booking-widget>",
    ].join("\n");
    const indentedWidgetTag = widgetTag
      .split("\n")
      .map((line) => `    ${line}`)
      .join("\n");

    return {
      inline: `${scriptTag}\n\n${widgetTag}`,
      dropdown: [
        scriptTag,
        "",
        '<details class="pilcore-booking-dropdown">',
        `  <summary>${bookNowLabel}</summary>`,
        '  <div class="pilcore-booking-dropdown__panel">',
        indentedWidgetTag,
        "  </div>",
        "</details>",
        "",
        "<style>",
        ".pilcore-booking-dropdown { max-width: 960px; }",
        ".pilcore-booking-dropdown > summary {",
        "  display: inline-flex; cursor: pointer; list-style: none;",
        "  padding: 12px 18px; border-radius: 8px;",
        "  background: #223A7A; color: #fff; font-family: Montserrat, sans-serif; font-weight: 700;",
        "}",
        ".pilcore-booking-dropdown > summary::-webkit-details-marker { display: none; }",
        ".pilcore-booking-dropdown__panel { margin-top: 12px; }",
        "</style>",
      ].join("\n"),
      floating: [
        scriptTag,
        "",
        '<details class="pilcore-booking-floating">',
        `  <summary>${bookNowLabel}</summary>`,
        '  <div class="pilcore-booking-floating__panel">',
        indentedWidgetTag,
        "  </div>",
        "</details>",
        "",
        "<style>",
        ".pilcore-booking-floating {",
        "  position: fixed; right: 24px; bottom: 24px; z-index: 9999;",
        "}",
        ".pilcore-booking-floating > summary {",
        "  display: inline-flex; float: right; cursor: pointer; list-style: none;",
        "  padding: 14px 20px; border-radius: 8px;",
        "  background: #223A7A; color: #fff; font-family: Montserrat, sans-serif; font-weight: 700;",
        "  box-shadow: 0 14px 34px rgba(0,0,0,.22);",
        "}",
        ".pilcore-booking-floating > summary::-webkit-details-marker { display: none; }",
        ".pilcore-booking-floating__panel {",
        "  position: absolute; right: 0; bottom: calc(100% + 12px);",
        "  width: min(760px, calc(100vw - 32px));",
        "}",
        "@media (max-width: 640px) {",
        "  .pilcore-booking-floating { right: 12px; bottom: 12px; left: 12px; }",
        "  .pilcore-booking-floating__panel { right: 0; left: 0; width: auto; }",
        "}",
        "</style>",
      ].join("\n"),
      wordpress: `${scriptTag}\n\n${widgetTag}`,
    };
  }, [demoModeActive, formData.booking_engine_default_language, formData.booking_engine_slug, languageId]);
  const bookingInstallGuideTabs = React.useMemo(() => {
    const guide = bookingEngineStudio.launch.installGuide;
    return [
      {
        id: "inline",
        label: guide.inlineTab,
        title: guide.inlineTitle,
        copy: guide.inlineCopy,
        note: guide.scriptOnce,
      },
      {
        id: "dropdown",
        label: guide.dropdownTab,
        title: guide.dropdownTitle,
        copy: guide.dropdownCopy,
        note: guide.scriptOnce,
      },
      {
        id: "floating",
        label: guide.floatingTab,
        title: guide.floatingTitle,
        copy: guide.floatingCopy,
        note: guide.scriptOnce,
      },
      {
        id: "wordpress",
        label: guide.wordpressTab,
        title: guide.wordpressTitle,
        copy: guide.wordpressCopy,
        note: guide.wordpressFallback,
      },
    ];
  }, [bookingEngineStudio.launch.installGuide]);
  const activeBookingInstallGuide =
    bookingInstallGuideTabs.find((tab) => tab.id === bookingInstallGuideTab) ||
    bookingInstallGuideTabs[0];
  const bookingEngineTerms = React.useMemo(
    () =>
      normalizeTerms(formData.booking_engine_terms)
        .slice()
        .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0)),
    [formData.booking_engine_terms]
  );
  const bookingEngineGuestEmailTemplates = React.useMemo(
    () => ensureGuestEmailTemplates(formData),
    [formData]
  );
  const bookingEngineEntryMode =
    formData.booking_engine_entry_mode === "widget" ? "widget" : "hosted";
  const bookingEngineHeroBackgroundScreens = normalizeBookingHeroBackgroundScreens(
    formData.booking_engine_hero_background_screens,
    bookingEngineEntryMode
  );
  const bookingEngineHasHeroBackgroundScreens =
    bookingEngineHeroBackgroundScreens.length > 0;
  const bookingEngineShowsOpeningScreen = bookingEngineEntryMode !== "widget";
  const bookingEngineShowsHeroCopyPreview =
    bookingEngineShowsOpeningScreen &&
    bookingEngineHeroBackgroundScreens.includes("search");
  const bookingEngineHeroPreviewSubtitle =
    getBookingEngineText("hero_subtitle", bookingEngineContentLanguage) ||
    (demoModeActive ? demoCopy.noValue : bookingEngineContentLanguage === "en"
      ? bookingEngineStudio.hero.subtitlePlaceholderEn
      : bookingEngineStudio.hero.subtitlePlaceholderEs);
  const bookingEngineHeroPreviewClaim =
    getBookingEngineText("hero_claim", bookingEngineContentLanguage) ||
    (demoModeActive ? demoCopy.noValue : bookingEngineContentLanguage === "en"
      ? bookingEngineStudio.hero.claimPlaceholderEn
      : bookingEngineStudio.hero.claimPlaceholderEs);
  const bookingEngineDisclosurePreview = React.useMemo(() => {
    const pricing = bookingEnginePublicStudio.pricing;
    const flags = {
      vat: !!formData.booking_engine_price_includes_vat,
      other: !!formData.booking_engine_price_includes_other_taxes,
      fees: !!formData.booking_engine_price_includes_fees,
      city: !!formData.booking_engine_price_includes_city_tax,
    };

    const allIncluded = flags.vat && flags.other && flags.fees && flags.city;
    const items = [];
    if (flags.vat) items.push(pricing.vatLabel);
    if (flags.other) items.push(pricing.otherTaxesLabel);
    if (flags.fees) items.push(pricing.feesLabel);
    if (flags.city) items.push(pricing.cityTaxLabel);

    const joinNatural = (values) => {
      const safe = Array.isArray(values) ? values.filter(Boolean) : [];
      if (safe.length <= 1) return safe[0] || "";
      if (safe.length === 2) return `${safe[0]} ${pricing.conjunction} ${safe[1]}`;
      const head = safe.slice(0, -1).join(", ");
      const last = safe[safe.length - 1];
      return `${head} ${pricing.conjunction} ${last}`;
    };

    if (items.length === 0) return pricing.noneIncluded;
    if (allIncluded) return pricing.allIncluded;
    return `${pricing.includingPrefix} ${joinNatural(items)}`;
  }, [
    bookingEnginePublicStudio,
    formData.booking_engine_price_includes_city_tax,
    formData.booking_engine_price_includes_fees,
    formData.booking_engine_price_includes_other_taxes,
    formData.booking_engine_price_includes_vat,
  ]);
  const bookingEngineVariableOptions = React.useMemo(() => {
    const labelsByLanguage =
      languageId === "es"
        ? {
            HotelName: "Nombre hotel",
            GuestName: "Nombre huesped",
            GuestEmail: "Email huesped",
            BookingReference: "Referencia reserva",
            CheckInDate: "Fecha entrada",
            CheckOutDate: "Fecha salida",
            RoomsCount: "Numero habitaciones",
            TotalPrice: "Precio total",
            NightlyPrices: "Precios por noche",
            CancelUrl: "Enlace cancelacion",
          }
        : languageId === "ca"
          ? {
              HotelName: "Nom hotel",
              GuestName: "Nom hoste",
              GuestEmail: "Email hoste",
              BookingReference: "Referencia reserva",
              CheckInDate: "Data entrada",
              CheckOutDate: "Data sortida",
              RoomsCount: "Nombre habitacions",
              TotalPrice: "Preu total",
              NightlyPrices: "Preus per nit",
              CancelUrl: "Enllac cancel.lacio",
            }
          : {
              HotelName: "Hotel name",
              GuestName: "Guest name",
              GuestEmail: "Guest email",
              BookingReference: "Booking reference",
              CheckInDate: "Check-in date",
              CheckOutDate: "Check-out date",
              RoomsCount: "Rooms count",
              TotalPrice: "Total price",
              NightlyPrices: "Nightly prices",
              CancelUrl: "Cancel link",
            };

    return [
      { token: "{{HotelName}}", label: labelsByLanguage.HotelName },
      { token: "{{GuestName}}", label: labelsByLanguage.GuestName },
      { token: "{{GuestEmail}}", label: labelsByLanguage.GuestEmail },
      { token: "{{BookingReference}}", label: labelsByLanguage.BookingReference },
      { token: "{{CheckInDate}}", label: labelsByLanguage.CheckInDate },
      { token: "{{CheckOutDate}}", label: labelsByLanguage.CheckOutDate },
      { token: "{{RoomsCount}}", label: labelsByLanguage.RoomsCount },
      { token: "{{TotalPrice}}", label: labelsByLanguage.TotalPrice },
      { token: "{{NightlyPrices}}", label: labelsByLanguage.NightlyPrices },
      { token: "{{CancelUrl}}", label: labelsByLanguage.CancelUrl },
    ];
  }, [languageId]);

  const buildBookingEngineProfilePreview = React.useCallback((profile) => {
    const hotelName = String(profile?.public_hotel_name || "").trim();
    const addressLine = String(profile?.address_line || "").trim();
    const postalCode = String(profile?.postal_code || "").trim();
    const city = String(profile?.city || "").trim();
    const country = String(profile?.country || "").trim();

    const normalizeContacts = (values, objectKeys = []) => {
      if (!Array.isArray(values)) return [];
      return values
        .map((value) => {
          if (typeof value === "string") return value;
          if (!value || typeof value !== "object") return "";
          for (const key of objectKeys) {
            const candidate = value?.[key];
            if (candidate != null && String(candidate).trim()) {
              return String(candidate).trim();
            }
          }
          return "";
        })
        .map((value) => String(value || "").trim())
        .filter(Boolean);
    };

    const phones = normalizeContacts(profile?.phones, ["value", "phone", "number"]);
    const emails = normalizeContacts(profile?.emails, ["value", "email", "address"]);
    const addressLines = [
      addressLine,
      [postalCode, city].filter(Boolean).join(" "),
      country,
    ].filter(Boolean);
    const contactLines = [...addressLines, ...phones, ...emails];

    return {
      hotelName,
      addressLines,
      phones,
      emails,
      contactLines,
      lines: contactLines,
    };
  }, []);

  const bookingEngineProfilePreview = React.useMemo(
    () => buildBookingEngineProfilePreview(beProfile),
    [beProfile, buildBookingEngineProfilePreview]
  );

  const renderBookingEngineProfilePlacementPreview = (preview, keyPrefix) => (
    <div className="beStudio__profilePreviewShell">
      <div className="beStudio__profilePreviewBlock">
        <div className="beStudio__profilePreviewContext">
          {bookingEngineStudio.profile.identityPreviewLabel}
        </div>
        {preview.hotelName ? (
          <div className="beStudio__profilePreviewName">{preview.hotelName}</div>
        ) : (
          <div className="beStudio__profilePreviewEmpty">
            {bookingEngineStudio.profile.emptyHotelName}
          </div>
        )}
      </div>

      <div className="beStudio__profilePreviewBlock">
        <div className="beStudio__profilePreviewContext">
          {bookingEngineStudio.profile.contactPreviewLabel}
        </div>
        {preview.contactLines.length ? (
          preview.contactLines.map((line, index) => (
            <div key={`${keyPrefix}-contact-${index}`} className="beStudio__profilePreviewLine">
              {line}
            </div>
          ))
        ) : (
          <div className="beStudio__profilePreviewEmpty">
            {bookingEngineStudio.profile.emptyContact}
          </div>
        )}
      </div>
    </div>
  );

  const renderBookingEngineRoomCopyPreview = (rc, row, lang, mainImg = null) => {
    const preview = buildRoomCopyPreviewData(rc, row, lang);
    const langOption =
      BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS.find((option) => option.id === preview.lang) ||
      BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS[0];
    const isConfigured = roomCopyLanguageIsConfigured(rc, row, preview.lang);

    return (
      <div
        className="beStudio__roomCopyPreviewShell bookingThemeScope"
        data-booking-theme={normalizeBookingTheme(formData.booking_engine_theme)}
      >
        <div
          className="beStudio__roomCopyPreviewMedia"
          style={
            mainImg?.public_url
              ? {
                  backgroundImage: `linear-gradient(180deg, transparent 62%, rgba(2, 7, 18, 0.38)), url(${mainImg.public_url})`,
                }
              : undefined
          }
        >
          {!mainImg?.public_url ? (
            <span className="beStudio__roomCopyPreviewPlaceholder">
              {demoModeActive && mainImg?.asset_id
                ? `${bookingEngineStudio.status.configured} · ${demoCopy.media}`
                : bookingEngineStudio.rooms.noImage}
            </span>
          ) : null}
        </div>

        <div className="beStudio__roomCopyPreviewBody">
          <div className="beStudio__chipRow">
            <span className="beStudio__chip beStudio__chip--accent">
              {bookingEngineStudio.rooms.previewTitle}
            </span>
            <span className="beStudio__chip">
              {bookingEngineStudio.rooms.previewLanguage}: {langOption.shortLabel}
            </span>
            <span className={`beStudio__chip ${isConfigured ? "beStudio__chip--accent" : ""}`}>
              {isConfigured
                ? bookingEngineStudio.status.configured
                : bookingEngineStudio.status.notConfigured}
            </span>
          </div>

          <h4 className="beStudio__roomCopyPreviewTitle">
            {preview.displayName || bookingEngineStudio.rooms.previewEmpty}
          </h4>

          {preview.specsLine ? (
            <div className="beStudio__roomCopyPreviewSpecs">{preview.specsLine}</div>
          ) : null}

          <p className="beStudio__roomCopyPreviewCopy">
            {preview.shortDescription || bookingEngineStudio.rooms.previewEmpty}
          </p>

          {preview.bedType || preview.view ? (
            <div className="beStudio__roomCopyPreviewMetaGrid">
              {preview.bedType ? (
                <div className="beStudio__roomCopyPreviewMetaCard">
                  <span className="beStudio__roomCopyPreviewMetaLabel">
                    {bookingEngineStudio.rooms.bedTypeLabel}
                  </span>
                  <strong className="beStudio__roomCopyPreviewMetaValue">{preview.bedType}</strong>
                </div>
              ) : null}

              {preview.view ? (
                <div className="beStudio__roomCopyPreviewMetaCard">
                  <span className="beStudio__roomCopyPreviewMetaLabel">
                    {bookingEngineStudio.rooms.viewLabel}
                  </span>
                  <strong className="beStudio__roomCopyPreviewMetaValue">{preview.view}</strong>
                </div>
              ) : null}
            </div>
          ) : null}

          {preview.highlightItems.length ? (
            <div className="beStudio__roomCopyPreviewHighlightSection">
              <span className="beStudio__roomCopyPreviewMetaLabel">
                {bookingEngineStudio.rooms.highlightsLabel}
              </span>
              <div className="beStudio__roomCopyPreviewHighlights">
                {preview.highlightItems.map((item, index) => (
                  <span
                    key={`${preview.lang}-${index}-${item}`}
                    className="beStudio__roomCopyPreviewHighlight"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const handleCopyBookingEngineUrl = React.useCallback(async () => {
    if (demoModeActive || isDemoMode()) return;
    const copyValue =
      formData.booking_engine_entry_mode === "widget"
        ? bookingWidgetEmbedCode
        : publicBookingUrl;

    if (!copyValue) {
      showBookingEngineToast(bookingEngineStudio.toasts.profileUrlMissing, "error");
      return;
    }

    try {
      await navigator.clipboard.writeText(copyValue);
      setBookingEngineCopiedUrl(true);
      showBookingEngineToast(bookingEngineStudio.toasts.urlCopied, "success");
    } catch (_err) {
      showBookingEngineToast(bookingEngineStudio.toasts.urlCopyError, "error");
    }
  }, [demoModeActive, bookingEngineStudio.toasts.profileUrlMissing, bookingEngineStudio.toasts.urlCopied, bookingEngineStudio.toasts.urlCopyError, bookingWidgetEmbedCode, formData.booking_engine_entry_mode, publicBookingUrl, showBookingEngineToast]);

  const handleCopyBookingInstallSnippet = React.useCallback(async (tabId) => {
    if (demoModeActive || isDemoMode()) return;
    const snippet = bookingInstallSnippets?.[tabId] || "";
    if (!snippet) return;

    try {
      await navigator.clipboard.writeText(snippet);
      setBookingInstallCopiedTab(tabId);
    } catch (_err) {
      showBookingEngineToast(bookingEngineStudio.toasts.urlCopyError, "error");
    }
  }, [demoModeActive, bookingEngineStudio.toasts.urlCopyError, bookingInstallSnippets, showBookingEngineToast]);

  const saveBookingEngineSettings = React.useCallback(async () => {
    if (demoModeActive || isDemoMode() || !canViewBookingEngineTab || bookingSettingsSaveRef.current) return;
    bookingSettingsSaveRef.current = true;
    setBookingEngineSaving(true);
    try {
      const requestedBookingTheme = normalizeBookingTheme(formData.booking_engine_theme);
      const requestedCardPolicy = normalizeBookingCardPolicy(formData.booking_engine_card_policy);
      const requestedEntryMode =
        formData.booking_engine_entry_mode === "widget" ? "widget" : "hosted";
      const requestedHeroBackgroundScreens = normalizeBookingHeroBackgroundScreens(
        formData.booking_engine_hero_background_screens,
        requestedEntryMode
      );
      console.log("[BOOKING_ENGINE][SAVE]", formData);
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/hotel-settings/${businessUnitId}`, {
        ...formData,
        booking_engine_entry_mode: requestedEntryMode,
        booking_engine_theme: requestedBookingTheme,
        booking_engine_card_policy: requestedCardPolicy,
        booking_engine_hero_background_screens: requestedHeroBackgroundScreens,
      });

      const persistedBookingTheme = response.data?.booking_engine_theme;
      if (persistedBookingTheme !== requestedBookingTheme) {
        const persistenceError = new Error(bookingEngineStudio.toasts.themeNotPersisted);
        persistenceError.isBookingThemePersistenceError = true;
        throw persistenceError;
      }

      if (response.data?.booking_engine_card_policy !== requestedCardPolicy) {
        const persistenceError = new Error(bookingEngineStudio.toasts.cardPolicyNotPersisted);
        persistenceError.isBookingCardPolicyPersistenceError = true;
        throw persistenceError;
      }

      const persistedEntryMode =
        response.data?.booking_engine_entry_mode === "widget" ? "widget" : requestedEntryMode;
      const persistedHeroBackgroundScreens = Array.isArray(
        response.data?.booking_engine_hero_background_screens
      )
        ? normalizeBookingHeroBackgroundScreens(
            response.data.booking_engine_hero_background_screens,
            persistedEntryMode
          )
        : requestedHeroBackgroundScreens;

      setFormData((prev) => ({
        ...prev,
        booking_engine_entry_mode: persistedEntryMode,
        booking_engine_theme: persistedBookingTheme,
        booking_engine_card_policy: response.data.booking_engine_card_policy,
        booking_engine_hero_background_screens: persistedHeroBackgroundScreens,
      }));
      console.log("[BOOKING_ENGINE][SAVE] OK");
      showBookingEngineToast(bookingEngineStudio.toasts.settingsSaved, "success");
    } catch (err) {
      console.error("[BOOKING_ENGINE][SAVE] ERROR", err);
      showBookingEngineToast(
        err.response?.data?.error ||
          (err.isBookingThemePersistenceError || err.isBookingCardPolicyPersistenceError
            ? err.message
            : bookingEngineStudio.toasts.settingsError),
        "error"
      );
    } finally {
      bookingSettingsSaveRef.current = false;
      setBookingEngineSaving(false);
    }
  }, [demoModeActive, canViewBookingEngineTab, bookingEngineStudio.toasts.settingsError, bookingEngineStudio.toasts.settingsSaved, bookingEngineStudio.toasts.themeNotPersisted, bookingEngineStudio.toasts.cardPolicyNotPersisted, businessUnitId, formData, showBookingEngineToast]);


useEffect(() => {
  if (selectedUser) {
    const rows = userAssignmentRows(selectedUser, managementOptions);
    setEditAssignments(rows.length ? rows : [{ ...EMPTY_USER_ASSIGNMENT }]);
  }
  // Preserve the current draft while authorization data refreshes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedUser]);


const RuleItem = ({ rule, onRemove }) => {
  const [name, setName] = useState(null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/roomRates/${rule.room_rate_id}`)
      .then(res => {
        const r = res.data;
        const rateName = r.name || `Tarifa ${rule.room_rate_id}`;
        const catName = r.room_category?.name || 'Sin categoría';
        setName(`${rateName} (${catName})`);
      })
      .catch(() => {
        setName(`#${rule.room_rate_id}`);
      });
  }, [rule.room_rate_id]);

  

  

  console.log("🎯 formData.rms_source:", formData.rms_source);
  return (
    <li className="list-group-item bg-dark text-light d-flex justify-content-between align-items-center">
      <div>
        <strong>{name || `#${rule.room_rate_id}`}</strong> → {rule.adjustment_type.toUpperCase()}
        {rule.adjustment_type !== "equal" && (
          <> {rule.unit === "percent" ? `${rule.value}%` : `${rule.value} ${baseCurrency}`}</>
        )}
      </div>
      <button
        type="button"
        className="btn btn-sm fw-bold text-white px-3 py-1"
        style={{
          background: "linear-gradient(to right, #e74c3c, #c0392b)",
          border: "none",
          borderRadius: "5px",
        }}
        onClick={onRemove}
      >
        Remove
      </button>
    </li>
  );
};

useEffect(() => {
  if (activeTab !== "crm") return;
  loadCrmPipelines();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeTab]);

  

  if (isUsersTabDenied) {
    return (
      <div className="text-center mt-4 fs-5" role="alert" style={{ color: theme?.preview?.text }}>
        You don&apos;t have permission to access Users &amp; Roles.
      </div>
    );
  }

  if (
    !showReservationTab &&
    !canViewFrontDeskSettingsTab &&
    !canViewRevenueTab &&
    !canViewCurrenciesTab &&
    !canViewExtrasTab &&
    !canViewChargesTab &&
    !canViewUsersTab &&
    !canViewAppearanceTab &&
    !canViewBookingEngineTab &&
    !canViewCrmTab
  ) {
    return (
      <div className="text-white text-center mt-4 fs-5">
        You don&apos;t have permission to access Settings.
      </div>
    );
  }

  const normalizeRoleAssignmentsForPayload = (rows) => {
    const normalizedRows = (Array.isArray(rows) ? rows : [])
      .map((row) => ({
        business_unit_id: Number(row?.business_unit_id),
        role_id: Number(row?.role_id),
      }))
      .filter(
        (row) =>
          Number.isInteger(row.business_unit_id) &&
          row.business_unit_id > 0 &&
          Number.isInteger(row.role_id) &&
          row.role_id > 0
      );

    const dedupByBusinessUnit = new Map();
    normalizedRows.forEach((row) => {
      dedupByBusinessUnit.set(row.business_unit_id, row);
    });

    return Array.from(dedupByBusinessUnit.values());
  };

  const newAssignmentsValid = canManageUsers && assignmentsAllowed(managementOptions, newUserAssignments);
  const editAssignmentsValid = canManageUsers && assignmentsAllowed(managementOptions, editAssignments, selectedUser)
    && (selectedUser?.management?.can_edit_profile || editName === selectedUser?.name)
    && ((editName === selectedUser?.name && editAssignments.some((row) => Number(row.business_unit_id) === Number(selectedUser?.active_business_unit_id)))
      || assignmentsAllowed(managementOptions, editAssignments));
  const newRoleValid = roleDraftAllowed(managementOptions, newRoleData.business_unit_ids, newRolePermissions);
  const editedRoleValid = roleDraftAllowed(managementOptions, selectedRoleBusinessUnitIds, editablePermissions, selectedRole);
  const roleHotelOptions = availableBusinessUnits.filter((hotel) => hotel.can_manage_roles).map((hotel) => ({ value: Number(hotel.id), label: hotel.name }));

  async function loadCrmPipelines() {
    if (!canViewCrmTab) return;
    setCrmPipelinesLoading(true);
    setCrmPipelinesError("");
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/crm/opportunity-pipelines`, {
        params: { include_inactive: 1 },
      });
      const rows = Array.isArray(response.data?.rows) ? response.data.rows : [];
      setCrmPipelines(rows);
      const selected = rows.find((item) => String(item.id) === String(crmSelectedPipelineId)) || rows[0] || null;
      setCrmSelectedPipelineId(selected?.id ? String(selected.id) : "");
      setCrmPipelineDraft(buildCrmPipelineDraft(selected));
    } catch (err) {
      console.error("[Settings][CRM] Pipeline load error:", err);
      setCrmPipelines([]);
      setCrmPipelinesError(err.response?.data?.error || "Could not load CRM opportunity pipelines.");
    } finally {
      setCrmPipelinesLoading(false);
    }
  }

  const selectCrmPipeline = (pipeline) => {
    setCrmSelectedPipelineId(pipeline?.id ? String(pipeline.id) : "");
    setCrmPipelineDraft(buildCrmPipelineDraft(pipeline));
    setCrmPipelinesError("");
  };

  const startNewCrmPipeline = () => {
    setCrmSelectedPipelineId("");
    setCrmPipelineDraft(buildEmptyCrmPipelineDraft());
    setCrmPipelinesError("");
  };

  const updateCrmPipelineDraft = (field, value) => {
    setCrmPipelineDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateCrmPipelineStageDraft = (index, field, value) => {
    setCrmPipelineDraft((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, stageIndex) =>
        stageIndex === index ? { ...stage, [field]: value } : stage
      ),
    }));
  };

  const addCrmPipelineStageDraft = () => {
    setCrmPipelineDraft((prev) => ({
      ...prev,
      stages: [
        ...prev.stages,
        {
          temp_id: `new-${Date.now()}`,
          name: "",
          stage_type: "open",
          color: "#7dd3fc",
          probability: "",
          status: "active",
          sort_order: prev.stages.length,
        },
      ],
    }));
  };

  const removeCrmPipelineStageDraft = (index) => {
    setCrmPipelineDraft((prev) => ({
      ...prev,
      stages: prev.stages
        .map((stage, stageIndex) =>
          stageIndex === index
            ? stage.id
              ? { ...stage, status: "inactive", _delete: true }
              : null
            : stage
        )
        .filter(Boolean),
    }));
  };

  const saveCrmPipelineDraft = async () => {
    if (!canUpdateCrmPipelines || crmPipelinesSaving) return;
    if (!String(crmPipelineDraft.name || "").trim()) {
      setCrmPipelinesError("Pipeline name is required.");
      return;
    }

    setCrmPipelinesSaving(true);
    setCrmPipelinesError("");
    const payload = {
      name: crmPipelineDraft.name,
      status: crmPipelineDraft.status,
      is_default: Boolean(crmPipelineDraft.is_default),
      sort_order: Number(crmPipelineDraft.sort_order || 0),
      stages: crmPipelineDraft.stages.map((stage, index) => ({
        id: stage.id || undefined,
        name: stage.name,
        stage_type: stage.stage_type || "open",
        color: stage.color || null,
        probability: stage.probability === "" ? null : Number(stage.probability),
        status: stage.status || "active",
        sort_order: Number(stage.sort_order ?? index),
        _delete: Boolean(stage._delete),
      })),
    };

    try {
      const response = crmPipelineDraft.id
        ? await axios.patch(
            `${process.env.REACT_APP_API_URL}/api/crm/opportunity-pipelines/${crmPipelineDraft.id}`,
            payload
          )
        : await axios.post(`${process.env.REACT_APP_API_URL}/api/crm/opportunity-pipelines`, payload);
      await loadCrmPipelines();
      selectCrmPipeline(response.data);
    } catch (err) {
      console.error("[Settings][CRM] Pipeline save error:", err);
      setCrmPipelinesError(err.response?.data?.error || "Could not save CRM opportunity pipeline.");
    } finally {
      setCrmPipelinesSaving(false);
    }
  };

  // Revert note: if this layout change causes regressions, remove the two constants below
  // and restore the previous inline style on `settings-scroll-wrapper`:
  // `{ maxHeight: "75vh", overflowY: "auto", paddingRight: "10px" }`.
  const reservationTabShellPadding = forcedTab ? "4px 10px 4px" : "12px 16px 8px";
  const bookingEngineTabShellPadding = forcedTab ? "4px 10px 4px" : "12px 16px 8px";
  const settingsScrollWrapperStyle = activeTab === "users"
    ? {
        width: "100%",
        paddingRight: "10px",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
        overflow: "visible",
      }
    : activeTab === "extras"
    ? { flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }
    : activeTab === "charges"
    ? { flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }
    : activeTab === "currencies"
    ? {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto",
        paddingRight: "10px",
      }
    : activeTab === "reservation" || activeTab === "front_desk"
    ? {
        width: "100%",
        paddingRight: "0",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
        overflow: "hidden",
        boxSizing: "border-box",
      }
    : activeTab === "appearance"
    ? {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto",
      }
    : activeTab === "booking_engine"
    ? {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
        overflow: "hidden",
      }
    : activeTab === "crm"
    ? {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto",
        paddingRight: "10px",
      }
    : {
        maxHeight: "75vh",
        overflowY: "auto",
        paddingRight: "10px",
      };

  const getTopTabStyle = (isActive) =>
    isLightTheme
      ? {
          background: isActive
            ? "linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(241, 234, 223, 0.96))"
            : "rgba(255, 255, 255, 0.40)",
          border: isActive
            ? "1px solid rgba(24, 54, 69, 0.12)"
            : "1px solid rgba(24, 54, 69, 0.08)",
          borderBottom: isActive
            ? "1px solid rgba(24, 54, 69, 0.12)"
            : "1px solid rgba(24, 54, 69, 0.08)",
          borderRadius: "999px",
          padding: "9px 16px",
          color: isActive ? "#183645" : "#6d7471",
          fontWeight: 800,
          boxShadow: isActive ? "0 12px 24px rgba(86, 65, 39, 0.08)" : "none",
        }
      : undefined;

  const getTopTabClassName = (isActive, extraClass = "") => {
    if (isLightTheme) return extraClass;
    return `${extraClass} ${isActive ? "text-warning border-bottom border-warning" : "text-light"}`.trim();
  };

  const preview = theme?.preview || {};
  const reservationStudioAccent = preview.sidebarAccent || (isLightTheme ? "#215968" : "#30d8ff");
  const reservationStudioHighlight = preview.highlight || (isLightTheme ? "#c28b35" : "#f6b756");
  const reservationStudioSuccess = preview.success || "#34d399";
  const reservationStudioText = preview.text || (isLightTheme ? "#183645" : "#eef4ff");
  const reservationStudioMuted = preview.muted || (isLightTheme ? "rgba(24, 54, 69, 0.66)" : "rgba(226, 232, 240, 0.72)");
  const reservationStudioBorder = isLightTheme
    ? "rgba(24, 54, 69, 0.12)"
    : "rgba(148, 163, 184, 0.20)";
  const reservationStudioSurface = preview.surface || (isLightTheme ? "rgba(255,255,255,0.98)" : "rgba(17, 24, 39, 0.96)");
  const reservationStudioSurfaceAlt =
    preview.surfaceAlt || (isLightTheme ? "rgba(243, 237, 228, 0.98)" : "rgba(10, 15, 26, 0.98)");
  const reservationStudioMenuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  const reservationStudioCss = `
    .reservationStudio {
      width: 100%;
      max-width: 100%;
      margin: 0 auto;
      color: ${reservationStudioText};
      box-sizing: border-box;
      min-width: 0;
      min-height: 0;
      height: 100%;
      max-height: 100%;
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
    }

    .reservationStudio__surface {
      position: relative;
      flex: 1 1 auto;
      min-width: 0;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      border-radius: 30px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, ${reservationStudioSurface}, ${reservationStudioSurfaceAlt}) padding-box,
        linear-gradient(135deg, ${reservationStudioAccent}38, ${reservationStudioHighlight}30, ${reservationStudioSuccess}22) border-box;
      box-shadow: ${
        isLightTheme
          ? "0 24px 60px rgba(86, 65, 39, 0.12)"
          : "0 28px 72px rgba(0, 0, 0, 0.34)"
      };
      padding: 0 clamp(18px, 1.6vw, 24px) clamp(18px, 1.6vw, 24px);
      display: flex;
      flex-direction: column;
      gap: 18px;
      scrollbar-gutter: stable both-edges;
      height: 100%;
      max-height: 100%;
    }

    .reservationStudio__surface::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background:
        radial-gradient(920px 300px at 8% -10%, ${reservationStudioAccent}12, transparent 62%),
        radial-gradient(760px 280px at 100% 0%, ${reservationStudioHighlight}10, transparent 64%),
        radial-gradient(980px 340px at 50% 120%, ${reservationStudioSuccess}08, transparent 68%);
      opacity: 1;
    }

    .reservationStudio__surface > * {
      position: relative;
      z-index: 1;
      min-width: 0;
      flex-shrink: 0;
    }

    .reservationStudio__hero {
      position: sticky;
      top: 0;
      z-index: 8;
      min-width: 0;
      overflow: hidden;
      border-radius: 28px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, ${reservationStudioSurface}, ${reservationStudioSurfaceAlt}) padding-box,
        linear-gradient(135deg, ${reservationStudioAccent}55, ${reservationStudioHighlight}45, ${reservationStudioSuccess}32) border-box;
      box-shadow: ${
        isLightTheme
          ? "0 30px 76px rgba(86, 65, 39, 0.16)"
          : "0 28px 80px rgba(0, 0, 0, 0.34)"
      };
      padding: clamp(16px, 1.4vw, 22px);
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 14px;
      align-items: start;
      backdrop-filter: blur(14px);
    }

    .reservationStudio__hero::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background:
        radial-gradient(840px 240px at 8% 0%, ${reservationStudioAccent}22, transparent 62%),
        radial-gradient(680px 260px at 100% 12%, ${reservationStudioHighlight}18, transparent 62%),
        radial-gradient(900px 280px at 50% 120%, ${reservationStudioSuccess}14, transparent 66%);
      opacity: 1;
    }

    .reservationStudio__hero > * {
      position: relative;
      z-index: 1;
    }

    .reservationStudio__eyebrow {
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: ${reservationStudioAccent};
      margin-bottom: 8px;
    }

    .reservationStudio__title {
      margin: 0;
      font-size: clamp(1.4rem, 1.75vw, 1.92rem);
      font-weight: 900;
      color: ${reservationStudioText};
      line-height: 1.06;
    }

    .reservationStudio__subtitle {
      margin: 8px 0 0;
      max-width: none;
      color: ${reservationStudioMuted};
      line-height: 1.5;
      font-size: 0.88rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .reservationStudio__heroAside {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-end;
      gap: 10px;
    }

    .reservationStudio__heroControls {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
    }

    .reservationStudio__heroPills {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      justify-content: flex-end;
    }

    .reservationStudio__pill {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      border-radius: 999px;
      padding: 7px 12px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255, 255, 255, 0.72)" : "rgba(255, 255, 255, 0.05)"};
      font-size: 0.74rem;
      font-weight: 700;
      color: ${reservationStudioText};
    }

    .reservationStudio__pill--accent {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.08)" : "rgba(48, 216, 255, 0.10)"};
      color: ${reservationStudioAccent};
    }

    .reservationStudio__summaryGrid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 14px;
      margin-top: 18px;
    }

    .reservationStudio__summaryCard {
      border-radius: 20px;
      padding: 16px 18px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.90)" : "rgba(255,255,255,0.04)"};
      box-shadow: ${isLightTheme ? "0 18px 34px rgba(84, 64, 38, 0.08)" : "0 20px 32px rgba(0,0,0,0.18)"};
    }

    .reservationStudio__summaryLabel {
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${reservationStudioMuted};
    }

    .reservationStudio__summaryValue {
      margin-top: 8px;
      font-size: 1.05rem;
      font-weight: 900;
      color: ${reservationStudioText};
    }

    .reservationStudio__summaryFoot {
      margin-top: 6px;
      color: ${reservationStudioMuted};
      font-size: 0.82rem;
      line-height: 1.45;
    }

    .reservationStudio__grid {
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      gap: 18px;
      margin-top: 18px;
      align-items: start;
      min-width: 0;
    }

    .reservationStudio__topComposition {
      grid-column: 1 / -1;
      display: grid;
      gap: 18px;
      align-items: stretch;
      min-width: 0;
    }

    .reservationStudio__topComposition--split {
      grid-template-columns: minmax(320px, 0.84fr) minmax(0, 1.16fr);
    }

    .reservationStudio__topComposition--single {
      grid-template-columns: minmax(0, 1fr);
    }

    .reservationStudio__topStack {
      display: grid;
      gap: 18px;
      align-content: start;
      min-width: 0;
    }

    .reservationStudio__topComposition > .reservationStudio__card {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .reservationStudio__card--fields {
      grid-column: span 8;
      align-self: stretch;
      display: flex;
      flex-direction: column;
    }

    .reservationStudio__card--roles {
      grid-column: span 4;
      align-self: stretch;
      display: flex;
      flex-direction: column;
    }

    .reservationStudio__card--full {
      grid-column: 1 / -1;
    }

    .reservationStudio__card {
      border-radius: 24px;
      border: 1px solid ${reservationStudioBorder};
      background: ${
        isLightTheme
          ? "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(244,238,230,0.95))"
          : "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.025))"
      };
      box-shadow: ${
        isLightTheme
          ? "0 24px 50px rgba(84, 64, 38, 0.10)"
          : "0 24px 50px rgba(0, 0, 0, 0.22)"
      };
      padding: 22px;
      position: relative;
      overflow: hidden;
    }

    .reservationStudio__card::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(140deg, ${reservationStudioAccent}10, transparent 40%, ${reservationStudioHighlight}08 78%, transparent 100%);
      opacity: 1;
    }

    .reservationStudio__card > * {
      position: relative;
      z-index: 1;
    }

    .reservationStudio__card--future {
      grid-column: 1 / -1;
      border-style: dashed;
      min-height: 220px;
    }

    .reservationStudio__cardEyebrow {
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${reservationStudioAccent};
      margin-bottom: 8px;
    }

    .reservationStudio__cardTitleRow {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: flex-start;
      flex-wrap: wrap;
    }

    .reservationStudio__cardTitle {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 900;
      color: ${reservationStudioText};
    }

    .reservationStudio__cardCopy {
      margin: 10px 0 0;
      color: ${reservationStudioMuted};
      line-height: 1.55;
      font-size: 0.9rem;
    }

    .reservationStudio__chip {
      border-radius: 999px;
      padding: 7px 12px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.04)"};
      font-size: 0.74rem;
      font-weight: 700;
      color: ${reservationStudioText};
      white-space: nowrap;
    }

    .reservationStudio__policyGrid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 14px;
      margin-top: 20px;
    }

    .reservationStudio__policySource {
      min-width: 0;
      border-radius: 20px;
      border: 1px solid ${reservationStudioBorder};
      background: ${
        isLightTheme
          ? "rgba(255, 255, 255, 0.72)"
          : "rgba(255, 255, 255, 0.035)"
      };
      padding: 16px;
      transition: border-color 150ms ease, opacity 150ms ease, transform 150ms ease;
    }

    .reservationStudio__policySource--disabled {
      opacity: 0.66;
    }

    .reservationStudio__policySourceHeader {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      padding-bottom: 14px;
      border-bottom: 1px solid ${reservationStudioBorder};
    }

    .reservationStudio__policySourceTitle {
      color: ${reservationStudioText};
      font-weight: 900;
      font-size: 0.96rem;
    }

    .reservationStudio__policySourceCopy {
      margin-top: 5px;
      color: ${reservationStudioMuted};
      font-size: 0.78rem;
      line-height: 1.45;
    }

    .reservationStudio__policyMaster,
    .reservationStudio__policyAction {
      appearance: none;
      font: inherit;
      color: inherit;
    }

    .reservationStudio__policyMaster {
      flex: 0 0 auto;
      border-radius: 999px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(24, 54, 69, 0.05)" : "rgba(255, 255, 255, 0.05)"};
      color: ${reservationStudioMuted};
      padding: 7px 10px;
      font-size: 0.72rem;
      font-weight: 800;
    }

    .reservationStudio__policyMaster--active {
      border-color: ${reservationStudioAccent};
      background: ${reservationStudioAccent}16;
      color: ${reservationStudioAccent};
    }

    .reservationStudio__policyActions {
      display: grid;
      gap: 8px;
      margin-top: 14px;
    }

    .reservationStudio__policyAction {
      width: 100%;
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      gap: 10px;
      align-items: start;
      text-align: left;
      border-radius: 14px;
      border: 1px solid transparent;
      background: transparent;
      padding: 10px;
      transition: background 140ms ease, border-color 140ms ease;
    }

    .reservationStudio__policyAction:hover:not(:disabled) {
      border-color: ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.05)" : "rgba(255, 255, 255, 0.04)"};
    }

    .reservationStudio__policyAction:disabled {
      cursor: not-allowed;
    }

    .reservationStudio__policyCheck {
      width: 20px;
      height: 20px;
      border-radius: 7px;
      border: 1px solid ${reservationStudioBorder};
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: transparent;
      font-size: 0.72rem;
      font-weight: 900;
      margin-top: 1px;
    }

    .reservationStudio__policyCheck--active {
      border-color: ${reservationStudioAccent};
      background: ${reservationStudioAccent};
      color: ${isLightTheme ? "#ffffff" : "#061019"};
    }

    .reservationStudio__policyActionTitle {
      display: block;
      color: ${reservationStudioText};
      font-size: 0.84rem;
      font-weight: 850;
    }

    .reservationStudio__policyActionCopy {
      display: block;
      margin-top: 3px;
      color: ${reservationStudioMuted};
      font-size: 0.74rem;
      line-height: 1.4;
    }

    .reservationStudio__fieldGrid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
      margin-top: 18px;
    }

    .reservationStudio__field,
    .reservationStudio__field--full {
      min-width: 0;
    }

    .reservationStudio__field--full {
      grid-column: 1 / -1;
    }

    .reservationStudio__label {
      display: block;
      margin-bottom: 7px;
      font-size: 0.84rem;
      font-weight: 700;
      color: ${reservationStudioText};
    }

    .reservationStudio__hint {
      display: block;
      margin-top: 6px;
      font-size: 0.78rem;
      color: ${reservationStudioMuted};
      line-height: 1.5;
    }

    .reservationStudio__input,
    .reservationStudio__textarea {
      width: 100%;
      border-radius: 14px;
      border: 1px solid ${isLightTheme ? "rgba(24, 54, 69, 0.16)" : "rgba(148, 163, 184, 0.28)"};
      background: ${isLightTheme ? "rgba(255,255,255,0.94)" : "rgba(7, 11, 19, 0.72)"};
      color: ${reservationStudioText};
      padding: 11px 13px;
      font-size: 0.94rem;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
    }

    .reservationStudio__input:focus,
    .reservationStudio__textarea:focus {
      outline: none;
      border-color: ${reservationStudioAccent};
      box-shadow: 0 0 0 3px ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.14)"};
    }

    .reservationStudio__textarea {
      min-height: 108px;
      resize: vertical;
    }

    .reservationStudio__recipientChipRow {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 12px;
    }

    .reservationStudio__recipientChip {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      max-width: 100%;
      border-radius: 14px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.05)"};
      color: ${reservationStudioText};
      padding: 9px 12px;
      font-size: 0.8rem;
      font-weight: 800;
      line-height: 1.2;
      transition: transform 140ms ease, border-color 140ms ease, background 140ms ease, box-shadow 140ms ease;
    }

    .reservationStudio__recipientChip:hover {
      transform: translateY(-1px);
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.08)" : "rgba(48, 216, 255, 0.10)"};
      box-shadow: 0 14px 24px ${isLightTheme ? "rgba(33, 89, 104, 0.12)" : "rgba(0, 0, 0, 0.22)"};
    }

    .reservationStudio__recipientChipLabel {
      word-break: break-all;
    }

    .reservationStudio__recipientChipClose {
      color: ${isLightTheme ? "#dc2626" : "#fca5a5"};
      font-weight: 900;
      flex: 0 0 auto;
    }

    .reservationStudio__recipientInputRow {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 10px;
      align-items: center;
    }

    .reservationStudio__recipientEmpty {
      padding: 12px 14px;
      border-radius: 14px;
      border: 1px dashed ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.02)"};
      color: ${reservationStudioMuted};
      font-size: 0.82rem;
      line-height: 1.5;
    }

    .reservationStudio__choiceGrid,
    .reservationStudio__futureGrid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      margin-top: 18px;
    }

    .reservationStudio__roleGrid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 12px;
      margin-top: 18px;
      align-items: start;
    }

    .reservationStudio__roleGrid--reservationFields,
    .reservationStudio__roleGrid--reservationRoles {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .reservationStudio__roleGrid--reservationFields .reservationStudio__roleBlock {
      padding: 18px;
    }

    .reservationStudio__roleGrid--reservationRoles .reservationStudio__roleButton {
      padding: 14px 15px;
      border-radius: 16px;
    }

    .reservationStudio__roleGrid--reservationRoles .reservationStudio__roleTitle {
      font-size: 0.9rem;
    }

    .reservationStudio__roleGrid--reservationRoles .reservationStudio__roleCopy {
      margin-top: 7px;
      font-size: 0.79rem;
      line-height: 1.45;
    }

    .reservationStudio__roleGrid--reservationRoles .reservationStudio__roleBadge {
      margin-top: 8px;
      padding: 5px 9px;
      font-size: 0.7rem;
    }

    .reservationStudio__choiceButton,
    .reservationStudio__roleButton {
      width: 100%;
      text-align: left;
      border-radius: 18px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.86)" : "rgba(255,255,255,0.03)"};
      color: ${reservationStudioText};
      padding: 15px 16px;
      transition: transform 140ms ease, border-color 140ms ease, box-shadow 140ms ease, background 140ms ease;
    }

    .reservationStudio__choiceButton:hover,
    .reservationStudio__roleButton:hover {
      transform: translateY(-1px);
      border-color: ${reservationStudioAccent};
      box-shadow: ${
        isLightTheme
          ? "0 18px 34px rgba(84, 64, 38, 0.12)"
          : "0 16px 32px rgba(0, 0, 0, 0.22)"
      };
    }

    .reservationStudio__roleButton:disabled {
      cursor: not-allowed;
    }

    .reservationStudio__roleButton:disabled:hover {
      transform: none;
    }

    .reservationStudio__choiceButton--active,
    .reservationStudio__roleButton--active {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.10)"};
      box-shadow: 0 0 0 3px ${isLightTheme ? "rgba(33, 89, 104, 0.08)" : "rgba(48, 216, 255, 0.12)"};
    }

    .reservationStudio__choiceTitle,
    .reservationStudio__roleTitle {
      font-size: 0.94rem;
      font-weight: 800;
      color: ${reservationStudioText};
    }

    .reservationStudio__choiceCopy,
    .reservationStudio__roleCopy,
    .reservationStudio__futureCopy {
      margin-top: 8px;
      font-size: 0.82rem;
      line-height: 1.5;
      color: ${reservationStudioMuted};
    }

    .reservationStudio__roleBadge {
      margin-top: 10px;
      display: inline-flex;
      align-items: center;
      border-radius: 999px;
      padding: 6px 10px;
      font-size: 0.72rem;
      font-weight: 800;
      border: 1px solid ${reservationStudioBorder};
      color: ${reservationStudioText};
      background: ${isLightTheme ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.04)"};
    }

    .reservationStudio__roleBlock {
      border-radius: 18px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.78)" : "rgba(255,255,255,0.03)"};
      padding: 16px;
    }

    .reservationStudio__roleBlockHeader {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 12px;
      flex-wrap: wrap;
    }

    .reservationStudio__fieldPickerSummary {
      display: grid;
      gap: 12px;
    }

    .reservationStudio__fieldPickerPreview {
      min-height: 48px;
      border-radius: 16px;
      border: 1px dashed ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.66)" : "rgba(255,255,255,0.025)"};
      padding: 10px;
      display: flex;
      align-items: center;
      align-content: center;
      flex-wrap: wrap;
      gap: 8px;
    }

    .reservationStudio__fieldPickerChip {
      display: inline-flex;
      align-items: center;
      border-radius: 999px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.08)" : "rgba(48, 216, 255, 0.09)"};
      color: ${reservationStudioText};
      padding: 6px 9px;
      font-size: 0.72rem;
      font-weight: 800;
      line-height: 1.15;
    }

    .reservationStudio__fieldPickerChip--muted {
      background: ${isLightTheme ? "rgba(255,255,255,0.78)" : "rgba(255,255,255,0.04)"};
      color: ${reservationStudioMuted};
    }

    .reservationStudio__fieldPickerEmpty {
      color: ${reservationStudioMuted};
      font-size: 0.82rem;
      font-weight: 700;
    }

    .reservationStudio__fieldPickerButton {
      width: 100%;
      border-radius: 14px;
      border: 1px solid ${isLightTheme ? "rgba(33, 89, 104, 0.24)" : "rgba(48, 216, 255, 0.36)"};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.09)" : "rgba(48, 216, 255, 0.09)"};
      color: ${reservationStudioText};
      padding: 10px 12px;
      font-size: 0.84rem;
      font-weight: 900;
      transition: border-color 140ms ease, background 140ms ease, transform 140ms ease;
    }

    .reservationStudio__fieldPickerButton:hover {
      transform: translateY(-1px);
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.14)" : "rgba(48, 216, 255, 0.14)"};
    }

    .reservationStudio__fieldPickerModalOverlay {
      position: fixed;
      inset: 0;
      z-index: 26000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 18px;
      background: ${isLightTheme ? "rgba(248, 250, 252, 0.82)" : "rgba(2, 6, 23, 0.78)"};
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }

    .reservationStudio__fieldPickerModal {
      width: min(980px, 100%);
      max-height: min(780px, calc(100vh - 36px));
      border-radius: 24px;
      border: 1px solid ${reservationStudioBorder};
      background: ${
        isLightTheme
          ? "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(244,238,230,0.98))"
          : "linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(6, 10, 20, 0.98))"
      };
      box-shadow: ${
        isLightTheme
          ? "0 34px 78px rgba(84, 64, 38, 0.22)"
          : "0 34px 78px rgba(0, 0, 0, 0.62)"
      };
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .reservationStudio__fieldPickerModalHeader {
      padding: 22px;
      border-bottom: 1px solid ${reservationStudioBorder};
      display: flex;
      justify-content: space-between;
      gap: 16px;
      align-items: flex-start;
    }

    .reservationStudio__fieldPickerModalTitle {
      margin: 0;
      color: ${reservationStudioText};
      font-size: 1.2rem;
      font-weight: 900;
      line-height: 1.2;
    }

    .reservationStudio__fieldPickerModalCopy {
      margin: 8px 0 0;
      color: ${reservationStudioMuted};
      font-size: 0.88rem;
      line-height: 1.45;
    }

    .reservationStudio__fieldPickerModalActions {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 8px;
      flex: 0 0 auto;
    }

    .reservationStudio__fieldPickerAction {
      border-radius: 12px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.78)" : "rgba(255,255,255,0.04)"};
      color: ${reservationStudioText};
      padding: 8px 11px;
      font-size: 0.78rem;
      font-weight: 900;
      line-height: 1.15;
    }

    .reservationStudio__fieldPickerAction--primary {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.12)" : "rgba(48, 216, 255, 0.12)"};
    }

    .reservationStudio__fieldPickerAction:hover {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.12)" : "rgba(48, 216, 255, 0.12)"};
    }

    .reservationStudio__fieldPickerModalBody {
      padding: 20px 22px 22px;
      overflow-y: auto;
      min-height: 0;
    }

    .reservationStudio__fieldPickerModalStats {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 14px;
    }

    .reservationStudio__fieldPickerGrid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
      gap: 10px;
    }

    .reservationStudio__fieldPickerCard {
      min-height: 72px;
      border-radius: 16px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.035)"};
      color: ${reservationStudioText};
      padding: 12px;
      display: flex;
      justify-content: space-between;
      gap: 10px;
      align-items: flex-start;
      text-align: left;
      transition: border-color 140ms ease, background 140ms ease, transform 140ms ease, box-shadow 140ms ease;
    }

    .reservationStudio__fieldPickerCard:hover {
      transform: translateY(-1px);
      border-color: ${reservationStudioAccent};
      box-shadow: ${
        isLightTheme
          ? "0 14px 26px rgba(84, 64, 38, 0.12)"
          : "0 14px 26px rgba(0, 0, 0, 0.25)"
      };
    }

    .reservationStudio__fieldPickerCard--active {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.12)" : "rgba(48, 216, 255, 0.12)"};
      box-shadow: 0 0 0 3px ${isLightTheme ? "rgba(33, 89, 104, 0.08)" : "rgba(48, 216, 255, 0.10)"};
    }

    .reservationStudio__fieldPickerCardText {
      display: grid;
      min-width: 0;
    }

    .reservationStudio__fieldPickerCardLabel {
      color: ${reservationStudioText};
      font-size: 0.86rem;
      font-weight: 900;
      line-height: 1.25;
      overflow-wrap: anywhere;
    }

    .reservationStudio__fieldPickerCheck {
      width: 22px;
      height: 22px;
      border-radius: 8px;
      border: 1px solid ${reservationStudioBorder};
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      color: ${reservationStudioAccent};
      font-size: 0.88rem;
      font-weight: 900;
      background: ${isLightTheme ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.04)"};
    }

    .reservationStudio__heroSaveButton {
      border: 1px solid ${isLightTheme ? "rgba(33, 89, 104, 0.32)" : "rgba(48, 216, 255, 0.58)"};
      color: ${isLightTheme ? "#183645" : "rgba(196, 239, 255, 0.98)"};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.11)"};
      padding: 7px 12px;
      border-radius: 12px;
      font-size: 0.86rem;
      font-weight: 700;
      line-height: 1.2;
      cursor: pointer;
      white-space: nowrap;
      transition: transform 120ms ease, border-color 120ms ease, background 120ms ease, box-shadow 120ms ease;
    }

    .reservationStudio__heroSaveButton:hover {
      transform: translateY(-1px);
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.16)" : "rgba(48, 216, 255, 0.16)"};
      box-shadow: 0 14px 22px ${isLightTheme ? "rgba(33, 89, 104, 0.14)" : "rgba(0, 0, 0, 0.28)"};
    }

    .reservationStudio__heroSaveButton:focus-visible {
      outline: 2px solid ${reservationStudioAccent};
      outline-offset: 2px;
    }

    .reservationStudio__card--emailStudio {
      border-style: solid;
    }

    .reservationStudio__emailHeaderControls {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
      flex-wrap: wrap;
    }

    .reservationStudio__emailLanguageTabs {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px;
      border-radius: 999px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.72)" : "rgba(6, 10, 19, 0.52)"};
      box-shadow: inset 0 1px 0 ${isLightTheme ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.04)"};
    }

    .reservationStudio__emailLanguageTabs--editor {
      margin-top: 10px;
      background: ${isLightTheme ? "rgba(255,255,255,0.92)" : "rgba(6, 10, 19, 0.76)"};
    }

    .reservationStudio__emailLanguageTab {
      border: none;
      border-radius: 999px;
      padding: 8px 12px;
      min-width: 52px;
      background: transparent;
      color: ${reservationStudioMuted};
      font-size: 0.78rem;
      font-weight: 900;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      transition: background 140ms ease, color 140ms ease, box-shadow 140ms ease, transform 140ms ease;
    }

    .reservationStudio__emailLanguageTab:hover {
      color: ${reservationStudioText};
      transform: translateY(-1px);
    }

    .reservationStudio__emailLanguageTab--active {
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.12)" : "rgba(48, 216, 255, 0.16)"};
      color: ${reservationStudioText};
      box-shadow: 0 0 0 1px ${isLightTheme ? "rgba(33, 89, 104, 0.12)" : "rgba(48, 216, 255, 0.18)"};
    }

    .reservationStudio__emailDispatchRow {
      margin-top: 18px;
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 14px;
      align-items: stretch;
    }

    .reservationStudio__emailDispatchRowMain,
    .reservationStudio__emailDispatchRowMeta {
      border-radius: 20px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.78)" : "rgba(7, 11, 19, 0.44)"};
      box-shadow: ${isLightTheme ? "0 16px 28px rgba(84, 64, 38, 0.06)" : "0 18px 34px rgba(0, 0, 0, 0.18)"};
    }

    .reservationStudio__emailDispatchRowMain {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 14px 16px;
      flex-wrap: wrap;
    }

    .reservationStudio__emailDispatchBlock {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .reservationStudio__emailDispatchPills {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .reservationStudio__emailDispatchPill {
      border-radius: 999px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(245, 248, 250, 0.96)" : "rgba(255,255,255,0.03)"};
      color: ${reservationStudioMuted};
      padding: 8px 12px;
      font-size: 0.78rem;
      font-weight: 900;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      transition: transform 140ms ease, border-color 140ms ease, background 140ms ease, color 140ms ease;
    }

    .reservationStudio__emailDispatchPill:hover {
      transform: translateY(-1px);
      border-color: ${reservationStudioAccent};
      color: ${reservationStudioText};
    }

    .reservationStudio__emailDispatchPill--active {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.12)" : "rgba(48, 216, 255, 0.14)"};
      color: ${reservationStudioText};
      box-shadow: 0 0 0 1px ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.12)"};
    }

    .reservationStudio__emailDispatchRowMeta {
      display: grid;
      grid-template-columns: repeat(2, minmax(96px, 1fr));
      gap: 10px;
      padding: 12px;
      min-width: 240px;
    }

    .reservationStudio__emailDispatchMiniStat {
      border-radius: 16px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.84)" : "rgba(255,255,255,0.03)"};
      padding: 11px 12px;
    }

    .reservationStudio__emailDispatchMiniStat span {
      display: block;
      margin-bottom: 6px;
      font-size: 0.68rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${reservationStudioMuted};
    }

    .reservationStudio__emailDispatchMiniStat strong {
      display: block;
      font-size: 0.92rem;
      font-weight: 900;
      color: ${reservationStudioText};
    }

    .reservationStudio__emailWorkbench {
      display: grid;
      grid-template-columns: minmax(0, 0.96fr) minmax(0, 1.04fr);
      gap: 18px;
      margin-top: 18px;
      align-items: start;
    }

    .reservationStudio__emailPreviewColumn {
      display: grid;
      gap: 18px;
    }

    .reservationStudio__emailEditorPanel,
    .reservationStudio__emailPreviewPanel,
    .reservationStudio__pdfPreviewPanel,
    .reservationStudio__card--emailBranding {
      border-radius: 22px;
      border: 1px solid ${reservationStudioBorder};
      background: linear-gradient(
        180deg,
        ${isLightTheme ? "rgba(255,255,255,0.92)" : "rgba(10, 15, 26, 0.92)"},
        ${isLightTheme ? "rgba(245, 248, 250, 0.90)" : "rgba(17, 24, 39, 0.94)"}
      );
      box-shadow: ${isLightTheme ? "0 20px 34px rgba(84, 64, 38, 0.08)" : "0 22px 38px rgba(0, 0, 0, 0.20)"};
    }

    .reservationStudio__emailEditorPanel,
    .reservationStudio__emailPreviewPanel {
      padding: 20px;
      min-height: 100%;
    }

    .reservationStudio__pdfPreviewPanel {
      padding: 20px;
      display: grid;
      gap: 16px;
      align-content: start;
    }

    .reservationStudio__emailEditorHeader {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }

    .reservationStudio__emailInlineActions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }

    .reservationStudio__emailEditorStatusRow {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      margin-top: 12px;
    }

    .reservationStudio__editorLanguageSwitch {
      margin-top: 14px;
    }

    .reservationStudio__emailInlineButton {
      border-radius: 14px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.04)"};
      color: ${reservationStudioText};
      padding: 10px 14px;
      font-size: 0.8rem;
      font-weight: 800;
      transition: transform 140ms ease, border-color 140ms ease, background 140ms ease, box-shadow 140ms ease,
        opacity 140ms ease;
    }

    .reservationStudio__emailInlineButton:hover:not(:disabled) {
      transform: translateY(-1px);
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.08)" : "rgba(48, 216, 255, 0.10)"};
      box-shadow: 0 14px 24px ${isLightTheme ? "rgba(33, 89, 104, 0.12)" : "rgba(0, 0, 0, 0.22)"};
    }

    .reservationStudio__emailInlineButton:disabled {
      opacity: 0.42;
      cursor: not-allowed;
      box-shadow: none;
    }

    .reservationStudio__emailInlineButton--danger {
      border-color: ${isLightTheme ? "rgba(190, 24, 93, 0.22)" : "rgba(248, 113, 113, 0.26)"};
      color: ${isLightTheme ? "#8a173f" : "#fecaca"};
      background: ${isLightTheme ? "rgba(255, 241, 242, 0.92)" : "rgba(127, 29, 29, 0.22)"};
    }

    .reservationStudio__emailInlineButton--danger:hover:not(:disabled) {
      border-color: ${isLightTheme ? "rgba(190, 24, 93, 0.34)" : "rgba(248, 113, 113, 0.40)"};
      background: ${isLightTheme ? "rgba(255, 228, 230, 0.96)" : "rgba(127, 29, 29, 0.30)"};
    }

    .reservationStudio__variableRail {
      margin-top: 18px;
      border-radius: 20px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.05)" : "rgba(48, 216, 255, 0.06)"};
      padding: 14px;
    }

    .reservationStudio__variableRailTop {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      flex-wrap: wrap;
      margin-bottom: 12px;
    }

    .reservationStudio__variableRailHint {
      font-size: 0.78rem;
      line-height: 1.5;
      color: ${reservationStudioMuted};
    }

    .reservationStudio__variableCloud--compact {
      margin-bottom: 0;
    }

    .reservationStudio__tokenComposer {
      width: 100%;
      min-height: 52px;
      border-radius: 16px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.94)" : "rgba(6, 10, 19, 0.80)"};
      color: ${reservationStudioText};
      padding: 13px 14px;
      font-size: 0.94rem;
      line-height: 1.65;
      outline: none;
      transition: border-color 140ms ease, box-shadow 140ms ease, background 140ms ease;
      overflow-x: auto;
      overflow-y: hidden;
      white-space: nowrap;
      cursor: text;
    }

    .reservationStudio__tokenComposer:focus {
      border-color: ${reservationStudioAccent};
      box-shadow: 0 0 0 3px ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.12)"};
      background: ${isLightTheme ? "rgba(255,255,255,0.98)" : "rgba(8, 14, 26, 0.92)"};
    }

    .reservationStudio__tokenComposer--multiline {
      min-height: 250px;
      white-space: pre-wrap;
      overflow-y: auto;
    }

    .reservationStudio__tokenComposer:empty::before {
      content: attr(data-placeholder);
      color: ${reservationStudioMuted};
      pointer-events: none;
    }

    .reservationStudio__inlineToken {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin: 2px 6px 2px 0;
      padding: 6px 11px;
      border-radius: 999px;
      border: 1px solid ${isLightTheme ? "rgba(33, 89, 104, 0.18)" : "rgba(48, 216, 255, 0.24)"};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.14)"};
      color: ${isLightTheme ? "#183645" : "#d9f6ff"};
      font-size: 0.78rem;
      font-weight: 900;
      line-height: 1;
      white-space: nowrap;
      vertical-align: middle;
      box-shadow: inset 0 1px 0 ${isLightTheme ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.05)"};
    }

    .reservationStudio__card--emailBranding {
      margin-top: 18px;
      padding: 20px;
    }

    .reservationStudio__emailAssetGrid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 14px;
      margin-top: 18px;
    }

    .reservationStudio__emailAssetCard {
      border-radius: 20px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.03)"};
      padding: 16px;
      min-height: 236px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .reservationStudio__emailAssetLabel {
      font-size: 0.76rem;
      font-weight: 900;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${reservationStudioMuted};
    }

    .reservationStudio__emailAssetVisual {
      min-height: 138px;
      border-radius: 18px;
      border: 1px dashed ${isLightTheme ? "rgba(33, 89, 104, 0.22)" : "rgba(48, 216, 255, 0.22)"};
      background: linear-gradient(
        135deg,
        ${isLightTheme ? "rgba(226, 237, 241, 0.92)" : "rgba(14, 23, 39, 0.92)"},
        ${isLightTheme ? "rgba(245, 248, 250, 0.98)" : "rgba(20, 29, 49, 0.98)"}
      );
      box-shadow: inset 0 1px 0 ${isLightTheme ? "rgba(255,255,255,0.76)" : "rgba(255,255,255,0.04)"};
      flex: 1 1 auto;
    }

    .reservationStudio__emailAssetVisual--logo {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.1rem;
      font-weight: 900;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: ${reservationStudioText};
      min-height: 112px;
    }

    .reservationStudio__emailAssetLogoImage {
      max-width: 100%;
      max-height: 88px;
      width: auto;
      height: auto;
      object-fit: contain;
      display: block;
    }

    .reservationStudio__emailAssetVisual--ambient {
      background:
        radial-gradient(circle at top left, ${isLightTheme ? "rgba(194, 139, 53, 0.22)" : "rgba(246, 183, 86, 0.22)"}, transparent 46%),
        radial-gradient(circle at bottom right, ${isLightTheme ? "rgba(33, 89, 104, 0.18)" : "rgba(48, 216, 255, 0.18)"}, transparent 42%),
        linear-gradient(145deg, ${isLightTheme ? "rgba(236, 241, 244, 0.98)" : "rgba(11, 18, 31, 0.98)"}, ${isLightTheme ? "rgba(248, 250, 252, 0.98)" : "rgba(18, 27, 44, 0.98)"});
    }

    .reservationStudio__emailAssetMeta {
      font-size: 0.78rem;
      line-height: 1.45;
      color: ${reservationStudioMuted};
      min-height: 20px;
    }

    .reservationStudio__emailAssetInput {
      width: 100%;
      border-radius: 12px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.92)" : "rgba(6, 10, 19, 0.72)"};
      color: ${reservationStudioText};
      padding: 10px 12px;
      font-size: 0.8rem;
    }

    .reservationStudio__emailAssetActions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      margin-top: auto;
    }

    .reservationStudio__emailStudioIntro {
      display: grid;
      grid-template-columns: minmax(320px, 0.92fr) minmax(320px, 1.08fr);
      gap: 14px;
      margin-top: 18px;
    }

    .reservationStudio__emailDispatchCard {
      border-radius: 20px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.86)" : "rgba(255,255,255,0.035)"};
      padding: 18px;
      box-shadow: ${isLightTheme ? "0 18px 30px rgba(84, 64, 38, 0.08)" : "0 16px 28px rgba(0,0,0,0.18)"};
    }

    .reservationStudio__emailDispatchCard--accent {
      background: ${
        isLightTheme
          ? "linear-gradient(180deg, rgba(33, 89, 104, 0.08), rgba(255,255,255,0.88))"
          : "linear-gradient(180deg, rgba(48, 216, 255, 0.10), rgba(255,255,255,0.04))"
      };
      border-color: ${isLightTheme ? "rgba(33, 89, 104, 0.18)" : "rgba(48, 216, 255, 0.26)"};
    }

    .reservationStudio__emailSectionEyebrow {
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: ${reservationStudioAccent};
    }

    .reservationStudio__emailSectionTitle,
    .reservationStudio__modalSectionTitle {
      margin: 8px 0 0;
      font-size: 1rem;
      font-weight: 900;
      color: ${reservationStudioText};
    }

    .reservationStudio__emailSectionCopy {
      margin: 8px 0 0;
      font-size: 0.84rem;
      line-height: 1.6;
      color: ${reservationStudioMuted};
    }

    .reservationStudio__emailLanguagePills {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 16px;
    }

    .reservationStudio__emailLangButton {
      display: inline-flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
      min-width: 150px;
      border-radius: 16px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.03)"};
      color: ${reservationStudioText};
      padding: 10px 14px;
      font-size: 0.84rem;
      font-weight: 800;
      transition: transform 140ms ease, border-color 140ms ease, background 140ms ease, box-shadow 140ms ease;
    }

    .reservationStudio__emailLangButton:hover {
      transform: translateY(-1px);
      border-color: ${reservationStudioAccent};
    }

    .reservationStudio__emailLangButton--active {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.10)"};
      box-shadow: 0 0 0 3px ${isLightTheme ? "rgba(33, 89, 104, 0.08)" : "rgba(48, 216, 255, 0.10)"};
    }

    .reservationStudio__emailDispatchStats {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      margin-top: 16px;
    }

    .reservationStudio__emailDispatchStat {
      border-radius: 16px;
      padding: 13px 14px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.76)" : "rgba(2, 6, 23, 0.44)"};
    }

    .reservationStudio__emailDispatchStatLabel {
      display: block;
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${reservationStudioMuted};
      margin-bottom: 6px;
    }

    .reservationStudio__emailDispatchStat strong {
      font-size: 0.96rem;
      color: ${reservationStudioText};
    }

    .reservationStudio__emailTemplateGrid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
      margin-top: 18px;
    }

    .reservationStudio__emailTemplateCard {
      width: 100%;
      text-align: left;
      border-radius: 20px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.84)" : "rgba(255,255,255,0.03)"};
      padding: 18px;
      color: ${reservationStudioText};
      transition: transform 140ms ease, border-color 140ms ease, box-shadow 140ms ease, background 140ms ease;
    }

    .reservationStudio__emailTemplateCard:hover {
      transform: translateY(-2px);
      border-color: ${reservationStudioAccent};
      box-shadow: ${isLightTheme ? "0 20px 34px rgba(84, 64, 38, 0.12)" : "0 18px 34px rgba(0, 0, 0, 0.24)"};
    }

    .reservationStudio__emailTemplateHeader {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 10px;
      flex-wrap: wrap;
    }

    .reservationStudio__emailTemplateLang {
      font-size: 1rem;
      font-weight: 900;
      color: ${reservationStudioText};
    }

    .reservationStudio__emailTemplateMeta {
      margin-top: 4px;
      font-size: 0.76rem;
      color: ${reservationStudioMuted};
    }

    .reservationStudio__roleBadge--accent {
      border-color: ${reservationStudioAccent};
      color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.08)" : "rgba(48, 216, 255, 0.08)"};
    }

    .reservationStudio__emailTemplateSnippetLabel {
      margin-top: 14px;
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${reservationStudioMuted};
    }

    .reservationStudio__emailTemplateSnippet {
      margin-top: 8px;
      font-size: 0.95rem;
      font-weight: 800;
      line-height: 1.45;
      color: ${reservationStudioText};
    }

    .reservationStudio__emailTemplateBodySnippet {
      margin-top: 8px;
      font-size: 0.82rem;
      line-height: 1.6;
      color: ${reservationStudioMuted};
      min-height: 60px;
    }

    .reservationStudio__emailTemplateFooter {
      margin-top: 16px;
      display: flex;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
      font-size: 0.74rem;
      font-weight: 700;
      color: ${reservationStudioMuted};
    }

    .reservationStudio__fieldGrid--single {
      grid-template-columns: minmax(0, 1fr);
    }

    .reservationStudio__textarea--email {
      min-height: 260px;
    }

    .reservationStudio__modalOverlay {
      position: fixed;
      inset: 0;
      z-index: 22000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      background: rgba(0, 0, 0, 0.82);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }

    .reservationStudio__modalShell {
      width: 100%;
      border-radius: 24px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, rgba(10, 10, 18, 0.96), rgba(8, 13, 25, 0.98)) padding-box,
        linear-gradient(170deg, rgba(14, 165, 233, 0.40), rgba(59, 130, 246, 0.26)) border-box;
      box-shadow: 0 34px 72px rgba(2, 6, 23, 0.68);
      display: flex;
      flex-direction: column;
      max-height: calc(100vh - 36px);
      overflow: hidden;
    }

    .reservationStudio__modalShell--wide {
      max-width: 1180px;
    }

    .reservationStudio__modalHeader {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 18px;
      flex-wrap: wrap;
      padding: 26px 28px 20px;
      border-bottom: 1px solid rgba(148, 163, 184, 0.16);
    }

    .reservationStudio__modalEyebrow {
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: #67e8f9;
    }

    .reservationStudio__modalTitle {
      margin: 10px 0 0;
      font-size: clamp(1.45rem, 2vw, 1.9rem);
      font-weight: 900;
      line-height: 1.08;
      color: #f8fafc;
    }

    .reservationStudio__modalCopy {
      margin: 10px 0 0;
      max-width: 720px;
      font-size: 0.9rem;
      line-height: 1.65;
      color: rgba(226, 232, 240, 0.72);
    }

    .reservationStudio__modalClose {
      border-radius: 12px;
      padding: 10px 14px;
      border: 1px solid rgba(148, 163, 184, 0.42);
      background: rgba(255, 255, 255, 0.04);
      color: #e2e8f0;
      font-size: 0.84rem;
      font-weight: 800;
    }

    .reservationStudio__modalBody {
      padding: 24px 28px;
      overflow-y: auto;
      min-height: 0;
    }

    .reservationStudio__emailModalGrid {
      display: grid;
      grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
      gap: 18px;
      align-items: start;
    }

    .reservationStudio__modalSection {
      border-radius: 20px;
      border: 1px solid rgba(148, 163, 184, 0.18);
      background: rgba(15, 23, 42, 0.76);
      padding: 20px;
    }

    .reservationStudio__modalSection--preview {
      background: rgba(8, 15, 30, 0.84);
    }

    .reservationStudio__modalSectionHeader {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 14px;
      flex-wrap: wrap;
      margin-bottom: 16px;
    }

    .reservationStudio__editorTargetSwitch {
      display: inline-flex;
      padding: 4px;
      border-radius: 14px;
      border: 1px solid rgba(148, 163, 184, 0.24);
      background: rgba(255, 255, 255, 0.03);
      gap: 4px;
    }

    .reservationStudio__editorTargetButton {
      border: none;
      border-radius: 10px;
      padding: 8px 12px;
      background: transparent;
      color: rgba(226, 232, 240, 0.72);
      font-size: 0.8rem;
      font-weight: 800;
    }

    .reservationStudio__editorTargetButton--active {
      background: linear-gradient(135deg, rgba(14, 165, 233, 0.88), rgba(59, 130, 246, 0.94));
      color: #eff6ff;
    }

    .reservationStudio__variableCloud {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-bottom: 18px;
    }

    .reservationStudio__variableChip {
      border-radius: 999px;
      border: 1px solid rgba(148, 163, 184, 0.34);
      background: rgba(255, 255, 255, 0.04);
      color: #e2e8f0;
      padding: 8px 12px;
      font-size: 0.8rem;
      font-weight: 700;
      transition: transform 120ms ease, border-color 120ms ease, background 120ms ease;
    }

    .reservationStudio__variableChip:hover {
      transform: translateY(-1px);
      border-color: rgba(56, 189, 248, 0.54);
      background: rgba(14, 165, 233, 0.14);
    }

    .reservationStudio__emailPreviewSubject {
      margin-top: 2px;
      font-size: 1rem;
      font-weight: 900;
      color: #f8fafc;
    }

    .reservationStudio__emailPreviewShell {
      margin-top: 18px;
      border-radius: 24px;
      border: 1px solid rgba(77, 171, 247, 0.26);
      background: #0f172a;
      overflow: hidden;
      box-shadow: 0 22px 46px rgba(2, 6, 23, 0.42);
    }

    .reservationStudio__emailPreviewHeroImage {
      height: 156px;
      background-size: cover;
      background-position: center;
      border-bottom: 1px solid rgba(148, 163, 184, 0.18);
    }

    .reservationStudio__emailPreviewHeader {
      padding: 20px 22px 18px;
      background: linear-gradient(135deg, rgba(14, 165, 233, 0.22), rgba(59, 130, 246, 0.10));
      border-bottom: 1px solid rgba(148, 163, 184, 0.18);
    }

    .reservationStudio__emailPreviewLogo {
      display: block;
      max-width: 154px;
      max-height: 56px;
      width: auto;
      height: auto;
      margin-bottom: 18px;
      object-fit: contain;
      filter: drop-shadow(0 10px 18px rgba(2, 6, 23, 0.20));
    }

    .reservationStudio__emailPreviewBadge {
      display: inline-flex;
      align-items: center;
      border-radius: 999px;
      padding: 6px 10px;
      border: 1px solid rgba(56, 189, 248, 0.44);
      background: rgba(8, 47, 73, 0.54);
      color: #a5f3fc;
      font-size: 0.68rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }

    .reservationStudio__emailPreviewHotel {
      margin-top: 14px;
      font-size: 1.5rem;
      font-weight: 900;
      color: #f8fafc;
      line-height: 1.08;
    }

    .reservationStudio__emailPreviewCopy {
      margin-top: 8px;
      font-size: 0.86rem;
      line-height: 1.55;
      color: rgba(219, 231, 244, 0.78);
    }

    .reservationStudio__emailPreviewMetaGrid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      padding: 18px 22px 0;
    }

    .reservationStudio__emailPreviewMetaCard {
      border-radius: 16px;
      padding: 12px 14px;
      border: 1px solid rgba(148, 163, 184, 0.16);
      background: rgba(15, 23, 42, 0.72);
    }

    .reservationStudio__emailPreviewMetaCard span {
      display: block;
      font-size: 0.68rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #8fb5d0;
      margin-bottom: 7px;
    }

    .reservationStudio__emailPreviewMetaCard strong {
      font-size: 0.9rem;
      color: #f8fafc;
    }

    .reservationStudio__emailPreviewMessage {
      padding: 24px;
      background: linear-gradient(180deg, rgba(15, 23, 42, 0.92), rgba(15, 23, 42, 0.97));
    }

    .reservationStudio__emailPreviewMessage p {
      margin: 0 0 14px;
      color: #dbe7f4;
      font-size: 0.92rem;
      line-height: 1.7;
    }

    .reservationStudio__pdfPreviewPanel {
      background: linear-gradient(
        180deg,
        ${isLightTheme ? "rgba(249, 250, 252, 0.96)" : "rgba(14, 19, 31, 0.96)"},
        ${isLightTheme ? "rgba(243, 246, 250, 0.94)" : "rgba(12, 17, 28, 0.98)"}
      );
    }

    .reservationStudio__pdfPreviewMetaRow {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }

    .reservationStudio__pdfPreviewStat {
      border-radius: 16px;
      border: 1px solid ${isLightTheme ? "rgba(24, 54, 69, 0.10)" : "rgba(148, 163, 184, 0.18)"};
      background: ${isLightTheme ? "rgba(255,255,255,0.90)" : "rgba(255,255,255,0.04)"};
      padding: 12px 14px;
    }

    .reservationStudio__pdfPreviewStat span {
      display: block;
      font-size: 0.68rem;
      font-weight: 900;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${isLightTheme ? "rgba(24, 54, 69, 0.56)" : "rgba(148, 163, 184, 0.74)"};
      margin-bottom: 6px;
    }

    .reservationStudio__pdfPreviewStat strong {
      display: block;
      color: ${reservationStudioText};
      font-size: 0.92rem;
      font-weight: 800;
    }

    .reservationStudio__pdfPreviewActions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }

    .reservationStudio__pdfPreviewCopy {
      margin: 0;
      font-size: 0.82rem;
      line-height: 1.55;
      color: ${reservationStudioMuted};
    }

    .reservationStudio__emailDeliveryPanel {
      margin-top: 18px;
    }

    .reservationStudio__modalActions {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
      padding: 0 28px 26px;
    }

    .reservationStudio__modalActionsLeft,
    .reservationStudio__modalActionsRight {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }

    .reservationStudio__modalButton {
      border-radius: 14px;
      padding: 11px 16px;
      font-size: 0.84rem;
      font-weight: 800;
      border: 1px solid transparent;
      transition: transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease;
    }

    .reservationStudio__modalButton:hover:not(:disabled) {
      transform: translateY(-1px);
    }

    .reservationStudio__modalButton:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .reservationStudio__modalButton--ghost,
    .reservationStudio__modalButton--secondary {
      background: rgba(255, 255, 255, 0.04);
      border-color: rgba(148, 163, 184, 0.32);
      color: #e2e8f0;
    }

    .reservationStudio__modalButton--danger {
      background: rgba(127, 29, 29, 0.34);
      border-color: rgba(248, 113, 113, 0.34);
      color: #fecaca;
    }

    .reservationStudio__modalButton--primary {
      background: linear-gradient(135deg, rgba(14, 165, 233, 0.88), rgba(59, 130, 246, 0.94));
      border-color: rgba(56, 189, 248, 0.56);
      color: #eff6ff;
      box-shadow: 0 18px 30px rgba(14, 165, 233, 0.22);
    }

    @media (max-width: 1120px) {
      .reservationStudio__hero {
        grid-template-columns: 1fr;
      }

      .reservationStudio__subtitle {
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
      }

      .reservationStudio__heroAside,
      .reservationStudio__heroControls,
      .reservationStudio__heroPills {
        align-items: flex-start;
        justify-content: flex-start;
      }

      .reservationStudio__grid {
        grid-template-columns: minmax(0, 1fr);
      }

      .reservationStudio__emailStudioIntro,
      .reservationStudio__emailTemplateGrid,
      .reservationStudio__emailModalGrid {
        grid-template-columns: minmax(0, 1fr);
      }

      .reservationStudio__emailDispatchRow,
      .reservationStudio__emailWorkbench,
      .reservationStudio__emailAssetGrid {
        grid-template-columns: minmax(0, 1fr);
      }

      .reservationStudio__emailHeaderControls {
        justify-content: flex-start;
      }

      .reservationStudio__emailDispatchRowMeta {
        min-width: 0;
      }

      .reservationStudio__topComposition--split,
      .reservationStudio__topComposition--single {
        grid-template-columns: minmax(0, 1fr);
      }

      .reservationStudio__card--fields,
      .reservationStudio__card--roles,
      .reservationStudio__card--future {
        grid-column: 1 / -1;
      }
    }

    @media (max-width: 900px) {
      .reservationStudio__fieldGrid,
      .reservationStudio__roleGrid--reservationFields,
      .reservationStudio__roleGrid--reservationRoles {
        grid-template-columns: 1fr;
      }

      .reservationStudio__emailDispatchStats,
      .reservationStudio__emailPreviewMetaGrid,
      .reservationStudio__pdfPreviewMetaRow {
        grid-template-columns: 1fr;
      }

      .reservationStudio__emailDispatchRowMain,
      .reservationStudio__emailEditorHeader,
      .reservationStudio__variableRailTop {
        align-items: flex-start;
        flex-direction: column;
      }

      .reservationStudio__emailDispatchPills,
      .reservationStudio__emailHeaderControls,
      .reservationStudio__emailInlineActions {
        width: 100%;
      }

      .reservationStudio__emailInlineButton {
        flex: 1 1 180px;
        justify-content: center;
      }

      .reservationStudio__recipientInputRow {
        grid-template-columns: 1fr;
      }

      .reservationStudio__emailDispatchRowMeta {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .reservationStudio__modalHeader,
      .reservationStudio__modalBody,
      .reservationStudio__modalActions {
        padding-left: 18px;
        padding-right: 18px;
      }
    }

    @media (max-width: 640px) {
      .reservationStudio__emailDispatchRowMeta {
        grid-template-columns: 1fr;
      }

      .reservationStudio__emailLanguageTabs {
        width: 100%;
        justify-content: space-between;
      }

      .reservationStudio__emailLanguageTab {
        flex: 1 1 0;
      }

      .reservationStudio__tokenComposer--multiline {
        min-height: 220px;
      }
    }

    @media (max-height: 820px) {
      .reservationStudio {
        zoom: 0.9;
        min-height: calc(100% / 0.9);
        height: calc(100% / 0.9);
        max-height: calc(100% / 0.9);
      }
    }

    @media (max-height: 720px) {
      .reservationStudio {
        zoom: 0.8;
        min-height: calc(100% / 0.8);
        height: calc(100% / 0.8);
        max-height: calc(100% / 0.8);
      }
    }

    @media (max-height: 620px) {
      .reservationStudio {
        zoom: 0.7;
        min-height: calc(100% / 0.7);
        height: calc(100% / 0.7);
        max-height: calc(100% / 0.7);
      }
    }
  `;

  const bookingEngineStudioCss = `
    .bookingEngineStudio {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      min-height: 0;
      height: 100%;
      max-height: 100%;
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      color: ${reservationStudioText};
    }

    .beStudio__surface {
      position: relative;
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      border-radius: 30px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, ${reservationStudioSurface}, ${reservationStudioSurfaceAlt}) padding-box,
        linear-gradient(145deg, ${reservationStudioAccent}38, ${reservationStudioHighlight}32, ${reservationStudioSuccess}26) border-box;
      box-shadow: ${
        isLightTheme
          ? "0 28px 68px rgba(86, 65, 39, 0.12)"
          : "0 28px 72px rgba(0, 0, 0, 0.36)"
      };
      padding: 0 clamp(18px, 1.6vw, 24px) clamp(18px, 1.6vw, 24px);
      display: flex;
      flex-direction: column;
      gap: 18px;
      scrollbar-gutter: stable both-edges;
      height: 100%;
      max-height: 100%;
    }

    .beStudio__surface::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background:
        radial-gradient(980px 320px at 6% -8%, ${reservationStudioAccent}14, transparent 62%),
        radial-gradient(760px 280px at 100% 0%, ${reservationStudioHighlight}14, transparent 64%),
        radial-gradient(940px 320px at 50% 120%, ${reservationStudioSuccess}10, transparent 66%);
    }

    .beStudio__surface > * {
      position: relative;
      z-index: 1;
      min-width: 0;
      flex-shrink: 0;
    }

    .beStudio__card--modalOpen {
      position: relative;
      z-index: 30000;
    }

    .beStudio__hero {
      position: sticky;
      top: 0;
      z-index: 8;
      isolation: isolate;
      display: grid;
      grid-template-columns: minmax(0, 1.25fr) auto;
      gap: 18px;
      align-items: start;
      padding: clamp(18px, 1.4vw, 24px);
      border-radius: 28px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, ${reservationStudioSurface}, ${reservationStudioSurfaceAlt}) padding-box,
        linear-gradient(145deg, ${reservationStudioAccent}55, ${reservationStudioHighlight}48, ${reservationStudioSuccess}34) border-box;
      box-shadow: ${
        isLightTheme
          ? "0 28px 72px rgba(86, 65, 39, 0.14)"
          : "0 28px 80px rgba(0, 0, 0, 0.34)"
      };
      overflow: hidden;
    }

    .beStudio__hero::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      border-radius: inherit;
      background:
        radial-gradient(760px 240px at 4% 0%, ${reservationStudioAccent}20, transparent 62%),
        radial-gradient(640px 240px at 100% 10%, ${reservationStudioHighlight}18, transparent 62%),
        radial-gradient(860px 260px at 50% 120%, ${reservationStudioSuccess}12, transparent 64%);
    }

    .beStudio__hero::after {
      content: "";
      position: absolute;
      inset: 1px;
      pointer-events: none;
      border-radius: 27px;
      background: linear-gradient(
        180deg,
        ${isLightTheme ? "rgb(255, 252, 246)" : "rgb(14, 20, 33)"},
        ${isLightTheme ? "rgb(243, 236, 226)" : "rgb(10, 15, 26)"}
      );
      z-index: -1;
    }

    .beStudio__hero > * {
      position: relative;
      z-index: 1;
    }

    .beStudio__eyebrow,
    .beStudio__cardEyebrow,
    .beStudio__modalEyebrow,
    .beStudio__sectionEyebrow {
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: ${reservationStudioAccent};
    }

    .beStudio__title {
      margin: 8px 0 0;
      font-size: clamp(1.5rem, 2vw, 2.1rem);
      line-height: 1.04;
      font-weight: 900;
      color: ${reservationStudioText};
    }

    .beStudio__subtitle {
      margin: 10px 0 0;
      color: ${reservationStudioMuted};
      font-size: 0.92rem;
      line-height: 1.65;
      max-width: 860px;
    }

    .beStudio__heroMeta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 16px;
    }

    .beStudio__heroAside {
      display: grid;
      gap: 12px;
      align-content: start;
      justify-items: end;
      min-width: 300px;
    }

    .beStudio__heroActions,
    .beStudio__actionRow,
    .beStudio__inlineActions {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      align-items: center;
    }

    .beStudio__heroActions {
      justify-content: flex-end;
    }

    .beStudio__pill,
    .beStudio__chip {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      border-radius: 999px;
      padding: 7px 12px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.86)" : "rgba(255,255,255,0.05)"};
      color: ${reservationStudioText};
      font-size: 0.76rem;
      font-weight: 800;
      line-height: 1;
      white-space: nowrap;
    }

    .beStudio__pill--accent,
    .beStudio__chip--accent {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.12)"};
      color: ${reservationStudioAccent};
    }

    .beStudio__pill--highlight {
      border-color: ${reservationStudioHighlight};
      background: ${isLightTheme ? "rgba(194, 139, 53, 0.10)" : "rgba(246, 183, 86, 0.12)"};
      color: ${reservationStudioHighlight};
    }

    .beStudio__pill--success {
      border-color: ${reservationStudioSuccess};
      background: ${isLightTheme ? "rgba(85, 119, 99, 0.12)" : "rgba(43, 220, 136, 0.12)"};
      color: ${reservationStudioSuccess};
    }

    .beStudio__inlineToken {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin: 2px 6px 2px 0;
      padding: 7px 12px;
      border-radius: 999px;
      border: 1px solid ${isLightTheme ? "rgba(33, 89, 104, 0.26)" : "rgba(48, 216, 255, 0.34)"};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.16)" : "rgba(48, 216, 255, 0.18)"};
      color: ${isLightTheme ? "#124055" : "#dcf8ff"};
      font-size: 0.78rem;
      font-weight: 900;
      line-height: 1;
      white-space: nowrap;
      vertical-align: middle;
      box-shadow: inset 0 1px 0 ${isLightTheme ? "rgba(255,255,255,0.62)" : "rgba(255,255,255,0.06)"};
    }

    .beStudio__languageCardSnippet--tokens {
      min-height: 48px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px 6px;
      align-items: flex-start;
      align-content: flex-start;
    }

    .beStudio__languageCardText {
      font-size: 0.84rem;
      line-height: 1.55;
      color: ${reservationStudioMuted};
    }

    .beStudio__urlCard,
    .beStudio__previewCard,
    .beStudio__miniPanel,
    .beStudio__languageCard,
    .beStudio__termCard,
    .beStudio__roomPanel,
    .beStudio__modalSection,
    .beStudio__statCard,
    .beStudio__miniStat {
      border-radius: 22px;
      border: 1px solid ${reservationStudioBorder};
      background: ${
        isLightTheme
          ? "linear-gradient(180deg, rgba(255,255,255,0.94), rgba(245, 240, 233, 0.94))"
          : "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.025))"
      };
      box-shadow: ${
        isLightTheme
          ? "0 18px 34px rgba(84, 64, 38, 0.08)"
          : "0 18px 34px rgba(0, 0, 0, 0.18)"
      };
    }

    .beStudio__urlCard {
      width: min(420px, 100%);
      padding: 16px 18px;
    }

    .beStudio__urlLabel,
    .beStudio__statLabel,
    .beStudio__miniLabel,
    .beStudio__roomMetaLabel {
      display: block;
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${reservationStudioMuted};
    }

    .beStudio__urlValue,
    .beStudio__statValue,
    .beStudio__miniValue {
      display: block;
      margin-top: 8px;
      color: ${reservationStudioText};
      font-size: 0.95rem;
      font-weight: 900;
      line-height: 1.4;
      word-break: break-word;
    }

    .beStudio__urlHint,
    .beStudio__statFoot,
    .beStudio__cardCopy,
    .beStudio__hint,
    .beStudio__modalCopy,
    .beStudio__emptyCopy,
    .beStudio__textMuted {
      color: ${reservationStudioMuted};
      font-size: 0.82rem;
      line-height: 1.6;
    }

    .beStudio__hint--error {
      color: ${isLightTheme ? "#b42318" : "#fda4af"};
      font-weight: 700;
    }

    .beStudio__hint--success {
      color: ${isLightTheme ? "#18794e" : "#86efac"};
      font-weight: 700;
    }

    .beStudio__cardTitle,
    .beStudio__modalTitle,
    .beStudio__sectionTitle {
      margin: 8px 0 0;
      font-size: 1.06rem;
      font-weight: 900;
      color: ${reservationStudioText};
      line-height: 1.2;
    }

    .beStudio__editorSurface {
      margin-top: 18px;
      padding: 16px;
      border-radius: 20px;
      border: 1px solid ${isLightTheme ? "rgba(33, 89, 104, 0.16)" : "rgba(48, 216, 255, 0.18)"};
      background: ${
        isLightTheme
          ? "linear-gradient(180deg, rgba(236, 244, 247, 0.94), rgba(244, 248, 250, 0.98))"
          : "linear-gradient(180deg, rgba(14, 27, 39, 0.94), rgba(8, 18, 28, 0.98))"
      };
      box-shadow: ${
        isLightTheme
          ? "inset 0 1px 0 rgba(255,255,255,0.72), 0 12px 24px rgba(84, 64, 38, 0.05)"
          : "inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px rgba(0, 0, 0, 0.18)"
      };
    }

    .beStudio__editorSurface .beStudio__fieldGrid {
      margin-top: 0;
    }

    .beStudio__grid {
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      gap: 18px;
      align-items: start;
    }

    .beStudio__grid--modalOpen {
      position: relative;
      z-index: 30000;
    }

    .beStudio__card {
      grid-column: span 6;
      border-radius: 24px;
      border: 1px solid ${reservationStudioBorder};
      background: ${
        isLightTheme
          ? "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(244,238,230,0.94))"
          : "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.025))"
      };
      box-shadow: ${
        isLightTheme
          ? "0 22px 48px rgba(84, 64, 38, 0.10)"
          : "0 24px 50px rgba(0, 0, 0, 0.22)"
      };
      padding: 22px;
      position: relative;
      overflow: hidden;
    }

    .beStudio__card--paired {
      align-self: stretch;
      display: flex;
      flex-direction: column;
    }

    .beStudio__card::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(
        140deg,
        ${reservationStudioAccent}10,
        transparent 42%,
        ${reservationStudioHighlight}08 78%,
        transparent 100%
      );
    }

    .beStudio__card > * {
      position: relative;
      z-index: 1;
    }

    .beStudio__card--wide {
      grid-column: 1 / -1;
    }

    .beStudio__card--third {
      grid-column: span 4;
      align-self: stretch;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .beStudio__cardHeader,
    .beStudio__modalHeader,
    .beStudio__roomHeader {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 14px;
      flex-wrap: wrap;
    }

    .beStudio__cardBody {
      flex: 1 1 auto;
      min-height: 0;
    }

    .beStudio__cardBody--scroll {
      overflow-y: auto;
      overflow-x: hidden;
      padding-right: 4px;
      scrollbar-width: thin;
      scrollbar-color: ${isLightTheme ? "rgba(33, 89, 104, 0.28) transparent" : "rgba(48, 216, 255, 0.30) transparent"};
    }

    .beStudio__cardBody--scroll::-webkit-scrollbar {
      width: 6px;
    }

    .beStudio__cardBody--scroll::-webkit-scrollbar-track {
      background: transparent;
    }

    .beStudio__cardBody--scroll::-webkit-scrollbar-thumb {
      border-radius: 999px;
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.24)" : "rgba(48, 216, 255, 0.24)"};
    }

    .beStudio__fieldGrid,
    .beStudio__miniGrid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
      margin-top: 18px;
    }

    .beStudio__miniGrid--three {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .beStudio__field--full {
      grid-column: 1 / -1;
    }

    .beStudio__label {
      display: block;
      margin-bottom: 7px;
      font-size: 0.84rem;
      font-weight: 700;
      color: ${reservationStudioText};
    }

    .beStudio__input,
    .beStudio__textarea,
    .beStudio__select,
    .beStudio__fileInput {
      width: 100%;
      border-radius: 14px;
      border: 1px solid ${isLightTheme ? "rgba(24, 54, 69, 0.16)" : "rgba(148, 163, 184, 0.28)"};
      background: ${isLightTheme ? "rgba(255,255,255,0.94)" : "rgba(7, 11, 19, 0.72)"};
      color: ${reservationStudioText};
      padding: 11px 13px;
      font-size: 0.94rem;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
    }

    .beStudio__tokenComposer {
      border-color: ${isLightTheme ? "rgba(33, 89, 104, 0.20)" : "rgba(48, 216, 255, 0.22)"};
      background: ${isLightTheme ? "rgba(255,255,255,0.98)" : "rgba(6, 13, 21, 0.92)"};
      box-shadow: ${
        isLightTheme
          ? "inset 0 1px 0 rgba(255,255,255,0.82), 0 6px 14px rgba(84, 64, 38, 0.05)"
          : "inset 0 1px 0 rgba(255,255,255,0.04), 0 10px 20px rgba(0, 0, 0, 0.16)"
      };
    }

    .beStudio__input[readonly] {
      color: ${reservationStudioMuted};
    }

    .beStudio__textarea {
      min-height: 116px;
      resize: vertical;
    }

    .beStudio__textarea--lg {
      min-height: 240px;
    }

    .beStudio__input:focus,
    .beStudio__textarea:focus,
    .beStudio__select:focus,
    .beStudio__fileInput:focus {
      outline: none;
      border-color: ${reservationStudioAccent};
      box-shadow: 0 0 0 3px ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.14)"};
    }

    .beStudio__fileInput[aria-invalid="true"] {
      border-color: ${isLightTheme ? "#b42318" : "#fb7185"};
      box-shadow: 0 0 0 3px ${isLightTheme ? "rgba(180, 35, 24, 0.10)" : "rgba(251, 113, 133, 0.14)"};
    }

    .beStudio__switchGrid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      margin-top: 18px;
    }

    .beStudio__switchCard {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      border-radius: 18px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.86)" : "rgba(255,255,255,0.035)"};
      padding: 15px 16px;
    }

    .beStudio__switchInput {
      position: relative;
      appearance: none;
      -webkit-appearance: none;
      width: 46px;
      height: 28px;
      border-radius: 999px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(24, 54, 69, 0.10)" : "rgba(255,255,255,0.08)"};
      cursor: pointer;
      flex: 0 0 auto;
      margin-top: 2px;
      transition: background 140ms ease, border-color 140ms ease, box-shadow 140ms ease;
    }

    .beStudio__switchInput::after {
      content: "";
      position: absolute;
      top: 3px;
      left: 3px;
      width: 20px;
      height: 20px;
      border-radius: 999px;
      background: ${isLightTheme ? "#ffffff" : "#e2e8f0"};
      box-shadow: 0 3px 8px rgba(2, 6, 23, 0.22);
      transition: transform 140ms ease;
    }

    .beStudio__switchInput:checked {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.18)" : "rgba(48, 216, 255, 0.22)"};
      box-shadow: 0 0 0 3px ${isLightTheme ? "rgba(33, 89, 104, 0.08)" : "rgba(48, 216, 255, 0.10)"};
    }

    .beStudio__switchInput:checked::after {
      transform: translateX(18px);
    }

    .beStudio__switchTitle {
      display: block;
      color: ${reservationStudioText};
      font-size: 0.9rem;
      font-weight: 800;
    }

    .beStudio__switchCopy {
      display: block;
      margin-top: 6px;
      color: ${reservationStudioMuted};
      font-size: 0.79rem;
      line-height: 1.55;
    }

    .beStudio__modeBlock,
    .beStudio__widgetCodeBlock {
      margin-top: 18px;
    }

    .beStudio__modeSelector {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
      padding: 5px;
      border-radius: 16px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(24, 54, 69, 0.06)" : "rgba(2, 6, 23, 0.36)"};
    }

    .beStudio__modeOption {
      min-width: 0;
      min-height: 82px;
      border-radius: 12px;
      border: 1px solid transparent;
      background: transparent;
      color: ${reservationStudioText};
      padding: 12px 14px;
      text-align: left;
      cursor: pointer;
      transition: border-color 140ms ease, background 140ms ease, box-shadow 140ms ease;
    }

    .beStudio__modeOption:hover,
    .beStudio__modeOption:focus-visible {
      outline: none;
      border-color: ${isLightTheme ? "rgba(33, 89, 104, 0.30)" : "rgba(48, 216, 255, 0.32)"};
    }

    .beStudio__modeOption--active {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(255,255,255,0.96)" : "rgba(48, 216, 255, 0.10)"};
      box-shadow: ${isLightTheme ? "0 8px 18px rgba(84, 64, 38, 0.08)" : "0 10px 20px rgba(0, 0, 0, 0.20)"};
    }

    .beStudio__modeOptionTitle,
    .beStudio__modeOptionCopy {
      display: block;
    }

    .beStudio__modeOptionTitle {
      font-size: 0.88rem;
      font-weight: 900;
    }

    .beStudio__modeOptionCopy {
      margin-top: 5px;
      color: ${reservationStudioMuted};
      font-size: 0.76rem;
      line-height: 1.45;
    }

    .beStudio__heroScreenBlock {
      margin-top: 20px;
      padding: 18px;
      border: 1px solid ${reservationStudioBorder};
      border-radius: 18px;
      background: ${isLightTheme ? "rgba(24, 54, 69, 0.045)" : "rgba(2, 6, 23, 0.30)"};
    }

    .beStudio__heroScreenHint {
      margin: 0;
      color: ${reservationStudioMuted};
      font-size: 0.8rem;
      line-height: 1.55;
    }

    .beStudio__heroScreenSelector {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
      gap: 9px;
      margin-top: 14px;
    }

    .beStudio__heroScreenOption {
      min-width: 0;
      min-height: 48px;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border: 1px solid ${reservationStudioBorder};
      border-radius: 12px;
      background: ${isLightTheme ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.035)"};
      color: ${reservationStudioText};
      font-size: 0.81rem;
      font-weight: 800;
      line-height: 1.25;
      text-align: left;
      cursor: pointer;
      transition: border-color 140ms ease, background 140ms ease, box-shadow 140ms ease;
    }

    .beStudio__heroScreenOption:hover,
    .beStudio__heroScreenOption:focus-visible {
      outline: none;
      border-color: ${reservationStudioAccent};
      box-shadow: 0 0 0 3px ${isLightTheme ? "rgba(33, 89, 104, 0.08)" : "rgba(48, 216, 255, 0.10)"};
    }

    .beStudio__heroScreenOption--active {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.12)"};
    }

    .beStudio__heroScreenCheck {
      flex: 0 0 20px;
      width: 20px;
      height: 20px;
      display: inline-grid;
      place-items: center;
      border: 1px solid ${reservationStudioBorder};
      border-radius: 6px;
      background: ${isLightTheme ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.06)"};
      color: ${reservationStudioAccent};
      font-size: 0.78rem;
      font-weight: 900;
    }

    .beStudio__heroScreenOption--active .beStudio__heroScreenCheck {
      border-color: ${reservationStudioAccent};
    }

    .beStudio__heroScreenEmpty {
      margin-top: 14px;
      padding: 11px 13px;
      border-radius: 11px;
      background: ${isLightTheme ? "rgba(84, 64, 38, 0.07)" : "rgba(255,255,255,0.045)"};
      color: ${reservationStudioMuted};
      font-size: 0.78rem;
      line-height: 1.5;
    }

    .beStudio__heroImageSection,
    .beStudio__heroCopySection {
      margin-top: 22px;
      padding-top: 22px;
      border-top: 1px solid ${reservationStudioBorder};
    }

    .beStudio__heroImageHeader {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 18px;
    }

    .beStudio__heroImageFields {
      margin-top: 14px;
    }

    .beStudio__themeBlock {
      margin-top: 18px;
      padding-top: 18px;
      border-top: 1px solid ${reservationStudioBorder};
    }

    .beStudio__themeTitle {
      margin: 7px 0 0;
      color: ${reservationStudioText};
      font-size: 1rem;
      font-weight: 900;
    }

    .beStudio__themeCopy {
      margin: 7px 0 0;
      color: ${reservationStudioMuted};
      font-size: 0.79rem;
      line-height: 1.5;
    }

    .beStudio__themeSelector {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 9px;
      margin-top: 12px;
    }

    .beStudio__themeOption {
      min-width: 0;
      min-height: 150px;
      padding: 10px;
      border-radius: 13px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.035)"};
      color: ${reservationStudioText};
      text-align: left;
      cursor: pointer;
      transition: border-color 140ms ease, background 140ms ease, box-shadow 140ms ease, transform 140ms ease;
    }

    .beStudio__themeOption:hover,
    .beStudio__themeOption:focus-visible {
      outline: none;
      border-color: ${reservationStudioAccent};
      transform: translateY(-1px);
    }

    .beStudio__themeOption:focus-visible {
      box-shadow: 0 0 0 3px ${isLightTheme ? "rgba(33, 89, 104, 0.14)" : "rgba(48, 216, 255, 0.16)"};
    }

    .beStudio__themeOption--active {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(255,255,255,0.98)" : "rgba(48,216,255,0.09)"};
      box-shadow: ${isLightTheme ? "0 9px 20px rgba(84, 64, 38, 0.08)" : "0 10px 22px rgba(0,0,0,0.22)"};
    }

    .beStudio__themeSwatches {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      height: 30px;
      overflow: hidden;
      border-radius: 8px;
      border: 1px solid rgba(128, 128, 128, 0.24);
    }

    .beStudio__themeSwatches > span {
      min-width: 0;
    }

    .beStudio__themeOptionHeading {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 6px;
      margin-top: 10px;
      font-size: 0.82rem;
      font-weight: 900;
    }

    .beStudio__themeOptionHeading small {
      flex: 0 0 auto;
      padding: 3px 6px;
      border-radius: 999px;
      background: ${isLightTheme ? "rgba(33,89,104,0.10)" : "rgba(48,216,255,0.12)"};
      color: ${reservationStudioAccent};
      font-size: 0.58rem;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }

    .beStudio__themeOptionCopy {
      display: block;
      margin-top: 7px;
      color: ${reservationStudioMuted};
      font-size: 0.7rem;
      line-height: 1.42;
    }

    .beStudio__widgetCode {
      min-height: 132px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.77rem;
      line-height: 1.55;
    }

    .beStudio__button {
      border-radius: 14px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.04)"};
      color: ${reservationStudioText};
      padding: 10px 14px;
      font-size: 0.82rem;
      font-weight: 800;
      line-height: 1.2;
      transition: transform 140ms ease, border-color 140ms ease, background 140ms ease, box-shadow 140ms ease,
        opacity 140ms ease;
    }

    .beStudio__button:hover:not(:disabled),
    .beStudio__tabButton:hover:not(:disabled),
    .beStudio__variableButton:hover:not(:disabled),
    .beStudio__languageCardButton:hover:not(:disabled) {
      transform: translateY(-1px);
    }

    .beStudio__button:disabled,
    .beStudio__tabButton:disabled {
      opacity: 0.46;
      cursor: not-allowed;
      box-shadow: none;
    }

    .beStudio__button--primary {
      border-color: ${reservationStudioAccent};
      background: ${
        isLightTheme
          ? "linear-gradient(135deg, rgba(33, 89, 104, 0.16), rgba(167, 124, 49, 0.12))"
          : "linear-gradient(135deg, rgba(48, 216, 255, 0.18), rgba(246, 183, 86, 0.10))"
      };
      box-shadow: ${isLightTheme ? "0 16px 26px rgba(84, 64, 38, 0.10)" : "0 16px 28px rgba(0, 0, 0, 0.24)"};
    }

    .beStudio__button--danger {
      border-color: ${isLightTheme ? "rgba(190, 24, 93, 0.22)" : "rgba(248, 113, 113, 0.32)"};
      background: ${isLightTheme ? "rgba(255, 241, 242, 0.92)" : "rgba(127, 29, 29, 0.22)"};
      color: ${isLightTheme ? "#8a173f" : "#fecaca"};
    }

    .beStudio__button--ghost {
      background: transparent;
    }

    .beStudio__previewCard {
      padding: 18px;
      display: grid;
      gap: 12px;
    }

    .beStudio__card--paired .beStudio__previewCard {
      flex: 1 1 auto;
    }

    .beStudio__profilePreviewShell {
      min-width: 0;
      overflow: hidden;
      border-radius: 14px;
      border: 1px solid ${reservationStudioBorder};
      background: ${
        isLightTheme
          ? "linear-gradient(180deg, rgba(255,255,255,0.84), rgba(246, 241, 233, 0.88))"
          : "linear-gradient(180deg, rgba(8, 15, 28, 0.64), rgba(8, 15, 28, 0.82))"
      };
      display: grid;
      gap: 0;
      box-shadow: none;
    }

    .beStudio__profilePreviewBlock {
      min-width: 0;
      display: grid;
      gap: 4px;
      padding: 15px 18px;
    }

    .beStudio__profilePreviewBlock + .beStudio__profilePreviewBlock {
      border-top: 1px solid ${reservationStudioBorder};
    }

    .beStudio__profilePreviewContext {
      margin-bottom: 3px;
      color: ${reservationStudioHighlight};
      font-size: 0.68rem;
      font-weight: 900;
      letter-spacing: 0.12em;
      line-height: 1.35;
      text-transform: uppercase;
    }

    .beStudio__profilePreviewName {
      color: ${reservationStudioText};
      font-size: 0.96rem;
      font-weight: 900;
      line-height: 1.35;
      word-break: break-word;
    }

    .beStudio__profilePreviewLine {
      color: ${reservationStudioMuted};
      font-size: 0.84rem;
      line-height: 1.45;
      word-break: break-word;
    }

    .beStudio__profilePreviewEmpty {
      color: ${reservationStudioMuted};
      font-size: 0.84rem;
      line-height: 1.45;
      font-style: italic;
    }

    .beStudio__pricingPreviewShell {
      margin-top: 18px;
      border-radius: 18px;
      border: 1px solid ${reservationStudioBorder};
      background: ${
        isLightTheme
          ? "linear-gradient(180deg, rgba(255,255,255,0.88), rgba(245, 239, 231, 0.92))"
          : "linear-gradient(180deg, rgba(8, 15, 28, 0.72), rgba(8, 15, 28, 0.90))"
      };
      padding: 16px 18px;
      box-shadow: ${isLightTheme ? "0 16px 30px rgba(84, 64, 38, 0.08)" : "0 18px 34px rgba(0, 0, 0, 0.22)"};
    }

    .beStudio__pricingPreviewLabel {
      color: ${reservationStudioHighlight};
      letter-spacing: 0.16em;
      font-size: 0.72rem;
      font-weight: 900;
      text-transform: uppercase;
    }

    .beStudio__pricingPreviewCopy {
      margin-top: 8px;
      color: ${isLightTheme ? "rgba(24, 54, 69, 0.74)" : "rgba(255,255,255,0.70)"};
      font-size: 0.86rem;
      line-height: 1.35;
      word-break: break-word;
    }

    .beStudio__infoList {
      display: grid;
      gap: 10px;
      margin-top: 14px;
    }

    .beStudio__infoItem {
      display: grid;
      gap: 4px;
      padding: 12px 14px;
      border-radius: 16px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.78)" : "rgba(2, 6, 23, 0.32)"};
    }

    .beStudio__amenityOption {
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 12px;
    }

    .beStudio__amenityOptionText {
      min-width: 0;
      display: grid;
      gap: 4px;
    }

    .beStudio__amenityOptionAction {
      justify-content: flex-end;
      flex-wrap: nowrap;
    }

    .beStudio__infoItemLabel {
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${reservationStudioMuted};
    }

    .beStudio__infoItemValue {
      color: ${reservationStudioText};
      font-size: 0.92rem;
      font-weight: 700;
      line-height: 1.5;
      word-break: break-word;
    }

    .beStudio__chipRow,
    .beStudio__languageCardMeta,
    .beStudio__amenityCloud,
    .beStudio__thumbGrid,
    .beStudio__variableCloud,
    .beStudio__toolbarGroup {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .beStudio__pillButton,
    .beStudio__languageCardButton,
    .beStudio__variableButton,
    .beStudio__tabButton,
    .beStudio__thumbButton {
      border-radius: 14px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.86)" : "rgba(255,255,255,0.04)"};
      color: ${reservationStudioText};
      padding: 9px 12px;
      font-size: 0.8rem;
      font-weight: 800;
      line-height: 1.2;
      transition: transform 140ms ease, border-color 140ms ease, background 140ms ease, box-shadow 140ms ease;
    }

    .beStudio__languageCardButton {
      width: 100%;
      text-align: left;
      border-radius: 18px;
      padding: 16px;
    }

    .beStudio__tabButton--active,
    .beStudio__pillButton--active,
    .beStudio__thumbButton--active {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.12)"};
      box-shadow: 0 0 0 3px ${isLightTheme ? "rgba(33, 89, 104, 0.08)" : "rgba(48, 216, 255, 0.10)"};
    }

    .beStudio__amenityRemovePill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .beStudio__pillButtonClose {
      color: ${isLightTheme ? "#dc2626" : "#f87171"};
      font-weight: 900;
    }

    .beStudio__heroPreview {
      position: relative;
      min-height: 264px;
      margin-top: 18px;
      border-radius: 26px;
      overflow: hidden;
      border: 1px solid ${reservationStudioBorder};
      background:
        linear-gradient(180deg, rgba(2, 6, 23, 0.08), rgba(2, 6, 23, 0.56)),
        linear-gradient(135deg, ${reservationStudioAccent}32, ${reservationStudioHighlight}28),
        ${isLightTheme ? "rgba(218, 230, 234, 0.88)" : "rgba(15, 23, 42, 0.94)"};
      background-size: cover;
      background-position: center;
      display: flex;
      align-items: flex-end;
      padding: 22px;
      box-shadow: ${isLightTheme ? "0 22px 36px rgba(95, 72, 40, 0.10)" : "0 20px 40px rgba(0, 0, 0, 0.26)"};
    }

    .beStudio__heroPreview::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(180deg, rgba(15, 23, 42, 0.04), rgba(15, 23, 42, 0.68));
    }

    .beStudio__heroPreviewContent {
      position: relative;
      z-index: 1;
      max-width: 560px;
      display: grid;
      gap: 8px;
    }

    .beStudio__heroPreviewKicker {
      display: inline-flex;
      align-items: center;
      width: fit-content;
      border-radius: 999px;
      padding: 7px 12px;
      background: rgba(255, 255, 255, 0.12);
      border: 1px solid rgba(255, 255, 255, 0.18);
      color: #f8fafc;
      font-size: 0.72rem;
      font-weight: 900;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      backdrop-filter: blur(10px);
    }

    .beStudio__heroPreviewTitle {
      margin: 0;
      color: #f8fafc;
      font-size: clamp(1.5rem, 2vw, 2rem);
      line-height: 1.08;
      font-weight: 900;
    }

    .beStudio__heroPreviewCopy {
      margin: 0;
      color: rgba(241, 245, 249, 0.88);
      font-size: 0.9rem;
      line-height: 1.6;
    }

    .beStudio__languageGrid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
      margin-top: 18px;
    }

    .beStudio__languageCard {
      padding: 18px;
      min-height: 190px;
      display: grid;
      gap: 12px;
      align-content: start;
    }

    .beStudio__languageCardTitle {
      font-size: 0.98rem;
      font-weight: 900;
      color: ${reservationStudioText};
    }

    .beStudio__languageCardSnippet {
      font-size: 0.84rem;
      line-height: 1.55;
      color: ${reservationStudioMuted};
      min-height: 48px;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .beStudio__termsList {
      margin-top: 18px;
      display: grid;
    }

    .beStudio__termsListRow {
      width: 100%;
      border: 0;
      border-top: 1px solid ${reservationStudioBorder};
      background: transparent;
      color: ${reservationStudioText};
      padding: 14px 0;
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      gap: 14px;
      align-items: center;
      text-align: left;
      transition: color 140ms ease, border-color 140ms ease, transform 140ms ease;
    }

    .beStudio__termsListRow:last-child {
      border-bottom: 1px solid ${reservationStudioBorder};
    }

    .beStudio__termsListRow:hover:not(:disabled) {
      color: ${reservationStudioAccent};
      border-color: ${isLightTheme ? "rgba(33, 89, 104, 0.28)" : "rgba(48, 216, 255, 0.26)"};
      transform: translateX(2px);
    }

    .beStudio__termsListOrder {
      width: 34px;
      height: 34px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.12)"};
      color: ${reservationStudioMuted};
      font-size: 0.74rem;
      font-weight: 900;
      letter-spacing: 0.08em;
      flex-shrink: 0;
    }

    .beStudio__termsListTitle {
      min-width: 0;
      color: inherit;
      font-size: 0.96rem;
      font-weight: 800;
      line-height: 1.4;
      word-break: break-word;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .beStudio__roomGrid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
      gap: 18px;
      margin-top: 18px;
      align-items: start;
    }

    .beStudio__roomCard {
      border-radius: 26px;
      border: 1px solid ${reservationStudioBorder};
      background: ${
        isLightTheme
          ? "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(244,238,230,0.94))"
          : "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.025))"
      };
      padding: 18px;
      box-shadow: ${
        isLightTheme
          ? "0 22px 42px rgba(84, 64, 38, 0.10)"
          : "0 20px 40px rgba(0, 0, 0, 0.22)"
      };
      display: grid;
      gap: 16px;
      align-content: start;
      align-self: start;
    }

    .beStudio__roomMedia {
      position: relative;
      height: 228px;
      min-height: 228px;
      border-radius: 24px;
      overflow: hidden;
      border: 1px solid ${reservationStudioBorder};
      background:
        linear-gradient(180deg, rgba(15, 23, 42, 0.08), rgba(15, 23, 42, 0.64)),
        linear-gradient(135deg, ${reservationStudioAccent}24, ${reservationStudioHighlight}22),
        ${isLightTheme ? "rgba(226, 236, 240, 0.90)" : "rgba(15, 23, 42, 0.92)"};
      background-size: cover;
      background-position: center;
      display: flex;
      align-items: flex-end;
      padding: 18px;
    }

    .beStudio__roomMedia::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(180deg, rgba(15, 23, 42, 0.08), rgba(15, 23, 42, 0.76));
    }

    .beStudio__roomMediaContent {
      position: relative;
      z-index: 1;
      width: 100%;
      display: grid;
      gap: 10px;
    }

    .beStudio__roomTitle {
      margin: 0;
      color: #f8fafc;
      font-size: 1.14rem;
      font-weight: 900;
      line-height: 1.18;
    }

    .beStudio__roomSubtitle {
      margin: 0;
      color: rgba(241, 245, 249, 0.82);
      font-size: 0.82rem;
      line-height: 1.55;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .beStudio__roomTabs {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: flex-start;
    }

    .beStudio__roomTabs .beStudio__tabButton {
      align-self: flex-start;
      white-space: nowrap;
      padding: 8px 14px;
    }

    .beStudio__roomPanel {
      padding: 16px;
    }

    .beStudio__roomCopyStack {
      display: grid;
      gap: 18px;
    }

    .beStudio__roomCopyPreviewShell {
      border-radius: var(--be-radius-card);
      overflow: hidden;
      border: 1px solid var(--be-border);
      background: var(--be-surface);
      color: var(--be-text);
      font-family: var(--be-font-body);
      box-shadow: var(--be-shadow-control);
    }

    .beStudio__roomCopyPreviewShell .beStudio__chip {
      color: var(--be-text-muted);
      background: var(--be-surface-muted);
      border-color: var(--be-border);
      border-radius: var(--be-radius-round);
    }

    .beStudio__roomCopyPreviewShell .beStudio__chip--accent {
      color: var(--be-accent-text);
      background: var(--be-selected-bg);
      border-color: var(--be-selected-border);
    }

    .beStudio__roomCopyPreviewMedia {
      min-height: 170px;
      padding: 18px;
      display: flex;
      align-items: flex-end;
      justify-content: flex-start;
      background-color: var(--be-surface-muted);
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }

    .beStudio__roomCopyPreviewPlaceholder {
      color: var(--be-text-muted);
      font-size: 0.78rem;
      font-weight: 900;
      letter-spacing: 0.16em;
      text-transform: uppercase;
    }

    .beStudio__roomCopyPreviewBody {
      padding: 18px;
      display: grid;
      gap: 12px;
      background: var(--be-surface);
    }

    .beStudio__roomCopyPreviewTitle {
      margin: 0;
      color: var(--be-text);
      font-family: var(--be-font-heading);
      font-size: 1.18rem;
      line-height: 1.15;
      font-weight: 900;
    }

    .beStudio__roomCopyPreviewSpecs {
      color: var(--be-text-muted);
      font-size: 0.82rem;
      line-height: 1.45;
      letter-spacing: 0.06em;
    }

    .beStudio__roomCopyPreviewCopy {
      margin: 0;
      color: var(--be-text-muted);
      font-size: 0.9rem;
      line-height: 1.7;
      white-space: pre-wrap;
    }

    .beStudio__roomCopyPreviewMetaGrid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }

    .beStudio__roomCopyPreviewMetaCard {
      border-radius: var(--be-radius-sm);
      border: 1px solid var(--be-border);
      background: var(--be-surface-muted);
      padding: 12px 14px;
      display: grid;
      gap: 6px;
    }

    .beStudio__roomCopyPreviewMetaLabel {
      color: var(--be-accent-text);
      font-size: 0.68rem;
      font-weight: 900;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }

    .beStudio__roomCopyPreviewMetaValue {
      color: var(--be-text);
      font-size: 0.9rem;
      line-height: 1.5;
      font-weight: 800;
    }

    .beStudio__roomCopyPreviewHighlightSection {
      display: grid;
      gap: 10px;
    }

    .beStudio__roomCopyPreviewHighlights {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .beStudio__roomCopyPreviewHighlight {
      display: inline-flex;
      align-items: center;
      border-radius: var(--be-radius-round);
      padding: 8px 12px;
      border: 1px solid var(--be-border);
      background: var(--be-surface-muted);
      color: var(--be-text);
      font-size: 0.78rem;
      font-weight: 800;
      line-height: 1.25;
    }

    .beStudio__assetRow {
      display: flex;
      flex-wrap: wrap;
      gap: 14px;
      align-items: flex-start;
      margin-top: 16px;
    }

    .beStudio__assetPreview {
      width: 152px;
      height: 98px;
      border-radius: 18px;
      border: 1px solid ${reservationStudioBorder};
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      background-color: ${isLightTheme ? "rgba(226, 236, 240, 0.88)" : "rgba(15, 23, 42, 0.86)"};
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
      flex: 0 0 auto;
    }

    .beStudio__assetMeta {
      min-width: 0;
      flex: 1 1 220px;
      display: grid;
      gap: 10px;
      align-content: start;
    }

    .beStudio__thumb {
      position: relative;
    }

    .beStudio__thumbButton {
      width: 86px;
      height: 58px;
      padding: 0;
      border-radius: 14px;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      background-color: ${isLightTheme ? "rgba(226, 236, 240, 0.88)" : "rgba(15, 23, 42, 0.86)"};
    }

    .beStudio__modalOverlay {
      position: fixed;
      inset: 0;
      z-index: 24000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      background: rgba(2, 6, 23, 0.78);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
    }

    .beStudio__modalOverlay--amenities {
      background: ${isLightTheme ? "rgba(248, 250, 252, 0.98)" : "rgba(2, 6, 23, 0.98)"};
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    .beStudio__modal {
      width: min(1100px, 100%);
      max-height: calc(100vh - 32px);
      border-radius: 28px;
      border: 1px solid transparent;
      background:
        linear-gradient(
          180deg,
          ${isLightTheme ? "rgba(255, 255, 255, 0.98)" : "rgba(10, 10, 18, 0.96)"},
          ${isLightTheme ? "rgba(245, 240, 233, 0.98)" : "rgba(8, 13, 25, 0.98)"}
        ) padding-box,
        linear-gradient(170deg, ${reservationStudioAccent}55, ${reservationStudioHighlight}28, ${reservationStudioSuccess}18) border-box;
      box-shadow: ${
        isLightTheme
          ? "0 34px 72px rgba(86, 65, 39, 0.26)"
          : "0 34px 72px rgba(2, 6, 23, 0.68)"
      };
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .beStudio__modal--wide {
      width: min(1240px, 100%);
    }

    /* Keep the editor frame still while its live preview changes height. */
    .beStudio__modalOverlay--roomCopy {
      align-items: stretch;
      padding-top: 10px;
      padding-bottom: 18px;
    }

    .beStudio__modal--roomCopy {
      align-self: stretch;
      height: auto;
      min-height: 0;
      max-height: none;
      box-sizing: border-box;
    }

    .beStudio__modal--roomCopy .beStudio__modalHeader,
    .beStudio__modal--roomCopy .beStudio__modalFooter {
      flex: 0 0 auto;
    }

    .beStudio__modal--roomCopy .beStudio__modalBody {
      flex: 1 1 auto;
      scrollbar-gutter: stable;
    }

    .beStudio__modal--amenities {
      height: min(820px, calc(100vh - 32px));
    }

    .beStudio__modalOverlay--guestEmail {
      align-items: stretch;
      padding-top: 10px;
      padding-bottom: 18px;
    }

    .beStudio__modal--guestEmail {
      align-self: stretch;
      margin-top: 0;
      margin-bottom: 0;
      height: auto !important;
      min-height: 0 !important;
      max-height: none !important;
      box-sizing: border-box;
    }

    .beStudio__modal--guestEmail .beStudio__modalBody {
      flex: 1 1 auto;
    }

    .beStudio__modalHeader {
      padding: 24px 28px 18px;
      border-bottom: 1px solid ${reservationStudioBorder};
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 16px;
      flex-wrap: wrap;
    }

    .beStudio__modalTitle {
      color: ${reservationStudioText};
    }

    .beStudio__modalCopy {
      margin-top: 10px;
      max-width: 760px;
      color: ${reservationStudioMuted};
      font-size: 0.9rem;
    }

    .beStudio__modalBody {
      padding: 22px 28px;
      overflow: auto;
      min-height: 0;
    }

    .beStudio__modalBody--amenities {
      flex: 1 1 auto;
      overflow: hidden;
      display: flex;
      min-height: 0;
    }

    .beStudio__modalFooter {
      padding: 18px 28px 24px;
      border-top: 1px solid ${reservationStudioBorder};
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }

    .beStudio__modalGrid {
      display: grid;
      grid-template-columns: minmax(0, 1.06fr) minmax(300px, 0.94fr);
      gap: 18px;
      align-items: start;
    }

    .beStudio__roomCopyPrefill {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 14px 24px;
      margin-bottom: 18px;
      padding: 18px 20px;
      border: 1px solid ${isLightTheme ? "rgba(33, 89, 104, 0.20)" : "rgba(48, 216, 255, 0.22)"};
      border-radius: 16px;
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.06)" : "rgba(48, 216, 255, 0.06)"};
    }

    .beStudio__roomCopyPrefillContent {
      flex: 1 1 360px;
      min-width: 0;
    }

    .beStudio__roomCopyPrefillTitle {
      margin: 0;
      color: ${reservationStudioText};
      font-size: 0.9rem;
      font-weight: 800;
    }

    .beStudio__roomCopyPrefill .beStudio__hint {
      margin-top: 6px;
      line-height: 1.55;
    }

    .beStudio__roomCopyPrefillStatus {
      flex-basis: 100%;
      margin: 0;
      padding-top: 12px;
      border-top: 1px solid ${reservationStudioBorder};
      color: ${reservationStudioText};
      font-size: 0.84rem;
      line-height: 1.55;
    }

    .beStudio__roomCopyPrefillStatus.beStudio__hint--error {
      color: ${isLightTheme ? "#b42318" : "#fda4af"};
    }

    .beStudio__installGuideModal {
      height: min(820px, calc(100vh - 32px));
    }

    .beStudio__installGuideBody {
      display: grid;
      grid-template-rows: auto auto minmax(0, 1fr);
      gap: 16px;
      overflow: hidden;
    }

    .beStudio__installGuideNotice {
      display: grid;
      gap: 5px;
      padding: 14px 16px;
      border: 1px solid ${isLightTheme ? "rgba(33, 89, 104, 0.20)" : "rgba(48, 216, 255, 0.22)"};
      border-radius: 12px;
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.07)" : "rgba(48, 216, 255, 0.07)"};
    }

    .beStudio__installGuideNoticeTitle {
      color: ${reservationStudioText};
      font-size: 0.84rem;
      font-weight: 900;
    }

    .beStudio__installGuideNoticeCopy,
    .beStudio__installGuidePanelCopy,
    .beStudio__installGuideNote {
      color: ${reservationStudioMuted};
      font-size: 0.82rem;
      line-height: 1.55;
    }

    .beStudio__installGuideTabs {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      padding: 5px;
      border: 1px solid ${reservationStudioBorder};
      border-radius: 12px;
      background: ${isLightTheme ? "rgba(24, 54, 69, 0.05)" : "rgba(2, 6, 23, 0.36)"};
    }

    .beStudio__installGuideTab {
      min-height: 38px;
      border: 1px solid transparent;
      border-radius: 8px;
      background: transparent;
      color: ${reservationStudioMuted};
      padding: 8px 14px;
      font-size: 0.8rem;
      font-weight: 800;
      cursor: pointer;
    }

    .beStudio__installGuideTab:hover,
    .beStudio__installGuideTab:focus-visible {
      outline: none;
      border-color: ${isLightTheme ? "rgba(33, 89, 104, 0.26)" : "rgba(48, 216, 255, 0.28)"};
    }

    .beStudio__installGuideTab--active {
      border-color: ${reservationStudioAccent};
      background: ${isLightTheme ? "rgba(255,255,255,0.96)" : "rgba(48, 216, 255, 0.10)"};
      color: ${reservationStudioText};
      box-shadow: ${isLightTheme ? "0 7px 16px rgba(84, 64, 38, 0.08)" : "0 8px 18px rgba(0, 0, 0, 0.18)"};
    }

    .beStudio__installGuidePanel {
      min-height: 0;
      display: flex;
      flex-direction: column;
      padding: 18px;
      border: 1px solid ${reservationStudioBorder};
      border-radius: 14px;
      background: ${isLightTheme ? "rgba(255,255,255,0.84)" : "rgba(2, 6, 23, 0.46)"};
      overflow: hidden;
    }

    .beStudio__installGuidePanelHeader {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
    }

    .beStudio__installGuidePanelCopy {
      margin: 8px 0 0;
      max-width: 760px;
    }

    .beStudio__installGuideNote {
      margin-top: 12px;
      padding-left: 12px;
      border-left: 3px solid ${reservationStudioHighlight};
    }

    .beStudio__installGuideCode {
      flex: 1 1 auto;
      min-height: 0;
      margin: 14px 0 0;
      overflow: auto;
      white-space: pre;
      border: 1px solid ${isLightTheme ? "rgba(24, 54, 69, 0.14)" : "rgba(148, 163, 184, 0.20)"};
      border-radius: 10px;
      background: ${isLightTheme ? "#17232d" : "#020617"};
      color: #e8eef3;
      padding: 16px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.76rem;
      line-height: 1.6;
    }

    .beStudio__modalSection {
      padding: 20px;
    }

    .beStudio__amenityModalGrid {
      flex: 1 1 auto;
      width: 100%;
      min-height: 0;
      align-items: stretch;
    }

    .beStudio__amenityPickerPanel,
    .beStudio__amenitySelectedPanel {
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .beStudio__amenityResultList {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
      display: grid;
      gap: 12px;
      margin-top: 18px;
      padding-right: 4px;
    }

    .beStudio__amenityCloud--selected {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
      align-content: flex-start;
      padding-right: 4px;
    }

    .beStudio__targetSwitch {
      display: inline-flex;
      gap: 4px;
      padding: 4px;
      border-radius: 14px;
      border: 1px solid ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(33, 89, 104, 0.05)" : "rgba(255, 255, 255, 0.03)"};
    }

    .beStudio__targetButton {
      border: none;
      border-radius: 10px;
      padding: 8px 12px;
      background: transparent;
      color: ${reservationStudioMuted};
      font-size: 0.8rem;
      font-weight: 800;
    }

    .beStudio__targetButton--active {
      background: linear-gradient(135deg, ${reservationStudioAccent}, ${reservationStudioHighlight});
      color: #f8fafc;
    }

    .beStudio__previewShell {
      border-radius: 24px;
      border: 1px solid rgba(77, 171, 247, 0.24);
      overflow: hidden;
      background: #0f172a;
      box-shadow: 0 22px 46px rgba(2, 6, 23, 0.42);
    }

    .beStudio__previewHeader {
      padding: 18px 20px 16px;
      border-bottom: 1px solid rgba(148, 163, 184, 0.18);
    }

    .beStudio__previewBody {
      padding: 20px;
      display: grid;
      gap: 14px;
    }

    .beStudio__previewSubject {
      font-size: 1rem;
      font-weight: 900;
      color: #f8fafc;
    }

    .beStudio__previewParagraph {
      margin: 0;
      color: rgba(226, 232, 240, 0.82);
      font-size: 0.9rem;
      line-height: 1.7;
      white-space: pre-wrap;
    }

    .beStudio__previewHeroImage {
      height: 154px;
      background-size: cover;
      background-position: center;
      border-bottom: 1px solid rgba(148, 163, 184, 0.18);
    }

    .beStudio__emptyState {
      margin-top: 16px;
      border-radius: 18px;
      border: 1px dashed ${reservationStudioBorder};
      background: ${isLightTheme ? "rgba(255,255,255,0.68)" : "rgba(255,255,255,0.02)"};
      padding: 18px;
    }

    .beStudio__toastOverlay {
      position: fixed;
      top: 18px;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      pointer-events: none;
      z-index: 26000;
    }

    .beStudio__toast {
      max-width: 520px;
      width: min(92vw, 520px);
      border-radius: 18px;
      padding: 14px 22px;
      font-size: 0.9rem;
      font-weight: 700;
      text-align: center;
      box-shadow: 0 20px 40px rgba(2, 6, 23, 0.52);
      color: #eff6ff;
      border: 1px solid rgba(148, 163, 184, 0.32);
    }

    .beStudio__toast--success {
      background: linear-gradient(180deg, rgba(14, 165, 233, 0.94), rgba(59, 130, 246, 0.90));
    }

    .beStudio__toast--error {
      background: linear-gradient(180deg, rgba(248, 113, 113, 0.96), rgba(220, 38, 38, 0.92));
    }

    .beStudio__toast--info {
      background: linear-gradient(180deg, rgba(191, 149, 100, 0.96), rgba(162, 117, 67, 0.94));
      color: #fffaf3;
    }

    @media (max-width: 1180px) {
      .beStudio__card {
        grid-column: span 12;
      }
    }

    @media (max-width: 980px) {
      .beStudio__hero,
      .beStudio__modalGrid,
      .beStudio__fieldGrid,
      .beStudio__languageGrid,
      .beStudio__miniGrid,
      .beStudio__miniGrid--three,
      .beStudio__roomCopyPreviewMetaGrid {
        grid-template-columns: 1fr;
      }

      .beStudio__heroAside {
        min-width: 0;
        justify-items: stretch;
      }

      .beStudio__heroActions {
        justify-content: flex-start;
      }

      .beStudio__roomGrid {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 720px) {
      .beStudio__modeSelector {
        grid-template-columns: 1fr;
      }

      .beStudio__heroScreenSelector {
        grid-template-columns: 1fr;
      }

      .beStudio__heroImageHeader {
        display: grid;
      }

      .beStudio__installGuideModal {
        height: calc(100vh - 16px);
        max-height: calc(100vh - 16px);
      }

      .beStudio__installGuideBody {
        padding: 14px;
      }

      .beStudio__installGuideTabs {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .beStudio__installGuidePanel {
        padding: 14px;
      }

      .beStudio__installGuidePanelHeader {
        flex-direction: column;
      }

      .beStudio__installGuidePanelHeader .beStudio__button {
        width: 100%;
      }

      .beStudio__surface {
        padding-left: 14px;
        padding-right: 14px;
      }

      .beStudio__hero,
      .beStudio__card,
      .beStudio__modalBody,
      .beStudio__modalHeader,
      .beStudio__modalFooter {
        padding-left: 16px;
        padding-right: 16px;
      }

      .beStudio__modalOverlay--roomCopy,
      .beStudio__modalOverlay--guestEmail {
        align-items: stretch;
        padding-top: 8px;
        padding-bottom: 14px;
      }

      .beStudio__modal--guestEmail {
        align-self: stretch;
        margin-top: 0;
        margin-bottom: 0;
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
      }

      .beStudio__heroPreview,
      .beStudio__roomMedia,
      .beStudio__roomCopyPreviewMedia {
        height: 220px;
        min-height: 220px;
      }
    }

    @media (max-height: 820px) {
      .bookingEngineStudio {
        zoom: 0.9;
        min-height: calc(100% / 0.9);
        height: calc(100% / 0.9);
        max-height: calc(100% / 0.9);
      }
    }

    @media (max-height: 720px) {
      .bookingEngineStudio {
        zoom: 0.8;
        min-height: calc(100% / 0.8);
        height: calc(100% / 0.8);
        max-height: calc(100% / 0.8);
      }
    }

    @media (max-height: 620px) {
      .bookingEngineStudio {
        zoom: 0.7;
        min-height: calc(100% / 0.7);
        height: calc(100% / 0.7);
        max-height: calc(100% / 0.7);
      }
    }
  `;

  const reservationSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "46px",
      borderRadius: "14px",
      background: isLightTheme ? "rgba(255,255,255,0.95)" : "rgba(7, 11, 19, 0.78)",
      borderColor: state.isFocused ? reservationStudioAccent : reservationStudioBorder,
      boxShadow: state.isFocused
        ? `0 0 0 3px ${isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.14)"}`
        : "none",
      ":hover": {
        borderColor: reservationStudioAccent,
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "5px 10px",
    }),
    input: (base) => ({
      ...base,
      color: reservationStudioText,
    }),
    placeholder: (base) => ({
      ...base,
      color: reservationStudioMuted,
    }),
    singleValue: (base) => ({
      ...base,
      color: reservationStudioText,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "16px",
      overflow: "hidden",
      border: `1px solid ${reservationStudioBorder}`,
      background: isLightTheme ? "rgba(255,255,255,0.98)" : "rgba(11, 18, 31, 0.98)",
      boxShadow: isLightTheme
        ? "0 24px 40px rgba(84, 64, 38, 0.14)"
        : "0 24px 40px rgba(0, 0, 0, 0.34)",
    }),
    option: (base, state) => ({
      ...base,
      background:
        state.isSelected
          ? isLightTheme
            ? "rgba(33, 89, 104, 0.14)"
            : "rgba(48, 216, 255, 0.18)"
          : state.isFocused
            ? isLightTheme
              ? "rgba(33, 89, 104, 0.08)"
              : "rgba(255, 255, 255, 0.06)"
            : "transparent",
      color: reservationStudioText,
      cursor: "pointer",
      fontSize: "0.9rem",
    }),
    multiValue: (base) => ({
      ...base,
      background: isLightTheme ? "rgba(33, 89, 104, 0.10)" : "rgba(48, 216, 255, 0.10)",
      borderRadius: "999px",
      border: `1px solid ${isLightTheme ? "rgba(33, 89, 104, 0.16)" : "rgba(48, 216, 255, 0.18)"}`,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: reservationStudioText,
      fontWeight: 700,
      fontSize: "0.82rem",
      padding: "4px 8px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: reservationStudioText,
      borderRadius: "999px",
      ":hover": {
        background: isLightTheme ? "rgba(33, 89, 104, 0.14)" : "rgba(255,255,255,0.10)",
        color: reservationStudioText,
      },
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({
      ...base,
      color: reservationStudioMuted,
      ":hover": {
        color: reservationStudioAccent,
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: reservationStudioMuted,
      ":hover": {
        color: reservationStudioAccent,
      },
    }),
  };

  // The client menu is portaled to document.body, so it needs its own theme values.
  if (activeTab === "reservation") {
    const text = isLightTheme ? "#26354f" : "#edf3ff";
    const muted = isLightTheme ? "#52617a" : "#b6c7df";
    const surface = isLightTheme ? "#ffffff" : "#152344";
    const soft = isLightTheme ? "#eaf7fc" : "#21375b";
    const border = isLightTheme ? "#7c90a7" : "#688bad";
    Object.assign(reservationSelectStyles, {
      control: (base, state) => ({ ...base, minHeight: 38, borderRadius: 8, background: surface, borderColor: state.isFocused ? "#3db6e9" : border, boxShadow: state.isFocused ? "0 0 0 3px rgba(61,182,233,.22)" : "none", fontFamily: "Montserrat, sans-serif", fontSize: 13, ":hover": { borderColor: "#3db6e9" } }),
      input: (base) => ({ ...base, color: text }),
      placeholder: (base) => ({ ...base, color: muted }),
      singleValue: (base) => ({ ...base, color: text }),
      valueContainer: (base) => ({ ...base, padding: "2px 8px" }),
      menuPortal: (base) => ({ ...base, zIndex: 27000, fontFamily: "Montserrat, sans-serif", fontSize: 13 }),
      menu: (base) => ({ ...base, background: surface, border: `1px solid ${border}`, borderRadius: 10, overflow: "hidden", boxShadow: "0 10px 28px rgba(23,38,83,.18)" }),
      option: (base, state) => ({ ...base, color: state.isSelected ? "#ffffff" : text, background: state.isSelected ? "#223a7a" : state.isFocused ? soft : surface, fontSize: 13, cursor: "pointer", ":active": { background: soft, color: text } }),
      multiValue: (base) => ({ ...base, background: soft, border: `1px solid ${border}`, borderRadius: 6 }),
      multiValueLabel: (base) => ({ ...base, color: text, fontSize: 12, fontWeight: 600, padding: "3px 6px" }),
      multiValueRemove: (base) => ({ ...base, color: text, ":hover": { background: "#223a7a", color: "#ffffff" } }),
      dropdownIndicator: (base) => ({ ...base, color: muted, padding: 6, ":hover": { color: text } }),
      clearIndicator: (base) => ({ ...base, color: muted, padding: 6, ":hover": { color: text } }),
    });
  }

  const totalReservationFieldRules = Object.values(formData.required_customer_fields_reservation || {}).reduce(
    (sum, fields) => sum + (Array.isArray(fields) ? fields.length : 0),
    0
  );
  const totalCheckInFieldRules = Object.values(formData.required_customer_fields_check_in || {}).reduce(
    (sum, fields) => sum + (Array.isArray(fields) ? fields.length : 0),
    0
  );
  const totalCheckInRoleRules = Array.isArray(formData.required_customer_roles_check_in)
    ? formData.required_customer_roles_check_in.length
    : 1;

  const reservationConfirmationLanguages = normalizeReservationConfirmationLanguages(
    formData.reservation_confirmation_email_languages
  );

  const reservationConfirmationLanguageNames = reservationConfirmationLanguages.map((lang) => {
    const option = RESERVATION_CONFIRMATION_LANGUAGE_OPTIONS.find((item) => item.key === lang);
    return option?.label || String(lang || "").toUpperCase();
  });
  const reservationConfirmationLanguagePackLabel =
    reservationConfirmationLanguageNames.join(" + ") || "English";

  const reservationEmailActiveLanguage =
    RESERVATION_CONFIRMATION_LANGUAGE_OPTIONS.find(
      (item) => item.key === reservationEmailLangTab
    ) || RESERVATION_CONFIRMATION_LANGUAGE_OPTIONS[0];

  const reservationEmailLangEnabledForSending = reservationConfirmationLanguages.includes(
    reservationEmailLangTab
  );

  const reservationEmailStoredTemplate =
    ensureReservationConfirmationStoredTemplates(formData)?.[reservationEmailLangTab] || {
      subject: "",
      body: "",
    };

  const reservationEmailTemplateSource = getReservationConfirmationTemplateSource(
    formData,
    reservationEmailLangTab
  );

  const reservationEmailEffectiveTemplate = getEffectiveReservationConfirmationTemplate(
    formData,
    reservationEmailLangTab
  );

  const reservationEmailBookingEngineFallback = {
    subject: sanitizeReservationTemplateSubject(
      ensureGuestEmailTemplates(formData)?.[reservationEmailLangTab]?.subject
    ),
    body: sanitizeReservationTemplateBody(
      ensureGuestEmailTemplates(formData)?.[reservationEmailLangTab]?.body
    ),
  };

  const reservationEmailHasBookingEngineFallback = hasReservationTemplateContent(
    reservationEmailBookingEngineFallback
  );
  const reservationEmailPreviewHotelName =
    String(businessUnitName || "").trim() ||
    String(
      (availableBusinessUnits || []).find((unit) => String(unit?.id) === String(businessUnitId))?.name || ""
    ).trim() ||
    String(beProfile?.public_hotel_name || "").trim() ||
    (businessUnitId
      ? `Business unit #${businessUnitId}`
      : RESERVATION_CONFIRMATION_PREVIEW_VARS.HotelName);
  const reservationEmailPreviewRoomVars =
    buildReservationConfirmationPreviewRoomVars(
      roomCategories,
      baseCurrency,
      currencyCatalog
    );
  const reservationEmailPreviewVars = {
    ...RESERVATION_CONFIRMATION_PREVIEW_VARS,
    ...reservationEmailPreviewRoomVars,
    HotelName: reservationEmailPreviewHotelName,
  };

  const reservationEmailPreviewSubject = renderReservationTemplateValue(
    reservationEmailEffectiveTemplate?.subject,
    reservationEmailPreviewVars
  ).trim();

  const reservationEmailPreviewBody = renderReservationTemplateValue(
    reservationEmailEffectiveTemplate?.body,
    reservationEmailPreviewVars
  );

  const reservationEmailPreviewParagraphs = buildReservationPreviewParagraphs(
    reservationEmailPreviewBody
  );

  const reservationEmailLogoAsset = reservationEmailAssets?.reservation_email_logo || null;
  const reservationEmailHeroAsset = reservationEmailAssets?.reservation_email_hero || null;
  const reservationEmailAmbientAsset = reservationEmailAssets?.reservation_email_ambient || null;

  const reservationEmailModalUiTexts =
    reservationEmailLangTab === "es"
      ? {
          emptyPreview: "Empieza a redactar el cuerpo para ver la vista previa en vivo.",
        }
      : {
          emptyPreview: "Start writing the body to see the live preview.",
        };

  const externalReservationNotificationUiTexts =
    getExternalReservationNotificationUiTexts(externalReservationNotificationLangTab);
  const externalReservationNotificationActiveLanguage =
    RESERVATION_CONFIRMATION_LANGUAGE_OPTIONS.find(
      (item) => item.key === externalReservationNotificationLangTab
    ) || RESERVATION_CONFIRMATION_LANGUAGE_OPTIONS[0];
  const externalReservationNotificationStoredTemplate =
    ensureExternalReservationNotificationTemplates(formData)?.[
      externalReservationNotificationLangTab
    ]?.[externalReservationNotificationEventTab] || {
      subject: "",
      body: "",
    };
  const externalReservationNotificationEffectiveTemplate =
    getEffectiveExternalReservationNotificationTemplate(
      formData,
      externalReservationNotificationLangTab,
      externalReservationNotificationEventTab
    );
  const externalReservationNotificationPreviewVars = {
    ...EXTERNAL_RESERVATION_NOTIFICATION_PREVIEW_VARS,
    TotalPrice: replaceSettingsPreviewCurrencyAmounts(
      EXTERNAL_RESERVATION_NOTIFICATION_PREVIEW_VARS.TotalPrice,
      baseCurrency,
      currencyCatalog
    ),
    NightlyPrices: replaceSettingsPreviewCurrencyAmounts(
      EXTERNAL_RESERVATION_NOTIFICATION_PREVIEW_VARS.NightlyPrices,
      baseCurrency,
      currencyCatalog
    ),
    ChangesSummary: replaceSettingsPreviewCurrencyAmounts(
      EXTERNAL_RESERVATION_NOTIFICATION_PREVIEW_VARS.ChangesSummary,
      baseCurrency,
      currencyCatalog
    ),
    EventType:
      externalReservationNotificationUiTexts.eventNames[
        externalReservationNotificationEventTab
      ] || EXTERNAL_RESERVATION_NOTIFICATION_PREVIEW_VARS.EventType,
    HotelName: reservationEmailPreviewHotelName,
  };
  const externalReservationNotificationPreviewSubject = renderReservationTemplateValue(
    externalReservationNotificationEffectiveTemplate?.subject,
    externalReservationNotificationPreviewVars
  ).trim();
  const externalReservationNotificationPreviewIntro = renderReservationTemplateValue(
    externalReservationNotificationEffectiveTemplate?.body,
    externalReservationNotificationPreviewVars
  );
  const externalReservationNotificationHasCustomBody = Boolean(
    String(externalReservationNotificationStoredTemplate?.body || "").trim()
  );
  const externalReservationNotificationPreviewSystemBlock =
    externalReservationNotificationHasCustomBody
      ? ""
      : buildExternalReservationNotificationSystemBlock({
          lang: externalReservationNotificationLangTab,
          eventKey: externalReservationNotificationEventTab,
          vars: externalReservationNotificationPreviewVars,
        });
  const externalReservationNotificationPreviewBody = [
    externalReservationNotificationPreviewIntro,
    externalReservationNotificationPreviewSystemBlock,
  ]
    .filter(Boolean)
    .join("\n\n");
  const externalReservationNotificationPreviewParagraphs =
    buildReservationPreviewParagraphs(externalReservationNotificationPreviewBody);
  const externalReservationNotificationRecipients = normalizeExternalReservationNotificationEmails(
    formData.external_reservation_notification_emails
  );
  const externalReservationNotificationSendingLanguage =
    String(formData.external_reservation_notification_language || "en")
      .toLowerCase()
      .startsWith("es")
      ? "es"
      : "en";
  const effectiveReservationModificationPolicy =
    normalizeReservationManualModificationPolicy(
      formData.reservation_manual_modification_policy
    );
  const enabledReservationModificationSources =
    RESERVATION_MODIFICATION_SOURCE_META.filter(
      ({ key }) =>
        effectiveReservationModificationPolicy[key]?.allow_modifications
    ).length;
  const enabledReservationModificationActions =
    RESERVATION_MODIFICATION_SOURCE_META.reduce(
      (total, { key }) =>
        total +
        (effectiveReservationModificationPolicy[key]?.allow_modifications
          ? RESERVATION_MODIFICATION_ACTION_META.filter(
              ({ key: actionKey }) =>
                effectiveReservationModificationPolicy[key]?.[actionKey]
            ).length
          : 0),
      0
    );

  const reservationSummaryCards = [
    permissions["hotel_settings.view_customer_settings"]
      ? {
          label: "Guest rules",
          value: `${formData.min_age_main_guest ?? "--"} / ${formData.min_age_booker ?? "--"}`,
          foot: "Main guest / booker minimum age",
        }
      : null,
    permissions["hotel_settings.view_stay_settings"]
      ? {
          label: "Stay defaults",
          value: `${String(formData.checkin_time || "--").slice(0, 5)} -> ${String(
            formData.checkout_time || "--"
          ).slice(0, 5)}`,
          foot: "Default check-in and check-out",
        }
      : null,
    permissions["hotel_settings.view_reservation_flow"]
      ? {
          label: "Group flow",
          value: formData.group_reservations_default_status || "Requested",
          foot: `${selectedClientOptions.length} client trigger${selectedClientOptions.length === 1 ? "" : "s"} in requested mode`,
        }
      : null,
    permissions["hotel_settings.view_reservation_flow"]
      ? {
          label: "Manual changes",
          value: `${enabledReservationModificationSources} / ${RESERVATION_MODIFICATION_SOURCE_META.length} sources`,
          foot: `${enabledReservationModificationActions} granular permissions enabled`,
        }
      : null,
    permissions["hotel_settings.view_required_fields"] || permissions["hotel_settings.view_required_roles"]
      ? {
          label: "Pilcore compliance",
          value: `${(formData.required_customer_roles_reservation || []).length} roles`,
          foot: `${totalReservationFieldRules} required field rules configured`,
        }
      : null,
  ].filter(Boolean);

  const showReservationCustomerSettings = permissions["hotel_settings.view_customer_settings"];
  const showReservationStaySettings = permissions["hotel_settings.view_stay_settings"];
  const showReservationFlowSettings = permissions["hotel_settings.view_reservation_flow"];
  const showReservationRequiredFields = permissions["hotel_settings.view_required_fields"];
  const showReservationRequiredRoles = permissions["hotel_settings.view_required_roles"];
  const showReservationRoutingColumn = showReservationStaySettings || showReservationFlowSettings;
  const showReservationTopComposition = showReservationCustomerSettings || showReservationRoutingColumn;
  const reservationTopCompositionClassName = [
    "reservationStudio__topComposition",
    showReservationCustomerSettings && showReservationRoutingColumn
      ? "reservationStudio__topComposition--split"
      : "reservationStudio__topComposition--single",
  ].join(" ");
  const reservationFieldsCardClassName = [
    "reservationStudio__card",
    showReservationRequiredRoles ? "reservationStudio__card--fields" : "reservationStudio__card--full",
  ].join(" ");
  const reservationRolesCardClassName = [
    "reservationStudio__card",
    showReservationRequiredFields ? "reservationStudio__card--roles" : "reservationStudio__card--full",
  ].join(" ");

  const reservationSections = [
    showReservationTopComposition && { id: "reservation-basics", label: "Guests & stay", Icon: FiUsers },
    showReservationFlowSettings && { id: "reservation-changes", label: "Manual changes", Icon: FiSliders },
    (showReservationRequiredFields || showReservationRequiredRoles) && { id: "reservation-profiles", label: "Required profiles", Icon: FiShield },
    !demoModeActive && { id: "reservation-guest-email", label: "Guest email", Icon: FiMail },
    !demoModeActive && { id: "reservation-hotel-alerts", label: "Hotel alerts", Icon: FiBell },
    !demoModeActive && { id: "reservation-email-visuals", label: "Email visuals", Icon: FiImage },
  ].filter(Boolean);

  const frontDeskSections = [
    { id: "front-desk-roles", label: "Required guests", Icon: FiUsers },
    { id: "front-desk-fields", label: "Profile data", Icon: FiShield },
    { id: "front-desk-operations", label: "Payment & timezone", Icon: FiClock },
  ];
  const activeSettingsSections = activeTab === "front_desk" ? frontDeskSections : reservationSections;

  const scrollToReservationSection = (event, sectionId) => {
    event.preventDefault();
    const surface = reservationSurfaceRef.current;
    const section = surface?.querySelector(`#${sectionId}`);
    if (!section) return;
    setReservationActiveSection(sectionId);
    const behavior = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? "auto" : "smooth";
    if (activeTab === "front_desk") {
      // Keep section navigation inside the card; do not move the surrounding PMS layout.
      surface.scrollTo({
        top: Math.max(0, surface.scrollTop + section.getBoundingClientRect().top - surface.getBoundingClientRect().top - 12),
        behavior,
      });
      return;
    }
    section.scrollIntoView({
      block: "start",
      behavior,
    });
  };

  const syncReservationSection = () => {
    const surface = reservationSurfaceRef.current;
    if (!surface) return;
    const surfaceTop = surface.getBoundingClientRect().top;
    let current = activeSettingsSections[0]?.id;
    for (const section of activeSettingsSections) {
      const element = surface.querySelector(`#${section.id}`);
      if (element && element.getBoundingClientRect().top <= surfaceTop + 72) current = section.id;
    }
    if (activeTab === "front_desk" && surface.scrollTop > 0 && surface.scrollTop + surface.clientHeight >= surface.scrollHeight - 2) {
      current = frontDeskSections[frontDeskSections.length - 1].id;
    }
    if (current) setReservationActiveSection(current);
  };

  const updateReservationModificationPolicy = (
    sourceKey,
    settingKey,
    nextValue
  ) => {
    if (demoModeActive || isDemoMode()) return;
    setFormData((prev) => {
      const policy = normalizeReservationManualModificationPolicy(
        prev.reservation_manual_modification_policy
      );
      return {
        ...prev,
        reservation_manual_modification_policy: {
          ...policy,
          [sourceKey]: {
            ...policy[sourceKey],
            [settingKey]: Boolean(nextValue),
          },
        },
      };
    });
  };

  const toggleRequiredReservationRole = (role) => {
    if (demoModeActive || isDemoMode()) return;
    if ((formData.required_customer_fields_reservation?.[role] || []).length > 0) {
      return;
    }

    const updated = formData.required_customer_roles_reservation || [];
    let newValue;

    if (updated.includes(role)) {
      newValue = updated.filter((r) => r !== role);
      const stillHasBooker = newValue.includes("booker");
      const stillHasMainGuest = newValue.includes("main_guest");

      if (!stillHasBooker && !stillHasMainGuest) {
        const force = role === "booker" ? "main_guest" : "booker";
        newValue.push(force);
      }
    } else {
      newValue = [...new Set([...updated, role])];
    }

    setFormData((prev) => ({
      ...prev,
      required_customer_roles_reservation: newValue,
    }));
  };

  const toggleRequiredCheckInRole = (role) => {
    if (demoModeActive || isDemoMode()) return;
    if (role === "main_guest") return;
    if ((formData.required_customer_fields_check_in?.[role] || []).length > 0) {
      return;
    }

    const current = [
      "main_guest",
      ...(Array.isArray(formData.required_customer_roles_check_in)
        ? formData.required_customer_roles_check_in.filter((item) => item !== "main_guest")
        : []),
    ];
    const newValue = current.includes(role)
      ? current.filter((item) => item !== role)
      : [...current, role];

    setFormData((prev) => ({
      ...prev,
      required_customer_roles_check_in: ["main_guest", ...newValue.filter((item) => item !== "main_guest")],
    }));
  };

  const getCustomerFieldLabel = (fieldValue) =>
    customerFieldOptions.find((option) => option.value === fieldValue)?.label || fieldValue;

  const openRequiredFieldPicker = ({ fieldKey, role, contextTitle, contextBadge }) => {
    if (demoModeActive && fieldKey !== (activeTab === "front_desk" ? "required_customer_fields_check_in" : "required_customer_fields_reservation")) return;
    setRequiredFieldPicker({
      fieldKey,
      roleKey: role.key,
      roleLabel: role.label,
      roleDescription: role.description,
      contextTitle,
      contextBadge,
    });
  };

  const updateRequiredFieldSelection = (fieldKey, roleKey, nextFields) => {
    if (demoModeActive || isDemoMode()) return;
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: {
        ...(prev?.[fieldKey] || {}),
        [roleKey]: [...new Set(nextFields)],
      },
      ...(fieldKey === "required_customer_fields_reservation" && nextFields.length > 0
        ? {
            required_customer_roles_reservation: ensureReservationRolesCoverFieldRules(
              prev.required_customer_roles_reservation,
              {
                ...(prev.required_customer_fields_reservation || {}),
                [roleKey]: [...new Set(nextFields)],
              }
            ),
          }
        : {}),
      ...(fieldKey === "required_customer_fields_check_in" && nextFields.length > 0
        ? {
            required_customer_roles_check_in: ensureCheckInRolesCoverFieldRules(
              prev.required_customer_roles_check_in,
              {
                ...(prev.required_customer_fields_check_in || {}),
                [roleKey]: [...new Set(nextFields)],
              }
            ),
          }
        : {}),
    }));
  };

  const toggleRequiredFieldInPicker = (fieldValue) => {
    if (demoModeActive || isDemoMode()) return;
    if (!requiredFieldPicker) return;
    const selectedFields =
      formData?.[requiredFieldPicker.fieldKey]?.[requiredFieldPicker.roleKey] || [];
    const nextFields = selectedFields.includes(fieldValue)
      ? selectedFields.filter((item) => item !== fieldValue)
      : [...selectedFields, fieldValue];

    updateRequiredFieldSelection(
      requiredFieldPicker.fieldKey,
      requiredFieldPicker.roleKey,
      nextFields
    );
  };

  const selectAllRequiredFieldsInPicker = () => {
    if (demoModeActive || isDemoMode()) return;
    if (!requiredFieldPicker) return;
    updateRequiredFieldSelection(
      requiredFieldPicker.fieldKey,
      requiredFieldPicker.roleKey,
      customerFieldOptions.map((option) => option.value)
    );
  };

  const clearRequiredFieldsInPicker = () => {
    if (demoModeActive || isDemoMode()) return;
    if (!requiredFieldPicker) return;
    updateRequiredFieldSelection(requiredFieldPicker.fieldKey, requiredFieldPicker.roleKey, []);
  };

  const renderRequiredFieldPickerTrigger = ({
    fieldKey,
    role,
    selectedFields,
    contextTitle,
    contextBadge,
  }) => {
    const previewFields = selectedFields.slice(0, 4);
    const hiddenCount = Math.max(selectedFields.length - previewFields.length, 0);

    return (
      <div className="reservationStudio__fieldPickerSummary">
        <div className="reservationStudio__fieldPickerPreview">
          {selectedFields.length ? (
            <>
              {previewFields.map((fieldValue) => (
                <span key={fieldValue} className="reservationStudio__fieldPickerChip">
                  {getCustomerFieldLabel(fieldValue)}
                </span>
              ))}
              {hiddenCount > 0 && (
                <span className="reservationStudio__fieldPickerChip reservationStudio__fieldPickerChip--muted">
                  +{hiddenCount} more
                </span>
              )}
            </>
          ) : (
            <span className="reservationStudio__fieldPickerEmpty">No fields required yet</span>
          )}
        </div>
        <button
          type="button"
          className="reservationStudio__fieldPickerButton"
          aria-label={`${demoModeActive ? "View" : "Configure"} required fields for ${role.label}`}
          onClick={() =>
            openRequiredFieldPicker({
              fieldKey,
              role,
              contextTitle,
              contextBadge,
            })
          }
        >
          Configure fields
        </button>
      </div>
    );
  };

  const requiredFieldPickerSelectedFields = requiredFieldPicker
    ? formData?.[requiredFieldPicker.fieldKey]?.[requiredFieldPicker.roleKey] || []
    : [];

  const isDemoTabLoading = demoLoading || demoLoadedTab !== activeTab;
  if (demoModeActive && !isDemoStandaloneTab && (isDemoTabLoading || demoError)) {
    return (
      <div className="settings-theme-shell settings-demo-loadState" role={isDemoTabLoading ? "status" : "alert"}>
        <h1>{demoSettingsTitle}</h1>
        <p>{isDemoTabLoading ? demoCopy.loading : demoError}</p>
        {!isDemoTabLoading && <button type="button" onClick={() => setDemoReload((value) => value + 1)}>{demoCopy.retry}</button>}
      </div>
    );
  }

  return (
    
    <div
      className={`${isLightTheme ? "text-dark" : "text-white"} settings-theme-shell${isPilcoreSettingsTab ? " reservationSettings" : ""}${activeTab === "front_desk" ? " frontDeskSettings" : ""}${activeTab === "booking_engine" ? " bookingEngineSettings" : ""}${activeTab === "appearance" ? " settingsUserExperience" : ""}${activeTab === "users" ? " usersRolesSettings" : ""}`}
      ref={activeTab === "booking_engine" ? bookingSettingsRootRef : activeTab === "users" ? usersRolesRootRef : null}
      data-theme={isLightTheme ? "light" : "dark"}
      data-demo-read-only={demoModeActive || undefined}
      style={{
        backgroundColor: isLightTheme ? "transparent" : "#000",
        ...(activeTab === "extras"
          ? { height: "calc(100dvh - 28px)", overflow: "hidden" }
          : activeTab === "charges"
          ? { height: "calc(100dvh - 28px)", overflow: "hidden" }
          : activeTab === "currencies"
          ? { height: "calc(100dvh - 28px)", overflow: "hidden" }
          : activeTab === "reservation"
          ? { height: "calc(100dvh - 28px)", overflow: "hidden" }
          : activeTab === "front_desk"
          ? { height: "calc(100dvh - 28px)", overflow: "hidden" }
          : activeTab === "booking_engine"
          ? { height: "calc(100dvh - 28px)", overflow: "hidden" }
          : activeTab === "users"
          ? { height: "calc(100dvh - 28px)", overflow: "hidden" }
          : activeTab === "crm"
          ? { height: "calc(100dvh - 28px)", overflow: "hidden" }
          : activeTab === "appearance"
          ? { height: "calc(100dvh - 28px)", overflow: "hidden" }
          : { minHeight: "calc(100vh - 88px)" }),
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        padding:
          activeTab === "appearance"
            ? "12px 24px"
            : activeTab === "extras"
            ? "0"
            : activeTab === "charges"
            ? "0"
            : activeTab === "currencies"
            ? "12px 24px"
            : activeTab === "reservation"
            ? reservationTabShellPadding
            : activeTab === "front_desk"
            ? reservationTabShellPadding
            : activeTab === "booking_engine"
            ? bookingEngineTabShellPadding
            : activeTab === "crm"
            ? "12px 24px"
            : "24px",
      }}
    >
    {!demoModeActive && !forcedTab && (
    <div
      className="tabs d-flex mb-3 settings-top-tabs"
      style={isLightTheme ? {
        gap: "8px",
        width: "fit-content",
        padding: "7px",
        borderRadius: "999px",
        border: "1px solid rgba(24, 54, 69, 0.08)",
        background: "rgba(248, 244, 236, 0.84)",
        boxShadow: "0 16px 36px rgba(86, 65, 39, 0.08)",
      } : undefined}
    >
      {showReservationTab && (
      <button
        className={getTopTabClassName(activeTab === "reservation", "me-3")}
        aria-current={activeTab === "reservation" ? "page" : undefined}
        onClick={() => setActiveTab("reservation")}
        style={getTopTabStyle(activeTab === "reservation")}
      >
        Reservation Settings
      </button>
      )}
      {canViewFrontDeskSettingsTab && (
      <button
        className={getTopTabClassName(activeTab === "front_desk", "me-3")}
        aria-current={activeTab === "front_desk" ? "page" : undefined}
        onClick={() => setActiveTab("front_desk")}
        style={getTopTabStyle(activeTab === "front_desk")}
      >
        Front Desk Settings
      </button>
      )}
      {canViewRevenueTab && (
        <button
          className={getTopTabClassName(activeTab === "revenue", "me-3")}
          onClick={() => setActiveTab("revenue")}
          style={getTopTabStyle(activeTab === "revenue")}
        >
          Revenue Management Settings
        </button>
      )}

      {canViewCurrenciesTab && (
        <button
          className={getTopTabClassName(activeTab === "currencies", "me-3")}
          onClick={() => setActiveTab("currencies")}
          style={getTopTabStyle(activeTab === "currencies")}
        >
          {currenciesTabLabel}
        </button>
      )}

      {canViewExtrasTab && (
        <button
          className={getTopTabClassName(activeTab === "extras", "me-3")}
          onClick={() => setActiveTab("extras")}
          style={getTopTabStyle(activeTab === "extras")}
        >
          {extrasTabLabel}
        </button>
      )}

      {canViewChargesTab && (
        <button
          className={getTopTabClassName(activeTab === "charges", "me-3")}
          onClick={() => setActiveTab("charges")}
          style={getTopTabStyle(activeTab === "charges")}
        >
          Reservation Charges
        </button>
      )}

      {canViewUsersTab && (
        <button
          className={getTopTabClassName(activeTab === "users", "me-3")}
          onClick={() => setActiveTab("users")}
          style={getTopTabStyle(activeTab === "users")}
        >
          Users & Roles
        </button>
      )}

      {canViewCrmTab && (
        <button
          className={getTopTabClassName(activeTab === "crm", "me-3")}
          onClick={() => setActiveTab("crm")}
          style={getTopTabStyle(activeTab === "crm")}
        >
          CRM
        </button>
      )}

      {canViewAppearanceTab && (
        <button
          className={getTopTabClassName(activeTab === "appearance", "me-3")}
          aria-current={activeTab === "appearance" ? "page" : undefined}
          onClick={() => setActiveTab("appearance")}
          style={getTopTabStyle(activeTab === "appearance")}
        >
          {userExperienceLabel}
        </button>
      )}

      {canViewBookingEngineTab && (
        <button
          className={getTopTabClassName(activeTab === "booking_engine", "me-3")}
          aria-current={activeTab === "booking_engine" ? "page" : undefined}
          onClick={() => setActiveTab("booking_engine")}
          style={getTopTabStyle(activeTab === "booking_engine")}
        >
          {bookingEngineTabLabel}
        </button>
      )}
    </div>
    )}

    
      <div className="settings-scroll-wrapper" style={settingsScrollWrapperStyle}>
      {activeTab === "front_desk" && (
        <form
          onSubmit={handleSubmit}
          className="reservationStudio"
          style={{
            margin: "0 auto",
            minHeight: 0,
            boxSizing: "border-box",
          }}
        >
          <style>{reservationStudioCss}</style>
            <section className="reservationStudio__hero">
              <div>
                <div className="reservationStudio__eyebrow">Settings</div>
                <h1 className="reservationStudio__title">Front Desk Settings</h1>
                <p className="reservationStudio__subtitle">
                  {demoModeActive ? demoCopy.readOnly : "Choose who must be registered, which data is required and when guests can check in."}
                </p>
              </div>

              <div className="reservationStudio__heroAside">
                <div className="reservationStudio__heroControls">
                  <button type="submit" className="reservationStudio__heroSaveButton" disabled={demoModeActive || reservationSettingsSaving} title={demoModeActive ? demoCopy.readOnly : undefined}>
                    <FiSave size={16} aria-hidden="true" />
                    {reservationSettingsSaving ? "Saving…" : "Save changes"}
                  </button>
                  {!demoModeActive && <span className="reservationSettings__saveState">Save to apply your settings</span>}
                </div>
              </div>
            </section>

            <nav className="reservationSettings__nav" aria-label="Front Desk settings sections">
              {frontDeskSections.map(({ id, label, Icon }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="reservationSettings__navLink"
                  aria-current={(frontDeskSections.some((section) => section.id === reservationActiveSection) ? reservationActiveSection : frontDeskSections[0].id) === id ? "location" : undefined}
                  onClick={(event) => scrollToReservationSection(event, id)}
                >
                  <Icon size={16} aria-hidden="true" />
                  {label}
                </a>
              ))}
            </nav>

            {reservationSettingsFeedback && (
              <div
                className={`reservationSettings__feedback${reservationSettingsFeedback.tone === "error" ? " reservationSettings__feedback--error" : ""}`}
                role={reservationSettingsFeedback.tone === "error" ? "alert" : "status"}
              >
                {reservationSettingsFeedback.tone === "error" ? <FiAlertCircle size={17} aria-hidden="true" /> : <FiCheckCircle size={17} aria-hidden="true" />}
                <span>{reservationSettingsFeedback.message}</span>
              </div>
            )}

          <div className="reservationStudio__surface" ref={reservationSurfaceRef} onScroll={syncReservationSection}>
            <div className="reservationStudio__summaryGrid" aria-label="Current check-in configuration">
              <div className="reservationStudio__summaryCard">
                <div className="reservationStudio__summaryLabel">Guest requirements</div>
                <div className="reservationStudio__summaryValue">{totalCheckInRoleRules} required role{totalCheckInRoleRules === 1 ? "" : "s"}</div>
                <div className="reservationStudio__summaryFoot">
                  {totalCheckInFieldRules} required field rule{totalCheckInFieldRules === 1 ? "" : "s"} checked before check-in.
                </div>
              </div>
              <div className="reservationStudio__summaryCard">
                <div className="reservationStudio__summaryLabel">Payment before check-in</div>
                <div className="reservationStudio__summaryValue">{formData.require_full_payment_for_check_in ? "Full payment" : "Balance allowed"}</div>
                <div className="reservationStudio__summaryFoot">
                  {formData.require_full_payment_for_check_in ? "An outstanding balance blocks check-in." : "An outstanding balance does not block check-in."}
                </div>
              </div>
              <div className="reservationStudio__summaryCard">
                <div className="reservationStudio__summaryLabel">Hotel business timezone</div>
                <div className="reservationStudio__summaryValue">{formData.timezone || "Europe/Madrid"}</div>
                <div className="reservationStudio__summaryFoot">
                  Used for the business date and daily revenue.
                </div>
              </div>
            </div>

            <div className="reservationStudio__grid">
              <section id="front-desk-roles" className="reservationStudio__card reservationStudio__card--roles">
                <div className="reservationStudio__cardEyebrow">1 · Choose the required guests</div>
                <div className="reservationStudio__cardTitleRow">
                  <div>
                    <h3 className="reservationStudio__cardTitle">Required Customer Roles</h3>
                    <p className="reservationStudio__cardCopy">
                      Highlighted roles must have a profile before check-in. Main guest is always required; roles with required fields are also mandatory.
                    </p>
                  </div>
                  <span className="reservationStudio__chip">{totalCheckInRoleRules} of 4 required</span>
                </div>

                <div className="reservationStudio__roleGrid reservationStudio__roleGrid--reservationRoles">
                  {FRONT_DESK_CHECK_IN_ROLE_DEFINITIONS.map((role) => {
                    const isLocked = role.key === "main_guest";
                    const hasRequiredFields =
                      (formData.required_customer_fields_check_in?.[role.key] || []).length > 0;
                    const isActive =
                      isLocked ||
                      hasRequiredFields ||
                      formData.required_customer_roles_check_in?.includes(role.key) ||
                      false;
                    return (
                      <button
                        key={role.key}
                        type="button"
                        className={`reservationStudio__roleButton ${
                          isActive ? "reservationStudio__roleButton--active" : ""
                        }`}
                        aria-pressed={isActive}
                        onClick={() => toggleRequiredCheckInRole(role.key)}
                        disabled={demoModeActive || isLocked || hasRequiredFields}
                        title={demoModeActive ? demoCopy.readOnly : undefined}
                      >
                        <div className="reservationStudio__roleTitle">{role.label}</div>
                        <div className="reservationStudio__roleBadge">
                          {isLocked
                            ? "Always required"
                            : hasRequiredFields
                              ? "Required by field rules"
                              : isActive
                              ? "Mandatory for check-in"
                              : "Optional role"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section id="front-desk-fields" className="reservationStudio__card reservationStudio__card--fields">
                <div className="reservationStudio__cardEyebrow">2 · Choose the data to collect</div>
                <div className="reservationStudio__cardTitleRow">
                  <div>
                    <h3 className="reservationStudio__cardTitle">Required Customer Fields per Role</h3>
                    <p className="reservationStudio__cardCopy">
                      Missing required data blocks check-in and directs reception to complete the reservation. Adding fields also makes that role mandatory.
                    </p>
                  </div>
                  <span className="reservationStudio__chip">Check-in only</span>
                </div>

                <div className="reservationStudio__roleGrid reservationStudio__roleGrid--reservationFields">
                  {FRONT_DESK_CHECK_IN_ROLE_DEFINITIONS.map((role) => {
                    const selectedFields = formData.required_customer_fields_check_in?.[role.key] || [];
                    return (
                      <div key={role.key} className="reservationStudio__roleBlock">
                        <div className="reservationStudio__roleBlockHeader">
                          <div>
                            <div className="reservationStudio__roleTitle">{role.label}</div>
                            <div className="reservationStudio__roleCopy">{role.description}</div>
                          </div>
                          <span className="reservationStudio__roleBadge">
                            {selectedFields.length} field{selectedFields.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        {renderRequiredFieldPickerTrigger({
                          fieldKey: "required_customer_fields_check_in",
                          role,
                          selectedFields,
                          contextTitle: "Check-in required fields",
                          contextBadge: "Front Desk check-in only",
                        })}
                      </div>
                    );
                  })}
                </div>
              </section>

              <div id="front-desk-operations" className="frontDeskSettings__operationsGrid">
              <section className="reservationStudio__card reservationStudio__card--full">
                <div className="reservationStudio__cardEyebrow">3 · Set the hotel clock</div>
                <div className="reservationStudio__cardTitleRow">
                  <div>
                    <h3 className="reservationStudio__cardTitle">Hotel Business Timezone</h3>
                    <p className="reservationStudio__cardCopy">
                      Controls the hotel business date used by Front Desk, Account and daily revenue posting.
                    </p>
                  </div>
                </div>
                <div className="reservationStudio__fieldGrid reservationStudio__fieldGrid--single">
                  <div className="reservationStudio__field--full">
                    <label className="reservationStudio__label" htmlFor="hotel-business-timezone">
                      Hotel timezone
                    </label>
                    <select
                      id="hotel-business-timezone"
                      disabled={demoModeActive}
                      title={demoModeActive ? demoCopy.readOnly : undefined}
                      name="timezone"
                      className="reservationStudio__input"
                      aria-describedby="hotel-business-timezone-help"
                      value={formData.timezone || "Europe/Madrid"}
                      onChange={handleChange}
                    >
                      {HOTEL_TIMEZONE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {demoModeActive ? `${option.label} · ${option.value}` : option.label}
                        </option>
                      ))}
                      {formData.timezone && !HOTEL_TIMEZONE_OPTIONS.some((option) => option.value === formData.timezone) && (
                        <option value={formData.timezone}>{formData.timezone}</option>
                      )}
                    </select>
                    <span id="hotel-business-timezone-help" className="reservationStudio__hint">
                      Uses the hotel's timezone, not the time on the receptionist's device.
                    </span>
                  </div>
                </div>
              </section>

              <section className="reservationStudio__card reservationStudio__card--full">
                <div className="reservationStudio__cardEyebrow">4 · Set the payment requirement</div>
                <div className="reservationStudio__cardTitleRow">
                  <div>
                    <h3 className="reservationStudio__cardTitle">Require Full Payment for Check-In</h3>
                    <p className="reservationStudio__cardCopy">
                      Decide whether reception can check in a reservation or group with an outstanding balance.
                    </p>
                  </div>
                </div>

                <div className="reservationStudio__choiceGrid">
                  <button
                    type="button"
                    aria-pressed={Boolean(formData.require_full_payment_for_check_in)}
                    disabled={demoModeActive}
                    title={demoModeActive ? demoCopy.readOnly : undefined}
                    className={`reservationStudio__choiceButton ${
                      formData.require_full_payment_for_check_in
                        ? "reservationStudio__choiceButton--active"
                        : ""
                    }`}
                    onClick={() => {
                      if (demoModeActive || isDemoMode()) return;
                      setFormData((prev) => ({
                        ...prev,
                        require_full_payment_for_check_in:
                          !prev.require_full_payment_for_check_in,
                      }));
                    }}
                  >
                    <div className="reservationStudio__choiceTitle">
                      <span className="frontDeskSettings__paymentIndicator" aria-hidden="true">{formData.require_full_payment_for_check_in ? "✓" : ""}</span>
                      Block check-in if there is pending balance
                    </div>
                    <div className="reservationStudio__choiceCopy">
                      {formData.require_full_payment_for_check_in ? "Enabled · The stay must be fully paid before check-in." : "Disabled · Reception can check in guests with a pending balance."}
                    </div>
                  </button>
                </div>
              </section>
              </div>
            </div>
          </div>
        </form>
      )}
      {activeTab === "reservation" && (
        <form
          onSubmit={handleSubmit}
          className="reservationStudio"
          style={{
            margin: "0 auto",
            minHeight: 0,
            boxSizing: "border-box",
          }}
        >
          <style>{reservationStudioCss}</style>
          <section className="reservationStudio__hero">
            <div>
              <div className="reservationStudio__eyebrow">Settings</div>
              <h1 className="reservationStudio__title">Reservation Settings</h1>
              <p className="reservationStudio__subtitle">
                {demoModeActive ? demoCopy.readOnly : "Guest requirements, stay defaults, reservation permissions and emails."}
              </p>
            </div>

            <div className="reservationStudio__heroAside">
              <div className="reservationStudio__heroControls">
                <button type="submit" className="reservationStudio__heroSaveButton" disabled={demoModeActive || reservationSettingsSaving} title={demoModeActive ? demoCopy.readOnly : undefined}>
                  <FiSave size={16} aria-hidden="true" />
                  {reservationSettingsSaving ? "Saving…" : "Save changes"}
                </button>
                {!demoModeActive && <span className="reservationSettings__saveState">Save to apply your settings</span>}
              </div>
            </div>
          </section>

          <nav className="reservationSettings__nav" aria-label="Reservation settings sections">
            {reservationSections.map(({ id, label, Icon }) => (
              <a
                key={id}
                href={`#${id}`}
                className="reservationSettings__navLink"
                aria-current={(reservationSections.some((section) => section.id === reservationActiveSection) ? reservationActiveSection : reservationSections[0]?.id) === id ? "location" : undefined}
                onClick={(event) => scrollToReservationSection(event, id)}
              >
                <Icon size={16} aria-hidden="true" />
                <span>{label}</span>
              </a>
            ))}
          </nav>

          {reservationSettingsFeedback && (
            <div
              className={`reservationSettings__feedback${reservationSettingsFeedback.tone === "error" ? " reservationSettings__feedback--error" : ""}`}
              role={reservationSettingsFeedback.tone === "error" ? "alert" : "status"}
            >
              {reservationSettingsFeedback.tone === "error" ? <FiAlertCircle size={17} aria-hidden="true" /> : <FiCheckCircle size={17} aria-hidden="true" />}
              <span>{reservationSettingsFeedback.message}</span>
            </div>
          )}

          <div className="reservationStudio__surface" ref={reservationSurfaceRef} onScroll={syncReservationSection}>

          {reservationSummaryCards.length > 0 && (
            <section className="reservationStudio__summaryGrid">
              {reservationSummaryCards.map((card) => (
                <div key={card.label} className="reservationStudio__summaryCard">
                  <div className="reservationStudio__summaryLabel">{card.label}</div>
                  <div className="reservationStudio__summaryValue">{card.value}</div>
                  <div className="reservationStudio__summaryFoot">{card.foot}</div>
                </div>
              ))}
            </section>
          )}

          <div className="reservationStudio__grid">
            {showReservationTopComposition && (
              <div id="reservation-basics" className={reservationTopCompositionClassName}>
                {showReservationCustomerSettings && (
                  <section className="reservationStudio__card">
                    <div className="reservationStudio__cardEyebrow">Guest policy</div>
                    <div className="reservationStudio__cardTitleRow">
                      <div>
                        <h3 className="reservationStudio__cardTitle">Customer Settings</h3>
                        <p className="reservationStudio__cardCopy">
                          Set age limits and contact requirements for new reservations.
                        </p>
                      </div>
                      <span className="reservationStudio__chip">Profile rules</span>
                    </div>

                    <div className="reservationStudio__fieldGrid">
                      <div className="reservationStudio__field">
                        <label className="reservationStudio__label" htmlFor="reservation-min-age-main-guest">
                          Minimum age for main guest
                        </label>
                        <input
                          id="reservation-min-age-main-guest"
                          disabled={demoModeActive}
                          placeholder={demoModeActive ? demoCopy.noValue : undefined}
                          type="number"
                          name="min_age_main_guest"
                          value={formData.min_age_main_guest ?? ""}
                          onChange={handleChange}
                          className="reservationStudio__input"
                        />
                        <span className="reservationStudio__hint">
                          Applied to the primary guest profile attached to the stay.
                        </span>
                      </div>

                      <div className="reservationStudio__field">
                        <label className="reservationStudio__label" htmlFor="reservation-min-age-booker">
                          Minimum age for booker
                        </label>
                        <input
                          id="reservation-min-age-booker"
                          disabled={demoModeActive}
                          placeholder={demoModeActive ? demoCopy.noValue : undefined}
                          type="number"
                          name="min_age_booker"
                          value={formData.min_age_booker ?? ""}
                          onChange={handleChange}
                          className="reservationStudio__input"
                        />
                        <span className="reservationStudio__hint">
                          Useful when the reservation holder is different from the staying guest.
                        </span>
                      </div>

                      <div className="reservationStudio__field">
                        <label className="reservationStudio__label" htmlFor="reservation-max-age-baby">
                          Maximum age for baby
                        </label>
                        <input
                          id="reservation-max-age-baby"
                          disabled={demoModeActive}
                          placeholder={demoModeActive ? demoCopy.noValue : undefined}
                          type="number"
                          name="max_age_baby"
                          value={formData.max_age_baby ?? ""}
                          onChange={handleChange}
                          className="reservationStudio__input"
                        />
                        <span className="reservationStudio__hint">
                          Sets the upper age threshold for baby pricing and segmentation.
                        </span>
                      </div>

                      <div className="reservationStudio__field">
                        <label className="reservationStudio__label" htmlFor="reservation-max-age-child">
                          Maximum age for child
                        </label>
                        <input
                          id="reservation-max-age-child"
                          disabled={demoModeActive}
                          placeholder={demoModeActive ? demoCopy.noValue : undefined}
                          type="number"
                          name="max_age_child"
                          value={formData.max_age_child ?? ""}
                          onChange={handleChange}
                          className="reservationStudio__input"
                        />
                        <span className="reservationStudio__hint">
                          Guests above this threshold will no longer be treated as children.
                        </span>
                      </div>

                      <div className="reservationStudio__field--full">
                        <label className="reservationStudio__label" htmlFor="reservation-company-booker-contact"
                          style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <input
                            id="reservation-company-booker-contact"
                            disabled={demoModeActive}
                            type="checkbox"
                            name="require_company_booker_contact"
                            checked={formData.require_company_booker_contact ?? true}
                            onChange={(event) => {
                              if (demoModeActive || isDemoMode()) return;
                              setFormData((previous) => ({ ...previous, require_company_booker_contact: event.target.checked }));
                            }}
                          />
                          Require a contact person for company bookers
                        </label>
                        <span className="reservationStudio__hint">
                          Ask for the full name of the person who called or contacted you to book for the company.
                          This contact is saved on the reservation group and is not assigned as a guest.
                          Turn this off to make the contact name optional.
                        </span>
                      </div>

                      <div className="reservationStudio__field--full">
                        <label className="reservationStudio__label" htmlFor="reservation-vip-categories">
                          VIP Categories
                        </label>
                        <textarea
                          id="reservation-vip-categories"
                          disabled={demoModeActive}
                          placeholder={demoModeActive ? demoCopy.noValue : undefined}
                          name="vip_categories"
                          value={formData.vip_categories ?? ""}
                          onChange={handleChange}
                          className="reservationStudio__textarea"
                        />
                        <span className="reservationStudio__hint">
                          Write comma-separated labels.
                        </span>
                      </div>
                    </div>
                  </section>
                )}

                {showReservationRoutingColumn && (
                  <div className="reservationStudio__topStack">
                    {showReservationStaySettings && (
                      <section className="reservationStudio__card">
                        <div className="reservationStudio__cardEyebrow">Stay defaults</div>
                        <div className="reservationStudio__cardTitleRow">
                          <div>
                            <h3 className="reservationStudio__cardTitle">Stay Settings</h3>
                            <p className="reservationStudio__cardCopy">
                              Default arrival and departure times for reservations.
                            </p>
                          </div>
                          <span className="reservationStudio__chip">Arrival window</span>
                        </div>

                        <div className="reservationStudio__fieldGrid">
                          <div className="reservationStudio__field">
                            <label className="reservationStudio__label" htmlFor="reservation-checkin-time">
                              Default check-in time
                            </label>
                            <input
                              id="reservation-checkin-time"
                              disabled={demoModeActive}
                              placeholder={demoModeActive ? demoCopy.noValue : undefined}
                              type={demoModeActive ? "text" : "time"}
                              name="checkin_time"
                              value={formData.checkin_time ?? ""}
                              onChange={handleChange}
                              className="reservationStudio__input"
                            />
                            <span className="reservationStudio__hint">
                              Used as the operational arrival baseline for reservation planning.
                            </span>
                          </div>

                          <div className="reservationStudio__field">
                            <label className="reservationStudio__label" htmlFor="reservation-checkout-time">
                              Default check-out time
                            </label>
                            <input
                              id="reservation-checkout-time"
                              disabled={demoModeActive}
                              placeholder={demoModeActive ? demoCopy.noValue : undefined}
                              type={demoModeActive ? "text" : "time"}
                              name="checkout_time"
                              value={formData.checkout_time ?? ""}
                              onChange={handleChange}
                              className="reservationStudio__input"
                            />
                            <span className="reservationStudio__hint">
                              Keeps departure timing coherent across reservations and reports.
                            </span>
                          </div>
                        </div>
                      </section>
                    )}

                    {showReservationFlowSettings && (
                      <section className="reservationStudio__card">
                        <div className="reservationStudio__cardEyebrow">Operational routing</div>
                        <div className="reservationStudio__cardTitleRow">
                          <div>
                            <h3 className="reservationStudio__cardTitle">Reservation Flow Settings</h3>
                            <p className="reservationStudio__cardCopy">
                              Control which clients must pass through Requested and decide the default landing
                              status for group reservations.
                            </p>
                          </div>
                          <span className="reservationStudio__chip">Initial status</span>
                        </div>

                        <div className="reservationStudio__fieldGrid">
                          <div className="reservationStudio__field--full">
                            <label className="reservationStudio__label">
                              Clients that require <strong>Requested</strong> state before Confirmed
                            </label>
                            <Select
                              isMulti
                              name="clients_require_requested_flow"
                              isDisabled={demoModeActive}
                              options={clientOptions}
                              value={selectedClientOptions}
                              onChange={(selected) => {
                                if (demoModeActive || isDemoMode()) return;
                                const safeSelected = selected || [];
                                setSelectedClientOptions(safeSelected);
                                const ids = safeSelected.map((option) => option.value);
                                setFormData((prev) => ({
                                  ...prev,
                                  clients_require_requested_flow: ids.join(","),
                                }));
                              }}
                              classNamePrefix="reservation-select"
                              placeholder="Select clients (or Direct Reservation)..."
                              styles={reservationSelectStyles}
                              menuPortalTarget={reservationStudioMenuPortalTarget}
                              menuPosition="fixed"
                            />
                            <span className="reservationStudio__hint">
                              Reservations from these clients need review before confirmation.
                            </span>
                          </div>
                        </div>

                        <div style={{ marginTop: "20px" }}>
                          <label className="reservationStudio__label">Save group reservations as</label>
                          <div className="reservationStudio__choiceGrid">
                            {Object.entries(GROUP_STATUS_META).map(([status, meta]) => {
                              const isActive = formData.group_reservations_default_status === status;
                              return (
                                <button
                                  key={status}
                                  type="button"
                                  disabled={demoModeActive}
                                  title={demoModeActive ? demoCopy.readOnly : undefined}
                                  className={`reservationStudio__choiceButton ${
                                    isActive ? "reservationStudio__choiceButton--active" : ""
                                  }`}
                                  aria-pressed={isActive}
                                  onClick={() => {
                                    if (demoModeActive || isDemoMode()) return;
                                    setFormData((prev) => ({
                                      ...prev,
                                      group_reservations_default_status: status,
                                    }));
                                  }}
                                >
                                  <div className="reservationStudio__choiceTitle">{meta.title}</div>
                                  <div className="reservationStudio__choiceCopy">{meta.description}</div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </section>
                    )}
                  </div>
                )}
              </div>
            )}
            {showReservationFlowSettings && (
              <section id="reservation-changes" className="reservationStudio__card reservationStudio__card--full">
                <div className="reservationStudio__cardEyebrow">Modification permissions</div>
                <div className="reservationStudio__cardTitleRow">
                  <div>
                    <h3 className="reservationStudio__cardTitle">
                      Manual Reservation Modifications
                    </h3>
                    <p className="reservationStudio__cardCopy">
                      Decide what the hotel team can change according to where each reservation
                      was created. Updates received from the original source are never blocked by
                      these rules.
                    </p>
                  </div>
                  <span className="reservationStudio__chip">
                    {enabledReservationModificationSources} sources enabled
                  </span>
                </div>

                <div className="reservationStudio__policyGrid">
                  {RESERVATION_MODIFICATION_SOURCE_META.map((source) => {
                    const sourcePolicy =
                      effectiveReservationModificationPolicy[source.key];
                    const sourceEnabled = sourcePolicy.allow_modifications;

                    return (
                      <div
                        key={source.key}
                        className={`reservationStudio__policySource ${
                          sourceEnabled
                            ? ""
                            : "reservationStudio__policySource--disabled"
                        }`}
                      >
                        <div className="reservationStudio__policySourceHeader">
                          <div>
                            <div className="reservationStudio__policySourceTitle">
                              {source.label}
                            </div>
                            <div className="reservationStudio__policySourceCopy">
                              {source.description}
                            </div>
                          </div>
                          <button
                            type="button"
                            className={`reservationStudio__policyMaster ${
                              sourceEnabled
                                ? "reservationStudio__policyMaster--active"
                                : ""
                            }`}
                            aria-pressed={sourceEnabled}
                            aria-label={`${source.label}: allow manual changes`}
                            disabled={demoModeActive}
                            title={demoModeActive ? demoCopy.readOnly : undefined}
                            onClick={() =>
                              updateReservationModificationPolicy(
                                source.key,
                                "allow_modifications",
                                !sourceEnabled
                              )
                            }
                          >
                            {sourceEnabled ? "Enabled" : "Blocked"}
                          </button>
                        </div>

                        <div className="reservationStudio__policyActions">
                          {RESERVATION_MODIFICATION_ACTION_META.map((action) => {
                            const actionEnabled = Boolean(
                              sourcePolicy[action.key]
                            );
                            return (
                              <button
                                key={action.key}
                                type="button"
                                className="reservationStudio__policyAction"
                                disabled={demoModeActive || !sourceEnabled}
                                title={demoModeActive ? demoCopy.readOnly : undefined}
                                aria-pressed={actionEnabled}
                                aria-label={`${source.label}: ${action.label}`}
                                onClick={() =>
                                  updateReservationModificationPolicy(
                                    source.key,
                                    action.key,
                                    !actionEnabled
                                  )
                                }
                              >
                                <span
                                  className={`reservationStudio__policyCheck ${
                                    actionEnabled
                                      ? "reservationStudio__policyCheck--active"
                                      : ""
                                  }`}
                                  aria-hidden="true"
                                >
                                  ✓
                                </span>
                                <span>
                                  <span className="reservationStudio__policyActionTitle">
                                    {action.label}
                                  </span>
                                  <span className="reservationStudio__policyActionCopy">
                                    {action.description}
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            {showReservationRequiredFields && (
              <section id="reservation-profiles" className={reservationFieldsCardClassName}>
                <div className="reservationStudio__cardEyebrow">Pilcore compliance</div>
                <div className="reservationStudio__cardTitleRow">
                  <div>
                    <h3 className="reservationStudio__cardTitle">
                      Required Customer Fields per Role
                    </h3>
                    <p className="reservationStudio__cardCopy">
                      Choose the information each guest profile must contain when creating a reservation in Pilcore.
                    </p>
                  </div>
                  <span className="reservationStudio__chip">Pilcore reservations only</span>
                </div>

                <div className="reservationStudio__roleGrid reservationStudio__roleGrid--reservationFields">
                  {RESERVATION_ROLE_DEFINITIONS.map((role) => {
                    const selectedFields = formData.required_customer_fields_reservation?.[role.key] || [];
                    return (
                      <div key={role.key} className="reservationStudio__roleBlock">
                        <div className="reservationStudio__roleBlockHeader">
                          <div>
                            <div className="reservationStudio__roleTitle">{role.label}</div>
                            <div className="reservationStudio__roleCopy">{role.description}</div>
                          </div>
                          <span className="reservationStudio__roleBadge">
                            {selectedFields.length} field{selectedFields.length === 1 ? "" : "s"}
                          </span>
                        </div>

                        {renderRequiredFieldPickerTrigger({
                          fieldKey: "required_customer_fields_reservation",
                          role,
                          selectedFields,
                          contextTitle: "Reservation required fields",
                          contextBadge: "Pilcore reservations only",
                        })}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            {showReservationRequiredRoles && (
              <section id={showReservationRequiredFields ? "reservation-roles" : "reservation-profiles"} className={reservationRolesCardClassName}>
                <div className="reservationStudio__cardEyebrow">Reservation backbone</div>
                <div className="reservationStudio__cardTitleRow">
                  <div>
                    <h3 className="reservationStudio__cardTitle">Required Customer Roles</h3>
                    <p className="reservationStudio__cardCopy">
                      Select the profiles required for every Pilcore reservation. At least the booker or main guest must remain required.
                    </p>
                  </div>
                  <span className="reservationStudio__chip">Mandatory presence</span>
                </div>

                <div className="reservationStudio__roleGrid reservationStudio__roleGrid--reservationRoles">
                  {RESERVATION_ROLE_DEFINITIONS.map((role) => {
                    const hasRequiredFields =
                      (formData.required_customer_fields_reservation?.[role.key] || []).length > 0;
                    const isActive =
                      hasRequiredFields ||
                      formData.required_customer_roles_reservation?.includes(role.key) ||
                      false;
                    return (
                      <button
                        key={role.key}
                        type="button"
                        className={`reservationStudio__roleButton ${
                          isActive ? "reservationStudio__roleButton--active" : ""
                        }`}
                        aria-pressed={isActive}
                        onClick={() => toggleRequiredReservationRole(role.key)}
                        disabled={demoModeActive || hasRequiredFields}
                        title={demoModeActive ? demoCopy.readOnly : undefined}
                      >
                        <div className="reservationStudio__roleTitle">{role.label}</div>
                        <div className="reservationStudio__roleCopy">{role.description}</div>
                        <div className="reservationStudio__roleBadge">
                          {hasRequiredFields
                            ? "Required by field rules"
                            : isActive
                              ? "Mandatory in reservation"
                              : "Optional role"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
            {/*
                    
                        // 🔒 Prevención: no permitir dejar sin booker y main_guest
            */}
            {!demoModeActive && <>
            <section id="reservation-guest-email" className="reservationStudio__card reservationStudio__card--full reservationStudio__card--emailStudio">
              <div className="reservationStudio__cardEyebrow">Outbound confirmation</div>
              <div className="reservationStudio__cardTitleRow">
                <div>
                  <h3 className="reservationStudio__cardTitle">Confirmation Email Studio</h3>
                  <p className="reservationStudio__cardCopy">
                    Compose the reservation confirmation email once and reuse it across manual sends,
                    channel-manager imports and every reservation flow.
                  </p>
                </div>
                <div className="reservationStudio__emailHeaderControls">
                  <span className="reservationStudio__chip">
                    Email + PDF share the same language pack
                  </span>
                </div>
              </div>

              <div className="reservationStudio__emailWorkbench">
                <section className="reservationStudio__emailEditorPanel">
                  <div className="reservationStudio__emailEditorHeader">
                    <div>
                      <div className="reservationStudio__emailSectionEyebrow">
                        {reservationEmailActiveLanguage.label}
                      </div>
                      <h4 className="reservationStudio__emailSectionTitle">Content editor</h4>
                      <p className="reservationStudio__emailSectionCopy">
                        Edit subject and body together. Token chips go into the field that currently has
                        focus.
                      </p>
                      <div className="reservationStudio__editorLanguageSwitch">
                        <span className="reservationStudio__emailDispatchStatLabel">
                          Editing language
                        </span>
                        <div className="reservationStudio__emailLanguageTabs reservationStudio__emailLanguageTabs--editor">
                          {RESERVATION_CONFIRMATION_LANGUAGE_OPTIONS.map((languageOption) => (
                            <button
                              key={languageOption.key}
                              type="button"
                              className={`reservationStudio__emailLanguageTab ${
                                reservationEmailLangTab === languageOption.key
                                  ? "reservationStudio__emailLanguageTab--active"
                                  : ""
                              }`}
                              onClick={() => setReservationEmailLangTab(languageOption.key)}
                            >
                              {languageOption.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="reservationStudio__emailEditorStatusRow">
                        <span className="reservationStudio__chip">
                          Editing {reservationEmailActiveLanguage.label}
                        </span>
                        <span className="reservationStudio__chip">
                          {reservationEmailLangEnabledForSending ? "Included in send stack" : "Draft only"}
                        </span>
                      </div>
                    </div>

                    <div className="reservationStudio__emailInlineActions">
                      <button
                        type="button"
                        className="reservationStudio__emailInlineButton"
                        onClick={copyBookingEngineIntoReservationTemplate}
                        disabled={!reservationEmailHasBookingEngineFallback}
                      >
                        Copy Booking Engine
                      </button>
                      <button
                        type="button"
                        className="reservationStudio__emailInlineButton reservationStudio__emailInlineButton--danger"
                        onClick={clearReservationEmailLanguage}
                      >
                        Reset language
                      </button>
                    </div>
                  </div>

                  <div className="reservationStudio__variableRail">
                    <div className="reservationStudio__variableRailTop">
                      <span className="reservationStudio__emailDispatchStatLabel">
                        Insert into {reservationEmailActiveField === "body" ? "Body" : "Subject"}
                      </span>
                      <span className="reservationStudio__variableRailHint">
                        Tokens stay visual inside the editor and render with real values in the preview.
                      </span>
                    </div>

                    <div className="reservationStudio__variableCloud reservationStudio__variableCloud--compact">
                      {RESERVATION_CONFIRMATION_VARIABLES.map((variable) => (
                        <button
                          key={variable.token}
                          type="button"
                          className="reservationStudio__variableChip"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => insertReservationConfirmationToken(variable.token)}
                        >
                          {variable.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="reservationStudio__fieldGrid reservationStudio__fieldGrid--single">
                    <div className="reservationStudio__field--full">
                      <label className="reservationStudio__label">Subject</label>
                      <TemplateTokenComposer
                        ref={reservationEmailSubjectRef}
                        value={String(reservationEmailStoredTemplate?.subject || "")}
                        tokenOptions={RESERVATION_CONFIRMATION_VARIABLES}
                        placeholder="Write the subject or import the Booking Engine copy..."
                        onFocus={() => setReservationEmailActiveField("subject")}
                        onChange={(nextValue) =>
                          updateReservationEmailTemplate(reservationEmailLangTab, {
                            subject: nextValue,
                          })
                        }
                      />
                    </div>

                    <div className="reservationStudio__field--full">
                      <label className="reservationStudio__label">Body</label>
                      <TemplateTokenComposer
                        ref={reservationEmailBodyRef}
                        multiline
                        value={String(reservationEmailStoredTemplate?.body || "")}
                        tokenOptions={RESERVATION_CONFIRMATION_VARIABLES}
                        placeholder="Write the email body here..."
                        onFocus={() => setReservationEmailActiveField("body")}
                        onChange={(nextValue) =>
                          updateReservationEmailTemplate(reservationEmailLangTab, {
                            body: nextValue,
                          })
                        }
                      />
                      <span className="reservationStudio__hint">
                        If this language stays empty, Pilcore still previews and sends the Booking Engine
                        fallback or the starter template.
                      </span>
                    </div>
                  </div>
                </section>

                <div className="reservationStudio__emailPreviewColumn">
                  <section className="reservationStudio__emailPreviewPanel">
                    <div className="reservationStudio__emailEditorHeader">
                      <div>
                        <div className="reservationStudio__emailSectionEyebrow">Live preview</div>
                        <h4 className="reservationStudio__emailSectionTitle">Guest preview</h4>
                      </div>
                      <span className="reservationStudio__chip">
                        {reservationEmailActiveLanguage.label}
                      </span>
                    </div>

                    <div className="reservationStudio__emailPreviewSubject">
                      {reservationEmailPreviewSubject || "Subject preview will appear here."}
                    </div>

                    <div
                      className="reservationStudio__emailPreviewShell"
                      style={
                        reservationEmailAmbientAsset?.public_url
                          ? {
                              backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.94), rgba(15, 23, 42, 0.98)), url(${reservationEmailAmbientAsset.public_url})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }
                          : undefined
                      }
                    >
                      {reservationEmailHeroAsset?.public_url ? (
                        <div
                          className="reservationStudio__emailPreviewHeroImage"
                          style={{ backgroundImage: `url(${reservationEmailHeroAsset.public_url})` }}
                        />
                      ) : null}
                      <div className="reservationStudio__emailPreviewMessage">
                        {reservationEmailLogoAsset?.public_url ? (
                          <img
                            src={reservationEmailLogoAsset.public_url}
                            alt="Reservation email logo"
                            className="reservationStudio__emailPreviewLogo"
                          />
                        ) : null}
                        {reservationEmailPreviewParagraphs.length > 0 ? (
                          reservationEmailPreviewParagraphs.map((paragraph, paragraphIndex) => {
                            const lines = paragraph.split("\n");
                            return (
                              <p key={`preview-paragraph-${paragraphIndex}`}>
                                {lines.map((line, lineIndex) => (
                                  <React.Fragment key={`preview-line-${paragraphIndex}-${lineIndex}`}>
                                    {line}
                                    {lineIndex < lines.length - 1 ? <br /> : null}
                                  </React.Fragment>
                                ))}
                              </p>
                            );
                          })
                        ) : (
                          <p>{reservationEmailModalUiTexts.emptyPreview}</p>
                        )}
                      </div>
                    </div>
                  </section>

                  <section className="reservationStudio__pdfPreviewPanel">
                    <div className="reservationStudio__emailEditorHeader">
                      <div>
                        <div className="reservationStudio__emailSectionEyebrow">PDF sample</div>
                        <h4 className="reservationStudio__emailSectionTitle">Attached confirmation</h4>
                        <p className="reservationStudio__emailSectionCopy">
                          Download the same PDF pack that will be attached to the guest email.
                        </p>
                      </div>
                      <span className="reservationStudio__chip">
                        {reservationConfirmationLanguagePackLabel}
                      </span>
                    </div>

                    <div className="reservationStudio__pdfPreviewMetaRow">
                      <div className="reservationStudio__pdfPreviewStat">
                        <span>Language pack</span>
                        <strong>{reservationConfirmationLanguagePackLabel}</strong>
                      </div>
                      <div className="reservationStudio__pdfPreviewStat">
                        <span>Format</span>
                        <strong>Downloadable PDF</strong>
                      </div>
                    </div>

                    <div className="reservationStudio__pdfPreviewActions">
                      <button
                        type="button"
                        className="reservationStudio__emailInlineButton"
                        onClick={() =>
                          downloadReservationConfirmationSamplePdf(reservationConfirmationLanguages)
                        }
                        disabled={isDownloadingReservationPdfSample}
                      >
                        {isDownloadingReservationPdfSample
                          ? "Preparing PDF..."
                          : "Download sample PDF"}
                      </button>
                    </div>

                    <p className="reservationStudio__pdfPreviewCopy">
                      No inline preview here. This downloads the real PDF sample using the same
                      selected language pack.
                    </p>
                  </section>
                </div>
              </div>

              <div className="reservationStudio__emailDeliveryPanel">
                <div className="reservationStudio__emailDispatchRow">
                  <div className="reservationStudio__emailDispatchRowMain">
                    <div className="reservationStudio__emailDispatchBlock">
                      <span className="reservationStudio__emailDispatchStatLabel">Send languages</span>
                      <div className="reservationStudio__emailDispatchPills">
                        {RESERVATION_CONFIRMATION_LANGUAGE_OPTIONS.map((languageOption) => {
                          const isActive = reservationConfirmationLanguages.includes(languageOption.key);
                          return (
                            <button
                              key={languageOption.key}
                              type="button"
                              className={`reservationStudio__emailDispatchPill ${
                                isActive ? "reservationStudio__emailDispatchPill--active" : ""
                              }`}
                              onClick={() => toggleReservationConfirmationLanguage(languageOption.key)}
                            >
                              {languageOption.shortLabel}
                            </button>
                          );
                        })}
                      </div>
                      <span className="reservationStudio__variableRailHint">
                        These languages are applied together to the guest email and to the attached PDF.
                      </span>
                    </div>
                  </div>

                  <div className="reservationStudio__emailDispatchRowMeta">
                    <div className="reservationStudio__emailDispatchMiniStat">
                      <span>PDF pack</span>
                      <strong>{reservationConfirmationLanguagePackLabel}</strong>
                    </div>
                    <div className="reservationStudio__emailDispatchMiniStat">
                      <span>Source</span>
                      <strong>
                        {reservationEmailTemplateSource === "reservation"
                          ? "Studio"
                          : reservationEmailTemplateSource === "booking_engine"
                            ? "Booking Engine"
                            : "Starter"}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section id="reservation-hotel-alerts" className="reservationStudio__card reservationStudio__card--full reservationStudio__card--emailStudio">
              <div className="reservationStudio__cardEyebrow">External reservation alerts</div>
              <div className="reservationStudio__cardTitleRow">
                <div>
                  <h3 className="reservationStudio__cardTitle">Hotel Notification Studio</h3>
                  <p className="reservationStudio__cardCopy">
                    Configure the internal email the hotel receives when a reservation coming from
                    outside Pilcore, such as an OTA, an agency or a channel manager, is created,
                    modified or cancelled.
                  </p>
                </div>
                <div className="reservationStudio__emailHeaderControls">
                  <span className="reservationStudio__chip">External sources only</span>
                  <span className="reservationStudio__chip">
                    {formData.external_reservation_notifications_enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>

              <div className="reservationStudio__emailDeliveryPanel" style={{ marginBottom: "20px" }}>
                <div className="reservationStudio__fieldGrid reservationStudio__fieldGrid--single">
                  <div className="reservationStudio__field--full">
                    <label
                      className="reservationStudio__label"
                      style={{ display: "flex", alignItems: "center", gap: "12px" }}
                    >
                      <input
                        type="checkbox"
                        checked={!!formData.external_reservation_notifications_enabled}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            external_reservation_notifications_enabled: event.target.checked,
                          }))
                        }
                      />
                      Enable hotel emails for external reservation changes
                    </label>
                    <span className="reservationStudio__hint">
                      {externalReservationNotificationUiTexts.labels.toggleCopy}
                    </span>
                  </div>

                  <div className="reservationStudio__field--full">
                    <label className="reservationStudio__label">
                      Recipient emails
                    </label>
                    <div className="reservationStudio__recipientChipRow">
                      {externalReservationNotificationRecipients.length > 0 ? (
                        externalReservationNotificationRecipients.map((email, index) => (
                          <button
                            key={`${email}_${index}`}
                            type="button"
                            className="reservationStudio__recipientChip"
                            onClick={() =>
                              removeExternalReservationNotificationRecipient(index)
                            }
                            title={`Remove ${email}`}
                            aria-label={`Remove ${email}`}
                          >
                            <span className="reservationStudio__recipientChipLabel">
                              {email}
                            </span>
                            <span
                              className="reservationStudio__recipientChipClose"
                              aria-hidden="true"
                            >
                              x
                            </span>
                          </button>
                        ))
                      ) : (
                        <div className="reservationStudio__recipientEmpty">
                          {externalReservationNotificationUiTexts.labels.recipientEmpty}
                        </div>
                      )}
                    </div>
                    <div className="reservationStudio__recipientInputRow">
                      <input
                        type="text"
                        className="reservationStudio__input"
                        placeholder={
                          externalReservationNotificationUiTexts.labels
                            .recipientInputPlaceholder
                        }
                        value={externalReservationNotificationEmailInput}
                        onChange={(event) =>
                          setExternalReservationNotificationEmailInput(
                            event.target.value
                          )
                        }
                        onKeyDown={(event) => {
                          if (
                            event.key !== "Enter" &&
                            event.key !== "," &&
                            event.key !== ";"
                          ) {
                            return;
                          }
                          event.preventDefault();
                          commitExternalReservationNotificationRecipientInput();
                        }}
                        onPaste={(event) => {
                          const pastedText = event.clipboardData?.getData("text") || "";
                          if (!/[,;\n]/.test(pastedText)) return;
                          event.preventDefault();
                          addExternalReservationNotificationRecipients(pastedText);
                          setExternalReservationNotificationEmailInput("");
                        }}
                      />
                      <button
                        type="button"
                        className="reservationStudio__emailInlineButton"
                        onClick={commitExternalReservationNotificationRecipientInput}
                      >
                        {externalReservationNotificationUiTexts.labels.recipientAddAction}
                      </button>
                    </div>
                    <span className="reservationStudio__hint">
                      {externalReservationNotificationUiTexts.labels.recipientHint}
                    </span>
                  </div>
                </div>
              </div>

              <div className="reservationStudio__emailWorkbench">
                <section className="reservationStudio__emailEditorPanel">
                  <div className="reservationStudio__emailEditorHeader">
                    <div>
                      <div className="reservationStudio__emailSectionEyebrow">
                        {externalReservationNotificationActiveLanguage.label}
                      </div>
                      <h4 className="reservationStudio__emailSectionTitle">Content editor</h4>
                      <p className="reservationStudio__emailSectionCopy">
                        Each event keeps its own editable message. When the body is empty, Pilcore uses
                        the standard fallback with the real changes and nightly prices.
                      </p>
                      <div className="reservationStudio__editorLanguageSwitch">
                        <span className="reservationStudio__emailDispatchStatLabel">
                          Editing language
                        </span>
                        <div className="reservationStudio__emailLanguageTabs reservationStudio__emailLanguageTabs--editor">
                          {RESERVATION_CONFIRMATION_LANGUAGE_OPTIONS.map((languageOption) => (
                            <button
                              key={`external-notification-lang-${languageOption.key}`}
                              type="button"
                              className={`reservationStudio__emailLanguageTab ${
                                externalReservationNotificationLangTab === languageOption.key
                                  ? "reservationStudio__emailLanguageTab--active"
                                  : ""
                              }`}
                              onClick={() =>
                                setExternalReservationNotificationLangTab(languageOption.key)
                              }
                            >
                              {languageOption.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="reservationStudio__editorLanguageSwitch" style={{ marginTop: "12px" }}>
                        <span className="reservationStudio__emailDispatchStatLabel">
                          Event copy
                        </span>
                        <div className="reservationStudio__emailLanguageTabs reservationStudio__emailLanguageTabs--editor">
                          {EXTERNAL_RESERVATION_NOTIFICATION_EVENT_OPTIONS.map((eventOption) => (
                            <button
                              key={`external-notification-event-${eventOption.key}`}
                              type="button"
                              className={`reservationStudio__emailLanguageTab ${
                                externalReservationNotificationEventTab === eventOption.key
                                  ? "reservationStudio__emailLanguageTab--active"
                                  : ""
                              }`}
                              onClick={() =>
                                setExternalReservationNotificationEventTab(eventOption.key)
                              }
                            >
                              {eventOption.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="reservationStudio__emailEditorStatusRow">
                        <span className="reservationStudio__chip">
                          Editing {externalReservationNotificationActiveLanguage.label}
                        </span>
                        <span className="reservationStudio__chip">
                          {
                            externalReservationNotificationUiTexts.eventNames[
                              externalReservationNotificationEventTab
                            ]
                          }
                        </span>
                      </div>
                    </div>

                    <div className="reservationStudio__emailInlineActions">
                      <button
                        type="button"
                        className="reservationStudio__emailInlineButton reservationStudio__emailInlineButton--danger"
                        onClick={clearExternalReservationNotificationTemplate}
                      >
                        Reset event copy
                      </button>
                    </div>
                  </div>

                  <div className="reservationStudio__variableRail">
                    <div className="reservationStudio__variableRailTop">
                      <span className="reservationStudio__emailDispatchStatLabel">
                        Insert into{" "}
                        {externalReservationNotificationActiveField === "body"
                          ? "Body"
                          : "Subject"}
                      </span>
                      <span className="reservationStudio__variableRailHint">
                        Tokens render with real values in the preview. A standard fallback is used only
                        while the body is empty.
                      </span>
                    </div>

                    <div className="reservationStudio__variableCloud reservationStudio__variableCloud--compact">
                      {EXTERNAL_RESERVATION_NOTIFICATION_VARIABLES.map((variable) => (
                        <button
                          key={variable.token}
                          type="button"
                          className="reservationStudio__variableChip"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() =>
                            insertExternalReservationNotificationToken(variable.token)
                          }
                        >
                          {variable.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="reservationStudio__fieldGrid reservationStudio__fieldGrid--single">
                    <div className="reservationStudio__field--full">
                      <label className="reservationStudio__label">Subject</label>
                      <TemplateTokenComposer
                        ref={externalReservationNotificationSubjectRef}
                        value={String(externalReservationNotificationStoredTemplate?.subject || "")}
                        tokenOptions={EXTERNAL_RESERVATION_NOTIFICATION_VARIABLES}
                        placeholder="Write the subject shown to the hotel team..."
                        onFocus={() =>
                          setExternalReservationNotificationActiveField("subject")
                        }
                        onChange={(nextValue) =>
                          updateExternalReservationNotificationTemplate(
                            externalReservationNotificationLangTab,
                            externalReservationNotificationEventTab,
                            { subject: nextValue }
                          )
                        }
                      />
                    </div>

                    <div className="reservationStudio__field--full">
                      <label className="reservationStudio__label">Body</label>
                      <TemplateTokenComposer
                        ref={externalReservationNotificationBodyRef}
                        multiline
                        value={String(externalReservationNotificationStoredTemplate?.body || "")}
                        tokenOptions={EXTERNAL_RESERVATION_NOTIFICATION_VARIABLES}
                        placeholder="Write the hotel-facing intro here..."
                        onFocus={() => setExternalReservationNotificationActiveField("body")}
                        onChange={(nextValue) =>
                          updateExternalReservationNotificationTemplate(
                            externalReservationNotificationLangTab,
                            externalReservationNotificationEventTab,
                            { body: nextValue }
                          )
                        }
                      />
                      <span className="reservationStudio__hint">
                        {externalReservationNotificationUiTexts.labels.bodyHint}
                      </span>
                    </div>
                  </div>
                </section>

                <div className="reservationStudio__emailPreviewColumn">
                  <section className="reservationStudio__emailPreviewPanel">
                    <div className="reservationStudio__emailEditorHeader">
                      <div>
                        <div className="reservationStudio__emailSectionEyebrow">Live preview</div>
                        <h4 className="reservationStudio__emailSectionTitle">
                          {externalReservationNotificationUiTexts.labels.previewTitle}
                        </h4>
                      </div>
                      <span className="reservationStudio__chip">
                        {externalReservationNotificationActiveLanguage.label}
                      </span>
                    </div>

                    <div className="reservationStudio__emailPreviewSubject">
                      {externalReservationNotificationPreviewSubject ||
                        "Subject preview will appear here."}
                    </div>

                    <div className="reservationStudio__emailPreviewShell">
                      <div className="reservationStudio__emailPreviewMessage">
                        <div className="reservationStudio__emailEditorStatusRow">
                          <span className="reservationStudio__chip">
                            {
                              externalReservationNotificationUiTexts.eventNames[
                                externalReservationNotificationEventTab
                              ]
                            }
                          </span>
                          {!externalReservationNotificationHasCustomBody ? (
                            <span className="reservationStudio__chip">
                              {externalReservationNotificationUiTexts.labels.operationalBlock}
                            </span>
                          ) : null}
                        </div>
                        {externalReservationNotificationPreviewParagraphs.length > 0 ? (
                          externalReservationNotificationPreviewParagraphs.map(
                            (paragraph, paragraphIndex) => {
                              const lines = paragraph.split("\n");
                              return (
                                <p key={`hotel-notification-preview-paragraph-${paragraphIndex}`}>
                                  {lines.map((line, lineIndex) => (
                                    <React.Fragment
                                      key={`hotel-notification-preview-line-${paragraphIndex}-${lineIndex}`}
                                    >
                                      {line}
                                      {lineIndex < lines.length - 1 ? <br /> : null}
                                    </React.Fragment>
                                  ))}
                                </p>
                              );
                            }
                          )
                        ) : (
                          <p>{externalReservationNotificationUiTexts.labels.emptyPreview}</p>
                        )}
                      </div>
                    </div>
                  </section>

                  <section className="reservationStudio__pdfPreviewPanel">
                    <div className="reservationStudio__emailEditorHeader">
                      <div>
                        <div className="reservationStudio__emailSectionEyebrow">Dispatch setup</div>
                        <h4 className="reservationStudio__emailSectionTitle">Send language</h4>
                        <p className="reservationStudio__emailSectionCopy">
                          Choose the language Pilcore will use when sending these hotel-facing
                          notifications.
                        </p>
                      </div>
                      <span className="reservationStudio__chip">
                        {externalReservationNotificationSendingLanguage === "es"
                          ? "Spanish"
                          : "English"}
                      </span>
                    </div>

                    <div className="reservationStudio__pdfPreviewMetaRow">
                      <div className="reservationStudio__pdfPreviewStat">
                        <span>{externalReservationNotificationUiTexts.labels.sendLanguage}</span>
                        <strong>
                          {externalReservationNotificationSendingLanguage === "es"
                            ? "Spanish"
                            : "English"}
                        </strong>
                      </div>
                      <div className="reservationStudio__pdfPreviewStat">
                        <span>Recipients</span>
                        <strong>
                          {externalReservationNotificationRecipients.length || 0}
                        </strong>
                      </div>
                    </div>

                    <div className="reservationStudio__emailDispatchPills" style={{ marginTop: "16px" }}>
                      {RESERVATION_CONFIRMATION_LANGUAGE_OPTIONS.map((languageOption) => {
                        const isActive =
                          externalReservationNotificationSendingLanguage === languageOption.key;
                        return (
                          <button
                            key={`external-notification-send-${languageOption.key}`}
                            type="button"
                            className={`reservationStudio__emailDispatchPill ${
                              isActive ? "reservationStudio__emailDispatchPill--active" : ""
                            }`}
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                external_reservation_notification_language:
                                  languageOption.key,
                              }))
                            }
                          >
                            {languageOption.shortLabel}
                          </button>
                        );
                      })}
                    </div>

                    <p className="reservationStudio__pdfPreviewCopy" style={{ marginTop: "16px" }}>
                      This studio only covers external reservations and always excludes internal room
                      assignment, guest assignment and check-in/check-out operational changes.
                    </p>
                  </section>
                </div>
              </div>
            </section>

            <section id="reservation-email-visuals" className="reservationStudio__card reservationStudio__card--full reservationStudio__card--emailBranding">
              <div className="reservationStudio__cardEyebrow">Brand & media</div>
              <div className="reservationStudio__cardTitleRow">
                <div>
                  <h3 className="reservationStudio__cardTitle">Email visuals</h3>
                  <p className="reservationStudio__cardCopy">
                    Upload the visuals used in confirmation emails. Uploading or removing an image takes effect immediately.
                  </p>
                </div>
                <span className="reservationStudio__chip">Brand assets</span>
              </div>

              <div className="reservationStudio__emailAssetGrid">
                {RESERVATION_CONFIRMATION_ASSET_DEFINITIONS.map((assetDef) => {
                  const asset = reservationEmailAssets?.[assetDef.kind] || null;
                  const selectedFile = reservationEmailAssetFiles?.[assetDef.kind] || null;
                  const isLoading = Boolean(reservationEmailAssetLoadingByKind?.[assetDef.kind]);
                  const isUploading = Boolean(reservationEmailAssetUploadingByKind?.[assetDef.kind]);

                  return (
                    <div key={assetDef.kind} className="reservationStudio__emailAssetCard">
                      <div className="reservationStudio__emailAssetLabel">{assetDef.label}</div>
                      <div
                        className={`reservationStudio__emailAssetVisual ${
                          assetDef.variant === "logo"
                            ? "reservationStudio__emailAssetVisual--logo"
                            : assetDef.variant === "ambient"
                              ? "reservationStudio__emailAssetVisual--ambient"
                              : ""
                        }`}
                        style={
                          asset?.public_url
                            ? {
                                backgroundImage:
                                  assetDef.variant === "logo"
                                    ? "none"
                                    : `url(${asset.public_url})`,
                                backgroundSize: assetDef.variant === "hero" ? "cover" : "cover",
                                backgroundPosition: "center",
                              }
                            : undefined
                        }
                      >
                        {assetDef.variant === "logo" ? (
                          asset?.public_url ? (
                            <img
                              src={asset.public_url}
                              alt={assetDef.label}
                              className="reservationStudio__emailAssetLogoImage"
                            />
                          ) : (
                            "Pilcore"
                          )
                        ) : null}
                      </div>
                      <p className="reservationStudio__futureCopy">{assetDef.description}</p>
                      <div className="reservationStudio__emailAssetMeta">
                        {isLoading
                          ? "Loading..."
                          : selectedFile
                            ? selectedFile.name
                            : asset?.public_url
                              ? "Active visual ready"
                              : assetDef.emptyLabel}
                      </div>
                      <input
                        key={`asset-input-${assetDef.kind}-${selectedFile?.name || "empty"}-${asset?.id || 0}`}
                        type="file"
                        accept="image/*"
                        className="reservationStudio__emailAssetInput"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          setReservationEmailAssetFiles((prev) => ({
                            ...(prev || {}),
                            [assetDef.kind]: file,
                          }));
                        }}
                      />
                      <div className="reservationStudio__emailAssetActions">
                        <button
                          type="button"
                          className="reservationStudio__emailInlineButton"
                          onClick={() => uploadReservationEmailAsset(assetDef.kind)}
                          disabled={!selectedFile || isUploading}
                        >
                          {isUploading ? "Uploading..." : asset?.public_url ? "Replace" : "Upload"}
                        </button>
                        <button
                          type="button"
                          className="reservationStudio__emailInlineButton reservationStudio__emailInlineButton--danger"
                          onClick={() => setReservationAssetToRemove({ kind: assetDef.kind, label: assetDef.label })}
                          disabled={!asset?.id || isUploading}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            </>}
            {demoModeActive && <section className="reservationStudio__card reservationStudio__card--full settings-demo-emailNotice">
              <h3 className="reservationStudio__cardTitle">{languageId === "es" ? "Configuración de correos" : languageId === "ca" ? "Configuració de correus" : "Email configuration"}</h3>
              <p className="reservationStudio__cardCopy">{languageId === "es" ? "La configuración de correos y sus recursos visuales no están incluidos en esta demo." : languageId === "ca" ? "La configuració de correus i els seus recursos visuals no estan inclosos en aquesta demo." : "Email configuration and visual assets are not included in this demo."}</p>
            </section>}
          </div>

          </div>
        </form>
      )}

      {activeTab === "extras" && (
        <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
          <SettingsExtras />
        </div>
      )}

      {activeTab === "currencies" && canViewCurrenciesTab && (
        <SettingsCurrencies businessUnitId={businessUnitId} />
      )}

      {activeTab === "charges" && (
        <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
          <SettingsCharges />
        </div>
      )}

      {activeTab === "appearance" && canViewAppearanceTab && (
        <div
          className="w-100 fs-6"
          style={{
            width: "100%",
            maxWidth: "1680px",
            margin: "0 auto",
          }}
        >
          <PmsThemeSettingsPanel previewModule={allowPurchasingSharedAccess ? "purchasing" : "pms"} />
        </div>
      )}


      
      {activeTab === "crm" && canViewCrmTab && (
        <div className="container-fluid" style={{ maxWidth: "1500px" }}>
          <div className="d-flex align-items-start justify-content-between gap-3 mb-3 flex-wrap">
            <div>
              <p className="text-info fw-bold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "0.08em" }}>
                CRM
              </p>
              <h2 className="text-white fw-bold mb-0">Opportunity pipelines</h2>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={loadCrmPipelines}
                disabled={crmPipelinesLoading}
              >
                {crmPipelinesLoading ? "Loading..." : "Reload"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-warning fw-bold"
                onClick={startNewCrmPipeline}
                disabled={!canUpdateCrmPipelines}
              >
                New pipeline
              </button>
            </div>
          </div>

          {crmPipelinesError ? (
            <div className="alert alert-danger py-2">{crmPipelinesError}</div>
          ) : null}

          <div className="row g-3">
            <div className="col-12 col-xl-4">
              <div className="card bg-dark text-light border-secondary h-100">
                <div className="card-header border-secondary d-flex justify-content-between align-items-center">
                  <strong>Pipelines</strong>
                  <span className="badge bg-info text-dark">{crmPipelines.length}</span>
                </div>
                <div className="list-group list-group-flush">
                  {crmPipelines.length ? (
                    crmPipelines.map((pipeline) => (
                      <button
                        key={pipeline.id}
                        type="button"
                        className={`list-group-item list-group-item-action bg-dark text-light border-secondary ${
                          String(crmSelectedPipelineId) === String(pipeline.id) ? "active" : ""
                        }`}
                        onClick={() => selectCrmPipeline(pipeline)}
                      >
                        <div className="d-flex justify-content-between gap-2">
                          <strong>{pipeline.name}</strong>
                          <span className={`badge ${pipeline.status === "inactive" ? "bg-secondary" : "bg-success"}`}>
                            {pipeline.status}
                          </span>
                        </div>
                        <small className="text-white-50">
                          {pipeline.is_default ? "Default pipeline · " : ""}
                          {(pipeline.stages || []).filter((stage) => stage.status !== "inactive").length} active stages
                        </small>
                      </button>
                    ))
                  ) : (
                    <div className="p-3 text-white-50">
                      {crmPipelinesLoading ? "Loading pipelines..." : "No pipelines created yet."}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-8">
              <div className="card bg-dark text-light border-secondary">
                <div className="card-header border-secondary d-flex justify-content-between align-items-center gap-2">
                  <strong>{crmPipelineDraft.id ? "Edit pipeline" : "New pipeline"}</strong>
                  <button
                    type="button"
                    className="btn btn-sm btn-success fw-bold"
                    onClick={saveCrmPipelineDraft}
                    disabled={!canUpdateCrmPipelines || crmPipelinesSaving}
                  >
                    {crmPipelinesSaving ? "Saving..." : "Save pipeline"}
                  </button>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12 col-lg-6">
                      <label className="form-label">Name</label>
                      <input
                        className="form-control bg-dark text-light border-secondary"
                        value={crmPipelineDraft.name}
                        onChange={(event) => updateCrmPipelineDraft("name", event.target.value)}
                        disabled={!canUpdateCrmPipelines}
                      />
                    </div>
                    <div className="col-6 col-lg-2">
                      <label className="form-label">Order</label>
                      <input
                        type="number"
                        className="form-control bg-dark text-light border-secondary"
                        value={crmPipelineDraft.sort_order}
                        onChange={(event) => updateCrmPipelineDraft("sort_order", event.target.value)}
                        disabled={!canUpdateCrmPipelines}
                      />
                    </div>
                    <div className="col-6 col-lg-2">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select bg-dark text-light border-secondary"
                        value={crmPipelineDraft.status}
                        onChange={(event) => updateCrmPipelineDraft("status", event.target.value)}
                        disabled={!canUpdateCrmPipelines}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    <div className="col-12 col-lg-2 d-flex align-items-end">
                      <label className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={Boolean(crmPipelineDraft.is_default)}
                          onChange={(event) => updateCrmPipelineDraft("is_default", event.target.checked)}
                          disabled={!canUpdateCrmPipelines || crmPipelineDraft.status === "inactive"}
                        />
                        <span className="form-check-label">Default</span>
                      </label>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
                    <h5 className="mb-0 text-info">Stages</h5>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-light"
                      onClick={addCrmPipelineStageDraft}
                      disabled={!canUpdateCrmPipelines}
                    >
                      Add stage
                    </button>
                  </div>

                  <div className="d-grid gap-2">
                    {crmPipelineDraft.stages.map((stage, index) => (
                      <div
                        key={stage.id || stage.temp_id}
                        className={`p-3 rounded border ${stage.status === "inactive" ? "border-secondary opacity-75" : "border-info"}`}
                        style={{ background: "rgba(255,255,255,0.035)" }}
                      >
                        <div className="row g-2 align-items-end">
                          <div className="col-12 col-lg-4">
                            <label className="form-label">Stage name</label>
                            <input
                              className="form-control bg-dark text-light border-secondary"
                              value={stage.name}
                              onChange={(event) => updateCrmPipelineStageDraft(index, "name", event.target.value)}
                              disabled={!canUpdateCrmPipelines || stage._delete}
                            />
                          </div>
                          <div className="col-6 col-lg-2">
                            <label className="form-label">Type</label>
                            <select
                              className="form-select bg-dark text-light border-secondary"
                              value={stage.stage_type}
                              onChange={(event) => updateCrmPipelineStageDraft(index, "stage_type", event.target.value)}
                              disabled={!canUpdateCrmPipelines || stage._delete}
                            >
                              <option value="open">Open</option>
                              <option value="won">Won</option>
                              <option value="lost">Lost</option>
                            </select>
                          </div>
                          <div className="col-6 col-lg-2">
                            <label className="form-label">Probability</label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              className="form-control bg-dark text-light border-secondary"
                              value={stage.probability}
                              onChange={(event) => updateCrmPipelineStageDraft(index, "probability", event.target.value)}
                              disabled={!canUpdateCrmPipelines || stage._delete}
                            />
                          </div>
                          <div className="col-6 col-lg-1">
                            <label className="form-label">Order</label>
                            <input
                              type="number"
                              className="form-control bg-dark text-light border-secondary"
                              value={stage.sort_order}
                              onChange={(event) => updateCrmPipelineStageDraft(index, "sort_order", event.target.value)}
                              disabled={!canUpdateCrmPipelines || stage._delete}
                            />
                          </div>
                          <div className="col-6 col-lg-1">
                            <label className="form-label">Color</label>
                            <input
                              type="color"
                              className="form-control form-control-color bg-dark border-secondary"
                              value={stage.color || "#7dd3fc"}
                              onChange={(event) => updateCrmPipelineStageDraft(index, "color", event.target.value)}
                              disabled={!canUpdateCrmPipelines || stage._delete}
                            />
                          </div>
                          <div className="col-12 col-lg-2 d-flex gap-2">
                            <select
                              className="form-select bg-dark text-light border-secondary"
                              value={stage.status}
                              onChange={(event) => updateCrmPipelineStageDraft(index, "status", event.target.value)}
                              disabled={!canUpdateCrmPipelines || stage._delete}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => removeCrmPipelineStageDraft(index)}
                              disabled={!canUpdateCrmPipelines}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {!canUpdateCrmPipelines ? (
                    <p className="text-white-50 mt-3 mb-0">You can view pipelines, but your role cannot edit them.</p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      

      {activeTab === "revenue" && (
        <form
          onSubmit={handleSubmit}
          className="container fs-6"
          style={{ zoom: "80%", transformOrigin: "top center", width: "100%" }}
        >
          
  

                    {/* POPUP de configuración */}
                    {showMarketRulePopup && (
                      <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1050 }}>
                        <div className="bg-dark text-light p-4 rounded" style={{ width: "460px", boxShadow: "0 0 20px rgba(255,255,255,0.1)" }}>
                          <h5 className="text-center mb-4 text-warning">New Market Price Rule</h5>
                          <div className="mb-3">
                            <label className="form-label">Room Category</label>
                            <select
                              className="form-select form-select-sm"
                              value={selectedCategoryId}
                              onChange={(e) => setSelectedCategoryId(e.target.value)}
                            >
                              <option value="">Select room category...</option>
                              {roomCategories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Room Rate</label>
                            <select
                              className="form-select form-select-sm"
                              value={autoRule.room_rate_id}
                              onChange={(e) => setAutoRule((prev) => ({ ...prev, room_rate_id: e.target.value }))}
                            >
                              <option value="">Select room rate...</option>
                              {availableRoomRates.map((rate) => (
                                <option key={rate.id} value={rate.id}>
                                  {rate.name} ({rate.rate_code})
                                </option>
                              ))}

                            </select>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Adjustment Type</label>
                            <select
                              className="form-select form-select-sm"
                              value={autoRule.adjustment_type}
                              onChange={(e) => setAutoRule((prev) => ({ ...prev, adjustment_type: e.target.value }))}
                            >
                              <option value="equal">Same as Market</option>
                              <option value="above">Above Market</option>
                              <option value="below">Below Market</option>
                            </select>
                          </div>

                          {autoRule.adjustment_type !== "equal" && (
                            <>
                              <div className="mb-3">
                                <label className="form-label">Adjustment Value</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={autoRule.value}
                                  onChange={(e) => setAutoRule((prev) => ({ ...prev, value: e.target.value }))}
                                />
                              </div>

                              <div className="mb-3">
                                <label className="form-label">Adjustment Unit</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={autoRule.unit}
                                  onChange={(e) => setAutoRule((prev) => ({ ...prev, unit: e.target.value }))}
                                >
                                  <option value="absolute">{baseCurrency} (absolute)</option>
                                  <option value="percent">% (percentage)</option>
                                </select>
                              </div>
                            </>
                          )}

                          <div className="d-flex justify-content-between mt-4">
                            <button
                              type="button"
                              className="btn btn-sm btn-danger w-45"
                              onClick={() => setShowMarketRulePopup(false)}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-success w-45"
                              onClick={() => {
                                if (!autoRule.room_rate_id) return alert("Please select a room rate.");

                                const newRule = {
                                  room_rate_id: Number(autoRule.room_rate_id),
                                  adjustment_type: autoRule.adjustment_type,
                                  unit: autoRule.unit,
                                  value: autoRule.adjustment_type === "equal" ? 0 : parseFloat(autoRule.value),
                                };

                                const updated = [...(formData.market_price_rules || []), newRule];
                                setFormData((prev) => ({
                                  ...prev,
                                  market_price_rules: updated
                                }));

                                setAutoRule({ room_rate_id: "", adjustment_type: "equal", unit: "absolute", value: 0 });
                                setShowMarketRulePopup(false);
                              }}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* 🟣 Tarjetas de configuración de precios y derivación */}
                    <div className="d-flex justify-content-center gap-4 mt-4 mb-5 flex-wrap">
                      {/* 🔹 Tarjeta: Modo de sobrescritura */}
                      <div
                        className="card bg-dark text-light p-4 flex-fill"
                        style={{ minWidth: "360px", maxWidth: "400px" }}
                      >
                        <h3 className="fs-6 fw-bold text-warning text-center mb-3">
                          Rate Price Overwrite Mode
                        </h3>
                        <p
                          className="text-white-50 text-center mb-3"
                          style={{ fontSize: "0.9rem" }}
                        >
                          When enabled, any new rate price created will{" "}
                          <strong>replace existing prices</strong> for the same dates
                          and their sister rates (same group key), instead of coexisting by
                          min/max stay.
                        </p>

                        <div className="form-check form-switch text-center mt-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="exclusiveRateSwitch"
                            checked={!!formData.exclusive_rate_prices_per_date}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                exclusive_rate_prices_per_date: e.target.checked,
                              }))
                            }
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="exclusiveRateSwitch"
                          >
                            Enable Exclusive Rate Prices per Date
                          </label>
                        </div>
                      </div>

                      {/* 🔹 Tarjeta: Redondeo de precios */}
                      <div
                        className="card bg-dark text-light p-4 flex-fill"
                        style={{ minWidth: "360px", maxWidth: "400px" }}
                      >
                        <h3 className="fs-6 fw-bold text-success text-center mb-3">
                          Whole-Number Rate Prices
                        </h3>
                        <p
                          className="text-white-50 text-center mb-3"
                          style={{ fontSize: "0.9rem" }}
                        >
                          When enabled, saved rate prices are rounded to the nearest whole
                          currency unit. Example: 123.51 → 124 and 123.49 → 123.
                        </p>

                        <div className="form-check form-switch text-center mt-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="alwaysRoundRatePricesSwitch"
                            checked={!!formData.always_round_rate_prices}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                always_round_rate_prices: e.target.checked,
                              }))
                            }
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="alwaysRoundRatePricesSwitch"
                          >
                            Always round saved rate prices
                          </label>
                        </div>
                      </div>

                      {/* 🔹 Tarjeta: Modo de cálculo de tarifas derivadas */}
                      <div
                        className="card bg-dark text-light p-4 flex-fill"
                        style={{ minWidth: "360px", maxWidth: "400px" }}
                      >
                        <h3 className="fs-6 fw-bold text-info text-center mb-3">
                          Derived Rate Calculation Mode
                        </h3>
                        <p
                          className="text-white-50 text-center mb-3"
                          style={{ fontSize: "0.9rem" }}
                        >
                          Select how derived rates are calculated in this hotel.
                          <br />
                          <strong>Simple:</strong> Uses base rate and adjustment (percent or absolute).
                          <br />
                          <strong>Complex:</strong> Uses advanced formulas combining multiple rates.
                        </p>

                        <div className="text-center">
                          <select
                            className="form-select bg-dark text-light border-secondary w-auto mx-auto"
                            style={{ minWidth: "220px" }}
                            value={formData.derived_rate_mode || "simple"}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                derived_rate_mode: e.target.value,
                              }))
                            }
                          >
                            <option value="simple">Simple (Base + Adjustment)</option>
                            <option value="complex">Complex (Formula System)</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    



                  
                

          <div className="text-center mt-4 mb-5">
            <button
              type="submit"
              className="btn text-white fw-bold border-0"
              style={{
                background: "linear-gradient(to right, #1abc9c, #3498db)",
                borderRadius: "5px",
                padding: "10px 30px",
              }}
            >
              Save Settings
            </button>
          </div>
        </form>
      )}

      {activeTab === "users" && canViewUsersTab && (
        <UsersRolesOverview
          languageId={languageId}
          currentUser={currentUser} users={userList} roles={roles} hotels={availableBusinessUnits}
          canManageUsers={canManageUsers && !!managementOptions} canCreateUsers={(canManageUsers && availableBusinessUnits.some((hotel) => hotel.assignable_role_ids?.length)) || (!canManageUsers && canCreateCorporateUsers)}
          canCreateRoles={roleHotelOptions.length > 0} accessReady={!!managementOptions} loadingUser={loadingUser}
          onCreateUser={() => { if (!canManageUsers && canCreateCorporateUsers) { setShowCorporateUserPopup(true); return; } setUsersRolesFeedback(null); setNewUserData({ name: "", email: "" }); setNewUserAssignments([{ ...EMPTY_USER_ASSIGNMENT }]); setShowNewUserPopup(true); }}
          onCreateRole={() => { if (!roleHotelOptions.length) return; setNewRolePermissions({}); setUsersRolesFeedback(null); setNewRoleData({ name: "", description: "", business_unit_ids: [] }); setShowNewRolePopup(true); }}
          onSendInvitation={(user) => runUsersRolesWrite(async () => {
            try {
              const { data } = await axios.post(`/api/users/${user.id}/send-invitation`);
              notifyUsersRoles(data.message, "success");
              const response = await axios.get("/api/users"); setUserList(response.data);
            } catch (error) { notifyUsersRoles(error.response?.data?.error || "No se ha podido enviar la invitación.", "error"); }
          })}
          onEditUser={handleEditUser} onChangeStatus={handleUserStatusChange} onDeleteUser={handleDeleteUser}
          onEditRole={handleEditRole} onDeleteRole={handleDeleteRole}
        />
      )}

      {activeTab === "booking_engine" && readOnlyBookingTree(
        <div
          className={`bookingEngineStudio${demoModeActive ? " bookingEngineStudio--demo" : ""}`}
          style={{
            margin: "0 auto",
            minHeight: 0,
            boxSizing: "border-box",
          }}
        >
          <style>{bookingEngineStudioCss}</style>

          {bookingEngineToast ? (
            <div className="beStudio__toastOverlay">
              <div className={`beStudio__toast beStudio__toast--${bookingEngineToast.tone || "info"}`} role={bookingEngineToast.tone === "error" ? "alert" : "status"}>
                <span>{bookingEngineToast.message}</span>
                <button data-demo-view={true} type="button" className="bookingEngineSettings__dismiss" aria-label={bookingSettingsCopy.dismiss} onClick={() => setBookingEngineToast(null)}>×</button>
              </div>
            </div>
          ) : null}

            <section className="beStudio__hero">
              <div>
                <div className="beStudio__eyebrow">{bookingEngineStudio.tabTitle}</div>
                <h1 className="beStudio__title">{bookingEngineStudio.header.title}</h1>
                <p className="beStudio__subtitle">{demoModeActive ? demoCopy.readOnly : bookingSettingsCopy.intro}</p>
                {demoModeActive && <p className="beStudio__hint">{demoCopy.inert}</p>}

              </div>

              <div className="beStudio__heroAside">
                <div className="beStudio__heroActions">
                  <button
                    type="button"
                    className="beStudio__button"
                    onClick={handleCopyBookingEngineUrl}
                  >
                    {bookingEngineCopiedUrl
                      ? bookingEngineStudio.actions.copied
                      : formData.booking_engine_entry_mode === "widget"
                      ? bookingEngineStudio.actions.copyWidget
                      : bookingEngineStudio.actions.copyUrl}
                  </button>
                  <button
                    type="button"
                    className="beStudio__button beStudio__button--primary"
                    onClick={saveBookingEngineSettings}
                    disabled={bookingEngineSaving}
                  >
                    {bookingEngineSaving
                      ? bookingEngineStudio.actions.saving
                      : bookingEngineStudio.actions.save}
                  </button>
                </div>

                <p className="bookingEngineSettings__saveHint">{bookingSettingsCopy.saveHint}</p>

              </div>
            </section>


            <nav className="bookingEngineSettings__nav" role="tablist" aria-label={bookingSettingsCopy.navigation}>
              {bookingSettingsSections.map((section, index) => (
                <button
                  key={section} id={`booking-settings-tab-${section}`}
                  data-demo-view={true} type="button" role="tab"
                  aria-selected={bookingSettingsSection === section}
                  aria-controls="booking-settings-panel"
                  tabIndex={bookingSettingsSection === section ? 0 : -1}
                  className="bookingEngineSettings__navLink"
                  onClick={() => selectBookingSettingsSection(section)}
                  onKeyDown={(event) => {
                    const next = event.key === "ArrowRight" ? (index + 1) % 4 : event.key === "ArrowLeft" ? (index + 3) % 4 : event.key === "Home" ? 0 : event.key === "End" ? 3 : null;
                    if (next === null) return;
                    event.preventDefault();
                    selectBookingSettingsSection(bookingSettingsSections[next]);
                    document.getElementById(`booking-settings-tab-${bookingSettingsSections[next]}`)?.focus();
                  }}
                >
                  <span aria-hidden="true">{index + 1}</span>{bookingSettingsCopy[section]}
                </button>
              ))}
            </nav>
          <div className="beStudio__surface" ref={bookingSettingsSurfaceRef}>
            <p className="bookingEngineSettings__sectionIntro">{bookingSettingsCopy[`${bookingSettingsSection}Hint`]}</p>
            <div id="booking-settings-panel" data-section={bookingSettingsSection} role="tabpanel" aria-labelledby={`booking-settings-tab-${bookingSettingsSection}`} className={`beStudio__grid ${amenityPickerOpen ? "beStudio__grid--modalOpen" : ""}`}>
              <section className="beStudio__card beStudio__card--paired" data-booking-section="general" hidden={bookingSettingsSection !== "general"}>
                <div className="beStudio__cardHeader">
                  <div>
                    <div className="beStudio__cardEyebrow">{bookingEngineStudio.launch.eyebrow}</div>
                    <h3 className="beStudio__cardTitle">{bookingEngineStudio.launch.title}</h3>
                    <p className="beStudio__cardCopy">{bookingSettingsCopy.launchCopy}</p>
                  </div>
                  <button data-demo-view={true}
                    type="button"
                    className="beStudio__button"
                    onClick={() => {
                      setBookingInstallCopiedTab("");
                      setBookingInstallGuideOpen(true);
                    }}
                  >
                    {bookingEngineStudio.launch.installGuide.button}
                  </button>
                </div>

                <div className="beStudio__switchGrid">
                  <label className="beStudio__switchCard" htmlFor="bookingEngineEnabledStudio">
                    <input
                      id="bookingEngineEnabledStudio"
                      className="beStudio__switchInput"
                      type="checkbox"
                      checked={!!formData.booking_engine_enabled}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          booking_engine_enabled: event.target.checked,
                        }))
                      }
                    />
                    <span>
                      <span className="beStudio__switchTitle">
                        {bookingEngineStudio.launch.enabledLabel}
                      </span>
                      <span className="beStudio__switchCopy">
                        {bookingEngineStudio.launch.disabledNote}
                      </span>
                    </span>
                  </label>
                </div>

                <div className="beStudio__modeBlock">
                  <span className="beStudio__label">
                    {bookingEngineStudio.launch.entryModeLabel}
                  </span>
                  <div
                    className="beStudio__modeSelector"
                    role="radiogroup"
                    aria-label={bookingEngineStudio.launch.entryModeLabel}
                  >
                    {[
                      {
                        value: "hosted",
                        label: bookingEngineStudio.launch.hostedMode,
                        copy: bookingEngineStudio.launch.hostedModeCopy,
                      },
                      {
                        value: "widget",
                        label: bookingEngineStudio.launch.widgetMode,
                        copy: bookingEngineStudio.launch.widgetModeCopy,
                      },
                    ].map((option) => {
                      const isActive =
                        (formData.booking_engine_entry_mode || "hosted") === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          role="radio"
                          aria-checked={isActive}
                          className={`beStudio__modeOption ${
                            isActive ? "beStudio__modeOption--active" : ""
                          }`}
                          onClick={() => {
                            setBookingEngineCopiedUrl(false);
                            setFormData((prev) => ({
                              ...prev,
                              booking_engine_entry_mode: option.value,
                              booking_engine_hero_background_screens:
                                normalizeBookingHeroBackgroundScreens(
                                  prev.booking_engine_hero_background_screens,
                                  option.value
                                ),
                            }));
                          }}
                        >
                          <span className="beStudio__modeOptionTitle">{option.label}</span>
                          <span className="beStudio__modeOptionCopy">{option.copy}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="beStudio__modeBlock">
                  <span className="beStudio__label" id="booking-card-policy-label">
                    {bookingEngineStudio.launch.cardPolicyLabel}
                  </span>
                  <div
                    className="beStudio__modeSelector"
                    role="radiogroup"
                    aria-labelledby="booking-card-policy-label"
                    aria-describedby="booking-card-policy-hint"
                  >
                    {[
                      {
                        value: "required",
                        label: bookingEngineStudio.launch.cardRequired,
                        copy: bookingEngineStudio.launch.cardRequiredCopy,
                      },
                      {
                        value: "not_required",
                        label: bookingEngineStudio.launch.cardNotRequired,
                        copy: bookingEngineStudio.launch.cardNotRequiredCopy,
                      },
                    ].map((option) => {
                      const isActive = normalizeBookingCardPolicy(formData.booking_engine_card_policy) === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          role="radio"
                          aria-checked={isActive}
                          data-card-policy={option.value}
                          className={`beStudio__modeOption ${isActive ? "beStudio__modeOption--active" : ""}`}
                          onClick={() => setFormData((prev) => ({
                            ...prev,
                            booking_engine_card_policy: option.value,
                          }))}
                        >
                          <span className="beStudio__modeOptionTitle">{option.label}</span>
                          <span className="beStudio__modeOptionCopy">{option.copy}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="beStudio__hint" id="booking-card-policy-hint">
                    {bookingEngineStudio.launch.cardPolicyHint}
                  </p>
                </div>

                <div className="beStudio__fieldGrid">
                  <div>
                    <label className="beStudio__label" htmlFor="booking-public-slug">{bookingEngineStudio.launch.slugLabel}</label>
                    <input
                      type="text"
                      className="beStudio__input"
                      id="booking-public-slug" placeholder={bookingEngineStudio.launch.slugPlaceholder}
                      value={formData.booking_engine_slug || ""}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          booking_engine_slug: event.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, ""),
                        }))
                      }
                    />
                    <div className="beStudio__hint">{bookingEngineStudio.launch.slugHint}</div>
                  </div>

                  <div>
                    <label className="beStudio__label" htmlFor="booking-public-url">{bookingEngineStudio.launch.urlLabel}</label>
                    <input
                      readOnly id="booking-public-url"
                      className="beStudio__input"
                      value={publicBookingUrl || ""}
                    />
                    <div className="beStudio__hint">{bookingEngineStudio.launch.urlHint}</div>
                  </div>
                </div>

                {formData.booking_engine_entry_mode === "widget" ? (
                  <div className="beStudio__widgetCodeBlock">
                    <label className="beStudio__label">
                      {bookingEngineStudio.launch.widgetCodeLabel}
                    </label>
                    <textarea
                      readOnly
                      rows={6}
                      className="beStudio__textarea beStudio__widgetCode"
                      aria-label={bookingEngineStudio.launch.widgetCodeLabel}
                      value={bookingWidgetEmbedCode}
                    />
                    <div className="beStudio__hint">
                      {bookingEngineStudio.launch.widgetCodeHint}
                    </div>
                  </div>
                ) : null}
              </section>

              <section className="beStudio__card beStudio__card--paired" data-booking-section="general" hidden={bookingSettingsSection !== "general"}>
                <div className="beStudio__cardHeader">
                  <div>
                    <div className="beStudio__cardEyebrow">{bookingEngineStudio.profile.eyebrow}</div>
                    <h3 className="beStudio__cardTitle">{bookingEngineStudio.profile.title}</h3>
                    <p className="beStudio__cardCopy">{bookingEngineStudio.profile.copy}</p>
                    <p className="bookingEngineSettings__saveHint">{bookingSettingsCopy.directSave}</p>
                  </div>

                  <button data-demo-view={true}
                    type="button"
                    className="beStudio__button"
                    onClick={() => setBeProfileOpen(true)}
                  >
                    {demoModeActive ? demoCopy.view : bookingEngineStudio.actions.edit}
                  </button>
                </div>

                <div className="beStudio__previewCard">
                  <span className="beStudio__urlLabel">{bookingEngineStudio.profile.previewTitle}</span>
                  <div className="beStudio__textMuted">{bookingEngineStudio.profile.previewHint}</div>

                  {renderBookingEngineProfilePlacementPreview(
                    bookingEngineProfilePreview,
                    "booking-engine-profile-preview"
                  )}
                </div>
              </section>

              <section className="beStudio__card beStudio__card--wide" data-booking-section="appearance" hidden={bookingSettingsSection !== "appearance"}>
                <div className="beStudio__themeBlock">
                  <div className="beStudio__sectionEyebrow">{bookingEngineStudio.theme.eyebrow}</div>
                  <h4 className="beStudio__themeTitle">{bookingEngineStudio.theme.title}</h4>
                  <p className="beStudio__themeCopy">{bookingEngineStudio.theme.copy}</p>
                  <div
                    className="beStudio__themeSelector"
                    role="radiogroup"
                    aria-label={bookingEngineStudio.theme.ariaLabel}
                  >
                    {BOOKING_THEME_IDS.map((themeId) => {
                      const isActive = normalizeBookingTheme(formData.booking_engine_theme) === themeId;
                      const preset = bookingEngineStudio.theme.presets[themeId];
                      return (
                        <button
                          key={themeId}
                          type="button"
                          role="radio"
                          aria-checked={isActive}
                          className={`beStudio__themeOption ${
                            isActive ? "beStudio__themeOption--active" : ""
                          }`}
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              booking_engine_theme: themeId,
                            }))
                          }
                        >
                          <span className="beStudio__themeSwatches" aria-hidden="true">
                            {BOOKING_THEME_SWATCHES[themeId].map((color) => (
                              <span key={color} style={{ backgroundColor: color }} />
                            ))}
                          </span>
                          <span className="beStudio__themeOptionHeading">
                            <span>{preset.name}</span>
                            {themeId === "pilcore-light" ? (
                              <small>{bookingEngineStudio.theme.recommended}</small>
                            ) : null}
                          </span>
                          <span className="beStudio__themeOptionCopy">{preset.description}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

              </section>

              <section className="beStudio__card beStudio__card--wide" data-booking-section="appearance" hidden={bookingSettingsSection !== "appearance"}>
                <div className="beStudio__cardHeader">
                  <div>
                    <div className="beStudio__cardEyebrow">{bookingEngineStudio.hero.eyebrow}</div>
                    <h3 className="beStudio__cardTitle">{bookingEngineStudio.hero.title}</h3>
                    <p className="beStudio__cardCopy">{bookingEngineStudio.hero.copy}</p>
                    {demoModeActive && <p className="beStudio__hint">{demoCopy.media}</p>}
                  </div>
                </div>

                <div className="beStudio__heroScreenBlock">
                  <span className="beStudio__label">{bookingEngineStudio.hero.screensLabel}</span>
                  <p className="beStudio__heroScreenHint">
                    {bookingEngineEntryMode === "widget"
                      ? bookingEngineStudio.hero.widgetScreensHint
                      : bookingEngineStudio.hero.screensHint}
                  </p>
                  <div
                    className="beStudio__heroScreenSelector"
                    role="group"
                    aria-label={bookingEngineStudio.hero.screensLabel}
                  >
                    {BOOKING_HERO_BACKGROUND_SCREEN_IDS
                      .filter(
                        (screenId) =>
                          bookingEngineEntryMode !== "widget" || screenId !== "search"
                      )
                      .map((screenId) => {
                        const isSelected = bookingEngineHeroBackgroundScreens.includes(screenId);
                        return (
                          <button
                            key={screenId}
                            type="button"
                            aria-pressed={isSelected}
                            className={`beStudio__heroScreenOption ${
                              isSelected ? "beStudio__heroScreenOption--active" : ""
                            }`}
                            onClick={() => toggleBookingHeroBackgroundScreen(screenId)}
                          >
                            <span className="beStudio__heroScreenCheck" aria-hidden="true">
                              {isSelected ? "✓" : ""}
                            </span>
                            <span>{bookingEngineStudio.hero.screenLabels[screenId]}</span>
                          </button>
                        );
                      })}
                  </div>
                  {!bookingEngineHasHeroBackgroundScreens ? (
                    <div className="beStudio__heroScreenEmpty" role="status">
                      {bookingEngineStudio.hero.noScreensSelected}
                    </div>
                  ) : null}
                </div>

                <p className="bookingEngineSettings__saveHint">{bookingSettingsCopy.mediaSave}</p>
                {bookingEngineHasHeroBackgroundScreens ? (
                  <div className="beStudio__heroImageSection">
                    <div className="beStudio__heroImageHeader">
                      <div>
                        <span className="beStudio__label">
                          {bookingEngineStudio.hero.backgroundLabel}
                        </span>
                        <div className="beStudio__hint">
                          {heroBgAsset?.id
                            ? bookingEngineStudio.hero.backgroundLocked
                            : bookingEngineStudio.hero.backgroundHint}
                        </div>
                      </div>
                      <div className="beStudio__inlineActions">
                        <button
                          type="button"
                          className="beStudio__button"
                          disabled={!heroBgFile || heroBgUploading || !!heroBgAsset?.id}
                          onClick={uploadHeroBackground}
                        >
                          {heroBgUploading
                            ? bookingEngineStudio.actions.uploading
                            : bookingEngineStudio.actions.upload}
                        </button>
                        <button
                          type="button"
                          className="beStudio__button beStudio__button--danger"
                          disabled={!heroBgAsset?.id}
                          onClick={() => setBookingImageToRemove({ kind: "hero" })}
                        >
                          {bookingEngineStudio.actions.remove}
                        </button>
                      </div>
                    </div>

                    <div className="beStudio__fieldGrid beStudio__heroImageFields">
                      <div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="beStudio__fileInput"
                          aria-label={bookingEngineStudio.hero.backgroundLabel}
                          disabled={!!heroBgAsset?.id}
                          onChange={(event) => setHeroBgFile(event.target.files?.[0] || null)}
                        />
                      </div>

                      <div>
                        <span className="beStudio__label">{bookingEngineStudio.hero.previewLabel}</span>
                        <div className="beStudio__chipRow">
                          <span className="beStudio__chip">
                            {heroBgLoading
                              ? bookingEngineStudio.status.loading
                              : heroBgAsset?.id
                              ? bookingEngineStudio.status.uploaded
                              : bookingEngineStudio.hero.noBackground}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div
                      className="beStudio__heroPreview"
                      style={
                        heroBgAsset?.public_url
                          ? {
                              backgroundImage: `linear-gradient(180deg, rgba(2, 6, 23, 0.08), rgba(2, 6, 23, 0.56)), url(${heroBgAsset.public_url})`,
                            }
                          : undefined
                      }
                    >
                      <div className="beStudio__heroPreviewContent">
                        <div className="beStudio__heroPreviewKicker">
                          {bookingEngineStudio.hero.previewLabel}
                        </div>
                        {demoModeActive && heroBgAsset?.id && (
                          <p className="beStudio__heroPreviewCopy">
                            {bookingEngineStudio.status.configured} · {demoCopy.media}
                          </p>
                        )}
                        {bookingEngineShowsHeroCopyPreview ? (
                          <>
                            <h3 className="beStudio__heroPreviewTitle">
                              {bookingEngineHeroPreviewSubtitle}
                            </h3>
                            <p className="beStudio__heroPreviewCopy">
                              {bookingEngineHeroPreviewClaim}
                            </p>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}

                {bookingEngineShowsOpeningScreen ? (
                  <div className="beStudio__heroCopySection">
                    <div className="beStudio__sectionEyebrow">
                      {bookingEngineStudio.hero.openingMessageTitle}
                    </div>
                    <p className="beStudio__heroScreenHint">
                      {bookingEngineStudio.hero.openingMessageHint}
                    </p>

                    <div className="beStudio__fieldGrid">
                      <div>
                        <label className="beStudio__label">{bookingEngineStudio.hero.subtitleLabel} (EN)</label>
                        <input
                          type="text"
                          className="beStudio__input"
                          placeholder={bookingEngineStudio.hero.subtitlePlaceholderEn}
                          value={getBookingEngineText("hero_subtitle", "en")}
                          onChange={(event) =>
                            setBookingEngineText("hero_subtitle", "en", event.target.value)
                          }
                        />
                      </div>

                      <div>
                        <label className="beStudio__label">{bookingEngineStudio.hero.subtitleLabel} (ES)</label>
                        <input
                          type="text"
                          className="beStudio__input"
                          placeholder={bookingEngineStudio.hero.subtitlePlaceholderEs}
                          value={getBookingEngineText("hero_subtitle", "es")}
                          onChange={(event) =>
                            setBookingEngineText("hero_subtitle", "es", event.target.value)
                          }
                        />
                      </div>

                      <div>
                        <label className="beStudio__label">{bookingEngineStudio.hero.claimLabel} (EN)</label>
                        <input
                          type="text"
                          className="beStudio__input"
                          placeholder={bookingEngineStudio.hero.claimPlaceholderEn}
                          value={getBookingEngineText("hero_claim", "en")}
                          onChange={(event) =>
                            setBookingEngineText("hero_claim", "en", event.target.value)
                          }
                        />
                      </div>

                      <div>
                        <label className="beStudio__label">{bookingEngineStudio.hero.claimLabel} (ES)</label>
                        <input
                          type="text"
                          className="beStudio__input"
                          placeholder={bookingEngineStudio.hero.claimPlaceholderEs}
                          value={getBookingEngineText("hero_claim", "es")}
                          onChange={(event) =>
                            setBookingEngineText("hero_claim", "es", event.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </section>

              <section
                className="beStudio__card beStudio__card--third"
                data-booking-section="guest" hidden={bookingSettingsSection !== "guest"}
              >
                <div className="beStudio__cardHeader">
                  <div>
                    <div className="beStudio__cardEyebrow">{bookingEngineStudio.pricing.eyebrow}</div>
                    <h3 className="beStudio__cardTitle">{bookingEngineStudio.pricing.title}</h3>
                    <p className="beStudio__cardCopy">{bookingSettingsCopy.pricingCopy}</p>
                  </div>
                </div>

                <div className="beStudio__switchGrid">
                  {[
                    {
                      key: "booking_engine_price_includes_vat",
                      label: bookingEngineStudio.pricing.vat,
                    },
                    {
                      key: "booking_engine_price_includes_other_taxes",
                      label: bookingEngineStudio.pricing.otherTaxes,
                    },
                    {
                      key: "booking_engine_price_includes_fees",
                      label: bookingEngineStudio.pricing.fees,
                    },
                    {
                      key: "booking_engine_price_includes_city_tax",
                      label: bookingEngineStudio.pricing.cityTax,
                    },
                  ].map((item) => (
                    <label className="beStudio__switchCard" key={item.key}>
                      <input
                        className="beStudio__switchInput"
                        type="checkbox"
                        checked={!!formData[item.key]}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            [item.key]: event.target.checked,
                          }))
                        }
                      />
                      <span className="beStudio__switchTitle">{item.label}</span>
                    </label>
                  ))}
                </div>

                <div className="beStudio__previewCard" style={{ marginTop: "18px" }}>
                  <span className="beStudio__urlLabel">{bookingEngineStudio.pricing.summaryTitle}</span>
                  <div className="beStudio__pricingPreviewShell">
                    <div className="beStudio__pricingPreviewLabel">
                      {bookingEnginePublicStudio.pricing.totalLabel}
                    </div>
                    <div className="beStudio__pricingPreviewCopy">
                      {bookingEngineDisclosurePreview}
                    </div>
                  </div>
                </div>
              </section>

              <section
                className="beStudio__card beStudio__card--third"
                data-booking-section="guest" hidden={bookingSettingsSection !== "guest"}
              >
                <div className="beStudio__cardHeader">
                  <div>
                    <div className="beStudio__cardEyebrow">{bookingEngineStudio.emails.eyebrow}</div>
                    <h3 className="beStudio__cardTitle">{bookingEngineStudio.emails.title}</h3>
                    <p className="beStudio__cardCopy">{bookingSettingsCopy.emailCopy}</p>
                  </div>
                </div>

                <div className="beStudio__cardBody beStudio__cardBody--scroll">
                  <div className="beStudio__languageGrid" style={{ gridTemplateColumns: "1fr" }}>
                    {BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS.map((option) => {
                      const tpl = bookingEngineGuestEmailTemplates?.[option.id] || {};
                      const hasCopy = Boolean(String(tpl.subject || "").trim() || String(tpl.body || "").trim());

                      return (
                        <button data-demo-view={true}
                          key={option.id}
                          type="button"
                          className="beStudio__languageCardButton"
                          onClick={() => {
                            setGuestEmailLangTab(option.id);
                            setGuestEmailDraft({
                              lang: option.id,
                              subject: String(tpl.subject || ""),
                              body: String(tpl.body || ""),
                            });
                            setGuestEmailInsertTarget("subject");
                            setGuestEmailOpen(true);
                          }}
                        >
                          <div className="beStudio__cardHeader">
                            <div>
                              <div className="beStudio__languageCardTitle">
                                {bookingEngineStudio.emails.title} ({option.shortLabel})
                              </div>
                              <TemplateTokenInlinePreview
                                className="beStudio__languageCardSnippet beStudio__languageCardSnippet--tokens"
                                value={String(tpl.subject || "").trim()}
                                tokenOptions={bookingEngineVariableOptions}
                                emptyText={bookingEngineStudio.emails.emptyLanguage}
                                tokenClassName="beStudio__inlineToken"
                                textClassName="beStudio__languageCardText"
                              />
                            </div>

                            <span
                              className={`beStudio__chip ${
                                hasCopy ? "beStudio__chip--accent" : ""
                              }`}
                            >
                              {hasCopy
                                ? bookingEngineStudio.emails.configured
                                : bookingEngineStudio.emails.missing}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              <section
                className="beStudio__card beStudio__card--third bookingEngineSettings__termsCard"
                data-booking-section="guest" hidden={bookingSettingsSection !== "guest"}
              >
                <div className="beStudio__cardHeader">
                  <div>
                    <div className="beStudio__cardEyebrow">{bookingEngineStudio.terms.eyebrow}</div>
                    <h3 className="beStudio__cardTitle">{bookingEngineStudio.terms.title}</h3>
                    <p className="beStudio__cardCopy">{bookingSettingsCopy.termsCopy}</p>
                  </div>

                  <button
                    type="button"
                    className="beStudio__button"
                    onClick={() => {
                      const nextId = `term_${Date.now()}`;
                      setTermsEditingId(nextId);
                      setTermsDraft({
                        id: nextId,
                        sort_order: (bookingEngineTerms.length + 1) * 10,
                        title: { en: "", es: "" },
                        text: { en: "", es: "" },
                      });
                      setTermsLangTab("en");
                      setTermsOpen(true);
                    }}
                  >
                    {bookingEngineStudio.actions.addNew}
                  </button>
                </div>

                <div className="beStudio__cardBody beStudio__cardBody--scroll">
                  {bookingEngineTerms.length ? (
                    <div className="beStudio__termsList">
                      {bookingEngineTerms.map((row, index) => {
                        const titleEn = String(row?.title?.en || "").trim();
                        const titleEs = String(row?.title?.es || "").trim();
                        const primaryTitle =
                          bookingEngineContentLanguage === "en" ? titleEn : titleEs;
                        const fallbackTitle =
                          bookingEngineContentLanguage === "en" ? titleEs : titleEn;
                        const visibleTitle =
                          primaryTitle || fallbackTitle || bookingEngineStudio.terms.notSet;
                        const rowTitle =
                          [titleEn, titleEs].filter(Boolean).join(" / ") ||
                          bookingEngineStudio.terms.notSet;

                        return (
                          <button data-demo-view={true}
                            key={row.id}
                            type="button"
                            className="beStudio__termsListRow"
                            title={rowTitle}
                            onClick={() => {
                              setTermsEditingId(row.id);
                              setTermsDraft({
                                id: row.id,
                                sort_order: Number(row.sort_order) || 0,
                                title: { ...(row.title || {}) },
                                text: { ...(row.text || {}) },
                              });
                              setTermsLangTab("en");
                              setTermsOpen(true);
                            }}
                          >
                            <span className="beStudio__termsListOrder">
                              {String(index + 1).padStart(2, "0")}
                            </span>
                            <span className="beStudio__termsListTitle">{visibleTitle}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="beStudio__emptyState">
                      <div className="beStudio__emptyCopy">{bookingEngineStudio.terms.empty}</div>
                    </div>
                  )}
                </div>
              </section>

              <section
                data-booking-section="rooms" hidden={bookingSettingsSection !== "rooms"}
                className={`beStudio__card beStudio__card--wide ${
                  amenityPickerOpen ? "beStudio__card--modalOpen" : ""
                }`}
              >
                <div className="beStudio__cardHeader">
                  <div>
                    <div className="beStudio__cardEyebrow">{bookingEngineStudio.rooms.eyebrow}</div>
                    <h3 className="beStudio__cardTitle">{bookingEngineStudio.rooms.title}</h3>
                    <p className="beStudio__cardCopy">{bookingSettingsCopy.roomsCopy}</p>
                  </div>
                </div>


                <div className="bookingEngineSettings__roomToolbar">
                  <label className="beStudio__label" htmlFor="booking-category-search">{bookingSettingsCopy.categoriesSearch}</label>
                  <input id="booking-category-search" data-demo-view={true} type="search" className="beStudio__input" value={bookingCategorySearch} onChange={(event) => setBookingCategorySearch(event.target.value)} />
                  <span className="beStudio__chip">{roomCategories.length} {bookingSettingsCopy.categoryLabel}</span>
                </div>
                {roomCategoryImagesLoading ? (
                  <div className="beStudio__emptyState">
                    <div className="beStudio__emptyCopy">{bookingEngineStudio.rooms.loading}</div>
                  </div>
                ) : !roomCategories.length ? (
                  <div className="beStudio__emptyState">
                    <div className="beStudio__emptyCopy">{bookingEngineStudio.rooms.empty}</div>
                  </div>
                ) : (
                  <>
                    {roomCategories.filter((rc) => Number(rc.id) === bookingRoomCategoryId).map((rc) => {
                      const rcId = Number(rc.id);
                      const imgs = roomCategoryImages?.[rcId] || [];
                      const cover = imgs.find((item) => item.kind === "cover") || null;
                      const mainImg = cover || imgs[0] || null;
                      const pickedFile = roomCategoryImageFileById?.[rcId] || null;
                      const storedImageValidation = roomCategoryImageValidationById?.[rcId] || null;
                      const imageValidation =
                        storedImageValidation?.file === pickedFile ? storedImageValidation : null;
                      const imageValidationStatus = imageValidation?.status || "idle";
                      const imageReadyToUpload = imageValidationStatus === "valid";
                      const imageDimensions =
                        imageValidation?.width && imageValidation?.height
                          ? `${imageValidation.width} x ${imageValidation.height} px`
                          : "";
                      const imageValidationMessage =
                        imageValidationStatus === "checking"
                          ? bookingEngineStudio.rooms.imageRatioChecking
                          : imageValidationStatus === "valid"
                            ? `${bookingEngineStudio.rooms.imageRatioValid} ${imageDimensions}`.trim()
                            : imageValidationStatus === "invalid"
                              ? `${
                                  imageValidation.reason === "unreadable"
                                    ? bookingEngineStudio.rooms.imageUnreadable
                                    : bookingEngineStudio.rooms.imageRatioInvalid
                                } ${imageDimensions}`.trim()
                              : "";
                      const count = imgs.length;
                      const tab = rcTabById?.[rcId] || "images";
                      const selectedAmenityIds = rcAmenityIdsByRcId?.[rcId] || [];
                      const categories = Array.from(
                        new Set((amenitiesAll || []).map((item) => item?.category).filter(Boolean))
                      ).sort((a, b) => String(a).localeCompare(String(b)));

                      return (
                        <section
                          key={rc.id}
                          id="booking-room-category-editor"
                          className="bookingEngineSettings__roomEditor"
                          data-room-category-id={rcId}
                          ref={bookingRoomEditorRef}
                          role="region"
                          aria-labelledby="booking-room-category-editor-title"
                          tabIndex={-1}
                        >
                          <div className="bookingEngineSettings__roomEditorHeader">
                            <div>
                              <div className="beStudio__sectionEyebrow">{bookingSettingsCopy.configureRoom}</div>
                              <h4 id="booking-room-category-editor-title" className="beStudio__sectionTitle">
                                {rc.name || `Room category #${rc.id}`}
                              </h4>
                            </div>
                            <button data-demo-view={true} type="button" className="beStudio__button" onClick={closeBookingRoomEditor}>
                              {bookingEngineStudio.actions.close}
                            </button>
                          </div>

                          <div className="beStudio__roomTabs">
                            <button data-demo-view={true}
                              type="button"
                              className={`beStudio__tabButton ${
                                tab === "images" ? "beStudio__tabButton--active" : ""
                              }`}
                              aria-pressed={tab === "images"}
                              onClick={() => setRcTabById((prev) => ({ ...(prev || {}), [rcId]: "images" }))}
                            >
                              {bookingEngineStudio.rooms.imagesTab}
                            </button>

                            <button data-demo-view={true}
                              type="button"
                              className={`beStudio__tabButton ${
                                tab === "amenities" ? "beStudio__tabButton--active" : ""
                              }`}
                              aria-pressed={tab === "amenities"}
                              onClick={async () => {
                                setRcTabById((prev) => ({ ...(prev || {}), [rcId]: "amenities" }));
                                await ensureRoomCategoryAmenitiesLoaded(rcId);
                              }}
                            >
                              {bookingEngineStudio.rooms.amenitiesTab}
                            </button>

                            <button data-demo-view={true}
                              type="button"
                              className={`beStudio__tabButton ${
                                tab === "booking_engine" ? "beStudio__tabButton--active" : ""
                              }`}
                              aria-pressed={tab === "booking_engine"}
                              onClick={async () => {
                                setRcTabById((prev) => ({ ...(prev || {}), [rcId]: "booking_engine" }));
                                await ensureBeRoomCategoryCopyLoaded();
                              }}
                            >
                              {bookingEngineStudio.rooms.copyTab}
                            </button>
                          </div>

                          {tab === "images" ? (
                            <div className="beStudio__roomPanel">
                              <div className="beStudio__cardHeader">
                                <div>
                                  <div className="beStudio__sectionEyebrow">
                                    {bookingEngineStudio.rooms.imagesLabel}
                                  </div>
                                  <h4 className="beStudio__sectionTitle">
                                    {bookingEngineStudio.rooms.imageHint}
                                  </h4>
                                </div>
                              </div>

                              <div className="beStudio__fieldGrid">
                                <div className="beStudio__field--full">
                                  <label
                                    className="beStudio__label"
                                    htmlFor={`booking-room-image-${rcId}`}
                                  >
                                    {bookingEngineStudio.rooms.imagesLabel}
                                  </label>
                                  <input
                                    id={`booking-room-image-${rcId}`}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    className="beStudio__fileInput"
                                    disabled={count >= 10}
                                    aria-busy={imageValidationStatus === "checking"}
                                    aria-invalid={imageValidationStatus === "invalid"}
                                    aria-describedby={`booking-room-image-requirement-${rcId} booking-room-image-status-${rcId}`}
                                    onChange={(event) =>
                                      handleRoomCategoryImageFileChange(
                                        rcId,
                                        event.target.files?.[0] || null
                                      )
                                    }
                                  />
                                  <div className="beStudio__hint">
                                    {count >= 10
                                      ? bookingEngineStudio.rooms.maxReached
                                      : pickedFile?.name || bookingEngineStudio.rooms.noFile}
                                  </div>
                                  <div
                                    id={`booking-room-image-requirement-${rcId}`}
                                    className="beStudio__hint"
                                  >
                                    {bookingEngineStudio.rooms.imageRatioHint}
                                  </div>
                                  <div
                                    id={`booking-room-image-status-${rcId}`}
                                    className={`beStudio__hint ${
                                      imageValidationStatus === "invalid"
                                        ? "beStudio__hint--error"
                                        : imageValidationStatus === "valid"
                                          ? "beStudio__hint--success"
                                          : ""
                                    }`}
                                    role={imageValidationStatus === "invalid" ? "alert" : "status"}
                                    aria-live="polite"
                                  >
                                    {imageValidationMessage}
                                  </div>
                                </div>
                              </div>

                              <div className="beStudio__actionRow" style={{ marginTop: "16px" }}>
                                <button
                                  type="button"
                                  className="beStudio__button"
                                  disabled={!pickedFile || !imageReadyToUpload || count >= 10}
                                  onClick={() => uploadRoomCategoryImage(rcId, "cover")}
                                >
                                  {bookingEngineStudio.rooms.uploadCover}
                                </button>
                                <button
                                  type="button"
                                  className="beStudio__button"
                                  disabled={!pickedFile || !imageReadyToUpload || count >= 10}
                                  onClick={() => uploadRoomCategoryImage(rcId, "gallery")}
                                >
                                  {bookingEngineStudio.rooms.uploadGallery}
                                </button>
                              </div>

                              {mainImg ? (
                                <>
                                  <div className="beStudio__assetRow">
                                    <div
                                      className="beStudio__assetPreview"
                                      style={{ backgroundImage: `url(${mainImg.public_url})` }}
                                    />
                                    <div className="beStudio__assetMeta">
                                      <span className="beStudio__urlLabel">
                                        {mainImg.kind === "cover"
                                          ? bookingEngineStudio.rooms.coverTag
                                          : bookingEngineStudio.rooms.galleryTag}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="beStudio__thumbGrid">
                                    {imgs.slice(0, 10).map((img, idx) => (
                                      <div key={img.asset_id} className="beStudio__thumb">
                                        <button data-demo-view={true}
                                          type="button"
                                          className={`beStudio__thumbButton ${
                                            idx === 0 ? "beStudio__thumbButton--active" : ""
                                          }`}
                                          style={{ backgroundImage: `url(${img.public_url})` }}
                                          aria-label={`${bookingEngineStudio.rooms.imageModalTitle}: ${rc.name || rcId} · ${idx + 1}`}
                                          onClick={() => openRoomCategoryImagesModal(rcId, idx)}
                                        >
                                          {demoModeActive ? `${idx + 1} · ${img.kind}` : null}
                                        </button>
                                        <div className="beStudio__actionRow" style={{ marginTop: "8px" }}>
                                          <button
                                            type="button"
                                            className="beStudio__button beStudio__button--danger"
                                            onClick={() => setBookingImageToRemove({ kind: "room", assetId: img.asset_id })}
                                          >
                                            {bookingEngineStudio.actions.remove}
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <div className="beStudio__emptyState">
                                  <div className="beStudio__emptyCopy">
                                    {bookingEngineStudio.rooms.noImage}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : tab === "amenities" ? (
                            <div className="beStudio__roomPanel">
                              <div className="beStudio__cardHeader">
                                <div>
                                  <div className="beStudio__sectionEyebrow">
                                    {bookingEngineStudio.rooms.amenitiesTitle}
                                  </div>
                                  <h4 className="beStudio__sectionTitle">
                                    {bookingEngineStudio.rooms.amenitiesHint}
                                  </h4>
                                </div>

                                <div className="beStudio__actionRow">
                                  <button data-demo-view={true}
                                    type="button"
                                    className="beStudio__button beStudio__button--primary"
                                    onClick={async () => {
                                      await fetchAmenitiesAll();
                                      const loadedIds = await ensureRoomCategoryAmenitiesLoaded(rcId);
                                      if (loadedIds !== null) openAmenityPicker(rcId, loadedIds);
                                    }}
                                  >
                                    {bookingEngineStudio.rooms.manageAmenities}
                                  </button>
                                </div>
                              </div>

                              <div className="beStudio__amenityCloud" style={{ marginTop: "16px" }}>
                                {uniqNums(selectedAmenityIds)
                                  .map((id) => amenitiesAll.find((item) => Number(item.id) === Number(id)))
                                  .filter(Boolean)
                                  .slice(0, 18)
                                  .map((item) => (
                                    <span
                                      key={item.id}
                                      className="beStudio__chip"
                                    >
                                      {amenityLabel(item)}
                                    </span>
                                  ))}

                                {!selectedAmenityIds.length ? (
                                  <div className="beStudio__emptyCopy">
                                    {bookingEngineStudio.rooms.noAmenities}
                                  </div>
                                ) : null}
                              </div>

                              {amenityPickerOpen && Number(amenityPickerRcId) === Number(rcId) ? (
                                <div
                                  className="beStudio__modalOverlay beStudio__modalOverlay--amenities"
                                  onMouseDown={(event) => {
                                    if (event.target === event.currentTarget) closeAmenityPicker();
                                  }}
                                >
                                  <div className="beStudio__modal beStudio__modal--wide beStudio__modal--amenities" role="dialog" aria-modal="true" tabIndex={-1} aria-label={`${bookingEngineStudio.rooms.amenityModalTitle}: ${rc.name || rc.id}`}>
                                    <div className="beStudio__modalHeader">
                                      <div>
                                        <div className="beStudio__modalEyebrow">
                                          {bookingEngineStudio.rooms.amenityModalTitle}
                                        </div>
                                        <h3 className="beStudio__modalTitle">
                                          {rc.name || `Room category #${rc.id}`}
                                        </h3>
                                        <div className="beStudio__modalCopy">
                                          {bookingEngineStudio.rooms.amenityModalSubtitle}
                                          <p className="bookingEngineSettings__saveHint">{bookingSettingsCopy.directSave}</p>
                                        </div>
                                      </div>

                                      <button data-demo-view={true}
                                        type="button"
                                        className="beStudio__button"
                                        onClick={closeAmenityPicker}
                                        disabled={Boolean(rcAmenitySavingById?.[rcId])}
                                      >
                                        {bookingEngineStudio.actions.close}
                                      </button>
                                    </div>

                                    <div className="beStudio__modalBody beStudio__modalBody--amenities" inert={rcAmenitySavingById?.[rcId] ? "" : undefined}>
                                      <div className="beStudio__modalGrid beStudio__amenityModalGrid">
                                        <div className="beStudio__modalSection beStudio__amenityPickerPanel">
                                          <div className="beStudio__fieldGrid">
                                            <div>
                                              <label className="beStudio__label">
                                                {bookingEngineStudio.rooms.searchAmenities}
                                              </label>
                                              <input data-demo-view={true}
                                                className="beStudio__input"
                                                value={amenityPickerQuery}
                                                onChange={(event) =>
                                                  setAmenityPickerQuery(event.target.value)
                                                }
                                                placeholder={bookingEngineStudio.rooms.searchAmenities}
                                              />
                                            </div>

                                            <div>
                                              <label className="beStudio__label">
                                                {bookingEngineStudio.rooms.allCategories}
                                              </label>
                                              <select data-demo-view={true}
                                                className="beStudio__select"
                                                value={amenityPickerCategory}
                                                onChange={(event) =>
                                                  setAmenityPickerCategory(event.target.value)
                                                }
                                              >
                                                <option value="">
                                                  {bookingEngineStudio.rooms.allCategories}
                                                </option>
                                                {categories.map((category) => (
                                                  <option key={category} value={category}>
                                                    {category}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                          </div>

                                          <div className="beStudio__amenityResultList">
                                            {amenitiesLoading ? (
                                              <div className="beStudio__emptyCopy">
                                                {bookingEngineStudio.status.loading}
                                              </div>
                                            ) : (
                                              (() => {
                                                const query = (amenityPickerQuery || "").trim().toLowerCase();
                                                const category = String(amenityPickerCategory || "");
                                                const rows = (amenitiesAll || [])
                                                  .filter((item) =>
                                                    category
                                                      ? String(item.category) === category
                                                      : true
                                                  )
                                                  .filter((item) => {
                                                    if (!query) return true;
                                                    const label = amenityLabel(item).toLowerCase();
                                                    const code = String(item.code || "").toLowerCase();
                                                    return label.includes(query) || code.includes(query);
                                                  })
                                                  .sort(
                                                    (a, b) =>
                                                      (Number(a.sort_order) || 0) -
                                                      (Number(b.sort_order) || 0)
                                                  );

                                                if (!rows.length) {
                                                  return (
                                                    <div className="beStudio__emptyState">
                                                      <div className="beStudio__emptyCopy">
                                                        {bookingEngineStudio.rooms.noAmenitiesMatch}
                                                      </div>
                                                    </div>
                                                  );
                                                }

                                                return rows.slice(0, 400).map((item) => {
                                                  const selected = isAmenitySelected(rcId, item.id);
                                                  return (
                                                    <div key={item.id} className="beStudio__infoItem beStudio__amenityOption">
                                                      <div className="beStudio__amenityOptionText">
                                                        <span className="beStudio__infoItemLabel">
                                                          {item.category ||
                                                            bookingEngineStudio.rooms.allCategories}
                                                        </span>
                                                        <span className="beStudio__infoItemValue">
                                                          {amenityLabel(item)}
                                                        </span>
                                                      </div>
                                                      <div className="beStudio__actionRow beStudio__amenityOptionAction">
                                                        <button
                                                          type="button"
                                                          className={`beStudio__button ${
                                                            selected
                                                              ? "beStudio__button--danger"
                                                              : "beStudio__button--primary"
                                                          }`}
                                                          onClick={() =>
                                                            selected
                                                              ? removeAmenity(rcId, item.id)
                                                              : addAmenity(rcId, item.id)
                                                          }
                                                        >
                                                          {selected
                                                            ? bookingEngineStudio.actions.remove
                                                            : bookingEngineStudio.actions.add}
                                                        </button>
                                                      </div>
                                                    </div>
                                                  );
                                                });
                                              })()
                                            )}
                                          </div>
                                        </div>

                                        <div className="beStudio__modalSection beStudio__amenitySelectedPanel">
                                          <div className="beStudio__sectionEyebrow">
                                            {bookingEngineStudio.rooms.selectedTitle}
                                          </div>
                                          <h4 className="beStudio__sectionTitle">
                                            {uniqNums(rcAmenityIdsByRcId?.[rcId] || []).length}
                                          </h4>

                                          <div className="beStudio__amenityCloud beStudio__amenityCloud--selected" style={{ marginTop: "16px" }}>
                                            {uniqNums(rcAmenityIdsByRcId?.[rcId] || [])
                                              .map((id) =>
                                                amenitiesAll.find((item) => Number(item.id) === Number(id))
                                              )
                                              .filter(Boolean)
                                              .map((item) => (
                                                <button
                                                  key={item.id}
                                                  type="button"
                                                  className="beStudio__pillButton beStudio__amenityRemovePill"
                                                  onClick={() => removeAmenity(rcId, item.id)}
                                                >
                                                  <span>{amenityLabel(item)}</span>
                                                  <span className="beStudio__pillButtonClose" aria-hidden="true">
                                                    x
                                                  </span>
                                                </button>
                                              ))}

                                            {uniqNums(rcAmenityIdsByRcId?.[rcId] || []).length === 0 ? (
                                              <div className="beStudio__emptyCopy">
                                                {bookingEngineStudio.rooms.noAmenities}
                                              </div>
                                            ) : null}
                                          </div>

                                        </div>
                                      </div>
                                    </div>
                                    <div className="beStudio__modalFooter">
                                            <button
                                              type="button"
                                              className="beStudio__button beStudio__button--danger"
                                              disabled={
                                                uniqNums(rcAmenityIdsByRcId?.[rcId] || []).length === 0
                                              }
                                              onClick={() =>
                                                setRcAmenityIdsByRcId((prev) => ({
                                                  ...(prev || {}),
                                                  [rcId]: [],
                                                }))
                                              }
                                            >
                                              {bookingEngineStudio.rooms.clearAmenities}
                                            </button>
                                            <button
                                              type="button"
                                              className="beStudio__button beStudio__button--primary"
                                              disabled={!!rcAmenitySavingById?.[rcId]}
                                              onClick={async () => {
                                                const saved = await saveRoomCategoryAmenities(rcId);
                                                if (saved) closeAmenityPicker(false);
                                              }}
                                            >
                                              {rcAmenitySavingById?.[rcId]
                                                ? bookingEngineStudio.actions.saving
                                                : bookingEngineStudio.rooms.saveAmenitiesAndClose}
                                            </button>
                                          </div>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <div className="beStudio__roomPanel">
                              {beRoomCategoryCopyLoading && !beRoomCategoryCopyLoaded ? (
                                <div className="beStudio__emptyState">
                                  <div className="beStudio__emptyCopy">
                                    {bookingEngineStudio.rooms.copyLoading}
                                  </div>
                                </div>
                              ) : (
                                (() => {
                                  const row =
                                    beRoomCategoryCopyByRcId?.[rcId] ||
                                    buildBeRoomCategoryCopyDefault(rc);
                                  const previewLanguage =
                                    normalizeBookingEngineEditorLanguage(
                                      bookingEngineContentLanguage
                                    );
                                  const disabled = !!beRoomCategoryCopySavingByRcId?.[rcId];

                                  return (
                                    <div className="beStudio__roomCopyStack">
                                      <div className="beStudio__cardHeader">
                                        <div>
                                          <div className="beStudio__sectionEyebrow">
                                            {bookingEngineStudio.rooms.copyTitle}
                                          </div>
                                          <h4 className="beStudio__sectionTitle">
                                            {bookingEngineStudio.rooms.previewTitle}
                                          </h4>
                                          <div className="beStudio__modalCopy" style={{ marginTop: "8px" }}>
                                            {bookingEngineStudio.rooms.previewHint}
                                          </div>
                                        </div>

                                        <button data-demo-view={true}
                                          type="button"
                                          className="beStudio__button"
                                          disabled={disabled}
                                          onClick={() => openRoomCopyEditor(rc, previewLanguage)}
                                        >
                                          {demoModeActive ? demoCopy.view : bookingEngineStudio.actions.openEditor}
                                        </button>
                                      </div>

                                      {renderBookingEngineRoomCopyPreview(
                                        rc,
                                        row,
                                        previewLanguage,
                                        mainImg
                                      )}

                                      <div>
                                        <div className="beStudio__sectionEyebrow">
                                          {bookingEngineStudio.rooms.languageTitle}
                                        </div>
                                        <div className="beStudio__modalCopy" style={{ marginTop: "8px" }}>
                                          {bookingEngineStudio.rooms.languageHint}
                                        </div>

                                        <div
                                          className="beStudio__languageGrid"
                                          style={{ gridTemplateColumns: "1fr", marginTop: "16px" }}
                                        >
                                          {BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS.map((option) => {
                                            const preview = buildRoomCopyPreviewData(
                                              rc,
                                              row,
                                              option.id
                                            );
                                            const configured = roomCopyLanguageIsConfigured(
                                              rc,
                                              row,
                                              option.id
                                            );

                                            return (
                                              <button data-demo-view={true}
                                                key={`${rcId}-room-copy-${option.id}`}
                                                type="button"
                                                className="beStudio__languageCardButton"
                                                disabled={disabled}
                                                onClick={() => openRoomCopyEditor(rc, option.id)}
                                              >
                                                <div className="beStudio__cardHeader">
                                                  <div>
                                                    <div className="beStudio__languageCardTitle">
                                                      {bookingEngineStudio.rooms.editorTitle} ({option.shortLabel})
                                                    </div>
                                                    <div className="beStudio__languageCardSnippet">
                                                      {preview.displayName ||
                                                        bookingEngineStudio.rooms.previewEmpty}
                                                    </div>
                                                  </div>

                                                  <span
                                                    className={`beStudio__chip ${
                                                      configured ? "beStudio__chip--accent" : ""
                                                    }`}
                                                  >
                                                    {configured
                                                      ? bookingEngineStudio.status.configured
                                                      : bookingEngineStudio.status.notConfigured}
                                                  </span>
                                                </div>

                                                <div className="beStudio__languageCardSnippet">
                                                  {preview.shortDescription ||
                                                    preview.specsLine ||
                                                    bookingEngineStudio.rooms.previewEmpty}
                                                </div>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()
                              )}
                            </div>
                          )}
                        </section>
                      );
                    })}
                    <div className="beStudio__roomGrid">
                      {!roomCategories.some((rc) => String(rc.name || rc.id).toLocaleLowerCase().includes(bookingCategorySearch.trim().toLocaleLowerCase())) && <p className="beStudio__emptyCopy">{bookingSettingsCopy.noCategoriesMatch}</p>}
                      {roomCategories.filter((rc) => String(rc.name || rc.id).toLocaleLowerCase().includes(bookingCategorySearch.trim().toLocaleLowerCase())).map((rc) => {
                        const rcId = Number(rc.id);
                        const imgs = roomCategoryImages?.[rcId] || [];
                        const mainImg = imgs.find((item) => item.kind === "cover") || imgs[0] || null;
                        const count = imgs.length;
                        const selectedAmenityIds = rcAmenityIdsByRcId?.[rcId] || [];
                        const selected = bookingRoomCategoryId === rcId;
                        return (
                          <article key={rc.id} className="beStudio__roomCard" data-room-category-id={rcId} data-selected={selected}>
                            <div
                              className="beStudio__roomMedia"
                              style={mainImg?.public_url ? {
                                backgroundImage: `linear-gradient(180deg, rgba(15, 23, 42, 0.08), rgba(15, 23, 42, 0.64)), url(${mainImg.public_url})`,
                              } : undefined}
                            >
                              <div className="beStudio__roomMediaContent">
                                <div className="beStudio__chipRow">
                                  <span className="beStudio__chip beStudio__chip--accent">{count}/10 {bookingEngineStudio.rooms.imagesLabel}</span>
                                  <span className="beStudio__chip">{selectedAmenityIds.length} {bookingEngineStudio.rooms.selectedAmenities}</span>
                                  <span className="beStudio__chip">
                                    {beRoomCategoryCopyByRcId?.[rcId]?.id ? bookingEngineStudio.status.copyReady : bookingEngineStudio.status.copyMissing}
                                  </span>
                                </div>
                                <h4 className="beStudio__roomTitle">{rc.name || `Room category #${rc.id}`}</h4>
                                <p className="beStudio__roomSubtitle">
                                  {count} {bookingEngineStudio.rooms.imagesLabel.toLowerCase()}{demoModeActive && count > 0 ? ` · ${demoCopy.media}` : ""}
                                </p>
                              </div>
                              <button
                                data-demo-view={true}
                                type="button"
                                className="bookingEngineSettings__roomExpand"
                                aria-expanded={selected}
                                aria-controls="booking-room-category-editor"
                                aria-label={`${bookingSettingsCopy.configureRoom}: ${rc.name || rc.id}`}
                                onClick={(event) => openBookingRoomEditor(rcId, event.currentTarget)}
                              >
                                {bookingSettingsCopy.configureRoom}
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </>
                )}
              </section>
            </div>
          </div>

          {bookingInstallGuideOpen ? (
            <div
              className="beStudio__modalOverlay"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setBookingInstallGuideOpen(false);
                }
              }}
            >
              <div
                className="beStudio__modal beStudio__modal--wide beStudio__installGuideModal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="booking-install-guide-title"
                tabIndex={-1}
              >
                <div className="beStudio__modalHeader">
                  <div>
                    <div className="beStudio__modalEyebrow">
                      {bookingEngineStudio.launch.installGuide.eyebrow}
                    </div>
                    <h3 id="booking-install-guide-title" className="beStudio__modalTitle">
                      {bookingEngineStudio.launch.installGuide.title}
                    </h3>
                    <div className="beStudio__modalCopy">
                      {bookingEngineStudio.launch.installGuide.subtitle}
                    </div>
                  </div>
                  <button data-demo-view={true}
                    type="button"
                    className="beStudio__button"
                    onClick={() => setBookingInstallGuideOpen(false)}
                  >
                    {bookingEngineStudio.actions.close}
                  </button>
                </div>

                <div className="beStudio__modalBody beStudio__installGuideBody">
                  <div className="beStudio__installGuideNotice">
                    {demoModeActive && <p>{demoCopy.inert}</p>}
                    <span className="beStudio__installGuideNoticeTitle">
                      {bookingEngineStudio.launch.installGuide.beforeTitle}
                    </span>
                    <span className="beStudio__installGuideNoticeCopy">
                      {bookingEngineStudio.launch.installGuide.beforeCopy}
                    </span>
                  </div>

                  <div
                    className="beStudio__installGuideTabs"
                    role="tablist"
                    aria-label={bookingEngineStudio.launch.installGuide.title}
                  >
                    {bookingInstallGuideTabs.map((tab) => {
                      const isActive = tab.id === activeBookingInstallGuide.id;
                      return (
                        <button data-demo-view={true}
                          key={tab.id}
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          className={`beStudio__installGuideTab ${
                            isActive ? "beStudio__installGuideTab--active" : ""
                          }`}
                          onClick={() => {
                            setBookingInstallGuideTab(tab.id);
                            setBookingInstallCopiedTab("");
                          }}
                        >
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>

                  <section
                    className="beStudio__installGuidePanel"
                    role="tabpanel"
                    aria-live="polite"
                  >
                    <div className="beStudio__installGuidePanelHeader">
                      <div>
                        <h4 className="beStudio__sectionTitle">
                          {activeBookingInstallGuide.title}
                        </h4>
                        <p className="beStudio__installGuidePanelCopy">
                          {activeBookingInstallGuide.copy}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="beStudio__button beStudio__button--primary"
                        onClick={() =>
                          handleCopyBookingInstallSnippet(activeBookingInstallGuide.id)
                        }
                      >
                        {bookingInstallCopiedTab === activeBookingInstallGuide.id
                          ? bookingEngineStudio.launch.installGuide.copied
                          : bookingEngineStudio.launch.installGuide.copyCode}
                      </button>
                    </div>

                    <div className="beStudio__installGuideNote">
                      {activeBookingInstallGuide.note}
                    </div>

                    <pre className="beStudio__installGuideCode">
                      <code>
                        {bookingInstallSnippets[activeBookingInstallGuide.id]}
                      </code>
                    </pre>
                  </section>
                </div>
              </div>
            </div>
          ) : null}

          {beProfileOpen ? (
            <div
              className="beStudio__modalOverlay"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closeBookingProfile();
                }
              }}
            >
              <div className="beStudio__modal beStudio__modal--wide" role="dialog" aria-modal="true" tabIndex={-1} aria-label={bookingEngineStudio.profile.modalTitle} aria-busy={beProfileSaving}>
                <div className="beStudio__modalHeader">
                  <div>
                    <div className="beStudio__modalEyebrow">{bookingEngineStudio.profile.eyebrow}</div>
                    <h3 className="beStudio__modalTitle">{bookingEngineStudio.profile.modalTitle}</h3>
                    <div className="beStudio__modalCopy">
                      {bookingEngineStudio.profile.modalSubtitle}
                      <p className="bookingEngineSettings__saveHint">{bookingSettingsCopy.directSave}</p>
                    </div>
                  </div>

                  <button data-demo-view={true}
                    type="button"
                    className="beStudio__button"
                    onClick={closeBookingProfile}
                    disabled={beProfileSaving}
                  >
                    {bookingEngineStudio.actions.close}
                  </button>
                </div>

                <div className="beStudio__modalBody" inert={beProfileSaving ? "" : undefined}>
                  <div className="beStudio__modalGrid">
                    <div className="beStudio__modalSection">
                      <div className="beStudio__fieldGrid">
                        <div className="beStudio__field--full">
                          <label className="beStudio__label" htmlFor="booking-profile-public_hotel_name">{bookingEngineStudio.profile.hotelName}</label>
                          <input
                            className="beStudio__input" disabled={beProfileSaving}
                            id="booking-profile-public_hotel_name" value={beProfileDraft?.public_hotel_name || ""}
                            onChange={(event) =>
                              setBeProfileDraft((prev) => ({
                                ...(prev || {}),
                                public_hotel_name: event.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="beStudio__field--full">
                          <label className="beStudio__label" htmlFor="booking-profile-address_line">{bookingEngineStudio.profile.addressLine}</label>
                          <input
                            className="beStudio__input" disabled={beProfileSaving}
                            id="booking-profile-address_line" value={beProfileDraft?.address_line || ""}
                            onChange={(event) =>
                              setBeProfileDraft((prev) => ({
                                ...(prev || {}),
                                address_line: event.target.value,
                              }))
                            }
                          />
                        </div>

                        <div>
                          <label className="beStudio__label" htmlFor="booking-profile-city">{bookingEngineStudio.profile.city}</label>
                          <input
                            className="beStudio__input" disabled={beProfileSaving}
                            id="booking-profile-city" value={beProfileDraft?.city || ""}
                            onChange={(event) =>
                              setBeProfileDraft((prev) => ({
                                ...(prev || {}),
                                city: event.target.value,
                              }))
                            }
                          />
                        </div>

                        <div>
                          <label className="beStudio__label" htmlFor="booking-profile-postal_code">{bookingEngineStudio.profile.postalCode}</label>
                          <input
                            className="beStudio__input" disabled={beProfileSaving}
                            id="booking-profile-postal_code" value={beProfileDraft?.postal_code || ""}
                            onChange={(event) =>
                              setBeProfileDraft((prev) => ({
                                ...(prev || {}),
                                postal_code: event.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="beStudio__field--full">
                          <label className="beStudio__label" htmlFor="booking-profile-country">{bookingEngineStudio.profile.country}</label>
                          <input
                            className="beStudio__input" disabled={beProfileSaving}
                            id="booking-profile-country" value={beProfileDraft?.country || ""}
                            onChange={(event) =>
                              setBeProfileDraft((prev) => ({
                                ...(prev || {}),
                                country: event.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="beStudio__field--full">
                          <label className="beStudio__label">{bookingEngineStudio.profile.phones}</label>
                          <div className="beStudio__chipRow">
                            {(Array.isArray(beProfileDraft?.phones) ? beProfileDraft.phones : []).map(
                              (phone, index) => (
                                <button
                                  key={`${phone}_${index}`}
                                  type="button"
                                  className="beStudio__pillButton" disabled={beProfileSaving}
                                  onClick={() => {
                                    const current = Array.isArray(beProfileDraft?.phones)
                                      ? beProfileDraft.phones
                                      : [];
                                    const next = current.filter((_, idx) => idx !== index);
                                    setBeProfileDraft((prev) => ({ ...(prev || {}), phones: next }));
                                  }}
                                >
                                  {phone} x
                                </button>
                              )
                            )}
                          </div>
                          <div className="beStudio__actionRow" style={{ marginTop: "12px" }}>
                            <input
                              className="beStudio__input" disabled={beProfileSaving}
                              placeholder={bookingEngineStudio.profile.phonePlaceholder}
                              value={beProfilePhoneInput}
                              onChange={(event) => setBeProfilePhoneInput(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key !== "Enter") return;
                                event.preventDefault();
                                const value = String(beProfilePhoneInput || "").trim();
                                if (!value) return;
                                const current = Array.isArray(beProfileDraft?.phones)
                                  ? beProfileDraft.phones
                                  : [];
                                setBeProfileDraft((prev) => ({
                                  ...(prev || {}),
                                  phones: [...current, value],
                                }));
                                setBeProfilePhoneInput("");
                              }}
                            />
                            <button
                              type="button"
                              className="beStudio__button"
                              disabled={beProfileSaving}
                              onClick={() => {
                                const value = String(beProfilePhoneInput || "").trim();
                                if (!value) return;
                                const current = Array.isArray(beProfileDraft?.phones)
                                  ? beProfileDraft.phones
                                  : [];
                                setBeProfileDraft((prev) => ({
                                  ...(prev || {}),
                                  phones: [...current, value],
                                }));
                                setBeProfilePhoneInput("");
                              }}
                            >
                              {bookingEngineStudio.actions.add}
                            </button>
                          </div>
                        </div>

                        <div className="beStudio__field--full">
                          <label className="beStudio__label">{bookingEngineStudio.profile.emails}</label>
                          <div className="beStudio__chipRow">
                            {(Array.isArray(beProfileDraft?.emails) ? beProfileDraft.emails : []).map(
                              (email, index) => (
                                <button
                                  key={`${email}_${index}`}
                                  type="button"
                                  className="beStudio__pillButton" disabled={beProfileSaving}
                                  onClick={() => {
                                    const current = Array.isArray(beProfileDraft?.emails)
                                      ? beProfileDraft.emails
                                      : [];
                                    const next = current.filter((_, idx) => idx !== index);
                                    setBeProfileDraft((prev) => ({ ...(prev || {}), emails: next }));
                                  }}
                                >
                                  {email} x
                                </button>
                              )
                            )}
                          </div>
                          <div className="beStudio__actionRow" style={{ marginTop: "12px" }}>
                            <input
                              className="beStudio__input" disabled={beProfileSaving}
                              placeholder={bookingEngineStudio.profile.emailPlaceholder}
                              value={beProfileEmailInput}
                              onChange={(event) => setBeProfileEmailInput(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key !== "Enter") return;
                                event.preventDefault();
                                const value = String(beProfileEmailInput || "").trim();
                                if (!value) return;
                                const current = Array.isArray(beProfileDraft?.emails)
                                  ? beProfileDraft.emails
                                  : [];
                                setBeProfileDraft((prev) => ({
                                  ...(prev || {}),
                                  emails: [...current, value],
                                }));
                                setBeProfileEmailInput("");
                              }}
                            />
                            <button
                              type="button"
                              className="beStudio__button"
                              disabled={beProfileSaving}
                              onClick={() => {
                                const value = String(beProfileEmailInput || "").trim();
                                if (!value) return;
                                const current = Array.isArray(beProfileDraft?.emails)
                                  ? beProfileDraft.emails
                                  : [];
                                setBeProfileDraft((prev) => ({
                                  ...(prev || {}),
                                  emails: [...current, value],
                                }));
                                setBeProfileEmailInput("");
                              }}
                            >
                              {bookingEngineStudio.actions.add}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="beStudio__modalSection">
                      <div className="beStudio__sectionEyebrow">
                        {bookingEngineStudio.profile.previewTitle}
                      </div>
                      <h4 className="beStudio__sectionTitle">
                        {bookingEngineStudio.profile.hotelInfoHeading}
                      </h4>
                      <div className="beStudio__modalCopy" style={{ marginTop: "8px" }}>
                        {bookingEngineStudio.profile.previewHint}
                      </div>

                      {(() => {
                        const draftPreview = buildBookingEngineProfilePreview(beProfileDraft);

                        return (
                          <div style={{ marginTop: "18px" }}>
                            {renderBookingEngineProfilePlacementPreview(
                              draftPreview,
                              "booking-engine-profile-draft"
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                <div className="beStudio__modalFooter">
                  <button data-demo-view={true}
                    type="button"
                    className="beStudio__button"
                    onClick={closeBookingProfile}
                    disabled={beProfileSaving}
                  >
                    {bookingEngineStudio.actions.cancel}
                  </button>

                  <button
                    type="button"
                    className="beStudio__button beStudio__button--primary"
                    disabled={beProfileSaving}
                    onClick={async () => {
                      if (demoModeActive || isDemoMode() || beProfileSaveRef.current) return;
                      beProfileSaveRef.current = true;
                      setBeProfileSaving(true);
                      try {
                        const token = localStorage.getItem("palantir_token");
                        await axios.put(
                          `${process.env.REACT_APP_API_URL}/api/booking-engine-profiles/${businessUnitId}`,
                          {
                            ...(beProfileDraft || {}),
                          },
                          {
                            headers: { Authorization: `Bearer ${token}` },
                          }
                        );

                        setBeProfileOpen(false);
                        setBeProfileDraft(null);
                        await fetchBeProfile();
                        showBookingEngineToast(bookingEngineStudio.toasts.profileSaved, "success");
                      } catch (err) {
                        console.error("[BOOKING_ENGINE_PROFILE][SAVE] ERROR", err);
                        showBookingEngineToast(
                          err.response?.data?.error || bookingEngineStudio.toasts.profileError,
                          "error"
                        );
                      } finally {
                        beProfileSaveRef.current = false;
                        setBeProfileSaving(false);
                      }
                    }}
                  >
                    {beProfileSaving ? bookingEngineStudio.actions.saving : bookingEngineStudio.actions.saveAndClose}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {roomCopyOpen && roomCopyDraft ? (
            (() => {
              const roomCopyEditorRoom =
                roomCategories.find(
                  (item) => Number(item.id) === Number(roomCopyEditingRcId)
                ) || null;

              if (!roomCopyEditorRoom) return null;

              const roomCopyEditorRcId = Number(roomCopyEditorRoom.id);
              const roomCopyEditorImages = roomCategoryImages?.[roomCopyEditorRcId] || [];
              const roomCopyEditorCover =
                roomCopyEditorImages.find((item) => item.kind === "cover") ||
                roomCopyEditorImages[0] ||
                null;
              const roomCopyLangOption =
                BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS.find(
                  (option) => option.id === roomCopyLangTab
                ) || BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS[0];
              const nameField = roomCopyLangTab === "es" ? "name_es" : "name_en";
              const shortField =
                roomCopyLangTab === "es" ? "short_description_es" : "short_description_en";
              const bedField = roomCopyLangTab === "es" ? "bed_type_es" : "bed_type_en";
              const viewField = roomCopyLangTab === "es" ? "view_es" : "view_en";
              const highlightsField =
                roomCopyLangTab === "es" ? "highlights_es" : "highlights_en";
              const roomCopySaving = !!beRoomCategoryCopySavingByRcId?.[roomCopyEditorRcId];
              const roomCopyEditorBusy = roomCopySaving || roomCopyPrefillLoading;
              const prefillStatusText = roomCopyPrefillLoading
                ? bookingEngineStudio.rooms.prefillLoading
                : roomCopyPrefillNotice?.kind === "success"
                ? bookingEngineStudio.rooms.prefillSuccess.replace("{room}", roomCopyPrefillNotice.room)
                : roomCopyPrefillNotice?.kind === "categoryOnly"
                ? bookingEngineStudio.rooms.prefillCategoryOnly
                : roomCopyPrefillNotice?.kind === "empty"
                ? bookingEngineStudio.rooms.prefillEmpty
                : roomCopyPrefillNotice?.kind === "error"
                ? bookingEngineStudio.rooms.prefillError
                : "";

              return (
                <div
                  className="beStudio__modalOverlay beStudio__modalOverlay--roomCopy"
                  onMouseDown={(event) => {
                    if (event.target === event.currentTarget) {
                      closeRoomCopyEditor();
                    }
                  }}
                >
                  <div className="beStudio__modal beStudio__modal--wide beStudio__modal--roomCopy" role="dialog" aria-modal="true" tabIndex={-1} aria-label={bookingEngineStudio.rooms.editorTitle}>
                    <div className="beStudio__modalHeader">
                      <div>
                        <div className="beStudio__modalEyebrow">
                          {bookingEngineStudio.rooms.copyTitle}
                        </div>
                        <h3 className="beStudio__modalTitle">
                          {roomCopyEditorRoom.name || `Room category #${roomCopyEditorRoom.id}`} (
                          {roomCopyLangOption.shortLabel})
                        </h3>
                        <div className="beStudio__modalCopy">
                          {bookingEngineStudio.rooms.editorSubtitle}
                          <p className="bookingEngineSettings__saveHint">{bookingSettingsCopy.directSave}</p>
                        </div>
                      </div>

                      <div className="beStudio__actionRow">
                        <div className="beStudio__targetSwitch">
                          {BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS.map((option) => (
                            <button data-demo-view={true}
                              key={`room-copy-target-${option.id}`}
                              type="button"
                              className={`beStudio__targetButton ${
                                roomCopyLangTab === option.id
                                  ? "beStudio__targetButton--active"
                                  : ""
                              }`}
                              disabled={roomCopyEditorBusy}
                              onClick={() => setRoomCopyLangTab(option.id)}
                            >
                              {option.shortLabel}
                            </button>
                          ))}
                        </div>

                        <button data-demo-view={true}
                          type="button"
                          className="beStudio__button"
                          disabled={roomCopySaving}
                          onClick={closeRoomCopyEditor}
                        >
                          {bookingEngineStudio.actions.close}
                        </button>
                      </div>
                    </div>

                    <div className="beStudio__modalBody">
                      <section
                        className="beStudio__roomCopyPrefill"
                        aria-labelledby="booking-room-copy-prefill-title"
                      >
                        <div className="beStudio__roomCopyPrefillContent">
                          <h4 id="booking-room-copy-prefill-title" className="beStudio__roomCopyPrefillTitle">
                            {bookingEngineStudio.rooms.prefillTitle}
                          </h4>
                          <div className="beStudio__hint" id="booking-room-copy-prefill-hint">
                            {bookingEngineStudio.rooms.prefillHint}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="beStudio__button beStudio__button--primary"
                          disabled={roomCopyEditorBusy}
                          aria-describedby="booking-room-copy-prefill-hint"
                          onClick={prefillRoomCopyEditor}
                        >
                          {roomCopyPrefillLoading
                            ? bookingEngineStudio.rooms.prefillLoading
                            : bookingEngineStudio.rooms.prefillAction}
                        </button>
                        {prefillStatusText ? (
                          <p
                            className={`beStudio__roomCopyPrefillStatus ${roomCopyPrefillNotice?.kind === "error" ? "beStudio__hint--error" : ""}`}
                            role={roomCopyPrefillNotice?.kind === "error" ? "alert" : "status"}
                          >
                            {prefillStatusText}
                          </p>
                        ) : null}
                      </section>
                      <div className="beStudio__modalGrid">
                        <div className="beStudio__modalSection">
                          <div className="beStudio__cardHeader" style={{ marginBottom: "16px" }}>
                            <div>
                              <div className="beStudio__sectionEyebrow">
                                {bookingEngineStudio.rooms.sharedTitle}
                              </div>
                              <div className="beStudio__modalCopy" style={{ marginTop: "6px" }}>
                                {bookingEngineStudio.rooms.sharedHint}
                              </div>
                            </div>
                          </div>

                          <fieldset
                            className="beStudio__editorSurface"
                            disabled={roomCopyEditorBusy}
                            aria-busy={roomCopyPrefillLoading}
                            aria-label={bookingEngineStudio.rooms.editorTitle}
                            style={{ minWidth: 0, marginInline: 0 }}
                          >
                            <div className="beStudio__fieldGrid">
                              <div>
                                <label className="beStudio__label">
                                  {bookingEngineStudio.rooms.size}
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  className="beStudio__input"
                                  value={roomCopyDraft?.size_m2 || ""}
                                  disabled={roomCopySaving}
                                  onChange={(event) =>
                                    setRoomCopyDraft((prev) => ({
                                      ...(prev || {}),
                                      size_m2: event.target.value,
                                    }))
                                  }
                                />
                              </div>

                              <div>
                                <label className="beStudio__label">
                                  {bookingEngineStudio.rooms.beds}
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  className="beStudio__input"
                                  value={roomCopyDraft?.beds_count || ""}
                                  disabled={roomCopySaving}
                                  onChange={(event) =>
                                    setRoomCopyDraft((prev) => ({
                                      ...(prev || {}),
                                      beds_count: event.target.value,
                                    }))
                                  }
                                />
                              </div>

                              <div>
                                <label className="beStudio__label">
                                  {bookingEngineStudio.rooms.maxPax}
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  className="beStudio__input"
                                  value={roomCopyDraft?.max_pax || ""}
                                  disabled={roomCopySaving}
                                  onChange={(event) =>
                                    setRoomCopyDraft((prev) => ({
                                      ...(prev || {}),
                                      max_pax: event.target.value,
                                    }))
                                  }
                                />
                              </div>

                              <div className="beStudio__field--full">
                                <div className="beStudio__sectionEyebrow">
                                  {bookingEngineStudio.rooms.languageTitle}
                                </div>
                                <div className="beStudio__hint" style={{ marginTop: "6px" }}>
                                  {bookingEngineStudio.rooms.languageHint}
                                </div>
                              </div>

                              <div className="beStudio__field--full">
                                <label className="beStudio__label">
                                  {roomCopyLangTab === "es"
                                    ? bookingEngineStudio.rooms.nameEs
                                    : bookingEngineStudio.rooms.nameEn}
                                </label>
                                <input
                                  className="beStudio__input"
                                  value={roomCopyDraft?.[nameField] || ""}
                                  disabled={roomCopySaving}
                                  onChange={(event) =>
                                    setRoomCopyDraft((prev) => ({
                                      ...(prev || {}),
                                      [nameField]: event.target.value,
                                    }))
                                  }
                                />
                              </div>

                              <div className="beStudio__field--full">
                                <label className="beStudio__label">
                                  {roomCopyLangTab === "es"
                                    ? bookingEngineStudio.rooms.shortEs
                                    : bookingEngineStudio.rooms.shortEn}
                                </label>
                                <textarea
                                  rows={4}
                                  className="beStudio__textarea"
                                  value={roomCopyDraft?.[shortField] || ""}
                                  disabled={roomCopySaving}
                                  onChange={(event) =>
                                    setRoomCopyDraft((prev) => ({
                                      ...(prev || {}),
                                      [shortField]: event.target.value,
                                    }))
                                  }
                                />
                              </div>

                              <div>
                                <label className="beStudio__label">
                                  {roomCopyLangTab === "es"
                                    ? bookingEngineStudio.rooms.bedTypeEs
                                    : bookingEngineStudio.rooms.bedTypeEn}
                                </label>
                                <input
                                  className="beStudio__input"
                                  value={roomCopyDraft?.[bedField] || ""}
                                  disabled={roomCopySaving}
                                  onChange={(event) =>
                                    setRoomCopyDraft((prev) => ({
                                      ...(prev || {}),
                                      [bedField]: event.target.value,
                                    }))
                                  }
                                />
                              </div>

                              <div>
                                <label className="beStudio__label">
                                  {roomCopyLangTab === "es"
                                    ? bookingEngineStudio.rooms.viewEs
                                    : bookingEngineStudio.rooms.viewEn}
                                </label>
                                <input
                                  className="beStudio__input"
                                  value={roomCopyDraft?.[viewField] || ""}
                                  disabled={roomCopySaving}
                                  onChange={(event) =>
                                    setRoomCopyDraft((prev) => ({
                                      ...(prev || {}),
                                      [viewField]: event.target.value,
                                    }))
                                  }
                                />
                              </div>

                              <div className="beStudio__field--full">
                                <label className="beStudio__label">
                                  {roomCopyLangTab === "es"
                                    ? bookingEngineStudio.rooms.highlightsEs
                                    : bookingEngineStudio.rooms.highlightsEn}
                                </label>
                                <textarea
                                  rows={4}
                                  className="beStudio__textarea"
                                  value={roomCopyDraft?.[highlightsField] || ""}
                                  disabled={roomCopySaving}
                                  onChange={(event) =>
                                    setRoomCopyDraft((prev) => ({
                                      ...(prev || {}),
                                      [highlightsField]: event.target.value,
                                    }))
                                  }
                                />
                              </div>
                            </div>
                          </fieldset>
                        </div>

                        <div className="beStudio__modalSection">
                          <div className="beStudio__sectionEyebrow">
                            {bookingEngineStudio.rooms.previewTitle}
                          </div>
                          <h4 className="beStudio__sectionTitle">
                            {bookingEngineStudio.rooms.editorTitle} ({roomCopyLangOption.shortLabel})
                          </h4>
                          <div className="beStudio__modalCopy" style={{ marginTop: "6px" }}>
                            {bookingEngineStudio.rooms.previewHint}
                          </div>

                          <div style={{ marginTop: "16px" }}>
                            {renderBookingEngineRoomCopyPreview(
                              roomCopyEditorRoom,
                              roomCopyDraft,
                              roomCopyLangTab,
                              roomCopyEditorCover
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="beStudio__modalFooter">
                      <button data-demo-view={true}
                        type="button"
                        className="beStudio__button"
                        disabled={roomCopySaving}
                        onClick={closeRoomCopyEditor}
                      >
                        {bookingEngineStudio.actions.cancel}
                      </button>

                      <button
                        type="button"
                        className="beStudio__button beStudio__button--primary"
                        disabled={roomCopyEditorBusy}
                        onClick={async () => {
                          if (roomCopyRequestRef.current.controller) return;
                          const saved = await saveBeRoomCategoryCopy(
                            roomCopyEditorRoom,
                            roomCopyDraft
                          );

                          if (saved) {
                            closeRoomCopyEditor(true);
                          }
                        }}
                      >
                        {roomCopySaving
                          ? bookingEngineStudio.actions.saving
                          : bookingEngineStudio.actions.saveAndClose}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : null}

          {guestEmailOpen ? (
              <div
                className="beStudio__modalOverlay beStudio__modalOverlay--guestEmail"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget) {
                    setGuestEmailOpen(false);
                    setGuestEmailDraft(null);
                  }
                }}
              >
                <div
                  className="beStudio__modal beStudio__modal--wide beStudio__modal--guestEmail" role="dialog" aria-modal="true" tabIndex={-1} aria-label={`${bookingEngineStudio.emails.editorTitle} (${guestEmailLangTab.toUpperCase()})`}

                >
                <div className="beStudio__modalHeader">
                  <div>
                    <div className="beStudio__modalEyebrow">{bookingEngineStudio.emails.eyebrow}</div>
                    <h3 className="beStudio__modalTitle">
                      {bookingEngineStudio.emails.editorTitle} ({guestEmailLangTab.toUpperCase()})
                    </h3>
                    <div className="beStudio__modalCopy">
                      {bookingEngineStudio.emails.editorSubtitle}
                      <p className="bookingEngineSettings__saveHint">{bookingSettingsCopy.pendingSave}</p>
                    </div>
                  </div>

                  <button data-demo-view={true}
                    type="button"
                    className="beStudio__button"
                    onClick={() => {
                      setGuestEmailOpen(false);
                      setGuestEmailDraft(null);
                    }}
                  >
                    {bookingEngineStudio.actions.close}
                  </button>
                </div>

                <div className="beStudio__modalBody">
                  <div className="beStudio__modalGrid">
                    <div className="beStudio__modalSection">
                      <div className="beStudio__cardHeader" style={{ marginBottom: "16px" }}>
                        <div>
                          <div className="beStudio__sectionEyebrow">
                            {bookingEngineStudio.emails.variablesTitle}
                          </div>
                          <div className="beStudio__modalCopy" style={{ marginTop: "6px" }}>
                            {bookingEngineStudio.emails.variablesHint}
                          </div>
                        </div>

                      </div>

                      <div className="beStudio__variableCloud">
                        {bookingEngineVariableOptions.map((variable) => (
                          <button
                            key={variable.token}
                            type="button"
                            className="beStudio__variableButton"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => insertBookingEngineGuestEmailToken(variable.token)}
                          >
                            {variable.label}
                          </button>
                        ))}
                      </div>

                      <div className="beStudio__editorSurface">
                        <div className="beStudio__fieldGrid">
                          <div className="beStudio__field--full">
                            <label className="beStudio__label">{bookingEngineStudio.emails.subjectLabel}</label>
                            <TemplateTokenComposer
                              ref={guestEmailSubjectRef}
                              className="beStudio__tokenComposer"
                              tokenClassName="beStudio__inlineToken"
                              value={String(guestEmailDraft?.subject || "")}
                              tokenOptions={bookingEngineVariableOptions}
                              placeholder={bookingEngineStudio.emails.subjectLabel}
                              onChange={(nextValue) =>
                                setGuestEmailDraft((prev) => ({
                                  ...(prev || {}),
                                  subject: nextValue,
                                }))
                              }
                              onFocus={() => setGuestEmailInsertTarget("subject")}
                            />
                          </div>

                          <div className="beStudio__field--full">
                            <label className="beStudio__label">{bookingEngineStudio.emails.bodyLabel}</label>
                            <TemplateTokenComposer
                              ref={guestEmailBodyRef}
                              className="beStudio__tokenComposer"
                              tokenClassName="beStudio__inlineToken"
                              multiline
                              value={String(guestEmailDraft?.body || "")}
                              tokenOptions={bookingEngineVariableOptions}
                              placeholder={bookingEngineStudio.emails.bodyLabel}
                              onChange={(nextValue) =>
                                setGuestEmailDraft((prev) => ({
                                  ...(prev || {}),
                                  body: nextValue,
                                }))
                              }
                              onFocus={() => setGuestEmailInsertTarget("body")}
                            />
                            {bookingEngineStudio.emails.bodyHint ? (
                              <div className="beStudio__hint">{bookingEngineStudio.emails.bodyHint}</div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="beStudio__modalSection">
                      {(() => {
                        const bookingEngineEmailPreviewVars = {
                          ...BOOKING_ENGINE_EMAIL_PREVIEW_VARS,
                          TotalPrice: formatSettingsPreviewMoney(
                            1248,
                            baseCurrency,
                            currencyCatalog
                          ),
                          NightlyPrices: replaceSettingsPreviewCurrencyAmounts(
                            BOOKING_ENGINE_EMAIL_PREVIEW_VARS.NightlyPrices,
                            baseCurrency,
                            currencyCatalog
                          ),
                        };
                        const renderedSubject = renderReservationTemplateValue(
                          String(guestEmailDraft?.subject || ""),
                          bookingEngineEmailPreviewVars
                        );
                        const renderedBody = renderReservationTemplateValue(
                          String(guestEmailDraft?.body || ""),
                          bookingEngineEmailPreviewVars
                        );
                        const paragraphs = buildReservationPreviewParagraphs(renderedBody);

                        return (
                          <>
                            <div className="beStudio__sectionEyebrow">
                              {bookingEngineStudio.emails.previewTitle}
                            </div>
                            {demoModeActive && <p className="beStudio__hint">{demoCopy.example}</p>}
                            <h4 className="beStudio__sectionTitle">
                              {bookingEngineStudio.emails.previewSubject}
                            </h4>

                            <div className="beStudio__previewShell" style={{ marginTop: "16px" }}>
                              {demoModeActive && heroBgAsset?.id && (
                                <p className="beStudio__hint">{bookingEngineStudio.status.configured} · {demoCopy.media}</p>
                              )}
                              {heroBgAsset?.public_url ? (
                                <div
                                  className="beStudio__previewHeroImage"
                                  style={{ backgroundImage: `url(${heroBgAsset.public_url})` }}
                                />
                              ) : null}
                              <div className="beStudio__previewHeader">
                                <div className="beStudio__previewSubject">
                                  {renderedSubject || bookingEngineStudio.emails.emptyLanguage}
                                </div>
                              </div>
                              <div className="beStudio__previewBody">
                                {paragraphs.length ? (
                                  paragraphs.map((paragraph, index) => (
                                    <p key={`guest-email-preview-${index}`} className="beStudio__previewParagraph">
                                      {paragraph}
                                    </p>
                                  ))
                                ) : (
                                  <p className="beStudio__previewParagraph">
                                    {bookingEngineStudio.emails.emptyLanguage}
                                  </p>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                <div className="beStudio__modalFooter">
                  <button
                    type="button"
                    className="beStudio__button beStudio__button--danger"
                    onClick={() => {
                      setGuestEmailDraft({ lang: guestEmailLangTab, subject: "", body: "" });
                    }}
                  >
                    {bookingEngineStudio.actions.clearLanguage}
                  </button>

                  <div className="beStudio__actionRow">
                    <button data-demo-view={true}
                      type="button"
                      className="beStudio__button"
                      onClick={() => {
                        setGuestEmailOpen(false);
                        setGuestEmailDraft(null);
                      }}
                    >
                      {bookingEngineStudio.actions.cancel}
                    </button>
                    <button
                      type="button"
                      className="beStudio__button beStudio__button--primary"
                      onClick={() => {
                        const draft = guestEmailDraft || null;
                        const next = {
                          ...ensureGuestEmailTemplates(formData),
                          [guestEmailLangTab]: {
                            subject: String(draft?.subject || ""),
                            body: String(draft?.body || ""),
                          },
                        };

                        setFormData((prev) => ({
                          ...prev,
                          booking_engine_guest_email_templates: next,
                        }));
                        setGuestEmailOpen(false);
                        setGuestEmailDraft(null);
                        showBookingEngineToast(bookingSettingsCopy.applied);
                      }}
                    >
                      {bookingSettingsCopy.apply}
                    </button>
                  </div>
                </div>
                </div>
              </div>
            ) : null}

          {termsOpen ? (
            <div
              className="beStudio__modalOverlay"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setTermsOpen(false);
                  setTermsDraft(null);
                  setTermsEditingId(null);
                }
              }}
            >
              <div className="beStudio__modal" role="dialog" aria-modal="true" tabIndex={-1} aria-label={bookingEngineStudio.terms.editorTitle}>
                <div className="beStudio__modalHeader">
                  <div>
                    <div className="beStudio__modalEyebrow">{bookingEngineStudio.terms.eyebrow}</div>
                    <h3 className="beStudio__modalTitle">{bookingEngineStudio.terms.editorTitle}</h3>
                    <div className="beStudio__modalCopy">
                      {bookingEngineStudio.terms.editorSubtitle}
                      <p className="bookingEngineSettings__saveHint">{bookingSettingsCopy.pendingSave}</p>
                    </div>
                  </div>

                  <div className="beStudio__actionRow">
                    <div className="beStudio__targetSwitch">
                      {BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS.map((option) => (
                        <button data-demo-view={true}
                          key={option.id}
                          type="button"
                          className={`beStudio__targetButton ${
                            termsLangTab === option.id ? "beStudio__targetButton--active" : ""
                          }`}
                          onClick={() => setTermsLangTab(option.id)}
                        >
                          {option.shortLabel}
                        </button>
                      ))}
                    </div>
                    <button data-demo-view={true}
                      type="button"
                      className="beStudio__button"
                      onClick={() => {
                        setTermsOpen(false);
                        setTermsDraft(null);
                        setTermsEditingId(null);
                      }}
                    >
                      {bookingEngineStudio.actions.close}
                    </button>
                  </div>
                </div>

                <div className="beStudio__modalBody">
                  <div className="beStudio__modalGrid">
                    <div className="beStudio__modalSection">
                      <div className="beStudio__fieldGrid">
                        <div>
                          <label className="beStudio__label">{bookingEngineStudio.terms.sortOrder}</label>
                          <input
                            type="number"
                            className="beStudio__input"
                            value={Number(termsDraft?.sort_order) || 0}
                            onChange={(event) =>
                              setTermsDraft((prev) => ({
                                ...(prev || {}),
                                sort_order: Number(event.target.value) || 0,
                              }))
                            }
                          />
                        </div>

                        <div>
                          <label className="beStudio__label">
                            {bookingEngineStudio.terms.titleLabel} ({termsLangTab.toUpperCase()})
                          </label>
                          <input
                            className="beStudio__input"
                            value={termsDraft?.title?.[termsLangTab] || ""}
                            onChange={(event) =>
                              setTermsDraft((prev) => ({
                                ...(prev || {}),
                                title: {
                                  ...((prev && prev.title) || {}),
                                  [termsLangTab]: event.target.value,
                                },
                              }))
                            }
                          />
                        </div>

                        <div className="beStudio__field--full">
                          <label className="beStudio__label">
                            {bookingEngineStudio.terms.textLabel} ({termsLangTab.toUpperCase()})
                          </label>
                          <textarea
                            rows={10}
                            className="beStudio__textarea beStudio__textarea--lg"
                            value={termsDraft?.text?.[termsLangTab] || ""}
                            onChange={(event) =>
                              setTermsDraft((prev) => ({
                                ...(prev || {}),
                                text: {
                                  ...((prev && prev.text) || {}),
                                  [termsLangTab]: event.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="beStudio__modalSection">
                      <div className="beStudio__sectionEyebrow">{bookingEngineStudio.terms.previewTitle}</div>
                      <div className="beStudio__previewCard" style={{ marginTop: "16px" }}>
                        <span className="beStudio__urlLabel">
                          {bookingEngineStudio.terms.titleLabel}
                        </span>
                        <span className="beStudio__urlValue">
                          {termsDraft?.title?.[termsLangTab] || bookingEngineStudio.terms.previewEmpty}
                        </span>
                        <span className="beStudio__urlLabel" style={{ marginTop: "8px" }}>
                          {bookingEngineStudio.terms.textLabel}
                        </span>
                        {buildReservationPreviewParagraphs(termsDraft?.text?.[termsLangTab] || "").length ? (
                          buildReservationPreviewParagraphs(termsDraft?.text?.[termsLangTab] || "").map(
                            (paragraph, index) => (
                              <p key={`term-preview-${index}`} className="beStudio__previewParagraph">
                                {paragraph}
                              </p>
                            )
                          )
                        ) : (
                          <div className="beStudio__textMuted">{bookingEngineStudio.terms.previewEmpty}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="beStudio__modalFooter">
                  <button
                    type="button"
                    className="beStudio__button beStudio__button--danger"
                    disabled={!termsEditingId}
                    onClick={() => {
                      const current = normalizeTerms(formData.booking_engine_terms);
                      const next = current.filter(
                        (item) => String(item.id) !== String(termsEditingId)
                      );
                      setFormData((prev) => ({ ...prev, booking_engine_terms: next }));
                      setTermsOpen(false);
                      setTermsDraft(null);
                      setTermsEditingId(null);
                    }}
                  >
                    {bookingEngineStudio.actions.delete}
                  </button>

                  <div className="beStudio__actionRow">
                    <button data-demo-view={true}
                      type="button"
                      className="beStudio__button"
                      onClick={() => {
                        setTermsOpen(false);
                        setTermsDraft(null);
                        setTermsEditingId(null);
                      }}
                    >
                      {bookingEngineStudio.actions.cancel}
                    </button>
                    <button
                      type="button"
                      className="beStudio__button beStudio__button--primary"
                      onClick={() => {
                        const draft = termsDraft || null;
                        if (!draft?.id) return;

                        const current = normalizeTerms(formData.booking_engine_terms);
                        const index = current.findIndex(
                          (item) => String(item.id) === String(draft.id)
                        );
                        const cleaned = {
                          id: draft.id,
                          sort_order: Number(draft.sort_order) || 0,
                          title: {
                            en: draft.title?.en || "",
                            es: draft.title?.es || "",
                          },
                          text: {
                            en: draft.text?.en || "",
                            es: draft.text?.es || "",
                          },
                        };
                        const next =
                          index >= 0
                            ? current.map((item, itemIndex) =>
                                itemIndex === index ? cleaned : item
                              )
                            : [...current, cleaned];

                        setFormData((prev) => ({ ...prev, booking_engine_terms: next }));
                        setTermsOpen(false);
                        setTermsDraft(null);
                        setTermsEditingId(null);
                      }}
                    >
                      {bookingSettingsCopy.apply}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {roomCategoryImagesModalOpen && roomCategoryImagesModalReady ? (
            <div
              className="beStudio__modalOverlay"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) closeRoomCategoryImagesModal();
              }}
            >
              <div className="beStudio__modal beStudio__modal--wide" role="dialog" aria-modal="true" tabIndex={-1} aria-label={bookingEngineStudio.rooms.imageModalTitle}>
                {(() => {
                  const rcId = Number(roomCategoryImagesModalRcId);
                  const imgs = (roomCategoryImages?.[rcId] || []).slice();
                  const safeIndex = Math.max(
                    0,
                    Math.min(roomCategoryImagesModalIndex, imgs.length - 1)
                  );
                  const current = imgs[safeIndex] || null;

                  return (
                    <>
                      <div className="beStudio__modalHeader">
                        <div>
                          <div className="beStudio__modalEyebrow">
                            {bookingEngineStudio.rooms.imageModalTitle}
                          </div>
                          <h3 className="beStudio__modalTitle">
                            {roomCategories?.find((item) => Number(item.id) === Number(rcId))?.name ||
                              "Room category"}
                          </h3>
                          <div className="beStudio__modalCopy">
                            {bookingEngineStudio.rooms.imageModalSubtitle}
                          </div>
                        </div>

                        <button data-demo-view={true}
                          type="button"
                          className="beStudio__button"
                          onClick={closeRoomCategoryImagesModal}
                        >
                          {bookingEngineStudio.actions.close}
                        </button>
                      </div>

                      <div className="beStudio__modalBody">
                        <div className="beStudio__previewShell">
                          {demoModeActive && <p className="beStudio__hint">{demoCopy.media} · {current?.kind} · #{current?.asset_id}</p>}
                          <div
                            className="beStudio__previewHeroImage bookingEngineSettings__galleryPreview"
                            style={{
                              backgroundSize: "contain",
                              backgroundRepeat: "no-repeat",
                              backgroundImage: current?.public_url ? `url(${current.public_url})` : "none",
                            }}
                          />
                        </div>

                        <div className="beStudio__actionRow" style={{ marginTop: "18px" }}>
                          <button data-demo-view={true}
                            type="button"
                            className="beStudio__button"
                            disabled={safeIndex <= 0}
                            onClick={() =>
                              setRoomCategoryImagesModalIndex((value) =>
                                Math.max(0, (Number(value) || 0) - 1)
                              )
                            }
                          >
                            {bookingEngineStudio.actions.previous}
                          </button>
                          <button data-demo-view={true}
                            type="button"
                            className="beStudio__button"
                            disabled={safeIndex >= imgs.length - 1}
                            onClick={() =>
                              setRoomCategoryImagesModalIndex((value) =>
                                Math.min(imgs.length - 1, (Number(value) || 0) + 1)
                              )
                            }
                          >
                            {bookingEngineStudio.actions.next}
                          </button>
                          {current && current.kind !== "cover" ? (
                            <button
                              type="button"
                              className="beStudio__button beStudio__button--primary"
                              onClick={async () => {
                                if (!current?.asset_id) return;
                                await sendImageToCover(current.asset_id);
                                const refreshed = (roomCategoryImages?.[rcId] || []).slice();
                                const nextIndex = refreshed.findIndex(
                                  (item) => Number(item.asset_id) === Number(current.asset_id)
                                );
                                setRoomCategoryImagesModalIndex(nextIndex >= 0 ? nextIndex : 0);
                              }}
                            >
                              {bookingEngineStudio.actions.setAsCover}
                            </button>
                          ) : null}
                        </div>

                        <div className="beStudio__thumbGrid" style={{ marginTop: "18px" }}>
                          {imgs.map((img, idx) => (
                            <button data-demo-view={true}
                              key={img.asset_id}
                              type="button"
                              className={`beStudio__thumbButton ${
                                idx === safeIndex ? "beStudio__thumbButton--active" : ""
                              }`}
                              style={{ backgroundImage: `url(${img.public_url})` }}
                              aria-label={`${bookingEngineStudio.rooms.imageModalTitle} ${idx + 1}`}
                              aria-pressed={idx === safeIndex}
                              onClick={() => setRoomCategoryImagesModalIndex(idx)}
                            >
                              {demoModeActive ? `${idx + 1} · ${img.kind}` : null}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          ) : null}
          {bookingImageToRemove && (
            <div className="beStudio__modalOverlay" onMouseDown={(event) => {
              if (event.target === event.currentTarget && !bookingImageRemoveRef.current) setBookingImageToRemove(null);
            }}>
              <div className="beStudio__modal bookingEngineSettings__confirmModal" role="alertdialog" aria-modal="true" tabIndex={-1} aria-labelledby="booking-remove-image-title" aria-describedby="booking-remove-image-copy" aria-busy={bookingImageRemoving}>
                <div className="beStudio__modalHeader">
                  <h3 id="booking-remove-image-title" className="beStudio__modalTitle">{bookingSettingsCopy.removeTitle}</h3>
                </div>
                <div className="beStudio__modalBody">
                  <p id="booking-remove-image-copy" className="beStudio__modalCopy">{bookingSettingsCopy.removeCopy}</p>
                </div>
                <div className="beStudio__modalFooter">
                  <button data-demo-view={true} type="button" className="beStudio__button" disabled={bookingImageRemoving} onClick={() => setBookingImageToRemove(null)}>{bookingEngineStudio.actions.cancel}</button>
                  <button type="button" className="beStudio__button beStudio__button--danger" disabled={bookingImageRemoving} onClick={confirmBookingImageRemoval}>{bookingImageRemoving ? bookingSettingsCopy.removing : bookingSettingsCopy.remove}</button>
                </div>
              </div>
            </div>
          )}
        </div>, demoModeActive, demoCopy.readOnly
      )}







      </div>

      {canViewUsersTab && activeTab === "users" && usersRolesFeedback && (
        <div className={`ur-toast${usersRolesFeedback.tone === "error" ? " ur-toast--error" : ""}`} role={usersRolesFeedback.tone === "error" ? "alert" : "status"}>
          <span>{usersRolesFeedback.message}</span>
          <button type="button" onClick={() => setUsersRolesFeedback(null)} aria-label={roleText("Dismiss message")}>×</button>
        </div>
      )}

      {!demoModeActive && canViewUsersTab && usersRolesConfirmation && (
        <div className="ur-modalBackdrop">
          <div className="ur-modal ur-modal--sm ur-modal--confirm" role="alertdialog" aria-modal="true" aria-labelledby="users-roles-confirm-title" aria-describedby="users-roles-confirm-copy" tabIndex={-1} aria-busy={usersRolesBusy}>
            <div className="ur-modalHead"><h3 id="users-roles-confirm-title" className="ur-modalTitle">{usersRolesConfirmation.kind === "status" ? usersRolesConfirmation.user.is_active ? "Deactivate user?" : "Activate user?" : usersRolesConfirmation.kind === "delete-role" ? "Delete role?" : "Permanently delete user?"}</h3></div>
            <div className="ur-modalBody"><p><strong>{usersRolesConfirmation.role?.name || usersRolesConfirmation.user?.name || usersRolesConfirmation.user?.email}</strong></p><p id="users-roles-confirm-copy">{usersRolesConfirmation.kind === "status" ? usersRolesConfirmation.user.is_active ? "This user will lose access. Their profile and existing records will be kept." : "This user will regain access with their existing hotel-role assignments." : usersRolesConfirmation.kind === "delete-role" ? "This permanently removes the role. The server will check whether it can be deleted." : "This cannot be undone. Deletion is only allowed when no historical records exist."}</p></div>
            <div className="ur-modalFoot"><button type="button" className="ur-btn" disabled={usersRolesBusy} onClick={closeUsersRolesDialog}>Cancel</button><button type="button" className="ur-btn ur-btn--dangerGhost" disabled={usersRolesBusy} onClick={confirmUsersRolesAction}>{usersRolesBusy ? "Applying…" : usersRolesConfirmation.kind === "status" ? usersRolesConfirmation.user.is_active ? "Deactivate" : "Activate" : "Delete"}</button></div>
          </div>
        </div>
      )}

      {!demoModeActive && canViewUsersTab && showCorporateUserPopup && canCreateCorporateUsers && (
        <CorporateUserDelegationDialog languageId={languageId} themeId={themeId} onClose={() => setShowCorporateUserPopup(false)} onCreated={() => {
          notifyUsersRoles(corporateUserText(languageId, "Usuario creado correctamente. Puedes enviarle una invitación cuando quieras"), "success");
          fetchUsers();
        }} />
      )}

      {!demoModeActive && canViewUsersTab && showNewUserPopup && canManageUsers && (
        <div className="ur-modalBackdrop">
          <div className="ur-modal ur-modal--md" role="dialog" aria-modal="true" aria-label="Create New User" tabIndex={-1} aria-busy={usersRolesBusy}>
            <div className="ur-modalHead">
              <h5 className="ur-modalTitle">Create New User</h5>
              <p className="ur-modalSub">Define user profile and hotel-role assignments.</p>
            </div>
            <div className="ur-modalBody" inert={usersRolesBusy ? "" : undefined}>

            <div className="mb-3">
              <label className="ur-fieldLabel">Name</label>
              <input
                type="text"
                className="ur-input"
                value={newUserData.name}
                onChange={(e) => setNewUserData(prev => ({ ...prev, name: e.target.value }))}
               disabled={usersRolesBusy} aria-label="Name"/>
            </div>

            <div className="mb-3">
              <label className="ur-fieldLabel">Email</label>
              <input
                type="email"
                className="ur-input"
                value={newUserData.email}
                onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
               disabled={usersRolesBusy} aria-label="Email"/>
            </div>
             
            <div className="mb-4">
              <label className="ur-fieldLabel">Role Assignments by Hotel</label>
              <UserHotelAssignments rows={newUserAssignments} onChange={setNewUserAssignments} options={managementOptions} roles={roles} busy={usersRolesBusy || !managementOptions} languageId={languageId} />
            </div>
            </div>


            {newUserAssignments.some((row) => roles.some((role) => Number(role.id) === Number(row.role_id) && role.permissions?.["corporate_purchasing.access"] === true)) && <p className="ur-notice" role="status">{roleText("Corporate purchasing is enabled automatically for the hotels assigned with a corporate role.")}</p>}

            <div className="ur-modalFoot">
              <button className="ur-btn ur-btn--soft" onClick={closeUsersRolesDialog} disabled={usersRolesBusy}>
                Cancel
              </button>
              <button
                className="ur-btn ur-btn--primary"
                onClick={() => runUsersRolesWrite(async () => {
                  try {
                    if (!newAssignmentsValid) return;
                    const normalizedAssignments = normalizeRoleAssignmentsForPayload(newUserAssignments);
                    if (normalizedAssignments.length === 0) {
                      notifyUsersRoles("Please add at least one valid hotel-role assignment.");
                      return;
                    }

                    const payload = {
                      name: newUserData.name,
                      email: newUserData.email,
                      role_assignments: normalizedAssignments,
                      active_business_unit_id: normalizedAssignments[0]?.business_unit_id || null,
                    };

                    await axios.post(`${process.env.REACT_APP_API_URL}/api/users`, payload);
                    notifyUsersRoles("Usuario creado correctamente. Puedes enviarle una invitación cuando quieras", "success");

                    // Cierra modal y refresca usuarios
                    setShowNewUserPopup(false);
                    setNewUserData({
                      name: "",
                      email: ""
                    });
                    setNewUserAssignments([{ ...EMPTY_USER_ASSIGNMENT }]);

                    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`);
                    setUserList(res.data);
                  } catch (err) {
                      console.error("❌ Error creating user:", err);
                      if (err.response?.status === 409) {
                        notifyUsersRoles("Ya existe un usuario registrado con este correo electrónico");
                      } else {
                        notifyUsersRoles("❌ Failed to create user.");
                      }
                    }

                })}
               disabled={usersRolesBusy || !newAssignmentsValid}>
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {!demoModeActive && canViewUsersTab && showRolePopup && selectedRole && (
        <div className="ur-modalBackdrop">
          <div className="ur-modal ur-modal--lg" role="dialog" aria-modal="true" aria-label={roleText("Edit Role")} tabIndex={-1} aria-busy={usersRolesBusy}>
            <div className="ur-modalHead">
              <h5 className="ur-modalTitle">{roleText("Edit Role")}: {selectedRole.name}</h5>
              <p className="ur-modalSub">{roleText("Update permissions and hotel scope for this role.")}</p>
            </div>
            <div className="ur-modalBody" inert={usersRolesBusy ? "" : undefined}>
            <p className="ur-notice">{roleText("Choose the hotels first, then review permissions by module. Save Changes applies this role to every user who has it.")}</p>

            <div className="mb-3">
              <label className="ur-fieldLabel">{roleText("Assigned Business Units")}</label>
              <CorporatePicker multiple label={roleText("Assigned hotels")} placeholder={roleText("Select hotels...")}
                options={roleHotelOptions} value={selectedRoleBusinessUnitIds} onChange={setSelectedRoleBusinessUnitIds} disabled={usersRolesBusy || !managementOptions} />
            </div>

            {!editedRoleValid && <p className="ur-notice" role="status">{roleText("Select hotels and keep every permission within your access in all affected hotels.")}</p>}
            <RolePermissionFields currentUser={currentUser} permissions={editablePermissions} permissionCeiling={rolePermissionCeiling(managementOptions, selectedRoleBusinessUnitIds, selectedRole)} onChange={setEditablePermissions} busy={usersRolesBusy || !managementOptions || !selectedRoleBusinessUnitIds.length} languageId={languageId} />

                  


            </div>
            <div className="ur-modalFoot">
              <button
                className="ur-btn ur-btn--soft"
                onClick={closeUsersRolesDialog}
               disabled={usersRolesBusy}>{roleText("Cancel")}</button>

              <button
                className="ur-btn ur-btn--primary"
                onClick={() => runUsersRolesWrite(async () => {
                  try {
                    if (!editedRoleValid) return;
                    const updatedPermissions = { ...editablePermissions };

                    await axios.put(`${process.env.REACT_APP_API_URL}/api/roles/${selectedRole.id}`, {
                      ...selectedRole,
                      permissions: updatedPermissions,
                      business_unit_ids: selectedRoleBusinessUnitIds
                    });

                    notifyUsersRoles(roleText("✅ Role updated successfully"), "success");
                    setShowRolePopup(false);
                    setSelectedRoleBusinessUnitIds([]);
                    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/roles`);
                    setRoles(res.data);
                    setManagementRevision((revision) => revision + 1);
                  } catch (err) {
                    console.error("❌ Error updating role:", err);
                    notifyUsersRoles(roleText("Error updating role."));
                  }
                })}
               disabled={usersRolesBusy || !editedRoleValid}>{roleText("Save Changes")}</button>
            </div>
          </div>
        </div>
      )}

      {!demoModeActive && canViewUsersTab && showNewRolePopup && (
        <div className="ur-modalBackdrop">
          <div className="ur-modal ur-modal--lg" role="dialog" aria-modal="true" aria-label={roleText("Create New Role")} tabIndex={-1} aria-busy={usersRolesBusy}>
            <div className="ur-modalHead">
              <h5 className="ur-modalTitle">{roleText("Create New Role")}</h5>
              <p className="ur-modalSub">{roleText("Create a reusable role and assign its hotel scope.")}</p>
            </div>
            <div className="ur-modalBody" inert={usersRolesBusy ? "" : undefined}>
            <p className="ur-notice">{roleText("Choose the hotels and permissions. The role is created only when you press Create Role.")}</p>

            <div className="mb-3">
              <label className="ur-fieldLabel">{roleText("Role Name")}</label>
              <input
                type="text"
                className="ur-input"
                value={newRoleData.name}
                onChange={(e) => setNewRoleData(prev => ({ ...prev, name: e.target.value }))}
               disabled={usersRolesBusy} aria-label={roleText("Role Name")}/>
            </div>

            <div className="mb-3">
              <label className="ur-fieldLabel">{roleText("Description")}</label>
              <textarea
                className="ur-textarea"
                value={newRoleData.description}
                onChange={(e) => setNewRoleData(prev => ({ ...prev, description: e.target.value }))}
               disabled={usersRolesBusy} aria-label={roleText("Description")}/>
            </div>

            <div className="mb-4">
              <label className="ur-fieldLabel">{roleText("Assigned Business Units")}</label>
              <CorporatePicker multiple label={roleText("Assigned hotels")} placeholder={roleText("Select hotels...")}
                options={roleHotelOptions} value={newRoleData.business_unit_ids} onChange={(ids) => setNewRoleData((previous) => ({ ...previous, business_unit_ids: ids }))} disabled={usersRolesBusy || !managementOptions} />
            </div>
            {!newRoleValid && <p className="ur-notice" role="status">{roleText("Select hotels and keep every permission within your access in all affected hotels.")}</p>}
            <RolePermissionFields currentUser={currentUser} permissions={newRolePermissions} permissionCeiling={rolePermissionCeiling(managementOptions, newRoleData.business_unit_ids)} onChange={setNewRolePermissions} busy={usersRolesBusy || !managementOptions || !newRoleData.business_unit_ids.length} languageId={languageId} />
            </div>

            <div className="ur-modalFoot">
              <button className="ur-btn ur-btn--soft" onClick={closeUsersRolesDialog} disabled={usersRolesBusy}>{roleText("Cancel")}</button>
              <button
                className="ur-btn ur-btn--primary"
                onClick={() => runUsersRolesWrite(async () => {
                  try {
                    if (!newRoleValid) return;
                    if (!newRoleData.name.trim()) { notifyUsersRoles(roleText("Please enter a role name.")); return; }
                    // Validate the hotel scope before creating the role.
                    if (!newRoleData.business_unit_ids || newRoleData.business_unit_ids.length === 0) {
                      notifyUsersRoles(roleText("Please, select at least one business unit"));
                      return;
                    }

                    await axios.post(`${process.env.REACT_APP_API_URL}/api/roles`, {
                      ...newRoleData, name: newRoleData.name.trim(),
                      permissions: newRolePermissions
                    });
                    notifyUsersRoles(roleText("✅ Role created"), "success");
                    setShowNewRolePopup(false);
                    setNewRoleData({ name: "", description: "", business_unit_ids: [] });

                    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/roles`);
                    setRoles(res.data);
                    setManagementRevision((revision) => revision + 1);
                  } catch (err) {
                    console.error("❌ Error creating role:", err);
                    notifyUsersRoles(roleText("Error creating role."));
                  }
                })}
               disabled={usersRolesBusy || !newRoleValid || !newRoleData.name.trim()}>{roleText("Create Role")}</button>

            </div>
          </div>
        </div>
      )}

   {!demoModeActive && canViewUsersTab && showUserModal && (
    <div className="ur-modalBackdrop">
      <div className="ur-modal ur-modal--md" role="dialog" aria-modal="true" aria-label="Edit User" tabIndex={-1} aria-busy={usersRolesBusy}>
        <div className="ur-modalHead">
          <h2 className="ur-modalTitle">Edit User</h2>
          <p className="ur-modalSub">Adjust profile and hotel-role assignments.</p>
        </div>
        <div className="ur-modalBody" inert={usersRolesBusy ? "" : undefined}>


          {loadingUser ? (
            <div className="text-center be-subtle">Loading…</div>
          ) : selectedUser ? (
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="ur-fieldLabel">ID</label>
                <div className="ur-input">{selectedUser.id}</div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="ur-fieldLabel">Email</label>
                <div className="ur-input">{selectedUser.email}</div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="ur-fieldLabel">Name</label>
                <input
                  type="text"
                  className="ur-input"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                 disabled={usersRolesBusy || !managementOptions || !selectedUser.management?.can_edit_profile} aria-label="Name"/>
              </div>

              <div className="col-md-12 mb-3">
                <label className="ur-fieldLabel">Role Assignments by Hotel</label>
                <UserHotelAssignments rows={editAssignments} onChange={setEditAssignments} options={managementOptions} roles={roles} user={selectedUser} busy={usersRolesBusy || !managementOptions || !canManageUsers} languageId={languageId} />
              </div>

              <div className="col-md-6 mb-3">
                <label className="ur-fieldLabel">Created At</label>
                <div className="ur-input">
                  {selectedUser.created_at
                    ? new Date(selectedUser.created_at).toLocaleString()
                    : "—"}
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="ur-fieldLabel">Updated At</label>
                <div className="ur-input">
                  {selectedUser.updated_at
                    ? new Date(selectedUser.updated_at).toLocaleString()
                    : "—"}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-danger">No user data found</div>
          )}
        </div>

        <div className="ur-modalFoot">
          <button
            type="button"
            className="ur-btn ur-btn--primary"
            onClick={() => runUsersRolesWrite(async () => {
              try {
                if (!editAssignmentsValid) return;
                const normalizedAssignments = normalizeRoleAssignmentsForPayload(editAssignments);
                if (normalizedAssignments.length === 0) {
                  notifyUsersRoles("Please add at least one valid hotel-role assignment.");
                  return;
                }

                const requestedActiveBu = Number(selectedUser?.active_business_unit_id);
                const active_business_unit_id = normalizedAssignments.some(
                  (row) => row.business_unit_id === requestedActiveBu
                )
                  ? requestedActiveBu
                  : normalizedAssignments[0].business_unit_id;

                const payload = {
                  name: editName,
                  role_assignments: normalizedAssignments,
                  active_business_unit_id,
                };

                const res = await axios.patch(
                  `/api/users/${selectedUser.id}`,
                  payload
                );

                if (res.data?.ok) {
                  console.log("[Users][Edit] User updated:", res.data.user);

                  setUserList(prev =>
                    prev.map(u =>
                      u.id === res.data.user.id ? { ...u, ...res.data.user } : u
                    )
                  );

                  setShowUserModal(false);
                  await fetchUsers();
                } else {
                  notifyUsersRoles("Error updating user: " + (res.data?.error || "unknown"));
                }
              } catch (err) {
                console.error("[Users][Edit] Error updating user:", err);
                notifyUsersRoles(err.response?.data?.error || "Network error while updating user");
              }
            })}
           disabled={usersRolesBusy || !editAssignmentsValid}>
            Save
          </button>

          <button
            type="button"
            className="ur-btn ur-btn--dangerGhost"
            onClick={closeUsersRolesDialog}
           disabled={usersRolesBusy}>
            Close
          </button>
        </div>
      </div>
    </div>
  )}



  {requiredFieldPicker && !isDemoStandaloneTab && (
    <div
      className="reservationStudio__fieldPickerModalOverlay"
      role="presentation"
      onClick={() => setRequiredFieldPicker(null)}
    >
      <div
        className="reservationStudio__fieldPickerModal"
        ref={reservationDialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="required-field-picker-title"
        aria-describedby="required-field-picker-description"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="reservationStudio__fieldPickerModalHeader">
          <div>
            <div className="reservationStudio__cardEyebrow">Pilcore compliance</div>
            <h2 id="required-field-picker-title" className="reservationStudio__fieldPickerModalTitle">
              {requiredFieldPicker.contextTitle}: {requiredFieldPicker.roleLabel}
            </h2>
            <p id="required-field-picker-description" className="reservationStudio__fieldPickerModalCopy">
              {requiredFieldPicker.roleDescription}
            </p>
          </div>
          <div className="reservationStudio__fieldPickerModalActions">
            <button
              type="button"
              className="reservationStudio__fieldPickerAction"
              onClick={clearRequiredFieldsInPicker}
              disabled={demoModeActive}
              title={demoModeActive ? demoCopy.readOnly : undefined}
            >
              Clear
            </button>
            <button
              type="button"
              className="reservationStudio__fieldPickerAction"
              onClick={selectAllRequiredFieldsInPicker}
              disabled={demoModeActive}
              title={demoModeActive ? demoCopy.readOnly : undefined}
            >
              Select all
            </button>
            {!isPilcoreSettingsTab && <button
              type="button"
              className="reservationStudio__fieldPickerAction reservationStudio__fieldPickerAction--primary"
              onClick={() => setRequiredFieldPicker(null)}
            >
              Done
            </button>}
          </div>
        </div>

        <div className="reservationStudio__fieldPickerModalBody">
          <div className="reservationStudio__fieldPickerModalStats">
            <span className="reservationStudio__chip">{requiredFieldPicker.contextBadge}</span>
            <span className="reservationStudio__chip">
              {requiredFieldPickerSelectedFields.length} of {customerFieldOptions.length} selected
            </span>
          </div>

          <div className="reservationStudio__fieldPickerGrid">
            {customerFieldOptions.map((option) => {
              const isSelected = requiredFieldPickerSelectedFields.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`reservationStudio__fieldPickerCard ${
                    isSelected ? "reservationStudio__fieldPickerCard--active" : ""
                  }`}
                  aria-pressed={isSelected}
                  disabled={demoModeActive}
                  title={demoModeActive ? demoCopy.readOnly : undefined}
                  onClick={() => toggleRequiredFieldInPicker(option.value)}
                >
                  <span className="reservationStudio__fieldPickerCardText">
                    <span className="reservationStudio__fieldPickerCardLabel">
                      {option.label}
                    </span>
                  </span>
                  <span className="reservationStudio__fieldPickerCheck" aria-hidden="true">
                    {isSelected ? "✓" : ""}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        {isPilcoreSettingsTab && (
          <div className="reservationSettings__fieldPickerFooter">
            <span>{demoModeActive ? demoCopy.readOnly : "Selected fields make this role required. Use Save changes on the page to apply."}</span>
            <button
              type="button"
              className="reservationStudio__fieldPickerAction reservationStudio__fieldPickerAction--primary"
              onClick={() => setRequiredFieldPicker(null)}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  )}

  {activeTab === "reservation" && reservationAssetToRemove && (
    <div className="reservationStudio__fieldPickerModalOverlay" onClick={() => { if (!reservationAssetRemoving) setReservationAssetToRemove(null); }}>
      <div
        className="reservationStudio__fieldPickerModal reservationSettings__confirmDialog"
        ref={reservationDialogRef}
        tabIndex={-1}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="reservation-remove-visual-title"
        aria-describedby="reservation-remove-visual-copy"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="reservationStudio__fieldPickerModalHeader">
          <div>
            <div className="reservationStudio__cardEyebrow">Email visuals</div>
            <h2 id="reservation-remove-visual-title" className="reservationStudio__fieldPickerModalTitle">Remove {reservationAssetToRemove.label.toLowerCase()}?</h2>
          </div>
        </div>
        <div className="reservationStudio__fieldPickerModalBody">
          <p id="reservation-remove-visual-copy" className="reservationStudio__fieldPickerModalCopy">This visual will stop appearing in confirmation emails immediately. You can upload a replacement at any time.</p>
        </div>
        <div className="reservationSettings__fieldPickerFooter">
          <button type="button" className="reservationStudio__fieldPickerAction" disabled={reservationAssetRemoving} onClick={() => setReservationAssetToRemove(null)}>Cancel</button>
          <button type="button" className="reservationStudio__emailInlineButton reservationStudio__emailInlineButton--danger" disabled={reservationAssetRemoving} onClick={() => removeReservationEmailAsset(reservationAssetToRemove.kind)}>{reservationAssetRemoving ? "Removing…" : "Remove visual"}</button>
        </div>
      </div>
    </div>
  )}






    </div>

    

    

    
  );
};

export default Settings;
